import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const EndUserIdField = z.string()
const IdentityIdField = z.string()
const AIPhoneLineTypeEnum = z.enum(['STABLE', 'ELEVEN_LABS', 'PHONIC'])
const AIPhoneLineOwnerEnum = z.enum(['DEMO', 'CUSTOMER'])
const AIPhoneLineConfigIdField = z.string()
const AIPhoneLineIdField = z.string()
const EnrichedAIPhoneLineConfig = z.object({
  id: AIPhoneLineConfigIdField.uuid(),
  phone_line: AIPhoneLineIdField.uuid(),
  name: z.string().optional(),
  priority: z.number().int().optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  greeting_message: z.string().nullish(),
  default_transfer_number: z.string().nullish(),
  default_transfer_extension: z.number().int().nullish(),
  days_of_week: z.array(z.number().int()).nullish(),
  timezone: z.string().nullish(),
  inactive_dates: z.array(z.string()).nullish(),
  applied_to_transfer_lines: z.boolean().nullish(),
})
const TwilioAccountConfigIdField = z.string()
const PhonicIntelligenceLevelEnum = z.enum(['standard', 'high'])
const PhonicMultilingualModeEnum = z.enum(['auto', 'request', 'initial'])
const PhonicBackgroundNoiseEnum = z.enum(['office', 'call-center', 'coffee-shop'])
const PhonicPronunciationDictionaryEntry = z.object({ word: z.string(), pronunciation: z.string() })
const AddAIPhoneLineViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  number: z.string(),
  type: AIPhoneLineTypeEnum.nullish(),
  owner_type: AIPhoneLineOwnerEnum.nullish(),
  name: z.string().optional(),
  configs: z.array(EnrichedAIPhoneLineConfig),
  twilio_account_config_id: TwilioAccountConfigIdField.nullish(),
  elevenlabs_voice_id: z.string().nullish(),
  elevenlabs_voice_speed: z.number().gte(0.7).lte(1.2).nullish(),
  elevenlabs_voice_similarity: z.number().gte(0).lte(1).nullish(),
  elevenlabs_voice_stability: z.number().gte(0).lte(1).nullish(),
  phonic_voice_id: z.string().nullish(),
  phonic_audio_speed: z.number().gte(0.5).lte(2).nullish(),
  phonic_intelligence_level: PhonicIntelligenceLevelEnum.optional(),
  phonic_is_welcome_message_interruptible: z.boolean().nullish(),
  phonic_min_words_to_interrupt: z.number().int().gte(1).lte(10).nullish(),
  phonic_multilingual_mode: PhonicMultilingualModeEnum.nullish(),
  phonic_enable_assistant_backchannel: z.boolean().nullish(),
  phonic_assistant_backchannel_aggressiveness: z.number().gte(0).lte(1).nullish(),
  phonic_background_noise: PhonicBackgroundNoiseEnum.nullish(),
  phonic_background_noise_level: z.number().gte(0).lte(1).nullish(),
  phonic_boosted_keywords: z.array(z.string()).nullish(),
  phonic_pronunciation_dictionary: z.array(PhonicPronunciationDictionaryEntry).nullish(),
})
const EmptyResponseSerializerEmptyResponse = z
  .object({ status: z.string().default('ok') })
  .partial()
const WorkflowIdField = z.string()
const AddAIProcessSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
  description: z.string().optional(),
})
const AIInteractionTypeEnum = z.enum([
  'SEARCH',
  'CHAT',
  'VOICE',
  'SMS',
  'WHATSAPP',
  'EMAIL',
  'ASSISTANT',
])
const DocumentTemplateIdField = z.string()
const AddAISkillViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
  document_template_ids: z.array(DocumentTemplateIdField).optional(),
})
const AISkillIdField = z.string()
const DraftEnum = z.enum(['DRAFT', 'LIVE', 'ARCHIVED'])
const OrganizationIdField = z.string()
const AISkill = z.object({
  id: AISkillIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  document_templates: z.array(DocumentTemplateIdField),
  code: z.string().regex(/^[a-z0-9-]+$/),
  version_number: z.number().int().gte(-2147483648).lte(2147483647),
  status: DraftEnum,
  name: z.string(),
  description: z.string(),
  content: z.string(),
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
  published: z.string().datetime({ offset: true }).nullish(),
  organization: OrganizationIdField.uuid(),
  created_by: IdentityIdField.nullish(),
})
const AddAISkillViewResponseData = z.object({ skill: AISkill })
const AIUsagePolicySnapshotIdField = z.string()
const InteractionDirectionEnum = z.enum(['INBOUND', 'OUTBOUND'])
const BlockEnum = z.enum(['BLOCK', 'FLAG'])
const AddAIUsagePolicyViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  snapshot_id: AIUsagePolicySnapshotIdField.uuid().optional(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  content: z.string(),
  type: InteractionDirectionEnum,
  action: BlockEnum,
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
})
const AIUsagePolicyIdField = z.string()
const AIUsagePolicy = z.object({
  id: AIUsagePolicyIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  content: z.string(),
  type: InteractionDirectionEnum,
  action: BlockEnum,
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
  archived: z.string().datetime({ offset: true }).nullish(),
  organization: OrganizationIdField.uuid(),
  created_by: IdentityIdField.nullish(),
})
const AddAIUsagePolicyViewResponseData = z.object({ policy: AIUsagePolicy })
const AddAIUsagePolicySnapshotViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
})
const AIUsagePolicySnapshot = z.object({
  id: AIUsagePolicySnapshotIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  policies: z.array(AIUsagePolicyIdField),
  name: z.string().nullish(),
  version_number: z.number().int().gte(-2147483648).lte(2147483647),
  status: DraftEnum,
  published: z.string().datetime({ offset: true }).nullish(),
  organization: OrganizationIdField.uuid(),
  parent_snapshot: AIUsagePolicySnapshotIdField.nullish(),
  published_by: IdentityIdField.nullish(),
})
const AddAIUsagePolicySnapshotViewResponseData = z.object({ snapshot: AIUsagePolicySnapshot })
const SourceAccessTypeEnum = z.enum(['external', 'internal'])
const FolderIdField = z.string()
const AddFAQSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  question: z.string(),
  answer: z.string(),
  access_type: SourceAccessTypeEnum.optional(),
  folder_id: FolderIdField.nullish(),
})
const AddFolderViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().max(255),
})
const Folder = z.object({
  id: FolderIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  name: z.string().max(255),
  parent_id: FolderIdField.nullish(),
  created: z.string().datetime({ offset: true }),
  created_by_id: IdentityIdField.nullish(),
  updated: z.string().datetime({ offset: true }),
  source_count: z.number().int().optional().default(0),
})
const AddFolderViewResponseData = z.object({ folder: Folder })
const AddGISLayerSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  description: z.string(),
  properties_legend: z.object({}).partial().passthrough().optional(),
})
const GISLayerSourceIdField = z.string()
const AddGISLayerSourceViewResponseData = z.object({ gis_layer_id: GISLayerSourceIdField.uuid() })
const ExpectedAnswerSourceEnum = z.enum(['human', 'ai_generated'])
const ScenarioQuery = z.object({
  question: z.string(),
  expected_answer: z.string().nullish(),
  expected_answer_source: ExpectedAnswerSourceEnum.nullish(),
  relevant_sources: z
    .array(
      z.union([
        z.string(),
        z.object({ source_id: z.string().uuid(), name: z.string() }).passthrough(),
      ]),
    )
    .nullish(),
})
const AddScenarioViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  queries: z.array(ScenarioQuery),
  category: z.string().nullable(),
})
const AITestScenarioIdField = z.string()
const AddScenarioViewResponseData = z.object({ id: AITestScenarioIdField.uuid() })
const TransferLineSourceConfigIdField = z.string()
const TransferLineSourceConfig = z.object({
  id: TransferLineSourceConfigIdField.uuid(),
  metadata: z.unknown().nullish(),
  name: z.string().max(100),
  start_time: z.string(),
  end_time: z.string(),
  days_of_week: z.unknown().optional(),
  timezone: z.string().max(50).optional(),
  alternate_number_name: z.string().max(100).nullish(),
  alternate_number: z.string().max(128).nullish(),
  alternate_transfer_confirmation_prompt: z.string().nullish(),
  priority: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  organization: OrganizationIdField.uuid(),
})
const AddTransferLineSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  number: z.string(),
  extension: z.number().int().optional(),
  name: z.string(),
  description: z.string(),
  transfer_confirmation_prompt: z.string().optional(),
  configs: z.array(TransferLineSourceConfig).optional(),
})
const AITestRunIdField = z.string()
const ArchiveTestRunViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  run_id: AITestRunIdField.uuid(),
  archived: z.boolean().optional().default(true),
})
const AIInteractionIdField = z.string()
const CaseIdField = z.string()
const AssociateAIInteractionCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const AIScenarioCreate = z.object({
  queries: z.array(ScenarioQuery),
  category: z.string().nullable(),
})
const BulkAddScenariosViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  scenario_adds: z.array(AIScenarioCreate),
})
const AITestQueryIdField = z.string()
const AITestQueryInfo = z.object({
  id: AITestQueryIdField.uuid(),
  scenario_id: AITestScenarioIdField.uuid(),
  expected_answer: z.string().nullish(),
  expected_answer_source: z.string().optional(),
  expected_source_description: z.unknown().nullish(),
  category: z.string().nullable(),
  question: z.string(),
  created: z.string(),
  updated: z.string(),
})
const AITestScenarioInfo = z.object({
  id: AITestScenarioIdField.uuid(),
  queries: z.array(AITestQueryInfo),
  category: z.string().nullable(),
  created: z.string(),
  updated: z.string(),
})
const BulkAddScenariosViewResponseData = z.object({ scenarios: z.array(AITestScenarioInfo) })
const PageSourceIdField = z.string()
const BulkArchivePageSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  archived: z.boolean(),
  page_source_ids: z.array(PageSourceIdField).optional(),
  title_pattern: z.string().optional(),
  url_pattern: z.string().optional(),
  source_type: z.string().optional(),
  folder_id: FolderIdField.nullish(),
})
const BulkArchivePageSourcesViewResponseData = z.object({
  updated_count: z.number().int(),
  page_source_ids: z.array(PageSourceIdField),
})
const FAQSourceIdField = z.string()
const BulkMovePageSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  page_source_ids: z.array(PageSourceIdField).optional(),
  faq_source_ids: z.array(FAQSourceIdField).optional(),
  folder_id: FolderIdField.nullable(),
})
const BulkMovePageSourcesViewResponseData = z.object({
  updated_count: z.number().int(),
  page_source_ids: z.array(PageSourceIdField),
})
const FormStepIdField = z.string()
const CancelFormGenerationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
})
const CustomCategory = z.object({ name: z.string(), definition: z.string().nullish() })
const CheckCategoryEvaluationConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  categories: z.array(CustomCategory),
  queries: z.array(z.string()).min(1).max(20),
})
const CheckCategoryEvaluationConfigViewResponseData = z.object({
  categorized_queries: z.array(z.array(z.string()).min(2).max(2)),
  are_categories_distinct: z.boolean(),
  categories_distinctness_reasoning: z.string(),
})
const ConnectArcGISViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  arcgis_url: z.string(),
  arcgis_layer_id: z.number().int(),
})
const ElevenLabsConversationInitiationViewRequestData = z.object({
  caller_id: z.string(),
  agent_id: z.string(),
  called_number: z.string(),
  call_sid: z.string(),
  conversation_id: z.string().nullish(),
})
const VoiceDynamicVariables = z.object({
  ai_interaction_id: z.string(),
  caller_id: z.string(),
  called_number: z.string(),
  call_sid: z.string(),
  organization_name: z.string(),
  organization_id: z.string(),
  enable_proactive_transfers: z.boolean(),
  current_date_and_time: z.string(),
  conversation_id: z.string().nullish(),
})
const ELPromptConfig = z.object({ prompt: z.string() })
const ELVoiceConfig = z.object({
  voice_id: z.string(),
  speed: z.number().nullable(),
  similarity_boost: z.number().nullable(),
  stability: z.number().nullable(),
})
const ELAgentConfig = z.object({
  prompt: ELPromptConfig.optional(),
  tts: ELVoiceConfig.optional(),
  first_message: z.string().nullable(),
  language: z.string().nullish(),
})
const ELConversationConfigOverride = z
  .object({ agent: ELAgentConfig, tts: ELVoiceConfig })
  .partial()
const ElevenLabsConversationInitiationViewResponseData = z.object({
  dynamic_variables: VoiceDynamicVariables,
  conversation_config_override: ELConversationConfigOverride,
})
const ConvertDocumentToAIUsagePoliciesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  file: z.string().url(),
})
const ConvertedAIUsagePolicy = z.object({
  name: z.string(),
  content: z.string(),
  type: InteractionDirectionEnum,
  action: BlockEnum,
})
const ConvertDocumentToAIUsagePoliciesViewResponseData = z.object({
  policies: z.array(ConvertedAIUsagePolicy),
})
const WebsiteTypeEnum = z.enum([
  'generic',
  'civicplus',
  'civicplus_old',
  'revize',
  'granicus',
  'granicus_opencities',
  'eztask',
  'proudcity',
  'municipalcode_online',
  'municode',
  'amlegal',
  'ecode',
  'encodeplus',
  'wordpress',
  'escribe',
  'civicclerk',
])
const CrawlDomainViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization: OrganizationIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  url: z.string().url(),
  crawl: z.boolean().optional(),
  cadence: z.string().optional(),
  trust_sitemap: z.boolean().optional(),
  type: WebsiteTypeEnum.optional(),
  metadata: z.unknown().optional(),
  allow_backward_links: z.boolean().optional().default(false),
  exclude_paths: z.string().optional(),
  include_only_paths: z.string().optional(),
  exclude_tags: z.string().optional(),
  include_tags: z.string().optional(),
  web_search_active: z.boolean().optional().default(true),
  web_search_require_sitemap: z.boolean().optional().default(false),
  max_depth: z.number().int().nullish(),
})
const CrawlTaskIdField = z.string()
const CrawlTaskLogsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  crawl_task_ids: z.array(CrawlTaskIdField),
  show_links: z.boolean().optional().default(false),
})
const SourceIdField = z.string()
const SourceActionEnum = z.enum(['Add', 'Edit', 'Archive', 'Other'])
const CrawlStatusEnum = z.enum([
  'pending',
  'queued',
  'running',
  'finished',
  'failed',
  'in_progress',
])
const QuerySourcesLog = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  source: SourceIdField.nullish(),
  user: EndUserIdField.nullish(),
  action: SourceActionEnum.optional(),
  description: z.string().nullish(),
  crawl_task: CrawlTaskIdField.uuid(),
  organization: OrganizationIdField.nullish(),
  site_crawler_used: WebsiteTypeEnum.nullish(),
  links_added: z.array(z.string()).optional(),
  links_removed: z.array(z.string()).optional(),
  links_scraped: z.array(z.string()).optional(),
  status: CrawlStatusEnum.nullish(),
  failure_message: z.string().nullish(),
  firecrawl_monitor_check_id: z.string().max(255).nullish(),
  crawl_task_organization_name: z.string(),
  crawl_task_url: z.string(),
})
const CrawlTaskLogsViewResponseData = z.object({ logs: z.array(QuerySourcesLog) })
const CreateCustomCategoryEvaluationConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  categories: z.array(CustomCategory),
})
const EvaluationConfigIdField = z.string()
const CreateCustomCategoryEvaluationConfigViewResponseData = z.object({
  evaluation_config_id: EvaluationConfigIdField.uuid(),
  prompt_template: z.string(),
})
const DeleteAIAssistantInteractionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
})
const AIEmailIdField = z.string()
const DeleteAIEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_email_id: AIEmailIdField.uuid(),
})
const DeleteAIPhoneLineViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_phone_line_id: AIPhoneLineIdField.uuid(),
})
const DeleteAISkillViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
})
const DeleteAllAISkillsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const DeleteAllAIUsagePoliciesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const DeleteAllAssistantChatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ChunkIdField = z.string()
const DeleteChunkViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  chunk_id: ChunkIdField.uuid(),
})
const DeleteCrawlTaskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  crawl_task_id: CrawlTaskIdField.uuid(),
})
const DeleteFAQSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  faq_source_ids: z.array(FAQSourceIdField),
})
const DeleteFolderViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  folder_id: FolderIdField.uuid(),
})
const DeleteFolderViewResponseData = z.object({ affected_source_count: z.number().int() })
const DeleteGISLayerSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
})
const TransferLineSourceIdField = z.string()
const DeleteTransferLineSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  transfer_line_source_ids: z.array(TransferLineSourceIdField),
})
const DevinAskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  message: z.string(),
  conversation_id: z.string().nullish(),
})
const DevinAskViewResponseData = z.object({
  answer: z.string(),
  conversation_id: z.string().nullish(),
})
const DomainSanityCheckViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  url: z.string().url(),
  crawler_type: z.string().nullish(),
})
const DomainSanityCheckViewResponseData = z.object({ is_compatible: z.boolean() })
const OnlyEnum = z.enum(['only', 'include'])
const AgentToolEnum = z.enum([
  'retrieve_sources',
  'gis_lookup',
  'external_api_call',
  'search_contacts',
  'search_cases',
  'search_entities',
  'vector_search',
  'text_search',
  'web_search',
  'expand_page_source',
  'search_page_sources',
  'generate_document',
  'get_skill',
  'create_skill',
  'update_skill',
  'map_topic_coverage',
  'unknown',
])
const WorkspaceIdField = z.string()
const AIInteractionFilter = z
  .object({
    ai_interaction_ids: z.array(z.string().uuid()).nullable(),
    organization_ids: z.array(z.string().uuid()).nullable(),
    user_ids: z.array(z.string().uuid()).nullable(),
    user_identity_ids: z.array(z.string().uuid()).nullable(),
    created_after: z.string().datetime({ offset: true }).nullable(),
    created_before: z.string().datetime({ offset: true }).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    query: z.string().nullable(),
    interaction_types: z.array(AIInteractionTypeEnum).nullable(),
    impressions: z.array(z.boolean()).nullable(),
    languages: z.array(z.string()).nullable(),
    include_test: z.boolean().nullable(),
    test_log_filter: OnlyEnum.nullable(),
    phone_numbers: z.array(z.string()).nullable(),
    categories: z.array(z.string()).nullable(),
    sentiments: z.array(z.string()).nullable(),
    outcomes: z.array(z.string()).nullable(),
    handleds: z.array(z.boolean()).nullable(),
    ratings: z.array(z.number().int()).nullable(),
    transfer_line_numbers: z.array(z.string()).nullable(),
    tool_names: z.array(AgentToolEnum).nullable(),
    llm_providers: z.array(z.string()).nullable(),
    capabilities: z.array(z.string()).nullable(),
    contact_book_code: z.string().nullable(),
    contact_handle: z.string().nullable(),
    workspace_id: WorkspaceIdField.nullable(),
  })
  .partial()
const DownloadAIInteractionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
})
const DownloadAIInteractionsViewResponseData = z.object({ email_recipient: z.string().email() })
const DownloadAssistantInteractionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
})
const DownloadAssistantInteractionsViewResponseData = z.object({
  email_recipient: z.string().email(),
})
const DownloadFAQSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  faq_source_ids: z.array(FAQSourceIdField).optional(),
})
const DownloadFAQSourcesViewResponseData = z.object({ email_recipient: z.string().email() })
const CsvEnum = z.enum(['csv', 'pdf'])
const DownloadTestRunViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  run_id: AITestRunIdField.uuid(),
  format: CsvEnum.optional(),
})
const DownloadTestRunViewResponseData = z.object({ email_recipient: z.string().email() })
const DownloadTransferLineSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  transfer_line_source_ids: z.array(TransferLineSourceIdField).optional(),
})
const DownloadTransferLineSourcesViewResponseData = z.object({
  email_recipient: z.string().email(),
})
const EditAIPhoneLineViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_phone_line_id: AIPhoneLineIdField.uuid(),
  number: z.string(),
  type: AIPhoneLineTypeEnum.nullish(),
  owner_type: AIPhoneLineOwnerEnum.nullish(),
  name: z.string().optional(),
  configs: z.array(EnrichedAIPhoneLineConfig),
  twilio_account_config_id: TwilioAccountConfigIdField.nullish(),
  elevenlabs_voice_id: z.string().nullish(),
  elevenlabs_voice_speed: z.number().gte(0.7).lte(1.2).nullish(),
  elevenlabs_voice_similarity: z.number().gte(0).lte(1).nullish(),
  elevenlabs_voice_stability: z.number().gte(0).lte(1).nullish(),
  phonic_voice_id: z.string().nullish(),
  phonic_audio_speed: z.number().gte(0.5).lte(2).nullish(),
  phonic_intelligence_level: PhonicIntelligenceLevelEnum.optional(),
  phonic_is_welcome_message_interruptible: z.boolean().nullish(),
  phonic_min_words_to_interrupt: z.number().int().gte(1).lte(10).nullish(),
  phonic_multilingual_mode: PhonicMultilingualModeEnum.nullish(),
  phonic_enable_assistant_backchannel: z.boolean().nullish(),
  phonic_assistant_backchannel_aggressiveness: z.number().gte(0).lte(1).nullish(),
  phonic_background_noise: PhonicBackgroundNoiseEnum.nullish(),
  phonic_background_noise_level: z.number().gte(0).lte(1).nullish(),
  phonic_boosted_keywords: z.array(z.string()).nullish(),
  phonic_pronunciation_dictionary: z.array(PhonicPronunciationDictionaryEntry).nullish(),
})
const AIProcessSourceIdField = z.string()
const EditAIProcessSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_process_source_id: AIProcessSourceIdField.uuid(),
  description: z.string().optional(),
  enabled_as_source: z.boolean().optional(),
})
const Live1Enum = z.enum(['LIVE', 'ARCHIVED'])
const EditAISkillViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  id: AISkillIdField.uuid(),
  name: z.string().optional(),
  description: z.string().optional(),
  content: z.string().optional(),
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
  document_template_ids: z.array(DocumentTemplateIdField).optional(),
  status: Live1Enum.optional(),
})
const EditAISkillViewResponseData = z.object({ skill: AISkill })
const EditAIUsagePolicyViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  snapshot_id: AIUsagePolicySnapshotIdField.uuid().optional(),
  id: AIUsagePolicyIdField.uuid(),
  name: z.string().optional(),
  content: z.string().optional(),
  type: InteractionDirectionEnum.optional(),
  action: BlockEnum.optional(),
  interaction_types: z.array(AIInteractionTypeEnum).optional(),
  archived: z.boolean().optional(),
})
const EditAIUsagePolicyViewResponseData = z.object({ policy: AIUsagePolicy })
const EditAIUsagePolicySnapshotViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  id: AIUsagePolicySnapshotIdField.uuid(),
  name: z.string().optional(),
  status: Live1Enum.optional(),
})
const EditAIUsagePolicySnapshotViewResponseData = z.object({ snapshot: AIUsagePolicySnapshot })
const EditChunkViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  chunk_id: ChunkIdField.uuid(),
  show_in_chat: z.boolean(),
})
const EditCrawlTaskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  crawl_task_id: CrawlTaskIdField.uuid(),
  url: z.string().url().optional(),
  cadence: z.string().nullish(),
  type: WebsiteTypeEnum.optional(),
  metadata: z.unknown().nullish(),
  allow_backward_links: z.boolean().optional(),
  exclude_paths: z.string().nullish(),
  include_only_paths: z.string().nullish(),
  exclude_tags: z.string().nullish(),
  include_tags: z.string().nullish(),
  web_search_active: z.boolean().optional(),
  web_search_require_sitemap: z.boolean().optional(),
  max_depth: z.number().int().nullish(),
})
const EditFAQSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  faq_source_id: FAQSourceIdField.uuid(),
  question: z.string().optional(),
  answer: z.string().optional(),
  access_type: SourceAccessTypeEnum.optional(),
})
const EditFolderViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  folder_id: FolderIdField.uuid(),
  name: z.string().max(255),
})
const EditFolderViewResponseData = z.object({ folder: Folder })
const EditGISLayerSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  name: z.string(),
  description: z.string(),
  properties_legend: z.object({}).partial().passthrough().optional(),
})
const EditOrganizationAIRetentionSettingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  chat_retention_days: z.number().int().nullish(),
  search_retention_days: z.number().int().nullish(),
  voice_retention_days: z.number().int().nullish(),
  sms_retention_days: z.number().int().nullish(),
  email_retention_days: z.number().int().nullish(),
  assistant_retention_days: z.number().int().nullish(),
})
const OrganizationAISettingsIdField = z.string()
const GeoPoint = z.object({ lon: z.number(), lat: z.number() })
const AIChatSizeEnum = z.enum(['SMALL', 'MEDIUM', 'LARGE'])
const SearchSuggestion = z.object({ text: z.string(), order: z.number().int() })
const ProcessIdField = z.string()
const Active1Enum = z.enum(['active', 'archived'])
const ProcessVerificationEnum = z.enum(['none', 'email'])
const AdHocTargetIdField = z.string()
const Workflow = z.object({
  id: WorkflowIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  status: Active1Enum.optional(),
  appliable: z.boolean(),
  apply_without_verification: ProcessVerificationEnum.nullish(),
  organization: OrganizationIdField.uuid(),
  ad_hoc_target: AdHocTargetIdField.uuid(),
})
const EnrichedOrganizationAISettings = z.object({
  id: OrganizationAISettingsIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  organization_location: GeoPoint.nullish(),
  chat_size: AIChatSizeEnum.nullish(),
  search_suggestions: z.array(SearchSuggestion),
  voicemail_process_id: ProcessIdField.nullish(),
  voicemail_workflow: Workflow.nullish(),
  organization_handle: z.string(),
  organization_name: z.string(),
  organization_type: z.string(),
  organization_image: z.string().nullable(),
  organization_logo: z.string().nullable(),
  chat_prompt: z.string().nullable(),
  search_prompt: z.string().nullable(),
  voice_prompt: z.string().nullable(),
  sms_prompt: z.string().nullable(),
  email_prompt: z.string().nullable(),
  assistant_prompt: z.string().nullable(),
  chat_temperature: z.number().nullable(),
  search_temperature: z.number().nullable(),
  voice_temperature: z.number().nullable(),
  sms_temperature: z.number().nullable(),
  email_temperature: z.number().nullable(),
  assistant_temperature: z.number().nullable(),
  chat_retention_days: z.number().int().nullable(),
  search_retention_days: z.number().int().nullable(),
  voice_retention_days: z.number().int().nullable(),
  sms_retention_days: z.number().int().nullable(),
  email_retention_days: z.number().int().nullable(),
  assistant_retention_days: z.number().int().nullable(),
  font_family: z.string().nullable(),
  font_color: z.string().nullable(),
  link_color: z.string().nullable(),
  bg_color: z.string().nullable(),
  organization_ai_logo: z.string().nullable(),
  corner_radius: z.number().int().nullable(),
  disclaimer_text: z.string().nullable(),
  language_support_text: z.string().nullable(),
  hide_privacy_policy: z.boolean().nullable(),
  hide_polimorphic_link: z.boolean().nullable(),
  disable_analytics: z.boolean().nullable(),
  chat_name: z.string().nullable(),
  chat_icon: z.string().nullable(),
  chat_greeting: z.string().nullable(),
  search_name: z.string().nullable(),
  search_url: z.string().nullable(),
  search_banner: z.string().nullable(),
  search_show_header_text: z.boolean().nullable(),
  search_show_header_image: z.boolean().nullable(),
  search_show_banner_text: z.boolean().nullable(),
  search_banner_text: z.string().nullable(),
  search_layout: z.string().nullable(),
  search_suggestion_style: z.string().nullable(),
  voice_apply_enabled: z.boolean().nullable(),
  enable_proactive_transfers: z.boolean().nullable(),
  scrape_cadence: z.string().nullable(),
  test_bank_template: z.string().nullable(),
  allowed_embed_domains: z.array(z.string()),
  chat_persistence_minutes: z.number().int().nullable(),
  llm_provider: z.string(),
})
const EditOrganizationAIRetentionSettingsViewResponseData = z.object({
  organization_ai_settings: EnrichedOrganizationAISettings,
})
const SearchLayoutTypeEnum = z.enum(['combined', 'tabbed', 'results_only'])
const SearchSuggestionStyleEnum = z.enum(['dropdown', 'chips'])
const EditOrganizationAISettingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  chat_name: z.string().nullish(),
  search_name: z.string().nullish(),
  bg_color: z.string().nullish(),
  font_color: z.string().nullish(),
  link_color: z.string().nullish(),
  font_family: z.string().nullish(),
  search_url: z.string().nullish(),
  search_banner: z.string().nullish(),
  organization_ai_logo: z.string().nullish(),
  chat_icon: z.string().nullish(),
  corner_radius: z.number().int().nullish(),
  chat_size: z.string().nullish(),
  chat_greeting: z.string().nullish(),
  disclaimer_text: z.string().nullish(),
  language_support_text: z.string().nullish(),
  search_show_header_text: z.boolean().nullish(),
  search_show_header_image: z.boolean().nullish(),
  search_show_banner_text: z.boolean().nullish(),
  search_banner_text: z.string().nullish(),
  search_layout: SearchLayoutTypeEnum.nullish(),
  search_suggestion_style: SearchSuggestionStyleEnum.nullish(),
})
const EditOrganizationAISettingsViewResponseData = z.object({
  organization_ai_settings: EnrichedOrganizationAISettings,
})
const EditPageSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  page_id: PageSourceIdField.uuid(),
  access_type: SourceAccessTypeEnum.nullish(),
  is_referenced: z.boolean().nullish(),
  title: z.string().optional(),
  archived: z.boolean().nullish(),
  tags: z.array(z.string()).optional(),
  removed_agent_tags: z.array(z.string()).optional(),
  folder_id: FolderIdField.nullish(),
})
const EditScenarioViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  id: AITestScenarioIdField.uuid(),
  category: z.string().nullish(),
  queries: z.array(ScenarioQuery).optional(),
})
const EditTransferLineSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  transfer_line_source_id: TransferLineSourceIdField.uuid(),
  number: z.string(),
  extension: z.number().int().optional(),
  name: z.string(),
  description: z.string(),
  transfer_confirmation_prompt: z.string().optional(),
  configs: z.array(TransferLineSourceConfig).optional(),
})
const APIConnectionIdField = z.string()
const ElevenLabsExternalAPICallViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  connection_id: APIConnectionIdField.uuid(),
  name: z.string(),
  parameters: z.unknown().optional(),
})
const ExternalAPICallResult = z.object({
  success: z.boolean(),
  endpoint: z.string().nullish(),
  data: z.unknown().nullish(),
  error: z.string().nullish(),
  requires_reconnect: z.boolean().optional(),
  reconnect_connection_id: z.string().nullish(),
})
const ElevenLabsExternalAPICallViewResponseData = z.object({
  success: z.boolean(),
  result: ExternalAPICallResult.nullish(),
  error: z.string().nullish(),
})
const GenerateAssistantResponseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid().optional(),
  query: z.string(),
  raw_text: z.boolean().optional().default(false),
  override_allowed_tools: z.array(z.string()).optional(),
  device_id: z.string().nullish(),
  attachment_ids: z.array(z.string()).optional(),
  is_test: z.boolean().optional().default(false),
})
const ResponseStreamIdField = z.string()
const GenerateAssistantResponseViewResponseData = z.object({
  response_stream_id: ResponseStreamIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid().optional(),
  user_id: EndUserIdField.uuid(),
  policy_blocked: z.boolean().optional(),
})
const GenerateFormFromPDFViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
  file_url: z.string().url(),
})
const GenerateFormFromPDFViewResponseData = z.object({ task_id: z.string() })
const ConstituentAIInteractionTypeEnum = z.enum([
  'SEARCH',
  'CHAT',
  'VOICE',
  'SMS',
  'WHATSAPP',
  'EMAIL',
])
const GenerateResponseViewRequestData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  organization_id: OrganizationIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid().optional(),
  query: z.string(),
  interaction_type: ConstituentAIInteractionTypeEnum,
  raw_text: z.boolean().optional().default(false),
  device_id: z.string().nullish(),
  is_test: z.boolean().optional().default(false),
})
const GenerateResponseViewResponseData = z.object({
  response_stream_id: ResponseStreamIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  user_id: EndUserIdField.uuid(),
  include_search_results: z.boolean(),
})
const GetAIInteractionCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
})
const GetAIInteractionCountViewResponseData = z.object({ count: z.number().int() })
const AccessReasonEnum = z.enum(['support', 'legal', 'audit', 'debug', 'other'])
const GetAIInteractionPIITranscriptViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  reason: AccessReasonEnum,
})
const EnrichedAttachment = z.object({
  filename: z.string(),
  download_url: z.string(),
  is_image: z.boolean().optional(),
  preview_url: z.string().nullish(),
})
const QueryIdField = z.string()
const AppliedUsagePolicy = z.object({ name: z.string(), action: BlockEnum })
const AppliedUsagePolicies = z.object({
  snapshot_version: z.number().int(),
  model: z.string().nullable(),
  policies: z.array(AppliedUsagePolicy),
})
const EnrichedQuery = z.object({
  attachments: z.array(EnrichedAttachment).optional(),
  id: QueryIdField.uuid(),
  body: z.string(),
  language: z.string().nullable(),
  english_text: z.string().nullable(),
  created: z.string().datetime({ offset: true }),
  inbound_policies: AppliedUsagePolicies.nullish(),
  outbound_policies: AppliedUsagePolicies.nullish(),
})
const TransferLineSourceConfigInfo = z
  .object({
    alternate_number: z.string().nullable(),
    alternate_number_name: z.string().nullable(),
    operating_hours: z.string().nullable(),
    inactive_dates: z.array(z.string()).nullable(),
    priority: z.number().int().nullable(),
  })
  .partial()
const TransferLineSourceHolidayInfo = z.object({
  name: z.string(),
  dates: z.array(z.string()),
  transfer_line_number: z.string().nullish(),
})
const SourceDisplayLink = z.object({
  organization_name: z.string(),
  title: z.string(),
  source_type: z.string(),
  source_access_type: SourceAccessTypeEnum,
  is_linked_organization: z.boolean(),
  source_id: z.string(),
  relevance_score: z.number().optional(),
  text: z.string().nullish(),
  page_url: z.string().nullish(),
  page_source_id: z.string().nullish(),
  page_gui_url: z.string().nullish(),
  transfer_line_name: z.string().nullish(),
  transfer_line_number: z.string().nullish(),
  transfer_line_extension: z.string().nullish(),
  transfer_line_config_infos: z.array(TransferLineSourceConfigInfo).nullish(),
  transfer_line_holidays: z.array(TransferLineSourceHolidayInfo).nullish(),
  faq_question: z.string().nullish(),
  faq_answer: z.string().nullish(),
})
const CaseDisplayLink = z.object({ name: z.string(), link: z.string(), status: z.string() })
const ContactDisplayLink = z.object({ name: z.string(), link: z.string() })
const CreatedSkill = z.object({ code: z.string(), name: z.string() })
const ResponseIdField = z.string()
const ResponseTypeEnum = z.enum([
  'ANSWER',
  'CALL_RECEIVED',
  'CALL_TRANSFERRED',
  'CALL_ENDED',
  'TOOL_CALL',
  'TOOL_RESULTS',
  'LANGUAGE_CHANGE',
  'BACKUP_CALL',
  'SMS_SENT',
  'REQUEST_LOCATION',
  'ACTION',
])
const EnrichedResponse = z.object({
  sources: z.array(SourceDisplayLink).nullish(),
  cases: z.array(CaseDisplayLink).nullish(),
  contacts: z.array(ContactDisplayLink).nullish(),
  files: z.array(EnrichedAttachment).nullish(),
  skills: z.array(CreatedSkill).nullish(),
  has_integrations: z.boolean().nullish(),
  id: ResponseIdField.uuid(),
  body: z.string(),
  language: z.string().nullable(),
  english_text: z.string().nullable(),
  impression: z.boolean().nullable(),
  type: ResponseTypeEnum.nullable(),
  created: z.string().datetime({ offset: true }),
  query_id: QueryIdField.nullish(),
  generation_started_at: z.string().nullish(),
  feedback: z.string().nullish(),
  submitter: z.string().nullish(),
  feedback_created: z.string().datetime({ offset: true }).nullish(),
  retrieved_sources: z.array(SourceDisplayLink).nullish(),
})
const AgentActionTypeEnum = z.enum([
  'CALL_RECEIVED',
  'CALL_TRANSFERRED',
  'CALL_ENDED',
  'LANGUAGE_CHANGE',
  'BACKUP_CALL',
  'SMS_SENT',
  'REQUEST_LOCATION',
  'SEARCH_RESULTS',
])
const AgentAction = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  action_type: AgentActionTypeEnum,
  body: z.string(),
  ai_interaction: AIInteractionIdField.uuid(),
  query: QueryIdField.nullish(),
})
const ToolCallIdField = z.string()
const ToolCallStatusEnum = z.enum([
  'in_progress',
  'completed',
  'abandoned',
  'interrupted',
  'failed',
  'timeout',
  'limit_exceeded',
])
const EnrichedToolCall = z.object({
  id: ToolCallIdField.uuid(),
  tool_name: z.string(),
  params_json: z.object({}).partial().passthrough(),
  status: ToolCallStatusEnum,
  created: z.string().datetime({ offset: true }),
  result_json: z.object({}).partial().passthrough().nullish(),
  time_taken_ms: z.number().int().nullish(),
  error_message: z.string().nullish(),
  external_api_failure: z.string().nullish(),
})
const VoiceCallStatusEnum = z.enum([
  'unknown',
  'queued',
  'ringing',
  'in_progress',
  'completed',
  'canceled',
  'failed',
  'busy',
  'no_answer',
])
const EnrichedVoiceCallLog = z.object({
  call_status: VoiceCallStatusEnum.nullable(),
  to_phone_number: z.string(),
  from_phone_number: z.string(),
  voice_implementation: AIPhoneLineTypeEnum,
  call_duration: z.number().int(),
  call_direction: InteractionDirectionEnum,
  call_sid: z.string().nullable(),
})
const TextChannelEnum = z.enum(['SMS', 'WHATSAPP'])
const EnrichedSmsTextLog = z.object({
  to_phone_number: z.string(),
  from_phone_number: z.string(),
  text_channel: TextChannelEnum,
  message_direction: z.string(),
  message_status: z.string().nullable(),
  message_sid: z.string().nullable(),
})
const MassCommunicationIdField = z.string()
const MassCommContext = z.object({
  mass_communication_id: MassCommunicationIdField.uuid(),
  name: z.string().nullable(),
  body: z.string().nullable(),
  sent_at: z.string().datetime({ offset: true }).nullable(),
})
const AgentTraceNodeTypeEnum = z.enum(['llm_call', 'tool_call'])
const AgentTraceNode = z.object({
  id: z.string(),
  node_type: AgentTraceNodeTypeEnum,
  label: z.string(),
  parent_id: z.string().nullish(),
  started_at: z.string().datetime({ offset: true }).nullish(),
  duration_ms: z.number().int().nullish(),
  status: z.string().nullish(),
  error_message: z.string().nullish(),
  trace_id: z.string().nullish(),
  model: z.string().nullish(),
  turn_index: z.number().int().nullish(),
  total_input_tokens: z.number().int().nullish(),
  cached_input_tokens: z.number().int().nullish(),
  output_tokens: z.number().int().nullish(),
  reasoning_tokens: z.number().int().nullish(),
  output_text: z.string().nullish(),
  selected_sources: z.array(z.object({}).partial().passthrough()).nullish(),
  time_to_first_token_ms: z.number().int().nullish(),
  is_agent_delegation: z.boolean().optional(),
  params_json: z.object({}).partial().passthrough().nullish(),
  result_json: z.object({}).partial().passthrough().nullish(),
})
const AIInteractionEvaluation = z
  .object({
    category: z.string().nullable(),
    summary: z.string().nullable(),
    sentiment: z.string().nullable(),
    is_angry: z.boolean().nullable(),
    handled: z.boolean().nullable(),
    rating: z.number().int().nullable(),
    outcome: z.string().nullable(),
  })
  .partial()
const AIInteractionTranscript = z.object({
  queries: z.array(EnrichedQuery),
  responses: z.array(EnrichedResponse),
  agent_actions: z.array(AgentAction),
  tool_calls: z.array(EnrichedToolCall),
  voice_call_log: EnrichedVoiceCallLog.nullish(),
  sms_text_log: EnrichedSmsTextLog.nullish(),
  mass_comm_context: MassCommContext.nullish(),
  agent_trace: z.array(AgentTraceNode).optional(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  interaction_type: AIInteractionTypeEnum,
  organization_name: z.string(),
  ai_interaction_evaluation: AIInteractionEvaluation.nullish(),
  audio_url: z.string().nullish(),
  eleven_labs_conversation_id: z.string().nullish(),
  phonic_conversation_id: z.string().nullish(),
  organization_image: z.string().nullish(),
  phone_line_type: z.string().nullish(),
  user_name: z.string().nullish(),
  case_name: z.string().nullish(),
  case_handle: z.string().nullish(),
  caller_id: z.string().nullish(),
  contact_identity_id: IdentityIdField.nullish(),
  contact_identity_name: z.string().nullish(),
  contact_handle: z.string().nullish(),
  llm_model: z.string().nullish(),
  generator_model: z.string().nullish(),
  retrieval_model: z.string().nullish(),
  used_granular_retrieval: z.boolean().optional(),
  has_redacted_content: z.boolean().optional(),
  is_test: z.boolean().optional(),
})
const GetAIInteractionPIITranscriptViewResponseData = z.object({
  interaction_transcript: AIInteractionTranscript,
  organization_ai_settings: EnrichedOrganizationAISettings,
})
const GetAIInteractionTranscriptViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  reveal_pii: z.boolean().optional().default(false),
})
const GetAIInteractionTranscriptViewResponseData = z.object({
  interaction_transcript: AIInteractionTranscript,
  organization_ai_settings: EnrichedOrganizationAISettings,
  response_stream_id: ResponseStreamIdField.nullish(),
})
const GetAIProcessSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
})
const AIProcessSource = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  ai_description: z.string(),
  enabled_as_source: z.boolean().optional(),
  process: ProcessIdField.uuid(),
  workflow: WorkflowIdField.nullish(),
  source: SourceIdField.uuid(),
})
const GetAIProcessSourceViewResponseData = z
  .object({ ai_process_source: AIProcessSource.nullable() })
  .partial()
const GetAIProcessSourcesCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
})
const GetAIProcessSourcesCountViewResponseData = z.object({ count: z.number().int() })
const GetArcGISLayersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  arcgis_url: z.string(),
})
const GISLayerOption = z.object({
  id: z.number().int(),
  name: z.string(),
  geometry_type: z.string(),
})
const GetArcGISLayersViewResponseData = z.object({ layers: z.array(GISLayerOption) })
const GetAssistantInteractionCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
})
const GetAssistantInteractionCountViewResponseData = z.object({ count: z.number().int() })
const GetCivicPlusFoldersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  url: z.string().url(),
  user_api_key: z.string(),
  org_api_key: z.string(),
  partition: z.string(),
  date_from: z.string().nullish(),
  date_to: z.string().nullish(),
})
const GetCivicPlusFoldersViewResponseData = z.object({
  folders: z.array(z.object({}).partial().passthrough()),
})
const GetConnectedResourceCountsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetConnectedResourceCountsViewResponseData = z.object({
  integrations_count: z.number().int().nullable(),
  integrations_with_tools_count: z.number().int().nullable(),
  transfer_lines_count: z.number().int(),
  workflows_count: z.number().int().nullable(),
  gis_layers_count: z.number().int(),
})
const GetEvaluationCategoriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetEvaluationCategoriesViewResponseData = z.object({
  categories: z.array(CustomCategory),
  is_custom: z.boolean(),
})
const GetFAQSourcesCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
})
const GetFAQSourcesCountViewResponseData = z.object({ count: z.number().int() })
const GetFormGenerationStatusViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  task_id: z.string(),
})
const GetFormGenerationStatusViewResponseData = z.object({ status: z.string() })
const GetGISLayerPolygonsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  limit: z.number().int().gte(1).lte(500).optional().default(100),
})
const GISFeatureData = z.object({
  layer: GISLayerSourceIdField.nullable(),
  geometry: z.string(),
  id: z.string(),
  geometry_type: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  properties: z.object({}).partial().passthrough(),
  distance_in_miles: z.number().nullish(),
})
const GetGISLayerPolygonsViewResponseData = z.object({
  polygons: z.array(GISFeatureData),
  lines: z.array(GISFeatureData),
  points: z.array(GISFeatureData),
  total_count: z.number().int(),
  layer_name: z.string(),
})
const GetGISLayerPropertiesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
})
const GISProperty = z.object({
  category: z.string(),
  display_name: z.string(),
  value_mapping: z.record(z.string()),
})
const GetGISLayerPropertiesViewResponseData = z.object({
  properties: z.record(GISProperty),
  layer_name: z.string(),
  undefined_properties: z.array(z.string()),
  categories: z.array(z.string()).optional(),
  example_polygon: z.object({}).partial().passthrough(),
})
const GetGISLayerSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
})
const EnrichedGISLayerSource = z.object({
  id: GISLayerSourceIdField.uuid(),
  source: SourceIdField.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  geometry_type: z.string(),
  properties_legend: z.object({}).partial().passthrough().nullable(),
  num_geometries: z.number().int(),
  arcgis_url: z.string().nullable(),
})
const GetGISLayerSourceViewResponseData = z.object({ gis_layer: EnrichedGISLayerSource })
const GetGISLayerSourcesCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
})
const GetGISLayerSourcesCountViewResponseData = z.object({ count: z.number().int() })
const GISPolygonIdField = z.string()
const GetGISPolygonsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  gis_polygon_ids: z.array(GISPolygonIdField),
})
const GetGISPolygonsViewResponseData = z.object({ polygons: z.array(GISFeatureData) })
const GetGpkgLayersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  asset_key: z.string(),
})
const GetGpkgLayersViewResponseData = z.object({ layers: z.array(GISLayerOption) })
const GetInteractionCategoryUpdateStatusViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetInteractionCategoryUpdateStatusViewResponseData = z.object({ in_progress: z.boolean() })
const GetPageSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  page_source_id: PageSourceIdField.uuid(),
})
const EnrichedChunk = z.object({
  chunk_id: ChunkIdField.uuid(),
  text: z.string(),
  chunk_type: z.string(),
  page_type: z.string(),
  date: z.string().nullable(),
  order: z.number().int().nullable(),
  dates: z.array(z.string()),
  urls: z.array(z.string()),
  image_url: z.string().nullish(),
  show_in_chat: z.boolean().nullish(),
})
const ExtractedEntityIdField = z.string()
const EnrichedExtractedEntity = z.object({
  entity_id: ExtractedEntityIdField.uuid(),
  name: z.string().nullable(),
  entity_type: z.string(),
  attributes: z.object({}).partial().passthrough(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
})
const EnrichedPageSourceTag = z.object({ tag: z.string(), origin: z.string() })
const EnrichedPageSource = z.object({
  page_source_id: PageSourceIdField.uuid(),
  chunks: z.array(EnrichedChunk),
  extracted_entities: z.array(EnrichedExtractedEntity),
  referenced_date: z.string().nullish(),
  chunking_skip_reason: z.string().nullish(),
  chunking_status: z.string().nullish(),
  extraction_status: z.string().nullish(),
  url: z.string(),
  title: z.string(),
  page_type: z.string(),
  source_access_type: SourceAccessTypeEnum,
  tags: z.array(EnrichedPageSourceTag),
  archived: z.boolean(),
  summary: z.string().nullish(),
  markdown: z.string().nullish(),
  folder_id: FolderIdField.nullish(),
  folder_name: z.string().nullish(),
  is_referenced: z.boolean().optional(),
})
const GetPageSourceViewResponseData = z
  .object({ page_source: EnrichedPageSource.nullable() })
  .partial()
const GetPageSourcesCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  filter_title: z.string().optional(),
  filter_url: z.string().optional(),
  source_type: z.string().optional(),
  archived: z.boolean(),
  folder_id: FolderIdField.nullish(),
})
const GetPageSourcesCountViewResponseData = z.object({ page_count: z.number().int() })
const GetSupportedDocumentTypesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const SupportedDocumentTypeEnum = z.enum([
  '.pdf',
  '.jpeg',
  '.jpg',
  '.png',
  '.tiff',
  '.tif',
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.txt',
  '.md',
])
const GetSupportedDocumentTypesViewResponseData = z.object({
  extensions: z.array(SupportedDocumentTypeEnum),
})
const GetTransferLineSourcesCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
})
const GetTransferLineSourcesCountViewResponseData = z.object({ count: z.number().int() })
const GetWebsiteTypeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  domain: z.string().url(),
})
const GetWebsiteTypeViewResponseData = z.object({ website_type: WebsiteTypeEnum })
const ElevenLabsGISLookupViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  address: z.string().nullish(),
})
const SourceTypeEnum = z.enum([
  'SourceWebsite',
  'SourceDocument',
  'SourceAPI',
  'SourceTransferLine',
  'SourceFAQ',
  'SourcePoliProcess',
  'SourceGISLayer',
])
const GISFeatureInfo = z.object({
  feature_id: z.string(),
  source_id: z.string(),
  organization_identity_id: z.string(),
  organization_name: z.string(),
  title: z.string(),
  text: z.string(),
  source_type: SourceTypeEnum,
  distance_in_miles: z.number(),
})
const NoMatchingPointEnum = z.enum([
  'no_matching_point',
  'one_matching_point',
  'multiple_matching_points',
  'point_outside_region',
])
const GISLookupResult = z.object({
  gis_features: z.array(GISFeatureInfo),
  response: NoMatchingPointEnum,
  addresses: z.array(z.string()),
  gis_layer_name: z.string().nullish(),
})
const ElevenLabsGISLookupViewResponseData = z.object({ gis_lookup_result: GISLookupResult })
const ImportGISPolygonsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  asset_key: z.string(),
  clear_existing: z.boolean().optional().default(false),
  gis_filename: z.string().optional().default(''),
  layer_index: z.number().int().optional().default(0),
})
const ImportGISPolygonsViewResponseData = z.object({ email_recipient: z.string() })
const UploadedDocument = z.object({
  key: z.string(),
  file_name: z.string().max(512),
  file_size: z.number().int().gte(0),
})
const IngestUploadedDocumentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  documents: z.array(UploadedDocument),
  access_type: SourceAccessTypeEnum.optional(),
  is_referenced: z.boolean().optional().default(true),
  folder_id: FolderIdField.nullish(),
})
const IngestUploadedDocumentsViewResponseData = z.object({
  success: z.boolean(),
  uploadedDocuments: z.array(z.string()),
})
const InitializeAIViewResponseData = z.object({
  organization_ai_settings: EnrichedOrganizationAISettings,
})
const InitializeAIViewRequestData = z
  .object({
    user_id: EndUserIdField.uuid(),
    organization_id: OrganizationIdField.uuid(),
    organization_handle: z.string().nullable(),
    device_id: z.string().nullable(),
  })
  .partial()
const ListActiveCallsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const WarmHandoffContact = z
  .object({
    name: z.string().nullable(),
    phone_number: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
  })
  .partial()
const Pending5Enum = z.enum(['pending', 'ready', 'failed'])
const WarmHandoffData = z.object({
  contact: WarmHandoffContact,
  status: Pending5Enum,
  requested_at: z.string(),
  transfer_number: z.string().nullish(),
  generated_at: z.string().nullish(),
  reason_for_transfer: z.string().nullish(),
  summary: z.string().nullish(),
  key_topics: z.array(z.string()).optional(),
  references: z.array(z.string()).optional(),
})
const ActiveCallContactRecord = z.object({
  handle: z.string(),
  contact_book_code: z.string(),
  name: z.string().nullish(),
})
const ActiveCall = z.object({
  warm_handoff: WarmHandoffData.nullish(),
  contact_record: ActiveCallContactRecord.nullish(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  from_phone_number: z.string(),
  to_phone_number: z.string(),
  started: z.string().datetime({ offset: true }),
  voice_implementation: z.string().nullish(),
  call_status: z.string().nullish(),
  ended: z.string().datetime({ offset: true }).nullish(),
})
const ListActiveCallsViewResponseData = z.object({
  active_calls: z.array(ActiveCall),
  recent_calls: z.array(ActiveCall),
})
const ListAIEmailsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const MessageTemplateIdField = z.string()
const AIEmail = z.object({
  id: AIEmailIdField.uuid(),
  outbound_address: z.string().email(),
  inbound_address: z.string().email(),
  form_letter_id: MessageTemplateIdField.nullish(),
  form_letter_name: z.string().nullish(),
  form_letter_body: z.string().nullish(),
  forward_only: z.boolean(),
  create_intake_case: z.boolean(),
  forwarding_prompting: z.record(z.string()).nullable(),
  default_forwarding_address: z.string().email().nullable(),
})
const ListAIEmailsViewResponseData = z.object({ ai_emails: z.array(AIEmail) })
const ListAIInteractionEvaluationEnumsViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const InteractionCategoryTypeEnum = z.enum([
  'appeals_and_dispute',
  'application_and_form_question',
  'appointment_and_scheduling',
  'complaint',
  'contact_and_routing',
  'document_and_form_request',
  'eligibility_question',
  'emergency_and_urgent',
  'feedback',
  'other',
  'out_of_scope',
  'payment_and_fees',
  'policy_question',
  'process_and_how_to',
  'service_request',
  'status_check',
])
const UserSentimentEnum = z.enum(['positive', 'neutral', 'negative'])
const InteractionRatingTypeEnum = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
])
const ResponseOutcomeEnum = z.enum([
  'answered',
  'deflected',
  'redirected',
  'clarification_needed',
  'partial_answer',
  'error',
  'safety_triggered',
])
const AIInteractionEvaluationEnums = z
  .object({
    category: InteractionCategoryTypeEnum.nullable(),
    sentiment: UserSentimentEnum.nullable(),
    rating: InteractionRatingTypeEnum.nullable(),
    outcome: ResponseOutcomeEnum.nullable(),
  })
  .partial()
const ListAIInteractionEvaluationEnumsViewResponseData = z.object({
  ai_interaction_evaluation_enums: AIInteractionEvaluationEnums,
})
const ListAIInteractionSummariesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  sort_ascending: z.boolean().optional(),
})
const AIInteractionRoleEnum = z.enum(['system', 'assistant', 'user'])
const TranscriptMessage = z.object({
  body: z.string(),
  role: AIInteractionRoleEnum,
  created: z.string().datetime({ offset: true }),
  language: z.string().optional(),
  language_name: z.string().optional(),
  timestamp_str: z.string().optional(),
  label: z.string().optional(),
})
const AIInteractionLogStats = z
  .object({
    total_turns: z.number().int().nullable(),
    total_tool_calls: z.number().int().nullable(),
    interaction_duration_ms: z.number().int().nullable(),
  })
  .partial()
const AIInteractionSummary = z.object({
  last_user_message: TranscriptMessage.nullish(),
  last_agent_message: TranscriptMessage.nullish(),
  ai_interaction_log_stats: AIInteractionLogStats.nullish(),
  ai_interaction_evaluation: AIInteractionEvaluation.nullish(),
  mass_comm_context: MassCommContext.nullish(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  interaction_type: AIInteractionTypeEnum,
  phone_line_type: z.string().nullable(),
  user_name: z.string().nullable(),
  case_name: z.string().nullable(),
  case_handle: z.string().nullable(),
  caller_id: z.string().nullable(),
  title: z.string().nullish(),
  is_test: z.boolean().optional(),
})
const ListAIInteractionSummariesViewResponseData = z.object({
  interaction_summaries: z.array(AIInteractionSummary),
})
const ListAIPhoneLinesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const AIPhoneLine = z.object({
  id: AIPhoneLineIdField.uuid(),
  type: AIPhoneLineTypeEnum,
  organization_ai_settings: OrganizationIdField.uuid(),
  configs: z.array(EnrichedAIPhoneLineConfig).optional().default([]),
  metadata: z.unknown().nullish(),
  eleven_labs_phone_id: z.string().max(100).nullish(),
  number: z.string().max(128),
  owner_type: AIPhoneLineOwnerEnum.optional(),
  name: z.string().nullish(),
  voice_id: z.string().max(100).nullish(),
  voice_speed: z.number().gte(0).lte(1.2).nullish(),
  voice_stability: z.number().gte(0).lte(1).nullish(),
  voice_similarity: z.number().gte(0).lte(1).nullish(),
  enabled_for_outbound_sms: z.boolean().optional(),
  enabled_for_outbound_calls: z.boolean().optional(),
  forward_all_calls: z.boolean().optional(),
  twilio_account_config: TwilioAccountConfigIdField.nullish(),
})
const ListAIPhoneLinesViewResponseData = z.object({ ai_phone_lines: z.array(AIPhoneLine) })
const ListAIProcessSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  ai_process_source_id: AIProcessSourceIdField.uuid().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
})
const ListAIProcessSourcesViewResponseData = z.object({
  ai_process_sources: z.array(AIProcessSource),
})
const ListAISkillsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  search: z.string().optional(),
  statuses: z.array(DraftEnum).optional(),
})
const ListAISkillsViewResponseData = z.object({ skills: z.array(AISkill) })
const ListAIUsagePoliciesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  snapshot_id: AIUsagePolicySnapshotIdField.uuid(),
})
const ListAIUsagePoliciesViewResponseData = z.object({ policies: z.array(AIUsagePolicy) })
const ListAIUsagePolicySnapshotsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  statuses: z.array(DraftEnum).optional(),
})
const ListAIUsagePolicySnapshotsViewResponseData = z.object({
  snapshots: z.array(AIUsagePolicySnapshot),
})
const ListAssistantInteractionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter: AIInteractionFilter,
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  sort_ascending: z.boolean().optional(),
})
const AssistantInteractionContact = z.object({
  identity_id: IdentityIdField.uuid(),
  name: z.string(),
  email: z.string(),
})
const ListAssistantInteractionsViewResponseData = z.object({
  interaction_summaries: z.array(AIInteractionSummary),
  contacts: z.array(AssistantInteractionContact),
})
const ListAssistantToolsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const ListAssistantToolsViewResponseData = z.object({
  available_assistant_tools: z.array(z.string()),
})
const CrawlTaskSortTypeEnum = z.enum(['crawled', 'url'])
const SortDirectionEnum = z.enum(['asc', 'desc'])
const ListCrawlTasksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter_query: z.string().nullish(),
  limit: z.number().int().nullish(),
  offset: z.number().int().nullish(),
  sort_field: CrawlTaskSortTypeEnum.nullish(),
  sort_direction: SortDirectionEnum.nullish(),
})
const CrawlTask = z.object({
  metadata: z.unknown().nullish(),
  id: CrawlTaskIdField.uuid(),
  url: z.string().max(200).url(),
  organization: OrganizationIdField.uuid(),
  cadence: z.string().nullish(),
  next_crawl: z.string().datetime({ offset: true }).nullish(),
  allow_backward_links: z.boolean().optional(),
  active: z.boolean().nullish(),
  limit: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  exclude_paths: z.string().nullish(),
  include_only_paths: z.string().nullish(),
  include_tags: z.string().nullish(),
  exclude_tags: z.string().nullish(),
  max_depth: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  site_type: WebsiteTypeEnum.nullish(),
  sitemap: z.string().nullish(),
  delay: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  max_concurrency: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  chunking_prompt: z.string().nullish(),
  firecrawl_id: z.string().max(255).nullish(),
  crawl: z.boolean().nullish(),
  status: CrawlStatusEnum.nullish(),
  crawled: z.string().datetime({ offset: true }).nullish(),
  ignore_sitemap: z.boolean().nullish(),
  trust_sitemap: z.boolean().nullish(),
  last_run_status: CrawlStatusEnum.nullish(),
  last_run_time: z.string().datetime({ offset: true }).nullish(),
  last_run_failure_message: z.string().nullish(),
  organization_name: z.string(),
  web_search_active: z.boolean().nullable(),
  web_search_require_sitemap: z.boolean().nullable(),
  access_type: z.string().nullable(),
})
const ListCrawlTasksViewResponseData = z.object({ crawl_tasks: z.array(CrawlTask) })
const ListFAQSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  faq_source_id: FAQSourceIdField.uuid().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
})
const FAQSource = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  access_type: SourceAccessTypeEnum,
  metadata: z.unknown().nullish(),
  question: z.string(),
  answer: z.string(),
  source: SourceIdField.uuid(),
})
const ListFAQSourcesViewResponseData = z.object({ faq_sources: z.array(FAQSource) })
const SourcewebsiteEnum = z.enum(['SourceWebsite', 'SourceDocument', 'SourceFAQ'])
const ListFoldersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  source_type: SourcewebsiteEnum.optional(),
  source_types: z.array(SourcewebsiteEnum).optional(),
  archived: z.boolean().optional().default(false),
})
const ListFoldersViewResponseData = z.object({ folders: z.array(Folder) })
const ListGISLayerSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  gis_layer_id: GISLayerSourceIdField.uuid().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  refresh_geometry_counts: z.boolean().optional().default(true),
})
const ListGISLayerSourcesViewResponseData = z.object({
  gis_layers: z.array(EnrichedGISLayerSource),
})
const PageSortTypeEnum = z.enum(['scraped', 'title', 'url', 'num_chunks', 'num_entities'])
const ListPageSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter_query: z.string().optional(),
  filter_title: z.string().optional(),
  filter_url: z.string().optional(),
  filter_source_type: z.string().optional(),
  filter_archived: z.boolean().nullish(),
  filter_folder_id: FolderIdField.nullish(),
  limit: z.number().int().nullish(),
  offset: z.number().int().nullish(),
  sort_field: PageSortTypeEnum.optional(),
  sort_direction: SortDirectionEnum.nullish(),
})
const PageSourceSummary = z.object({
  referenced_date: z.string().nullish(),
  chunking_status: z.string().nullish(),
  tags: z.array(EnrichedPageSourceTag),
  page_source_id: PageSourceIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  source_id: SourceIdField.uuid(),
  access_type: SourceAccessTypeEnum,
  url: z.string(),
  gui_url: z.string().nullable(),
  title: z.string(),
  page_type: z.string(),
  num_chunks: z.number().int(),
  num_entities: z.number().int(),
  scraped: z.string().datetime({ offset: true }).nullable(),
  archived: z.boolean(),
  is_referenced: z.boolean().optional(),
  extraction_status: z.string().nullish(),
  folder_id: FolderIdField.nullish(),
  folder_name: z.string().nullish(),
})
const ListPageSourcesViewResponseData = z.object({ pages: z.array(PageSourceSummary) })
const HealthCheckOutcomeEnum = z.enum(['PASS', 'FAIL', 'UNEVALUATED', 'NEEDS_REVIEW'])
const ListRunResponsesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  run_id: AITestRunIdField.uuid(),
  search: z.string().optional(),
  outcome: HealthCheckOutcomeEnum.optional(),
  category: z.string().optional(),
  null_category: z.boolean().optional().default(false),
})
const AITestResponseIdField = z.string()
const EvaluationIssueLabelEnum = z.enum([
  'Off-Topic Response',
  'Incomplete Answer',
  'Incorrect Answer',
  'Contradicts Sources',
  'Ignored Available Sources',
  'Irrelevant Pages Retrieved',
  'No Pages Retrieved',
  'Missing Information',
  'Missing Sources',
  'Inferred Response',
  'Made Up Information',
  'Unsupported Claim',
])
const EvaluationIssue = z.object({ label: EvaluationIssueLabelEnum, reason: z.string() })
const EvaluationMetricResult = z
  .object({
    issues: z.array(EvaluationIssue).nullable(),
    warnings: z.array(EvaluationIssue).nullable(),
    missing_urls: z.array(z.string()).nullable(),
    score: z.number().nullable(),
    reasoning: z.string().nullable(),
    matched_count: z.number().int().nullable(),
    expected_count: z.number().int().nullable(),
    relevant_count: z.number().int().nullable(),
    total_count: z.number().int().nullable(),
  })
  .partial()
const AITestResponseInfo = z.object({
  id: AITestResponseIdField.uuid(),
  query: AITestQueryInfo,
  response: z.unknown().nullable(),
  evaluations: z.record(EvaluationMetricResult).nullable(),
  sources_used: z.unknown().nullable(),
  ai_interaction_id: AIInteractionIdField.nullable(),
  created: z.string(),
  updated: z.string(),
  status: z.string(),
  error: z.string().nullable(),
})
const ListRunResponsesViewResponseData = z.object({ responses: z.array(AITestResponseInfo) })
const ListScenarioTemplatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string(),
})
const Organization = z.object({
  id: OrganizationIdField.uuid(),
  identity: IdentityIdField.uuid(),
  parent: OrganizationIdField.uuid(),
  information: z.string(),
  banner: z.string().nullish(),
  bg_color: z.string(),
  logo: z.string().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
})
const OrganizationTemplate = z.object({ organization: Organization, name: z.string() })
const ListScenarioTemplatesViewResponseData = z.object({
  organizations: z.array(OrganizationTemplate),
})
const ListSuggestionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  organization_id: OrganizationIdField.uuid(),
})
const SuggestionIdField = z.string()
const Suggestion = z.object({
  id: SuggestionIdField.uuid(),
  text: z.string(),
  order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  organization: OrganizationIdField.uuid(),
})
const ListSuggestionsViewResponseData = z.object({ suggestions: z.array(Suggestion) })
const ListTestRunsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  include_archived: z.boolean().optional().default(false),
})
const AITestRunConfig = z.object({
  version: z.number().int(),
  exclude_faqs: z.boolean().optional().default(false),
  ignore_organization_prompt: z.boolean().optional().default(false),
  default_user_address: z.string().nullish(),
  category: z.string().nullish(),
})
const AITestRunTriggerTypeEnum = z.enum(['MANUAL', 'SCHEDULED'])
const AITestRun = z.object({
  id: AITestRunIdField.uuid(),
  archived: z.boolean(),
  notes: z.string().nullable(),
  label: z.string().nullable(),
  run_config: AITestRunConfig,
  metadata: z.unknown().nullish(),
  started_at: z.string().datetime({ offset: true }).optional(),
  completed_at: z.string().datetime({ offset: true }).nullish(),
  trigger_type: AITestRunTriggerTypeEnum.optional(),
  timestamp: z.string().datetime({ offset: true }).nullish(),
  organization: OrganizationIdField.uuid(),
  end_user: EndUserIdField.nullish(),
})
const AITestRunWithStats = z.object({
  test_run: AITestRun,
  total_responses: z.number().int(),
  success: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
  in_progress: z.number().int(),
  total_scenarios: z.number().int(),
  scenarios_passed: z.number().int(),
  scenarios_failed: z.number().int(),
})
const ListTestRunsViewResponseData = z.object({
  runs: z.array(AITestRunWithStats),
  total_count: z.number().int(),
})
const ListScenariosViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  category: z.string().optional(),
  null_category: z.boolean().optional().default(false),
  search: z.string().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
})
const ListScenariosViewResponseData = z.object({
  scenarios: z.array(AITestScenarioInfo),
  total_count: z.number().int(),
})
const ListTransferLineSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().optional(),
  transfer_line_source_id: TransferLineSourceIdField.uuid().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
})
const TransferLineSource = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  configs: z.array(TransferLineSourceConfig).optional().default([]),
  metadata: z.unknown().nullish(),
  name: z.string(),
  description: z.string(),
  transfer_confirmation_prompt: z.string().nullish(),
  number: z.string().max(128),
  extension: z.number().int().gte(-9223372036854776000).lte(9223372036854776000).nullish(),
  source: SourceIdField.uuid(),
})
const ListTransferLineSourcesViewResponseData = z.object({
  transfer_lines: z.array(TransferLineSource),
})
const ListTransferredNumbersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const TransferredNumber = z.object({ number: z.string(), names: z.array(z.string()) })
const ListTransferredNumbersViewResponseData = z.object({
  transfer_numbers: z.array(TransferredNumber),
})
const ListVoicesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().nullish(),
  limit: z.number().int().nullish(),
  voice_ids: z.array(z.string()).nullish(),
})
const ELVoice = z.object({
  voice_id: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string().nullable(),
  verified_languages: z.array(z.string()).nullable(),
  preview_url: z.string().nullish(),
})
const ListVoicesViewResponseData = z.object({ voices: z.array(ELVoice) })
const LoadScenariosFromTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  template_organization_id: OrganizationIdField.uuid(),
})
const LoadScenariosFromTemplateViewResponseData = z.object({
  scenarios: z.array(AITestScenarioInfo),
})
const ELToolCall = z.object({
  request_id: z.string(),
  tool_name: z.string(),
  tool_details_body: z.string(),
  tool_has_been_called: z.boolean(),
})
const ELToolResult = z.object({
  request_id: z.string(),
  tool_name: z.string(),
  result_value: z.string(),
  tool_latency_secs: z.number(),
  is_error: z.boolean(),
  tool_has_been_called: z.boolean(),
})
const ELConversationTurn = z.object({
  message: z.string().nullish(),
  tool_calls: z.array(ELToolCall),
  tool_results: z.array(ELToolResult),
  conversation_turn_metrics: z.unknown().nullish(),
  feedback: z.string().nullish(),
  role: z.string(),
  time_in_call_secs: z.number().int(),
})
const ELTranscriptData = z.object({
  transcript: z.array(ELConversationTurn),
  agent_id: z.string(),
  conversation_id: z.string(),
  status: z.string(),
  metadata: z.object({}).partial().passthrough(),
  analysis: z.object({}).partial().passthrough(),
  conversation_initiation_client_data: z.object({}).partial().passthrough(),
})
const ElevenLabsLogConversationViewRequestData = z.object({
  type: z.string(),
  event_timestamp: z.number().int(),
  data: ELTranscriptData,
})
const MarkAIInteractionAsTestViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  is_test: z.boolean(),
})
const PhonicConversationInitiationViewRequestData = z.object({
  from_phone_number: z.string(),
  to_phone_number: z.string(),
  conversation_id: z.string(),
  twilio_call_sid: z.string().nullish(),
  agent: z.object({}).partial().passthrough().optional(),
  template_variables: z.object({}).partial().passthrough().optional(),
  project: z.object({}).partial().passthrough().optional(),
})
const PhonicConversationInitiationViewResponseData = z.object({
  welcome_message: z.string(),
  system_prompt: z.string(),
  template_variables: z.object({}).partial().passthrough(),
})
const PhonicEventsViewRequestData = z.object({
  event_type: z.string(),
  created_at: z.string(),
  data: z.object({}).partial().passthrough(),
})
const PhonicExternalAPICallViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  connection_id: APIConnectionIdField.uuid(),
  name: z.string(),
  parameters: z.unknown().optional(),
})
const PhonicExternalAPICallViewResponseData = z.object({
  success: z.boolean(),
  result: ExternalAPICallResult.nullish(),
  error: z.string().nullish(),
  time_taken_ms: z.number().int(),
})
const PhonicGISLookupViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
  address: z.string().nullish(),
})
const PhonicGISLookupViewResponseData = z.object({
  gis_lookup_result: GISLookupResult,
  time_taken_ms: z.number().int(),
})
const ListPhonicVoicesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const PhonicVoice = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  audio_url: z.string(),
})
const ListPhonicVoicesViewResponseData = z.object({ voices: z.array(PhonicVoice) })
const PhonicRetrieveSourcesViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  query: z.string(),
  has_transfer_intent: z.boolean().nullish(),
  has_application_intent: z.boolean().nullish(),
  service_keys: z.array(z.string()).nullish(),
  input_address: z.string().nullish(),
  page_types: z.array(z.string()).nullish(),
  date_after: z.string().nullish(),
  date_before: z.string().nullish(),
})
const VoiceChunkInfo = z.object({
  organization_name: z.string(),
  title: z.string(),
  text: z.string(),
  chunk_id: z.string(),
  source_id: z.string(),
  source_type: z.string(),
  is_sms_sendable: z.boolean(),
  page_source_id: z.string().nullable(),
  gis_layer_id: z.string().nullish(),
})
const VoicePageInfo = z.object({
  organization_name: z.string(),
  title: z.string(),
  text: z.string(),
  source_id: z.string(),
  source_type: z.string(),
  is_sms_sendable: z.boolean(),
  page_source_id: z.string().nullable(),
})
const VoiceTransferLineInfo = z.object({
  organization_name: z.string(),
  name: z.string(),
  services_and_departments: z.string(),
  transfer_confirmation_prompt: z.string(),
  source_id: z.string(),
  chunk_id: z.string(),
})
const VoiceProcessInfo = z.object({
  organization_name: z.string(),
  process_id: ProcessIdField.uuid(),
  process_name: z.string(),
  process_description: z.string(),
  chunk_id: z.string(),
  source_id: z.string(),
  voice_apply_enabled: z.boolean(),
})
const ChunkFilters = z
  .object({
    must_match: z.object({}).partial().passthrough(),
    must_not_match: z.object({}).partial().passthrough(),
    should_match: z.object({}).partial().passthrough(),
    boost_values: z.record(z.number()),
  })
  .partial()
const RetrievalLogMetrics = z.object({
  time_taken_ms: z.number().int(),
  chunks_found: z.number().int(),
  query: z.string(),
  search_strategies_used: z.array(z.string()),
  rerankers_used: z.array(z.string()).optional(),
  vector_time_ms: z.number().int().nullish(),
  text_time_ms: z.number().int().nullish(),
  expansion_time_ms: z.number().int().nullish(),
  web_search_time_ms: z.number().int().nullish(),
  use_linked_organizations: z.boolean().optional(),
  chunk_filters: ChunkFilters.nullish(),
  error: z.string().nullish(),
  graph_time_ms: z.number().int().nullish(),
  graph_nodes_found: z.number().int().nullish(),
  graph_relations_found: z.number().int().nullish(),
})
const PhonicRetrieveSourcesViewResponseData = z.object({
  faq_info: z.array(VoiceChunkInfo),
  page_sources_info: z.array(VoiceChunkInfo),
  expanded_page_sources_info: z.array(VoicePageInfo),
  transfer_lines: z.array(VoiceTransferLineInfo),
  processes: z.array(VoiceProcessInfo).optional(),
  gis_layers: z.array(VoiceChunkInfo),
  retrieval_metrics: RetrievalLogMetrics,
  time_taken_ms: z.number().int(),
  transfer_lines_disambiguation_needed: z.boolean().optional(),
})
const PhonicSendSMSViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  process_id: ProcessIdField.nullish(),
  page_source_id: PageSourceIdField.nullish(),
  url: z.string().nullish(),
})
const PhonicSendSMSViewResponseData = z.object({
  status: z.string(),
  message: z.string(),
  time_taken_ms: z.number().int(),
})
const PhonicTransferCallViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  transfer_line_source_id: z.string().nullish(),
  number: z.string().nullish(),
})
const PhonicTransferCallViewResponseData = z.object({
  status: z.string(),
  message: z.string(),
  instructions: z.string(),
  transfer_number: z.string(),
  transfer_extension: z.string().nullable(),
  time_taken_ms: z.number().int(),
})
const PreviewBulkArchivePageSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  archived: z.boolean(),
  page_source_ids: z.array(PageSourceIdField).optional(),
  title_pattern: z.string().optional(),
  url_pattern: z.string().optional(),
  source_type: z.string().optional(),
  folder_id: FolderIdField.nullish(),
})
const PreviewBulkArchivePageSourcesViewResponseData = z.object({
  total_count: z.number().int(),
  page_sources: z.array(z.object({}).partial().passthrough()),
})
const PreviewVoiceRequest = z.object({
  user_id: z.string().uuid(),
  organization_identity_id: z.string().uuid(),
  voice_id: z.string(),
  text: z.string(),
  speed: z.number().nullish(),
  stability: z.number().nullish(),
  similarity_boost: z.number().nullish(),
})
const RechunkPageSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  page_source_id: PageSourceIdField.uuid(),
})
const RechunkPageSourceViewResponseData = z.object({
  queued: z.boolean(),
  celery_task_id: z.string().nullish(),
})
const ReextractPageSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  page_source_id: PageSourceIdField.uuid(),
})
const ReextractPageSourceViewResponseData = z.object({
  queued: z.boolean(),
  celery_task_id: z.string().nullish(),
})
const RemoveGISPolygonsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  gis_layer_id: GISLayerSourceIdField.uuid(),
})
const RemoveScenarioViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  id: AITestScenarioIdField.uuid(),
})
const ResolveAIInteractionOrganizationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
})
const ResolveAIInteractionOrganizationViewResponseData = z.object({
  organization_handle: z.string(),
})
const ElevenLabsRetrieveSourcesViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  query: z.string(),
  has_transfer_intent: z.boolean().nullish(),
  has_application_intent: z.boolean().nullish(),
  service_keys: z.array(z.string()).nullish(),
  input_address: z.string().nullish(),
  page_types: z.array(z.string()).nullish(),
  date_after: z.string().nullish(),
  date_before: z.string().nullish(),
})
const ElevenLabsRetrieveSourcesViewResponseData = z.object({
  faq_info: z.array(VoiceChunkInfo),
  page_sources_info: z.array(VoiceChunkInfo),
  expanded_page_sources_info: z.array(VoicePageInfo),
  transfer_lines: z.array(VoiceTransferLineInfo),
  processes: z.array(VoiceProcessInfo).optional(),
  gis_layers: z.array(VoiceChunkInfo),
  retrieval_metrics: RetrievalLogMetrics,
  transfer_lines_disambiguation_needed: z.boolean().optional(),
})
const ScrapePagesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  pages: z.array(z.string()),
  access_type: SourceAccessTypeEnum.optional(),
  folder_id: FolderIdField.nullish(),
})
const ElevenLabsSendSMSViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  process_id: ProcessIdField.nullish(),
  page_source_id: PageSourceIdField.nullish(),
  url: z.string().nullish(),
  called_number: z.string(),
  caller_id: z.string(),
})
const ElevenLabsSendSMSViewResponseData = z.object({ status: z.string(), message: z.string() })
const SetResponseFeedbackImpressionViewRequestData = z.object({
  response_id: ResponseIdField.uuid(),
  user_id: EndUserIdField.nullish(),
  impression: z.boolean().nullable(),
})
const SetSuggestionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  suggestions: z.array(z.string().nullable()).max(4),
})
const SetSuggestionsViewResponseData = z.object({ suggestions: z.array(Suggestion) })
const StartTestRunViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  notes: z.string().nullish(),
  label: z.string().max(40).nullish(),
  exclude_faqs: z.boolean().optional().default(false),
  ignore_organization_prompt: z.boolean().optional().default(false),
  default_user_address: z.string().max(255).nullish(),
  category: z.string().max(255).nullish(),
})
const StartTestRunViewResponseData = z.object({ email_recipient: z.string().email() })
const SubmitResponseFeedbackViewRequestData = z.object({
  response_id: ResponseIdField.uuid(),
  user_id: EndUserIdField.nullish(),
  submitter: z.string().nullish(),
  feedback: z.string(),
})
const SuggestCrawlConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  url: z.string().url(),
  prompt: z.string().optional(),
  current_settings: z.unknown().nullish(),
})
const SuggestCrawlConfigViewResponseData = z.object({ config: z.unknown() })
const SuperGetAIInteractionSystemPromptViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
})
const SuperGetAIInteractionSystemPromptViewResponseData = z.object({
  system_prompt: z.string().nullable(),
})
const SuperGetAIInteractionTranscriptViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
})
const SuperGetAIInteractionTranscriptViewResponseData = z.object({
  interaction_transcript: AIInteractionTranscript,
  organization_ai_settings: EnrichedOrganizationAISettings,
})
const TranscribeAssistantAudioViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  audio: z.string().url(),
})
const TranscribeAssistantAudioViewResponseData = z.object({ text: z.string() })
const ElevenLabsTransferCallViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  call_sid: z.string(),
  transfer_line_source_id: z.string().nullish(),
  number: z.string().nullish(),
  conversation_id: z.string().nullish(),
})
const ElevenLabsTransferCallViewResponseData = z.object({ status: z.string(), message: z.string() })
const TranslateTextViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  text: z.string(),
  source_language: z.string(),
  target_language: z.string(),
})
const TranslateTextViewResponseData = z.object({ translated_text: z.string() })
const UpdateAIInteractionTitleViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  title: z.string().max(255),
})
const UpdateAIInteractionTitleViewResponseData = z.object({ title: z.string() })
const UpdateCrawlTaskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  crawl_task_id: CrawlTaskIdField.uuid(),
})
const UpdateCustomCategoryEvaluationConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  categories: z.array(CustomCategory),
})
const UpdateCustomCategoryEvaluationConfigViewResponseData = z.object({
  prompt_template: z.string(),
})
const UpdateInteractionCategoriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_date: z.string(),
  end_date: z.string(),
})
const TaskIdField = z.string()
const UpdateInteractionCategoriesViewResponseData = z.object({
  task_id: TaskIdField.uuid(),
  interaction_count: z.number().int(),
})
const UpdateTestRunViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  run_id: AITestRunIdField.uuid(),
  notes: z.string().nullish(),
  label: z.string().max(40).nullish(),
})
const UploadAssistantAttachmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  document: z.string().url(),
})
const UploadAssistantAttachmentViewResponseData = z.object({
  attachment_id: z.string(),
  filename: z.string(),
  content_type: z.string().nullable(),
  download_url: z.string(),
  is_image: z.boolean(),
  preview_url: z.string().nullish(),
})
const UpsertAIEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  outbound_address: z.string().email(),
  form_letter_id: MessageTemplateIdField.nullish(),
  forward_only: z.boolean().optional().default(false),
  create_intake_case: z.boolean().optional().default(false),
  forwarding_prompting: z.record(z.string()).nullish(),
  default_forwarding_address: z.string().email(),
})
const UpsertAIEmailViewResponseData = z.object({
  id: AIEmailIdField.uuid(),
  inbound_address: z.string(),
})
const IdentifiedWorkflowRowIndexSerializerIdentifiedWorkflowRowIndex = z.object({
  tab_index: z.number().int().gte(0),
  row_index: z.number().int().gte(0),
})
const BuildIdentifiedWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  rows: z.array(IdentifiedWorkflowRowIndexSerializerIdentifiedWorkflowRowIndex),
})
const BuildIdentifiedWorkflowsViewResponseData = z.object({
  process_ids: z.array(ProcessIdField),
  workflow_codes: z.array(z.string()),
  organization_handle: z.string(),
})
const ClearIdentifiedWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const ClearIdentifiedWorkflowsViewResponseData = z.object({ cleared: z.boolean() })
const PricingTierEnum = z.enum(['Activate', 'Connect', 'Deliver', 'Unify'])
const CostDriverEnum = z.enum([
  'SEARCH',
  'CHAT',
  'VOICE',
  'SMS',
  'WHATSAPP',
  'EMAIL',
  'ASSISTANT',
  'KB_BUILD',
  'AI_ANALYTICS',
  'HEALTH_CHECK',
  'PUBLIC_RECORDS',
  'AGENTIC_BUILDER',
  'OTHER',
])
const ExportLLMCostViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  organization_id: OrganizationIdField.nullish(),
  customer_tiers: z.array(PricingTierEnum).optional(),
  drivers: z.array(CostDriverEnum).optional(),
  providers: z.array(z.string()).optional(),
  models: z.array(z.string()).optional(),
  purposes: z.array(z.string()).optional(),
  include_test: z.boolean().optional().default(false),
})
const ExportLLMCostViewResponseData = z.object({ email_recipient: z.string().email() })
const GetChannelVolumeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  days: z.number().int().gte(1).lte(90).optional().default(7),
})
const ChannelVolumeSeries = z.object({
  id: z.string(),
  label: z.string(),
  daily_counts: z.array(z.number().int()),
})
const GetChannelVolumeViewResponseData = z.object({
  days: z.array(z.string()),
  series: z.array(ChannelVolumeSeries),
})
const GetCrawlTaskDashboardViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  filter_organization: OrganizationIdField.nullish(),
  days_back: z.number().int().optional().default(7),
  limit_recent_logs: z.number().int().optional().default(50),
})
const LightweightQuerySourcesLog = z.object({
  id: z.string().uuid().optional(),
  crawl_task: CrawlTaskIdField.nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  firecrawl_monitor_check_id: z.string().max(255).nullish(),
  links_added_count: z.number().int(),
  links_removed_count: z.number().int(),
  links_scraped_count: z.number().int(),
  crawl_task_organization_name: z.string(),
  crawl_task_url: z.string(),
  organization_name: z.string(),
  page_url: z.string(),
  chunks_created: z.number().int().nullable(),
  site_crawler_used: WebsiteTypeEnum.nullish(),
  action: SourceActionEnum.optional(),
  description: z.string().nullish(),
  status: CrawlStatusEnum.nullish(),
  failure_message: z.string().nullish(),
  source: SourceIdField.nullish(),
  user: EndUserIdField.nullish(),
  organization: OrganizationIdField.nullish(),
})
const CrawlTaskDailyCounts = z.object({
  date: z.string(),
  total: z.number().int(),
  success: z.number().int(),
  failure: z.number().int(),
})
const CrawlTaskSummaryStats = z.object({
  total_tasks: z.number().int(),
  total_active_tasks: z.number().int(),
  total_scheduled_tasks: z.number().int(),
  total_queued_tasks: z.number().int(),
  total_running_tasks: z.number().int(),
  total_recent_logs: z.number().int(),
})
const GetCrawlTaskDashboardViewResponseData = z.object({
  scheduled_tasks: z.array(CrawlTask),
  queued_tasks: z.array(CrawlTask),
  running_tasks: z.array(CrawlTask),
  recent_task_logs: z.array(LightweightQuerySourcesLog),
  recent_manual_scrapes: z.array(LightweightQuerySourcesLog),
  status_data: z.record(z.number().int()),
  daily_counts: z.array(CrawlTaskDailyCounts),
  summary_stats: CrawlTaskSummaryStats,
})
const GetFirecrawlConcurrencyViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetFirecrawlConcurrencyViewResponseData = z.object({
  current: z.number().int(),
  limit: z.number().int(),
})
const GetFirecrawlConcurrencyHistoryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  lookback_days: z.number().int().gte(1).lte(14).optional().default(14),
})
const FirecrawlConcurrencyLogIdField = z.string()
const FirecrawlConcurrencyLog = z.object({
  id: FirecrawlConcurrencyLogIdField.uuid(),
  timestamp: z.string().datetime({ offset: true }),
  current: z.number().int(),
  limit: z.number().int(),
})
const GetFirecrawlConcurrencyHistoryViewResponseData = z.object({
  points: z.array(FirecrawlConcurrencyLog),
})
const FrontDeskInteractionTypeEnum = z.enum(['ALL', 'CHAT', 'VOICE', 'SEARCH', 'EMAIL'])
const GetFrontDeskDataRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_date: z.string(),
  end_date: z.string(),
  interaction_type: FrontDeskInteractionTypeEnum,
})
const HandledInteractions = z.object({ handled: z.number().int(), not_handled: z.number().int() })
const GetFrontDeskDataResponseData = z.object({
  total_engaged_interactions: z.number().int(),
  time_saved_ms: z.number().int().nullable(),
  handle_rate: z.number().nullable(),
  average_quality: z.number().nullable(),
  user_satisfaction_rate: z.number().nullable(),
  interactions_over_time: z.record(z.record(z.number().int())),
  interactions_by_hour_of_day: z.record(z.number().int()),
  interactions_by_category: z.record(HandledInteractions),
  max_concurrent_interactions: z.number().int(),
  num_multi_turn_interactions: z.number().int(),
  language_distribution: z.record(z.number().int()),
  total_interactions: z.number().int(),
  total_calls: z.number().int(),
  total_engaged_calls: z.number().int(),
  transferred_interactions: z.number().int(),
  abandoned_interactions: z.number().int(),
  interactions_by_transfer_line: z.record(z.number().int()),
})
const GetHealthCheckSummaryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const GetHealthCheckSummaryViewResponseData = z.object({
  pass_rate: z.number().nullable(),
  health_check_setup: z.boolean(),
  last_run_at: z.string().datetime({ offset: true }).nullable(),
  latest_health_check_url: z.string().optional(),
})
const GetIdentifiedWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const IdentifiedWorkflowRowSerializerIdentifiedWorkflowRow = z.object({
  row_index: z.number().int(),
  values: z.array(z.string()),
  workflow_name: z.string().nullable(),
  existing_workflow_code: z.string().nullable(),
  builder_status: z.string().nullable(),
})
const IdentifiedWorkflowTabSerializerIdentifiedWorkflowTab = z.object({
  tab_index: z.number().int(),
  name: z.string(),
  columns: z.array(z.string()),
  rows: z.array(IdentifiedWorkflowRowSerializerIdentifiedWorkflowRow),
})
const GetIdentifiedWorkflowsViewResponseData = z.object({
  organization_handle: z.string(),
  file_name: z.string().nullable(),
  file_url: z.string().nullable(),
  uploaded: z.string().datetime({ offset: true }).nullable(),
  tabs: z.array(IdentifiedWorkflowTabSerializerIdentifiedWorkflowTab),
})
const GetIntegrationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const ConnectionTypeEnum = z.enum([
  'generic',
  'fasttrack',
  'request311',
  'tripspark',
  'munis_arws',
  'cityworks',
  'sharepoint',
  'cayenta_utility_billing',
  'opengov',
  'central_square',
  'energov_epl',
])
const ActiveIntegration = z.object({
  id: z.string(),
  name: z.string(),
  connection_type: ConnectionTypeEnum,
  workflow_count: z.number().int(),
  interaction_count: z.number().int(),
})
const SuggestedIntegration = z.object({
  connection_type: ConnectionTypeEnum,
  monthly_question_count: z.number().int(),
  question_topic: z.string(),
})
const GetIntegrationsViewResponseData = z.object({
  active_integrations: z.array(ActiveIntegration),
  suggested_integrations: z.array(SuggestedIntegration),
})
const GetInteractionDashboardStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  organization_id: OrganizationIdField.nullish(),
  interaction_type: AIInteractionTypeEnum.nullish(),
  bucket_hours: z.number().int().gte(1).lte(24).optional().default(1),
  top_orgs_limit: z.number().int().gte(1).lte(100).optional().default(20),
})
const InteractionTypeBreakdown = z.object({
  interaction_type: AIInteractionTypeEnum,
  count: z.number().int(),
  percent: z.number(),
})
const InteractionBucket = z.object({
  bucket_start: z.string().datetime({ offset: true }),
  total: z.number().int(),
  by_type: z.record(z.number().int()),
})
const OrgInteractionSummary = z.object({
  organization_id: OrganizationIdField.uuid(),
  organization_name: z.string(),
  total: z.number().int(),
  by_type: z.record(z.number().int()),
})
const DurationBucket = z.object({
  bin_start_ms: z.number().int(),
  bin_end_ms: z.number().int(),
  count: z.number().int(),
})
const DurationStats = z.object({
  histogram: z.array(DurationBucket),
  interaction_type: AIInteractionTypeEnum,
  avg_ms: z.number().nullable(),
  p50_ms: z.number().int().nullable(),
  p90_ms: z.number().int().nullable(),
})
const HourOfDayRow = z.object({
  hour: z.number().int(),
  total: z.number().int(),
  by_type: z.record(z.number().int()),
})
const InteractionDashboardStats = z.object({
  by_type: z.array(InteractionTypeBreakdown),
  buckets: z.array(InteractionBucket),
  top_orgs: z.array(OrgInteractionSummary),
  duration_stats: z.array(DurationStats),
  hours_of_day: z.array(HourOfDayRow),
  total: z.number().int(),
})
const GetInteractionDashboardStatsViewResponseData = z.object({
  interaction_stats: InteractionDashboardStats,
})
const GetKnowledgeBaseQualityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const GetKnowledgeBaseQualityViewResponseData = z.object({
  sources: z.number().int(),
  stale: z.number().int(),
  gaps: z.number().int(),
  status: z.string(),
})
const GetKPIsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const GetKPIsViewResponseData = z.object({ values: z.record(z.number().nullable()) })
const GetLLMCostDashboardStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  organization_id: OrganizationIdField.nullish(),
  customer_tiers: z.array(PricingTierEnum).optional(),
  drivers: z.array(CostDriverEnum).optional(),
  providers: z.array(z.string()).optional(),
  models: z.array(z.string()).optional(),
  purposes: z.array(z.string()).optional(),
  include_test: z.boolean().optional().default(false),
  include_hourly: z.boolean().optional().default(false),
  top_orgs_limit: z.number().int().gte(1).lte(500).optional().default(50),
  top_interactions_limit: z.number().int().gte(1).lte(500).optional().default(50),
  top_crawls_limit: z.number().int().gte(1).lte(500).optional().default(50),
  top_devin_sessions_limit: z.number().int().gte(1).lte(500).optional().default(50),
  totals_only: z.boolean().optional().default(false),
  recent_start_date: z.string().datetime({ offset: true }).nullish(),
})
const LLMTokenTotals = z
  .object({
    total_input_tokens: z.number().int(),
    cached_input_tokens: z.number().int(),
    cache_write_tokens: z.number().int(),
    output_tokens: z.number().int(),
    reasoning_tokens: z.number().int(),
  })
  .partial()
const LLMCostBucket = z.object({
  tokens: LLMTokenTotals,
  calls: z.number().int().optional(),
  unpriced_calls: z.number().int().optional(),
  firecrawl_credits: z.number().int().optional(),
  unpriced_firecrawl_credits: z.number().int().optional(),
  devin_acus: z.number().optional(),
  cost_micro_usd: z.number().int().optional(),
  cost_by_driver_micro_usd: z.record(z.number().int()).optional(),
  voice_seconds: z.number().int().optional(),
})
const LLMCostTimeRow = z.object({
  tokens: LLMTokenTotals,
  calls: z.number().int().optional(),
  unpriced_calls: z.number().int().optional(),
  firecrawl_credits: z.number().int().optional(),
  unpriced_firecrawl_credits: z.number().int().optional(),
  devin_acus: z.number().optional(),
  cost_micro_usd: z.number().int().optional(),
  cost_by_driver_micro_usd: z.record(z.number().int()).optional(),
  voice_seconds: z.number().int().optional(),
  day: z.string().nullish(),
  hour: z.string().datetime({ offset: true }).nullish(),
})
const LLMCostOrganizationRow = z.object({
  tokens: LLMTokenTotals,
  recent_cost_micro_usd: z.number().int().nullish(),
  calls: z.number().int().optional(),
  unpriced_calls: z.number().int().optional(),
  firecrawl_credits: z.number().int().optional(),
  unpriced_firecrawl_credits: z.number().int().optional(),
  devin_acus: z.number().optional(),
  cost_micro_usd: z.number().int().optional(),
  cost_by_driver_micro_usd: z.record(z.number().int()).optional(),
  voice_seconds: z.number().int().optional(),
  organization_id: OrganizationIdField.nullish(),
  organization_name: z.string().optional(),
  organization_image: z.string().nullish(),
  organization_state: z.string().nullish(),
  customer_tier: z.string().nullish(),
  first_day: z.string().nullish(),
  interactions: z.number().int().optional(),
  interactions_by_driver: z.record(z.number().int()).optional(),
})
const LLMCostModelRow = z.object({
  tokens: LLMTokenTotals,
  calls: z.number().int().optional(),
  unpriced_calls: z.number().int().optional(),
  firecrawl_credits: z.number().int().optional(),
  unpriced_firecrawl_credits: z.number().int().optional(),
  devin_acus: z.number().optional(),
  cost_micro_usd: z.number().int().optional(),
  cost_by_driver_micro_usd: z.record(z.number().int()).optional(),
  voice_seconds: z.number().int().optional(),
  provider: z.string().optional(),
  model: z.string().optional(),
  has_pricing: z.boolean().optional(),
  priced_units: z.array(z.string()).optional(),
})
const LLMCostInteractionRow = z.object({
  interaction_id: AIInteractionIdField.uuid(),
  started: z.string().datetime({ offset: true }),
  purposes: z.array(z.string()),
  models: z.array(z.string()),
  total_tokens: z.number().int(),
  cost_micro_usd: z.number().int(),
  unpriced_calls: z.number().int(),
  code: z.string().nullish(),
  channel: z.string().nullish(),
  duration_seconds: z.number().int().nullish(),
})
const QuerySourcesLogIdField = z.string()
const LLMCostCrawlRow = z.object({
  cost_micro_usd: z.number().int().nullish(),
  log_id: QuerySourcesLogIdField.uuid(),
  started: z.string().datetime({ offset: true }),
  credits: z.number().int(),
  credits_by_operation: z.record(z.number().int()),
  organization_id: OrganizationIdField.nullish(),
  organization_name: z.string().optional(),
  url: z.string().nullish(),
  status: z.string().nullish(),
})
const AgenticImplementationLogIdField = z.string()
const LLMCostDevinSessionRow = z.object({
  log_id: AgenticImplementationLogIdField.uuid(),
  started: z.string().datetime({ offset: true }),
  subject_type: z.string(),
  acus: z.number(),
  organization_id: OrganizationIdField.nullish(),
  organization_name: z.string().optional(),
  name: z.string().nullish(),
  cost_micro_usd: z.number().int().optional(),
})
const LLMCostFilterOptions = z
  .object({
    providers: z.array(z.string()),
    models: z.array(z.string()),
    purposes: z.array(z.string()),
  })
  .partial()
const LLMCostDashboardStats = z.object({
  totals: LLMCostBucket,
  by_day: z.array(LLMCostTimeRow),
  by_organization: z.array(LLMCostOrganizationRow),
  by_model: z.array(LLMCostModelRow),
  by_hour: z.array(LLMCostTimeRow),
  top_interactions: z.array(LLMCostInteractionRow),
  top_crawls: z.array(LLMCostCrawlRow),
  top_devin_sessions: z.array(LLMCostDevinSessionRow),
  filter_options: LLMCostFilterOptions,
  recent_cost_micro_usd: z.number().int().nullish(),
  interactions_by_driver: z.record(z.number().int()).optional(),
  interactions: z.number().int().optional(),
})
const GetLLMCostDashboardStatsViewResponseData = z.object({ llm_cost_stats: LLMCostDashboardStats })
const GetOrgWorkflowScorecardViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const GetOrgWorkflowScorecardViewResponseData = z.object({
  has_voicemail_workflow: z.boolean(),
  custom_workflow_count: z.number().int(),
  has_sso: z.boolean(),
  contact_count: z.number().int(),
  has_payments: z.boolean(),
  has_payment_terminals: z.boolean(),
  domains: z.array(z.string()),
  pricing_tier: z.string(),
})
const SankeyInteractionTypeEnum = z.enum(['search', 'chat', 'voice'])
const ViewTypeEnum = z.enum(['outcomes', 'topics'])
const GetOrganizationAIInteractionFlowsRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_date: z.string(),
  end_date: z.string(),
  interaction_type: SankeyInteractionTypeEnum,
  view_type: ViewTypeEnum,
})
const AIInteractionFlow = z.object({
  value: z.number().int(),
  source: z.string(),
  target: z.string(),
})
const GetOrganizationAIInteractionFlowsResponseData = z.object({
  flows: z.array(AIInteractionFlow),
})
const GetOrganizationAIInteractionStatsRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_date: z.string(),
  end_date: z.string(),
  interaction_type: AIInteractionTypeEnum,
})
const GetOrganizationAIInteractionStatsResponseData = z.object({
  total_interactions: z.number().int(),
  no_engagement_interactions: z.number().int(),
  avg_quality: z.number().nullable(),
  avg_sentiment: UserSentimentEnum.nullable(),
  handle_rate: z.number().nullable(),
  staffer_time_saved: z.number().nullable(),
})
const CaseTaskEnum = z.enum(['case_task', 'case_step', 'case_reply'])
const OverdueEnum = z.enum(['overdue', 'due_today', 'at_risk', 'on_track'])
const AssignedToYouEnum = z.enum(['assigned_to_you', 'unassigned', 'overdue', 'assigned_to_others'])
const GetTasksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  organization_identity_ids: z.array(IdentityIdField).optional(),
  assignee_ids: z.array(IdentityIdField).optional(),
  work_types: z.array(CaseTaskEnum).optional(),
  sla_statuses: z.array(OverdueEnum).optional(),
  due_after: z.string().datetime({ offset: true }).optional(),
  due_before: z.string().datetime({ offset: true }).optional(),
  tab: AssignedToYouEnum.optional(),
  page: z.number().int().gte(1).optional().default(1),
  page_size: z.number().int().gte(1).lte(100).optional(),
})
const BlockInstanceIdField = z.string()
const ItemIdField = z.string()
const StepFormEnum = z.enum([
  'step_form',
  'step_license',
  'step_review',
  'step_payment',
  'step_communication',
  'case_reply',
])
const HomePageTask = z.object({
  block_instance_id: BlockInstanceIdField.nullable(),
  item_id: ItemIdField.nullable(),
  title: z.string(),
  type: StepFormEnum,
  description: z.string().nullable(),
  case_name: z.string(),
  case_handle: z.string(),
  due_date: z.string().datetime({ offset: true }).nullable(),
  assignee_ids: z.array(IdentityIdField),
})
const GetTasksViewResponseData = z.object({
  tasks: z.array(HomePageTask),
  total_count: z.number().int(),
})
const GetToolCallDashboardStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  start_date: z.string().datetime({ offset: true }),
  end_date: z.string().datetime({ offset: true }),
  organization_id: OrganizationIdField.nullish(),
  tool_name: z.string().nullish(),
  status: ToolCallStatusEnum.nullish(),
  bucket_hours: z.number().int().gte(1).lte(24).optional().default(1),
})
const ToolCallBucket = z.object({
  total: z.number().int(),
  error_rate: z.number(),
  bucket_start: z.string().datetime({ offset: true }),
  success: z.number().int(),
  warning: z.number().int(),
  error: z.number().int(),
})
const ToolCallDashboardStats = z.object({
  buckets: z.array(ToolCallBucket),
  total: z.number().int(),
  success_count: z.number().int(),
  warning_count: z.number().int(),
  error_count: z.number().int(),
  success_rate: z.number(),
  warning_rate: z.number(),
  error_rate: z.number(),
  top_error_tool: z.string().nullable(),
})
const GetToolCallDashboardStatsViewResponseData = z.object({
  tool_call_stats: ToolCallDashboardStats,
})
const GetTopCategoriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const TopCategory = z.object({
  id: z.string(),
  category: z.string(),
  count: z.number().int(),
  change_percent: z.number(),
})
const GetTopCategoriesViewResponseData = z.object({ top_categories: z.array(TopCategory) })
const GetTopDomainsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const GetTopDomainsViewResponseData = z.object({ domains: z.array(z.string()) })
const GetWorkflowPerformanceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  limit: z.number().int().gte(1).lte(50).optional().default(4),
  min_completion_seconds: z.number().int().gte(0).optional().default(60),
})
const BlockIdField = z.string()
const WorkflowPerformanceItem = z.object({
  origin_block_id: BlockIdField.uuid(),
  block_name: z.string(),
  workflow_name: z.string(),
  workflow_code: z.string(),
  organization_handle: z.string(),
  stage_name: z.string().nullable(),
  last_7_days_average_seconds: z.number(),
  last_7_days_median_seconds: z.number(),
  last_7_days_case_count: z.number().int(),
  all_time_average_seconds: z.number(),
  all_time_median_seconds: z.number(),
  all_time_case_count: z.number().int(),
})
const GetWorkflowPerformanceViewResponseData = z.object({ items: z.array(WorkflowPerformanceItem) })
const GetWorkflowUsageDashboardStatsViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const MonthlyCaseCountSerializerMonthlyCaseCount = z.object({
  month: z.string(),
  unify_case_count: z.number().int(),
  deliver_case_count: z.number().int(),
})
const WorkspaceWorkflowUsageSerializerWorkspaceWorkflowUsage = z.object({
  workspace_id: WorkspaceIdField.uuid(),
  workspace_name: z.string(),
  organization_count: z.number().int(),
  interaction_count: z.number().int(),
  last_month_interaction_count: z.number().int(),
  case_count: z.number().int(),
  avg_cases_per_month: z.number(),
  workflow_count: z.number().int(),
  live_workflow_count: z.number().int(),
  busy_live_workflow_count: z.number().int(),
})
const OrgWorkflowUsageSerializerOrgWorkflowUsage = z.object({
  organization_id: OrganizationIdField.uuid(),
  organization_name: z.string(),
  workspace_id: WorkspaceIdField.uuid(),
  interaction_count: z.number().int(),
  last_month_interaction_count: z.number().int(),
  case_count: z.number().int(),
  avg_cases_per_month: z.number(),
  workflow_count: z.number().int(),
  live_workflow_count: z.number().int(),
  busy_live_workflow_count: z.number().int(),
})
const ProcessStatusEnum = z.enum(['draft', 'live', 'archived'])
const WorkflowComplexitySerializerWorkflowComplexity = z.object({
  workflow_id: WorkflowIdField.uuid(),
  workflow_name: z.string(),
  organization_id: OrganizationIdField.uuid(),
  organization_name: z.string(),
  block_count: z.number().int(),
  status: ProcessStatusEnum,
  first_case_date: z.string().datetime({ offset: true }).nullable(),
  has_communication_block: z.boolean(),
  has_ai_pre_review: z.boolean(),
  is_general_intake: z.boolean(),
  is_agentic: z.boolean(),
  case_count: z.number().int(),
  avg_cases_per_week: z.number(),
})
const WorkflowUsageStatsSerializerWorkflowUsageStats = z.object({
  cases_by_month: z.array(MonthlyCaseCountSerializerMonthlyCaseCount),
  workspaces: z.array(WorkspaceWorkflowUsageSerializerWorkspaceWorkflowUsage),
  orgs: z.array(OrgWorkflowUsageSerializerOrgWorkflowUsage),
  workflows: z.array(WorkflowComplexitySerializerWorkflowComplexity),
})
const GetWorkflowUsageDashboardStatsViewResponseData = z.object({
  workflow_usage_stats: WorkflowUsageStatsSerializerWorkflowUsageStats,
})
const ListCrawlTasksMetricsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  filter_organization: OrganizationIdField.nullish(),
  filter_query: z.string().nullish(),
  limit: z.number().int().nullish(),
  offset: z.number().int().nullish(),
  sort_field: CrawlTaskSortTypeEnum.nullish(),
  sort_direction: SortDirectionEnum.nullish(),
})
const ListCrawlTasksMetricsViewResponseData = z.object({ crawl_tasks: z.array(CrawlTask) })
const ListToolCallSummariesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.nullish(),
  tool_name: z.string().nullish(),
  start_date: z.string().datetime({ offset: true }).nullish(),
  end_date: z.string().datetime({ offset: true }).nullish(),
  status: ToolCallStatusEnum.nullish(),
  limit: z.number().int().gte(1).optional(),
  offset: z.number().int().gte(0).optional(),
})
const ToolCallSummary = z.object({
  status: ToolCallStatusEnum,
  tool_name: z.string(),
  created: z.string().datetime({ offset: true }),
  ai_interaction_id: AIInteractionIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  organization_name: z.string(),
  error_message: z.string().nullable(),
  time_taken_ms: z.number().int().nullable(),
})
const ListToolCallSummariesViewResponseData = z.object({ tool_calls: z.array(ToolCallSummary) })
const RequestAIAnalyticsReportViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_date: z.string(),
  end_date: z.string(),
  interaction_type: FrontDeskInteractionTypeEnum,
})
const RequestAIAnalyticsReportViewResponseData = z.object({ email_recipient: z.string().email() })
const SetWorkflowGeneralIntakeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
  general_intake: z.boolean(),
})
const SetWorkflowGeneralIntakeViewResponseData = z.object({ general_intake: z.boolean() })
const UploadIdentifiedWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  file: z.string().url(),
})
const UploadIdentifiedWorkflowsViewResponseData = z.object({ file_name: z.string() })
const AddCaseMemberViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  member_id: IdentityIdField.uuid(),
})
const FormIdField = z.string()
const InputIdField = z.string()
const CommentThreadIdField = z.string()
const AddCommentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  content: z.string(),
  author_id: IdentityIdField.uuid(),
  form_id: FormIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  input_id: InputIdField.uuid().optional(),
  thread_id: CommentThreadIdField.uuid().optional(),
})
const CommentIdField = z.string()
const Comment = z.object({
  id: CommentIdField.uuid(),
  comment_thread: CommentThreadIdField.uuid(),
  commenter: IdentityIdField.nullish(),
  comment: z.string(),
  time: z.string().datetime({ offset: true }).optional(),
  commenter_name: z.string().nullish(),
  commenter_profile: z.string().nullish(),
  commenter_email: z.string().email().nullish(),
})
const AddCommentViewResponseData = z.object({ comment: Comment })
const ReviewCommentThreadIdField = z.string()
const AddReviewCommentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  review_comment_thread_id: ReviewCommentThreadIdField.uuid(),
  content: z.string(),
})
const ReviewCommentIdField = z.string()
const ReviewComment = z.object({
  id: ReviewCommentIdField.uuid(),
  review_comment_thread: ReviewCommentThreadIdField.uuid(),
  author: EndUserIdField.uuid(),
  content: z.string(),
  timestamp: z.string().datetime({ offset: true }).optional(),
})
const AddReviewCommentViewResponseData = z.object({ review_comment: ReviewComment })
const AgenticImplementationAttachmentSerializerAgenticImplementationAttachment = z.object({
  attachment_id: z.string(),
  filename: z.string(),
  content_type: z.string().nullish(),
  s3_url: z.string(),
})
const AppendAgenticImplementationMessageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  content: z.string(),
  display_content: z.string().optional(),
  attachments: z
    .array(AgenticImplementationAttachmentSerializerAgenticImplementationAttachment)
    .optional(),
})
const AgenticImplementationMessageKindEnum = z.enum(['user', 'agent', 'event'])
const AgenticImplementationMessageSerializerAgenticImplementationMessage = z.object({
  message_id: z.string(),
  kind: AgenticImplementationMessageKindEnum,
  content: z.string(),
  display_content: z.string().optional(),
  sender_name: z.string().optional(),
  created: z.string(),
  attachments: z
    .array(AgenticImplementationAttachmentSerializerAgenticImplementationAttachment)
    .optional(),
})
const AppendAgenticImplementationMessageViewResponseData = z.object({
  log_id: z.string(),
  messages: z.array(AgenticImplementationMessageSerializerAgenticImplementationMessage),
  status: z.string(),
  status_message: z.string().nullish(),
})
const AttachProcessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const BeginReviewViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.uuid(),
})
const ReviewIdField = z.string()
const AcceptEnum = z.enum(['accept', 'request_changes', 'reject'])
const Review = z.object({
  id: ReviewIdField.uuid(),
  reviewer: EndUserIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
  review_outcome: AcceptEnum.nullish(),
  top_level_comment: z.string().optional(),
  deadline: z.string().datetime({ offset: true }).nullish(),
  completion_timestamp: z.string().datetime({ offset: true }).nullish(),
})
const BeginReviewViewResponseData = z.object({ review: Review })
const InputStepIdField = z.string()
const BulkCreateInputExpressionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  input_step_ids: z.array(InputStepIdField),
})
const ExpressionIdField = z.string()
const ValueTypeEnum = z.enum([
  'bool',
  'datetime',
  'file',
  'identity',
  'location',
  'number',
  'signature',
  'text',
])
const CaseIdentifierTypeEnum = z.literal('case_handle')
const CustomExpressionNameEnum = z.literal('sequential_yearly_id')
const OperatorTypeEnum = z.enum([
  'addition',
  'and',
  'concat',
  'conditional',
  'equals',
  'greater_than',
  'less_than',
  'maximum',
  'minimum',
  'multiplication',
  'or',
  'subtraction',
])
const ValueIdField = z.string()
const ReviewBlockIdField = z.string()
const Expression = z.object({
  id: ExpressionIdField.uuid(),
  metadata: z.unknown().nullish(),
  type: ValueTypeEnum,
  case_creator: z.boolean().nullish(),
  case_identifier: CaseIdentifierTypeEnum.nullish(),
  custom: CustomExpressionNameEnum.nullish(),
  name: z.string().nullish(),
  operator: OperatorTypeEnum.nullish(),
  today: z.boolean().nullish(),
  parameter_one_type: ValueTypeEnum.nullish(),
  parameter_two_type: ValueTypeEnum.nullish(),
  parameter_three_type: ValueTypeEnum.nullish(),
  collapsed: z.boolean().optional(),
  constant: ValueIdField.nullish(),
  input_value: InputStepIdField.nullish(),
  review_outcome: ReviewBlockIdField.nullish(),
  process: ProcessIdField.uuid(),
  parameter_one: ExpressionIdField.nullish(),
  parameter_two: ExpressionIdField.nullish(),
  parameter_three: ExpressionIdField.nullish(),
})
const BulkCreateInputExpressionsViewResponseData = z.object({ expressions: z.array(Expression) })
const StatusEnum = z.enum(['Unsubmitted', 'Open', 'Closed', 'Deleted', 'Hidden'])
const BulkEditCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  case_ids: z.array(CaseIdField).min(1),
  status: StatusEnum.nullish(),
})
const BulkEditWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  workflow_ids: z.array(WorkflowIdField).min(1),
  status: Active1Enum,
})
const DatetimeStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  type: ValueTypeEnum,
  request_time: z.boolean(),
})
const IdentityStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  metadata: z.object({}).partial().passthrough().nullish(),
  type: ValueTypeEnum,
  require_me: z.boolean(),
  show_address: z.boolean(),
  require_address: z.boolean(),
  show_date_of_birth: z.boolean(),
  require_date_of_birth: z.boolean(),
  show_email: z.boolean(),
  require_email: z.boolean(),
  verify_email: z.boolean(),
  show_name: z.boolean(),
  require_name: z.boolean(),
  show_phone: z.boolean(),
  require_phone: z.boolean(),
  add_contact: z.boolean(),
})
const NumberStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  type: ValueTypeEnum,
  allow_negative_numbers: z.boolean(),
  allow_zero: z.boolean(),
  decimal_places: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  prefix_unit: z.string().nullish(),
  suffix_unit: z.string().nullish(),
})
const FileStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  type: ValueTypeEnum,
  allowed_file_types: z.array(z.string()).nullish(),
})
const SignatureStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  type: ValueTypeEnum,
  is_typed_signature: z.boolean(),
  check_outdated: z.boolean().optional(),
})
const LocationStep = z.object({
  id: z.string().uuid().optional(),
  input_step: InputStepIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  type: ValueTypeEnum,
  request_address: z.boolean(),
  request_coordinates: z.boolean(),
  valid_area: GISPolygonIdField.nullish(),
  overlay: GISLayerSourceIdField.nullish(),
})
const InputStepSub0 = z.object({
  datetime_step_sub: DatetimeStep.nullish(),
  identity_step_sub: IdentityStep.nullish(),
  number_step_sub: NumberStep.nullish(),
  file_step_sub: FileStep.nullish(),
  signature_step_sub: SignatureStep.nullish(),
  location_step_sub: LocationStep.nullish(),
  type: ValueTypeEnum,
})
const BoolValueIdField = z.string()
const BoolValue0 = z.object({
  id: BoolValueIdField.uuid(),
  value: ValueIdField.uuid(),
  checked: z.boolean(),
})
const DatetimeValueIdField = z.string()
const DatetimeValue0 = z.object({
  id: DatetimeValueIdField.uuid(),
  value: ValueIdField.uuid(),
  datetime: z.string().datetime({ offset: true }),
})
const FileValueIdField = z.string()
const FileValue0 = z.object({
  id: FileValueIdField.uuid(),
  value: ValueIdField.uuid(),
  files: z.array(z.string()),
})
const IdentityValueIdField = z.string()
const IdentityValue0 = z.object({
  id: IdentityValueIdField.uuid(),
  value: ValueIdField.uuid(),
  identity: IdentityIdField.uuid(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  address_street: z.string().nullable(),
  address_city: z.string().nullable(),
  address_state: z.string().nullable(),
  address_zip: z.string().nullable(),
})
const LocationValueIdField = z.string()
const LocationIdField = z.string()
const AddressIdField = z.string()
const LocationValue0 = z.object({
  id: LocationValueIdField.uuid(),
  value: ValueIdField.uuid(),
  location: LocationIdField.uuid(),
  address: AddressIdField.nullish(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
})
const NumberValueIdField = z.string()
const NumberValue0 = z.object({
  id: NumberValueIdField.uuid(),
  value: ValueIdField.uuid(),
  number: z.string().regex(/^-?\d{0,18}(?:\.\d{0,12})?$/),
})
const SignatureValueIdField = z.string()
const SignatureValue0 = z.object({
  id: SignatureValueIdField.uuid(),
  value: ValueIdField.uuid(),
  signer: EndUserIdField.nullable(),
  signature: z.string(),
  signer_name: z.string().nullable(),
  form_data_hash: z.number().int().nullable(),
  timestamp: z.string().datetime({ offset: true }).nullable(),
})
const TextValueIdField = z.string()
const TextValue0 = z.object({
  id: TextValueIdField.uuid(),
  value: ValueIdField.uuid(),
  text: z.string(),
})
const Value0 = z.object({
  bool_value: BoolValue0.nullish(),
  datetime_value: DatetimeValue0.nullish(),
  file_value: FileValue0.nullish(),
  identity_value: IdentityValue0.nullish(),
  location_value: LocationValue0.nullish(),
  number_value: NumberValue0.nullish(),
  signature_value: SignatureValue0.nullish(),
  text_value: TextValue0.nullish(),
  type: ValueTypeEnum,
})
const InputStepChoice1 = z.object({
  value: Value0,
  step: z.string().uuid(),
  order: z.number().int(),
  allow_custom_text: z.boolean(),
})
const InputStyleEnum = z.enum([
  'RawInputStyle',
  'DropdownInputStyle',
  'RadioInputStyle',
  'LongTextInputStyle',
  'SignatureInputStyle',
])
const InputSubstep2 = z.object({
  sub: InputStepSub0,
  choices: z.array(InputStepChoice1),
  template: Value0.nullish(),
  form: FormStepIdField.uuid(),
  calculated_value: ExpressionIdField.nullable(),
  id: z.string().uuid(),
  type: ValueTypeEnum,
  process: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  order: z.number().int(),
  style: InputStyleEnum,
  section: z.string().uuid().nullable(),
  required: z.boolean(),
  external: z.boolean(),
  sla: z.string().nullable(),
  due: z.string().nullable(),
  pinned: z.boolean(),
  added_at: z.string().datetime({ offset: true }).nullable(),
  removed_at: z.string().datetime({ offset: true }).nullable(),
})
const SectionSubstep0 = z.object({
  id: z.string().uuid(),
  process: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  order: z.number().int(),
  form: z.string().uuid(),
  section: z.string().uuid().nullable(),
  button_name: z.string().nullable(),
  added_at: z.string().datetime({ offset: true }).nullable(),
  removed_at: z.string().datetime({ offset: true }).nullable(),
  multiple: z.boolean(),
  min: z.number().int(),
  max: z.number().int(),
  render: z.string().uuid().nullable(),
  default_to_grid_view_on_desktop: z.boolean().optional(),
  role_section: z.boolean().optional(),
})
const TagIdField = z.string()
const CalculatedTag = z.object({
  tag: TagIdField.uuid(),
  expression: ExpressionIdField.nullish(),
  block: BlockIdField.nullish(),
})
const FormStep0 = z.object({
  id: FormStepIdField.uuid(),
  description: z.string(),
  calculated_tags: z.array(CalculatedTag),
  ai_review_input_step_ids: z.array(InputStepIdField).optional(),
  ai_assign_case_input_step_ids: z.array(InputStepIdField).optional(),
  process: z.string().uuid(),
  process_stage: z.string().uuid().nullable(),
  code: z.string(),
  step: z.string().uuid(),
  name: z.string(),
  order: z.number().int(),
  external: z.boolean(),
  due: z.string().nullable(),
  sla: z.string().nullable(),
  button_name: z.string().nullable(),
  requires_submission_confirmation: z.boolean(),
  set_status: StatusEnum.nullable(),
  added_at: z.string().datetime({ offset: true }).nullable(),
  removed_at: z.string().datetime({ offset: true }).nullable(),
  ai_review_prompt: z.string().nullable(),
  ai_assign_case_prompt: z.string().nullable(),
  generation_task_id: z.string().nullable(),
  block: z.string().uuid(),
})
const IdentitySnapshot = z
  .object({
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    address_street: z.string().nullable(),
    address_city: z.string().nullable(),
    address_state: z.string().nullable(),
    address_zip: z.string().nullable(),
  })
  .partial()
const ValueCreate = z
  .object({
    number_value: z
      .string()
      .regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)
      .nullable(),
    identity_snapshot: IdentitySnapshot.nullable(),
    bool_value: z.boolean().nullable(),
    datetime_value: z.string().datetime({ offset: true }).nullable(),
    file_value: z.string().nullable(),
    identity_value: IdentityIdField.nullable(),
    location_value: LocationIdField.nullable(),
    signature_value: z.string().nullable(),
    text_value: z.string().nullable(),
  })
  .partial()
const FormNodeCreateTypeEnum = z.enum(['input', 'section', 'step'])
const FormDataNode = z.object({
  value: ValueCreate.nullish(),
  parent_section: z.string().nullish(),
  type: FormNodeCreateTypeEnum,
  id: z.string(),
  last_saved_input: z.string().uuid().nullish(),
})
const FormNodeCreate = z.object({
  id: z.string().uuid(),
  input: InputSubstep2.nullish(),
  section: SectionSubstep0.nullish(),
  form_step: FormStep0.nullish(),
  instances: z.array(FormDataNode),
  type: FormNodeCreateTypeEnum,
  children: z.array(z.string().uuid()).nullable(),
  parent: z.string().uuid().nullish(),
  name: z.string(),
  description: z.string().nullable(),
  order: z.number().int(),
  required: z.boolean().nullable(),
  shouldRender: z.record(z.boolean()).nullable(),
  siblings: z.array(z.string()).nullable(),
})
const CalculateFormDataHashViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  form_nodes: z.array(FormNodeCreate),
})
const CalculateFormDataHashViewResponseData = z.object({ hash_value: z.number().int() })
const GetCaseApplyStepperStageStatusesRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const ProcessStageIdField = z.string()
const ProcessStage = z.object({
  id: ProcessStageIdField.uuid(),
  order: z.number().int(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  name: z.string(),
  description: z.string().nullish(),
  process: ProcessIdField.uuid(),
  origin: ProcessStageIdField.nullish(),
})
const CompleteEnum = z.enum(['complete', 'in_progress', 'incomplete', 'skipped'])
const GetCaseApplyStepperStageStatusesResponseData = z.object({
  process_stages: z.array(ProcessStage),
  process_stage_statuses: z.array(CompleteEnum),
})
const GetActiveTasksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const EagerEnum = z.enum(['eager', 'confirmation_needed', 'dormant'])
const BlockInstance = z.object({
  id: BlockInstanceIdField.uuid(),
  block: BlockIdField.uuid(),
  case: CaseIdField.uuid(),
  metadata: z.unknown().nullish(),
  due: z.string().datetime({ offset: true }).nullish(),
  entry_criteria_last_satisfied: z.string().datetime({ offset: true }).nullish(),
  exit_criteria_last_satisfied: z.string().datetime({ offset: true }).nullish(),
  current_replay_status: EagerEnum.nullish(),
  round: z.number().int().gte(0).lte(2147483647).optional(),
  ad_hoc_target: AdHocTargetIdField.uuid(),
})
const ActiveTask = z.object({
  block_instance: BlockInstance.nullable(),
  assignees: z.array(IdentityIdField),
  name: z.string(),
})
const GetActiveTasksViewResponseData = z.object({ active_tasks: z.array(ActiveTask) })
const GetCaseFeedActivityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const NotificationIdField = z.string()
const GroupIdField = z.string()
const Notification = z.object({
  id: NotificationIdField.uuid(),
  after: z.string().datetime({ offset: true }),
  read: z.boolean(),
  assigned: z.boolean(),
  sent: z.string().datetime({ offset: true }).nullish(),
  overdue_sent: z.string().datetime({ offset: true }).nullish(),
  item: ItemIdField.nullish(),
  block_instance: BlockInstanceIdField.nullish(),
  assignee: IdentityIdField.nullish(),
  group: GroupIdField.nullish(),
})
const CaseStatusChangeIdField = z.string()
const CaseStatusChange = z.object({
  id: CaseStatusChangeIdField.uuid(),
  status: StatusEnum.nullable(),
  time: z.string().datetime({ offset: true }).optional(),
  case: CaseIdField.uuid(),
  setter: EndUserIdField.nullish(),
})
const CaseAutomationEventLogIdField = z.string()
const AddcasememberEnum = z.enum(['AddCaseMember', 'FormReview'])
const CaseAutomationEventLog = z.object({
  id: CaseAutomationEventLogIdField.uuid(),
  event_type: AddcasememberEnum,
  trigger: z.string(),
  log: z.string(),
  time: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  case: CaseIdField.uuid(),
})
const CaseEventLogIdField = z.string()
const AddCaseLocationEnum = z.enum([
  'add_case_location',
  'change_case_location',
  'remove_case_location',
  'export_case',
  'waive_payment',
  'edit_case_name',
  'share_direct_access',
  'add_tag',
  'remove_tag',
  'add_case_requester',
  'change_case_requester',
  'remove_case_requester',
  'add_case_manager',
  'change_case_manager',
  'remove_case_manager',
  'create_new_task',
  'assign_task',
  'complete_one_off_task',
  'add_due_date_to_task',
  'create_related_case',
  'upload_file',
  'archive_file',
  'regenerate_document',
  'add_case_member',
  'update_form',
  'add_payment_line_item',
  'remove_payment_line_item',
  'record_payment',
  'process_payment',
  'send_assisted_email',
  'run_ai_pre_review',
])
const CaseEventLog = z.object({
  id: CaseEventLogIdField.uuid(),
  case: CaseIdField.uuid(),
  user: EndUserIdField.nullable(),
  event_type: AddCaseLocationEnum,
  time: z.string().datetime({ offset: true }),
  log: z.string().nullable(),
  details: z.unknown().nullable(),
})
const ActionIdField = z.string()
const Form = z.object({
  id: FormIdField.uuid(),
  step: FormStepIdField.uuid(),
  completer: EndUserIdField.nullish(),
  action: ActionIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
})
const BillIdField = z.string()
const ContactIdentifierIdField = z.string()
const BillStepIdField = z.string()
const Bill = z.object({
  id: BillIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  description: z.string(),
  installments: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  editable: z.boolean(),
  receiver: IdentityIdField.uuid(),
  payer: IdentityIdField.nullish(),
  payer_identifier: ContactIdentifierIdField.nullish(),
  step: BillStepIdField.uuid(),
  action: ActionIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
})
const ChargeIdField = z.string()
const Charge = z.object({
  id: ChargeIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  description: z.string(),
  amount: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  created: z.string().datetime({ offset: true }).optional(),
  account_receivable_code: z.string().nullish(),
  bill: BillIdField.uuid(),
})
const Task = z.object({
  id: TaskIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  description: z.string(),
  created: z.string().datetime({ offset: true }).optional(),
  owner: IdentityIdField.uuid(),
  creator: EndUserIdField.nullish(),
  item: ItemIdField.uuid(),
})
const Item = z.object({
  id: ItemIdField.uuid(),
  completed: z.string().datetime({ offset: true }).nullish(),
  due: z.string().datetime({ offset: true }).nullish(),
  created: z.string().datetime({ offset: true }).optional(),
  case: CaseIdField.nullish(),
})
const LicenseIdField = z.string()
const LicenseTemplateIdField = z.string()
const ActiveEnum = z.enum(['active', 'suspended', 'revoked', 'expired', 'deleted'])
const License = z.object({
  id: LicenseIdField.uuid(),
  license_template: LicenseTemplateIdField.uuid(),
  number: z.string(),
  status: ActiveEnum,
  issued_on: z.string(),
  expires_at: z.string().nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const LiveEnum = z.enum(['live', 'archived'])
const LicenseTemplate = z.object({
  id: LicenseTemplateIdField.uuid(),
  owner: IdentityIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  prefix: z.string().nullish(),
  status: LiveEnum.optional(),
  expiration_config: z.unknown(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const Tag = z.object({
  id: TagIdField.uuid(),
  name: z.string(),
  color: z.string(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  description: z.string().nullish(),
  owner: IdentityIdField.uuid(),
})
const GetCaseFeedActivityViewResponseData = z.object({
  notifications: z.array(Notification),
  case_status_changes: z.array(CaseStatusChange),
  case_automation_event_logs: z.array(CaseAutomationEventLog),
  case_event_logs: z.array(CaseEventLog),
  forms: z.array(Form),
  bills: z.array(Bill),
  charges: z.array(Charge),
  tasks: z.array(Task),
  items: z.array(Item),
  licenses: z.array(License),
  license_templates: z.array(LicenseTemplate),
  event_tags: z.array(Tag),
})
const GetCaseFeedContactRecordInfoViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const HandleIdField = z.string()
const TargetIdField = z.string()
const Case = z.object({
  id: CaseIdField.uuid(),
  name: z.string(),
  location: LocationIdField.nullish(),
  status: StatusEnum.nullable(),
  owner_id: IdentityIdField.uuid(),
  requester_id: IdentityIdField.nullish(),
  assignee_id: IdentityIdField.nullish(),
  created: z.string().datetime({ offset: true }).optional(),
  creator_id: EndUserIdField.nullish(),
  process_id: ProcessIdField.nullish(),
  handle_id: HandleIdField.nullish(),
  target_id: TargetIdField.nullish(),
})
const CaseIdentity = z.object({
  id: IdentityIdField.uuid(),
  name: z.string().nullable(),
  email: z.string().nullish(),
  image: z.string().nullish(),
})
const ContactIdField = z.string()
const Contact = z.object({
  id: ContactIdField.uuid(),
  name: z.string(),
  profile: z.string().nullish(),
})
const Address0 = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  location: GeoPoint,
  id: AddressIdField.uuid(),
  location_id: LocationIdField.uuid(),
  contact_id: ContactIdField.nullable(),
})
const TaggingIdField = z.string()
const Tagging = z.object({
  id: TaggingIdField.uuid(),
  calculated: z.boolean().optional(),
  target: TargetIdField.uuid(),
  tag: TagIdField.uuid(),
})
const GetCaseFeedContactRecordInfoViewResponseData = z.object({
  case: Case,
  process_stage_ids: z.array(ProcessStageIdField),
  case_identities: z.array(CaseIdentity),
  case_owner: Contact,
  location: Address0.nullish(),
  tags: z.array(Tag).optional(),
  taggings: z.array(Tagging).optional(),
  case_email: z.string().email(),
  current_stage_number: z.number().int(),
  total_stage_number: z.number().int(),
  case_duration_days: z.number().int(),
})
const GetCaseFeedContactRecordPartiesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const AddressBase = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  location: GeoPoint.nullish(),
})
const Party = z.object({
  id: IdentityIdField.uuid(),
  user_id: EndUserIdField.nullish(),
  name: z.string().nullable(),
  handle: z.string(),
  owner_handle: z.string().nullish(),
  is_contact: z.boolean(),
  date_of_birth: z.string().nullish(),
  image: z.string().nullish(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  addresses: z.array(AddressBase),
})
const GetCaseFeedContactRecordPartiesViewResponseData = z.object({ parties: z.array(Party) })
const GetCaseFeedDocumentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const AnnotatedDocumentIdField = z.string()
const DocumentStepIdField = z.string()
const AnnotatedDocument = z.object({
  id: AnnotatedDocumentIdField.uuid(),
  case: CaseIdField.uuid(),
  document_step: DocumentStepIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  aws_url: z.string(),
  aws_access_url: z.string().nullable(),
  name: z.string(),
})
const StepIdField = z.string()
const HarbourEnum = z.enum(['harbour', 'superdoc'])
const DocumentTemplate = z.object({
  id: DocumentTemplateIdField.uuid(),
  owner: IdentityIdField.uuid(),
  harbour_template_id: z.string().nullish(),
  name: z.string(),
  source: HarbourEnum.optional(),
  docx_asset_url: z.string().optional(),
  template_fields: z.unknown().nullish(),
})
const DocumentStep = z.object({
  id: DocumentStepIdField.uuid(),
  step: StepIdField.nullish(),
  document_template: DocumentTemplate.nullable(),
  field_values: z.unknown(),
  metadata: z.unknown().nullish(),
  output_filename: ExpressionIdField.nullable(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
})
const SectionStepIdField = z.string()
const InputStep = z.object({
  id: z.string().uuid().optional(),
  type: ValueTypeEnum,
  name: z.string(),
  description: z.string(),
  style: InputStyleEnum,
  required: z.boolean(),
  pinned: z.boolean(),
  order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  removed_at: z.string().datetime({ offset: true }).nullish(),
  added_at: z.string().datetime({ offset: true }).nullish(),
  template: ValueIdField.nullish(),
  section: SectionStepIdField.nullish(),
  form: FormStepIdField.uuid(),
  process: ProcessIdField.uuid(),
  calculated_value: ExpressionIdField.nullish(),
  origin: InputStepIdField.nullish(),
})
const SectionIdField = z.string()
const Input = z.object({
  id: z.string().uuid().optional(),
  type: ValueTypeEnum,
  created: z.string().datetime({ offset: true }).optional(),
  value: ValueIdField.nullish(),
  setter: IdentityIdField.nullish(),
  setter_identifier: ContactIdentifierIdField.nullish(),
  setter_staff_user: EndUserIdField.nullish(),
  form: FormIdField.uuid(),
  substep: InputStepIdField.uuid(),
  form_step: FormStepIdField.uuid(),
  section: SectionIdField.nullish(),
})
const Value = z.object({ id: z.string().uuid().optional(), type: ValueTypeEnum })
const FileValue = z.object({
  id: z.string().uuid().optional(),
  type: ValueTypeEnum,
  files: z.array(z.string()),
  file: z.string().nullish(),
  value: ValueIdField.uuid(),
})
const LicenseDocumentIdField = z.string()
const LicenseDocumentTemplateIdField = z.string()
const LicenseDocument = z.object({
  id: LicenseDocumentIdField.uuid(),
  license: LicenseIdField.uuid(),
  license_document_template: LicenseDocumentTemplateIdField.uuid(),
  url: z.string().max(2048),
  access_url: z.string(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const LicenseDocumentTemplate = z.object({
  id: LicenseDocumentTemplateIdField.uuid(),
  license_template: LicenseTemplateIdField.uuid(),
  document_template: DocumentTemplate.nullable(),
  field_values: z.unknown(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const RedactionRequestExportIdField = z.string()
const RedactionRequestIdField = z.string()
const Pending4Enum = z.enum(['pending', 'running', 'done', 'failed'])
const RedactionDocumentIdField = z.string()
const RedactionRequestExport = z.object({
  id: RedactionRequestExportIdField.uuid(),
  redaction_request: RedactionRequestIdField.uuid(),
  status: Pending4Enum.optional(),
  document_ids: z.array(RedactionDocumentIdField),
  documents_done: z.number().int(),
  documents_total: z.number().int(),
  error: z.string().nullable(),
  release: z.boolean().optional().default(false),
  download_url: z.string().nullable(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const Pending3Enum = z.enum(['pending', 'started', 'approved', 'rejected', 'complete'])
const RedactionRequest = z.object({
  id: RedactionRequestIdField.uuid(),
  name: z.string().nullish(),
  status: Pending3Enum.optional(),
  requested_by: EndUserIdField.nullable(),
  requested_by_name: z.string().nullable(),
  document_count: z.number().int(),
  block_instance: BlockInstanceIdField.nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const RedactionBlockIdField = z.string()
const RedactionBlock = z.object({
  id: RedactionBlockIdField.uuid(),
  block: BlockIdField.uuid(),
  metadata: z.unknown().nullish(),
  name: z.string().max(200).optional(),
})
const GetCaseFeedDocumentsViewResponseData = z.object({
  annotated_documents: z.array(AnnotatedDocument),
  document_steps: z.array(DocumentStep),
  file_input_steps: z.array(InputStep),
  file_inputs: z.array(Input),
  values: z.array(Value),
  file_values: z.array(FileValue),
  license_documents: z.array(LicenseDocument),
  license_document_templates: z.array(LicenseDocumentTemplate),
  licenses: z.array(License),
  license_templates: z.array(LicenseTemplate),
  redaction_request_exports: z.array(RedactionRequestExport),
  redaction_requests: z.array(RedactionRequest),
  redaction_block_instances: z.array(BlockInstance),
  redaction_blocks: z.array(RedactionBlock),
})
const GetCaseFeedInfoViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const GetCaseFeedInfoViewResponseData = z.object({
  case: Case,
  process_stage_ids: z.array(ProcessStageIdField),
  case_identities: z.array(CaseIdentity),
  case_owner: Contact,
  location: Address0.nullish(),
  tags: z.array(Tag).optional(),
  taggings: z.array(Tagging).optional(),
  case_email: z.string().email(),
  current_stage_number: z.number().int(),
  total_stage_number: z.number().int(),
  case_duration_days: z.number().int(),
})
const GetCaseFeedInteractionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const ViasiteEnum = z.enum(['ViaSite', 'ViaPhone', 'ViaEmail', 'ViaInPerson', 'ViaAIVoice'])
const Message = z.object({
  id: z.string().uuid().optional(),
  metadata: z.unknown().nullish(),
  time: z.string().datetime({ offset: true }).optional(),
  draft: z.boolean(),
  medium: ViasiteEnum,
  created: z.string().datetime({ offset: true }).optional(),
  modified: z.string().datetime({ offset: true }),
  body: z.string(),
  text: z.string(),
  author: IdentityIdField.nullish(),
  author_identifier: ContactIdentifierIdField.nullish(),
  author_staff_user: EndUserIdField.nullish(),
  creator: IdentityIdField.nullish(),
  creator_identifier: ContactIdentifierIdField.nullish(),
  creator_staff_user: EndUserIdField.nullish(),
  item: ItemIdField.uuid(),
  block_instance: BlockInstanceIdField.nullish(),
})
const GetCaseFeedInteractionsViewAudioMessage = z.object({
  message_id: z.string().uuid(),
  audio_url: z.string(),
  ai_interaction_id: z.string().nullable(),
})
const MessageIdField = z.string()
const Log = z.object({
  id: z.string().uuid().optional(),
  identity: IdentityIdField.nullish(),
  creator_identifier: ContactIdentifierIdField.nullish(),
  creator_staff_user: EndUserIdField.nullish(),
  message: MessageIdField.uuid(),
})
const MessageFile = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  url: z.string(),
  message: MessageIdField.uuid(),
})
const EmailTypeEnum = z.enum(['EmailReceived', 'EmailMessageNotification'])
const Email = z.object({
  id: z.string().uuid().optional(),
  raw: z.string().nullish(),
  external_message_id: z.string().nullish(),
  html: z.string().nullish(),
  type: EmailTypeEnum,
  subject: z.string(),
  mailbox: IdentityIdField.uuid(),
  message: MessageIdField.uuid(),
})
const EmailParticipantTypeEnum = z.enum(['to', 'cc', 'bcc', 'from'])
const EmailIdField = z.string()
const EmailAddressIdField = z.string()
const EmailParticipant = z.object({
  id: z.string().uuid().optional(),
  type: EmailParticipantTypeEnum,
  name: z.string(),
  email: EmailIdField.uuid(),
  address: EmailAddressIdField.uuid(),
})
const DomainIdField = z.string()
const EmailAddress = z.object({
  id: z.string().uuid().optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  address: z.string().email(),
  insensitive: z.boolean(),
  domain_name: z.string(),
  domain: DomainIdField.uuid(),
})
const GetCaseFeedInteractionsViewResponseData = z.object({
  messages: z.array(Message),
  audio_messages: z.array(GetCaseFeedInteractionsViewAudioMessage),
  logs: z.array(Log),
  message_files: z.array(MessageFile),
  emails: z.array(Email),
  email_participants: z.array(EmailParticipant),
  email_addresses: z.array(EmailAddress),
})
const GetCaseFeedPartiesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const GetCaseFeedPartiesViewResponseData = z.object({ parties: z.array(Party) })
const GetCaseFeedWorkflowViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const ConnectorInstanceIdField = z.string()
const ConnectorIdField = z.string()
const ConnectorInstance = z.object({
  id: ConnectorInstanceIdField.uuid(),
  connector: ConnectorIdField.uuid(),
  case: CaseIdField.uuid(),
  metadata: z.unknown().nullish(),
  gate_last_opened: z.string().datetime({ offset: true }).nullish(),
  round: z.number().int().gte(0).lte(2147483647).optional(),
})
const FormEnum = z.enum([
  'form',
  'license',
  'payment',
  'status',
  'api_automation',
  'tag',
  'role',
  'communication',
  'document_generation',
  'review',
  'demo',
  'redaction',
  'start',
])
const AlwaysEnum = z.enum(['always', 'confirm', 'never'])
const AnyInboundEnum = z.enum(['any_inbound', 'all_inbound'])
const Block = z.object({
  id: BlockIdField.uuid(),
  entry_criteria: ExpressionIdField.nullish(),
  process_stage: ProcessStageIdField.nullish(),
  metadata: z.unknown().nullish(),
  type: FormEnum,
  control_flow_replay: AlwaysEnum,
  data_dependency_replay: AlwaysEnum.optional(),
  gate_type: AnyInboundEnum.optional(),
  process_version: ProcessIdField.uuid(),
  ad_hoc_target: AdHocTargetIdField.uuid(),
  origin: BlockIdField.nullish(),
})
const Connector = z.object({
  id: ConnectorIdField.uuid(),
  from_block: BlockIdField.nullable(),
  to_block: BlockIdField.nullable(),
  gate: ExpressionIdField.nullish(),
  metadata: z.unknown().nullish(),
  force_replay: z.boolean().optional(),
})
const FormStep = z.object({
  id: FormStepIdField.uuid(),
  block: BlockIdField.uuid(),
  step: StepIdField.uuid(),
  metadata: z.unknown().nullish(),
  due: z.string().nullish(),
  sla: z.string().nullish(),
  external: z.boolean(),
  name: z.string(),
  description: z.string(),
  button_name: z.string().nullish(),
  requires_submission_confirmation: z.boolean().optional(),
})
const BillStep = z.object({
  id: BillStepIdField.uuid(),
  name: z.string(),
  description: z.string(),
  installments: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  account: z.string().nullish(),
  distribution: z.string().nullish(),
  allow_ach: z.boolean().optional(),
  step: StepIdField.uuid(),
  block: BlockIdField.uuid(),
})
const CaseStatusAutomationIdField = z.string()
const CaseStatusAutomation = z.object({
  id: CaseStatusAutomationIdField.uuid(),
  metadata: z.unknown().nullish(),
  status: StatusEnum.nullable(),
  block: BlockIdField.uuid(),
})
const TagAutomationIdField = z.string()
const TagAutomationActionEnum = z.enum(['add', 'remove'])
const TagAutomationTargetEnum = z.enum(['case', 'contact'])
const TagAutomation = z.object({
  id: TagAutomationIdField.uuid(),
  tag: TagIdField.nullish(),
  apply: ExpressionIdField.nullish(),
  identity_expression: ExpressionIdField.nullish(),
  metadata: z.unknown().nullish(),
  action: TagAutomationActionEnum.optional(),
  applies_to: TagAutomationTargetEnum.optional(),
  block: BlockIdField.uuid(),
})
const CaseRoleAutomationIdField = z.string()
const SetRequesterEnum = z.enum(['set_requester', 'set_manager', 'invite_to_case'])
const CaseRoleAutomation = z.object({
  id: CaseRoleAutomationIdField.uuid(),
  identity_expression: ExpressionIdField.nullish(),
  metadata: z.unknown().nullish(),
  case_role_action: SetRequesterEnum,
  block: BlockIdField.uuid(),
})
const CommunicationAutomationIdField = z.string()
const ContactEmailIdField = z.string()
const CommunicationAutomationRecipientIdField = z.string()
const ToEnum = z.enum(['to', 'cc', 'bcc'])
const CommunicationAutomationRecipient = z.object({
  id: CommunicationAutomationRecipientIdField.uuid(),
  communication_automation: CommunicationAutomationIdField.uuid(),
  recipient_expression: ExpressionIdField.uuid(),
  recipient_type: ToEnum,
})
const CommunicationAutomation = z.object({
  id: CommunicationAutomationIdField.uuid(),
  from_email: ContactEmailIdField.nullish(),
  form_letter_template: MessageTemplateIdField.nullish(),
  recipients: z.array(CommunicationAutomationRecipient).optional(),
  body: z.string(),
  metadata: z.unknown().nullish(),
  name: z.string().max(200).optional(),
  subject: z.string().max(500).optional(),
  staff_review_required: z.boolean().optional(),
  due: z.string().nullish(),
  ai_prompt: z.string().optional(),
  block: BlockIdField.uuid(),
})
const DemoBlockIdField = z.string()
const DemoBlock = z.object({
  id: DemoBlockIdField.uuid(),
  block: BlockIdField.uuid(),
  metadata: z.unknown().nullish(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
  icon: z.string().max(100).optional(),
  color: z.string().max(50).optional(),
})
const LicenseBlockIdField = z.string()
const NewEnum = z.enum(['new', 'renewal', 'amendment'])
const LicenseBlock = z.object({
  id: LicenseBlockIdField.uuid(),
  license_template: LicenseTemplateIdField.nullable(),
  block: BlockIdField.uuid(),
  metadata: z.unknown().nullish(),
  application_type: NewEnum.optional(),
})
const LicenseBlockInstanceIdField = z.string()
const LicenseBlockInstance = z.object({
  id: LicenseBlockInstanceIdField.uuid(),
  license: LicenseIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const PaymentIdField = z.string()
const PaymentMethodEnum = z.enum([
  'BankPayment',
  'CardPayment',
  'CashPayment',
  'CheckPayment',
  'MoneyOrderPayment',
  'OtherPayment',
])
const PaymentStatusEnum = z.enum(['waiting', 'processing', 'succeeded'])
const PaymentMediumEnum = z.enum(['external', 'phone', 'site', 'terminal'])
const PayerEnum = z.enum(['payer', 'organization', 'non_stripe'])
const PayoutIdField = z.string()
const Payment = z.object({
  id: PaymentIdField.uuid(),
  stripe_id: z.string().nullish(),
  created: z.string().datetime({ offset: true }).optional(),
  completed: z.string().datetime({ offset: true }).nullish(),
  amount: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  reconciled: z.boolean(),
  fee: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  method: PaymentMethodEnum,
  save_source: z.boolean().nullish(),
  status: PaymentStatusEnum,
  attempted: z.string().datetime({ offset: true }).nullish(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  medium: PaymentMediumEnum,
  fee_burden: PayerEnum,
  payer: EndUserIdField.nullish(),
  bill: BillIdField.uuid(),
  payout: PayoutIdField.nullish(),
})
const BlockInstanceMemberIdField = z.string()
const InteractionIdField = z.string()
const BlockInstanceMember = z.object({
  id: BlockInstanceMemberIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
  identity: IdentityIdField.uuid(),
  metadata: z.unknown().nullish(),
  member_identifier: ContactIdentifierIdField.nullish(),
  interaction: InteractionIdField.nullish(),
})
const GetCaseFeedWorkflowViewResponseData = z.object({
  stages: z.array(ProcessStage),
  block_instances: z.array(BlockInstance),
  connector_instances: z.array(ConnectorInstance),
  blocks: z.record(Block),
  connectors: z.record(Connector),
  form_steps: z.record(FormStep),
  bill_steps: z.record(BillStep),
  status_automations: z.record(CaseStatusAutomation),
  tag_automations: z.record(TagAutomation),
  role_automations: z.record(CaseRoleAutomation),
  communication_automations: z.record(CommunicationAutomation),
  demo_blocks: z.record(DemoBlock),
  redaction_blocks: z.record(RedactionBlock),
  redaction_requests: z.record(RedactionRequestIdField),
  license_blocks: z.record(LicenseBlock),
  license_block_instances: z.array(LicenseBlockInstance),
  licenses: z.array(License),
  license_templates: z.array(LicenseTemplate),
  bills: z.array(Bill),
  charges: z.array(Charge),
  payments: z.array(Payment),
  block_instance_members: z.array(BlockInstanceMember),
  forms: z.array(Form),
  process_stage_statuses: z.record(z.enum(['complete', 'in_progress', 'incomplete', 'skipped'])),
  process_block_statuses: z.record(z.enum(['complete', 'in_progress', 'incomplete', 'skipped'])),
})
const CompleteRedactionBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.uuid(),
  include_exemption_summary: z.boolean().optional().default(false),
})
const CompleteRedactionBlockViewResponseData = z.object({ block_instance: BlockInstance })
const CompleteReviewViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  review_id: ReviewIdField.uuid(),
  review_outcome: AcceptEnum,
  top_level_comment: z.string().optional(),
})
const CompleteReviewViewResponseData = z.object({ review: Review })
const CopyExpressionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  expression_id: ExpressionIdField.uuid(),
  collapsed: z.boolean().optional().default(false),
})
const CopyExpressionViewResponseData = z.object({
  expressions: z.array(Expression),
  root_expression_id: ExpressionIdField.uuid(),
})
const CopyProcessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  old_process_id: ProcessIdField.uuid(),
  name: z.string(),
  description: z.string(),
  keep_assignees: z.boolean(),
})
const CopyProcessViewResponseData = z.object({ process_id: ProcessIdField.uuid() })
const CopySectionSubStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  section_step_id: SectionStepIdField.uuid(),
  section_id: SectionStepIdField.nullish(),
  form_step_id: FormStepIdField.uuid().optional(),
  order: z.number().int(),
})
const CopySectionSubStepViewResponseData = z.object({
  section_step_id: SectionStepIdField.uuid(),
  copied_sections: z.record(SectionStepIdField),
  copied_inputs: z.record(InputSubstep2),
})
const CopyWorkflowViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
  name: z.string(),
  description: z.string(),
  keep_assignees: z.boolean(),
})
const CopyWorkflowViewResponseData = z.object({ workflow: Workflow })
const ContactRecordIdField = z.string()
const CreateCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  name: z.string(),
  status: StatusEnum.nullable(),
  organization_identity_id: IdentityIdField.uuid(),
  assignee: IdentityIdField.nullish(),
  requester: IdentityIdField.nullish(),
  address: AddressIdField.nullish(),
  contact_record_id: ContactRecordIdField.nullish(),
  workflow_id: WorkflowIdField.nullish(),
})
const Identity0 = z.object({
  name: z.string(),
  id: IdentityIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  handle: z.string(),
  image: z.string().nullable(),
  stripe_id: z.string().nullable(),
  email: z.string().nullable(),
})
const Location0 = z.object({ id: LocationIdField.uuid(), location: GeoPoint, address: Address0 })
const ContactEntryIdField = z.string()
const ContactEmail0 = z.object({
  signature: z.string(),
  address: z.string(),
  id: ContactEmailIdField.uuid(),
  email: EmailAddressIdField.uuid(),
  contact: ContactIdField.uuid(),
  primary: z.boolean(),
  verified: z.boolean(),
  ses_verified: z.boolean(),
})
const NoteIdField = z.string()
const Note0 = z.object({
  id: NoteIdField.uuid(),
  target: TargetIdField.uuid(),
  note: z.string(),
  created: z.string().datetime({ offset: true }),
  modified: z.string().datetime({ offset: true }),
  author: Identity0,
  owner: IdentityIdField.uuid(),
})
const Identity2 = z.object({
  id: IdentityIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  contact_entry_id: ContactEntryIdField.nullish(),
  emails: z.array(ContactEmail0),
  notes: z.array(Note0),
  first_name: z.string(),
  last_name: z.string(),
  owner: z.string().nullable(),
  handle: z.string(),
  name: z.string(),
  date_of_birth: z.string().nullable(),
  image: z.string().nullable(),
  is_contact: z.boolean(),
  phones: z.array(z.string()),
  addresses: z.array(z.string()),
  address_objects: z.array(Address0).nullish(),
})
const Case2 = z.object({
  creator: Identity0.nullable(),
  location: Location0.nullable(),
  assignee: Identity0.nullable(),
  requester: Identity2.nullable(),
  tags: z.array(Tag),
  id: z.string().uuid(),
  handle: z.string(),
  target_id: z.string().uuid(),
  owner_identity_id: z.string().uuid(),
  process: z.string().uuid().nullable(),
  status: StatusEnum.nullable(),
  title: z.string(),
  created_time: z.string().datetime({ offset: true }),
  last_action_time: z.string().datetime({ offset: true }).nullable(),
  message: z.string().nullable(),
  message_time: z.string().datetime({ offset: true }).nullable(),
  unread: z.boolean(),
  unverified_apply: z.boolean(),
})
const CreateCaseViewResponseData = z.object({ case: Case2 })
const CreateCasesTabViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  code: z.string(),
  name: z.string(),
  order: z.number().int(),
})
const CasesTabIdField = z.string()
const CreateCasesTabViewResponseData = z.object({ tab_id: CasesTabIdField.uuid() })
const CreateDraftVersionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
})
const CreateDraftVersionViewResponseData = z.object({ process_id: ProcessIdField.uuid() })
const ExpressionTypeEnum = z.enum([
  'case_creator',
  'case_identifier',
  'constant',
  'custom',
  'input_value',
  'operator',
  'review_outcome',
  'today',
])
const CreateExpressionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  type: ExpressionTypeEnum,
  workflow_version_id: ProcessIdField.uuid().optional(),
  value_id: ValueIdField.uuid().optional(),
  input_step_id: InputStepIdField.uuid().optional(),
  operator: OperatorTypeEnum.optional(),
  case_identifier: CaseIdentifierTypeEnum.optional(),
  custom: CustomExpressionNameEnum.optional(),
  parameter_one: ExpressionIdField.uuid().optional(),
  parameter_two: ExpressionIdField.uuid().optional(),
  parameter_three: ExpressionIdField.uuid().optional(),
  review_block_id: ReviewBlockIdField.uuid().optional(),
  name: z.string().optional(),
  metadata: z.unknown().optional(),
})
const CreateExpressionViewResponseData = z.object({ expression: Expression })
const FormStyleElementTypeEnum = z.literal('page_break')
const CreateFormStyleElementViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
  order: z.number().int(),
  type: FormStyleElementTypeEnum,
})
const FormStyleElementIdField = z.string()
const FormStyleElement = z.object({
  id: FormStyleElementIdField.uuid(),
  order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  type: FormStyleElementTypeEnum,
  form_step: FormStepIdField.uuid(),
})
const CreateFormStyleElementViewResponseData = z.object({ form_style_element: FormStyleElement })
const CreateInputStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  value_type: ValueTypeEnum,
  name: z.string(),
  description: z.string(),
  template: ValueIdField.nullable(),
  style: InputStyleEnum,
  required: z.boolean(),
  section: SectionStepIdField.nullable(),
  form: FormStepIdField.uuid(),
  pinned: z.boolean(),
  order: z.number().int(),
  process_id: ProcessIdField.uuid(),
})
const CreateInputStepViewResponseData = z.object({ input_step: InputSubstep2 })
const Attachment = z.object({ name: z.string(), url: z.string(), access_url: z.string().nullish() })
const CreateMessageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  author_identity_id: IdentityIdField.uuid(),
  recipients: z.array(IdentityIdField),
  case_id: CaseIdField.uuid(),
  medium: ViasiteEnum,
  body: z.string(),
  attachments: z.array(Attachment),
  mentioned_identity_ids: z.array(IdentityIdField).optional(),
})
const CreateMessageViewResponseData = z.object({
  message_id: MessageIdField.uuid(),
  item_id: ItemIdField.uuid(),
})
const CreateProcessStageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  name: z.string(),
  description: z.string(),
  order: z.number().int(),
})
const CreateProcessStageViewResponseData = z.object({
  process_stage_id: ProcessStageIdField.uuid(),
})
const CreateRelationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  from_case_id: CaseIdField.uuid(),
  to_case_id: CaseIdField.uuid(),
})
const CreateReviewCommentThreadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.uuid(),
  content: z.string(),
  is_public: z.boolean(),
  commented_block_id: BlockIdField.uuid().optional(),
  commented_section_step_id: SectionStepIdField.uuid().optional(),
  commented_input_step_id: InputStepIdField.uuid().optional(),
})
const ReviewCommentThread = z.object({
  id: ReviewCommentThreadIdField.uuid(),
  block_instance: BlockInstanceIdField.uuid(),
  is_public: z.boolean().optional(),
  commented_block: BlockIdField.nullish(),
  commented_section_step: SectionStepIdField.nullish(),
  commented_input_step: InputStepIdField.nullish(),
  comments: z.array(ReviewComment),
})
const CreateReviewCommentThreadViewResponseData = z.object({
  review_comment_thread: ReviewCommentThread,
})
const CreateSectionStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  description: z.string(),
  section: SectionStepIdField.nullable(),
  form: FormStepIdField.uuid(),
  order: z.number().int(),
  button_name: z.string().nullable(),
  min: z.number().int(),
  max: z.number().int(),
  render: ExpressionIdField.nullable(),
})
const CreateSectionStepViewResponseData = z.object({ section_step_id: SectionStepIdField.uuid() })
const CreateTaskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  assignees: z.array(IdentityIdField),
  name: z.string(),
  description: z.string(),
  due: z.string().datetime({ offset: true }).optional(),
})
const CreateTaskViewResponseData = z.object({ task_id: TaskIdField.uuid() })
const CreateTestCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  process_id: ProcessIdField.nullish(),
  workflow_id: WorkflowIdField.nullish(),
  requester: IdentityIdField.nullish(),
  assignee: IdentityIdField.nullish(),
})
const CreateTestCaseViewResponseData = z.object({ case_id: CaseIdField.uuid(), handle: z.string() })
const CreateValueViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  type: ValueTypeEnum,
  bool: z.boolean().optional(),
  datetime: z.string().datetime({ offset: true }).optional(),
  file: z.string().optional(),
  identity: IdentityIdField.uuid().optional(),
  location: LocationIdField.uuid().optional(),
  number: z
    .string()
    .regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)
    .optional(),
  text: z.string().optional(),
})
const CreateValueViewResponseData = z.object({ value_id: ValueIdField.uuid() })
const CreateVoicemailProcessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const CreateVoicemailProcessViewResponseData = z.object({ process_id: ProcessIdField.uuid() })
const CreateWorkflowViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  description: z.string(),
  appliable: z.boolean(),
})
const CreateWorkflowViewResponseData = z.object({ workflow: Workflow })
const DeleteAssignmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  notification_id: NotificationIdField.uuid(),
})
const DeleteBillStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_step_id: BillStepIdField.uuid(),
})
const DeleteCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
})
const DeleteCasesTabViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  tab_id: CasesTabIdField.uuid(),
})
const DeleteCommentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  comment_id: CommentIdField.uuid().optional(),
  thread_id: CommentThreadIdField.uuid().optional(),
})
const DeleteExpressionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  expression_ids: z.array(ExpressionIdField),
})
const DeleteFormStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
})
const DeleteFormStyleElementViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_style_element_id: FormStyleElementIdField.uuid(),
})
const DeleteInputSubstepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  input_step_id: InputStepIdField.uuid(),
})
const DeleteProcessStageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_stage_id: ProcessStageIdField.uuid(),
})
const DeleteSectionSubstepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  section_substep_id: SectionStepIdField.uuid(),
})
const DeleteStepAssignmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  user_expression_id: ExpressionIdField.uuid(),
})
const DeleteTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  template_id: MessageTemplateIdField.uuid(),
})
const NumberInputComparisonOperatorEnum = z.enum(['=', '>', '<', '>=', '<='])
const NumberComparison = z.object({
  value: z.string(),
  operator: NumberInputComparisonOperatorEnum.optional(),
})
const ShortTextEnum = z.enum([
  'short_text',
  'paragraph',
  'dropdown',
  'single_select',
  'number',
  'date',
  'checkbox',
  'mark_as_complete',
  'document',
  'contact',
  'location',
  'signature',
])
const CaseFilter = z
  .object({
    assignees: z.array(z.string().uuid()).nullable(),
    completed_by_ids: z.array(z.string().uuid()).nullable(),
    no_manager: z.boolean().nullable(),
    item_assignee_ids: z.array(z.string().uuid()).nullable(),
    owner_ids: z.array(z.string().uuid()).nullable(),
    governing_units: z.array(z.string().uuid()).nullable(),
    requester_ids: z.array(z.string().uuid()).nullable(),
    statuses: z.array(StatusEnum).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    tag_ids: z.array(z.string().uuid()).nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    process_statuses: z.array(ProcessStatusEnum).nullable(),
    workflow_ids: z.array(z.string().uuid()).nullable(),
    workflow_statuses: z.array(Active1Enum).nullable(),
    created_after: z.string().datetime({ offset: true }).nullable(),
    created_before: z.string().datetime({ offset: true }).nullable(),
    next_due_after: z.string().datetime({ offset: true }).nullable(),
    next_due_before: z.string().datetime({ offset: true }).nullable(),
    closed_after: z.string().datetime({ offset: true }).nullable(),
    closed_before: z.string().datetime({ offset: true }).nullable(),
    process_step_ids: z.array(z.string().uuid().nullable()).nullable(),
    input_choices: z.record(z.string()),
    input_text: z.record(z.string()),
    input_bool: z.record(z.boolean()),
    input_number: z.record(NumberComparison),
    has_payment_step: z.boolean().nullable(),
    has_add_an_item_section: z.boolean().nullable(),
    add_an_item_grid_view: z.boolean().nullable(),
    input_kinds: z.array(ShortTextEnum).nullable(),
  })
  .partial()
const DownloadCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  filter: CaseFilter,
})
const DownloadCasesViewResponseData = z.object({ email_recipient: z.string().email() })
const DraftCommunicationMessageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.uuid(),
})
const DraftCommunicationRecipient = z.object({
  email_id: EmailAddressIdField.uuid(),
  type: ToEnum,
  address: z.string(),
})
const DraftCommunicationMessageViewResponseData = z.object({
  message: z.string(),
  from_id: EmailAddressIdField.nullable(),
  from_label: z.string(),
  recipients: z.array(DraftCommunicationRecipient),
  attachments: z.array(Attachment),
})
const ChargeSubStepCreate = z.object({
  account_receivable_code: z.string().nullish(),
  distribution_code: z.string().nullish(),
  name: z.string(),
  description: z.string(),
  dollars: ExpressionIdField.uuid(),
})
const EditBillStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_step_id: BillStepIdField.uuid(),
  name: z.string().optional(),
  description: z.string().optional(),
  order: z.number().int().optional(),
  charges: z.array(ChargeSubStepCreate).optional(),
  account: z.string().nullish(),
  distribution: z.string().nullish(),
})
const ChargeSubstepIdField = z.string()
const ChargeSubstep = z.object({
  id: ChargeSubstepIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  description: z.string(),
  dollars_type: ValueTypeEnum,
  account_receivable_code: z.string().nullish(),
  distribution_code: z.string().nullish(),
  bill: BillStepIdField.uuid(),
  dollars: ExpressionIdField.uuid(),
})
const BillStep1 = z.object({
  id: BillStepIdField.uuid(),
  charges: z.array(ChargeSubstep),
  calculated_tags: z.array(CalculatedTag),
  process: z.string().uuid(),
  process_stage: z.string().uuid().nullable(),
  code: z.string(),
  step: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  installments: z.number().int(),
  order: z.number().int(),
  account: z.string().nullable(),
  distribution: z.string().nullable(),
  set_status: StatusEnum.nullable(),
  added_at: z.string().datetime({ offset: true }).nullable(),
  removed_at: z.string().datetime({ offset: true }).nullable(),
  allow_ach: z.boolean(),
  block: z.string().uuid(),
})
const EditBillStepViewResponseData = z.object({ bill_step: BillStep1 })
const EditCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  owner: IdentityIdField.uuid().optional(),
  assignee: IdentityIdField.nullish(),
  requester: IdentityIdField.nullish(),
  name: z.string().optional(),
  address: AddressIdField.nullish(),
})
const EditCasesTabViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  tab_id: CasesTabIdField.uuid(),
  code: z.string().optional(),
  name: z.string().optional(),
  order: z.number().int().optional(),
  page_size: z.number().int().optional(),
})
const DatetimeStepIdField = z.string()
const EditDateTimeStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  datetime_step_id: DatetimeStepIdField.uuid(),
  request_time: z.boolean(),
})
const EditDueDateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  item_ids: z.array(ItemIdField).optional(),
  block_instance_ids: z.array(BlockInstanceIdField).optional(),
  case_ids: z.array(CaseIdField).optional(),
  due_date: z.string().datetime({ offset: true }).optional(),
})
const EditExpressionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  expression_id: ExpressionIdField.uuid(),
  name: z.string().optional(),
  number_value: z
    .string()
    .regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)
    .nullish(),
  datetime_value: z.string().datetime({ offset: true }).nullish(),
  identity_value: IdentityIdField.nullish(),
  text_value: z.string().nullish(),
  file_value: z.string().nullish(),
  input_step_id: InputStepIdField.nullish(),
  metadata: z.unknown().optional(),
})
const FileStepIdField = z.string()
const EditFileStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  file_step_id: FileStepIdField.uuid(),
  allowed_file_types: z.array(z.string()).nullish(),
})
const EditFormStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
  name: z.string().optional(),
  description: z.string().optional(),
  order: z.number().int().optional(),
  external: z.boolean().optional(),
  sla: z.string().nullish(),
  due: z.string().nullish(),
  button_name: z.string().nullish(),
  requires_submission_confirmation: z.boolean().optional(),
})
const EditFormStyleElementViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_style_element_id: FormStyleElementIdField.uuid(),
  order: z.number().int().optional(),
})
const EditFormStyleElementViewResponseData = z.object({ form_style_element: FormStyleElement })
const IdentityStepIdField = z.string()
const EditIdentityStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  identity_step_id: IdentityStepIdField.uuid(),
  add_contact: z.boolean().optional(),
  require_me: z.boolean().optional(),
  show_address: z.boolean().optional(),
  require_address: z.boolean().optional(),
  show_date_of_birth: z.boolean().optional(),
  require_date_of_birth: z.boolean().optional(),
  show_email: z.boolean().optional(),
  require_email: z.boolean().optional(),
  verify_email: z.boolean().optional(),
  show_name: z.boolean().optional(),
  require_name: z.boolean().optional(),
  show_phone: z.boolean().optional(),
  require_phone: z.boolean().optional(),
  invite_to_case: z.boolean().optional(),
  set_case_requester: z.boolean().optional(),
})
const InputStepChoiceData = z.object({
  value_id: z.string().uuid(),
  allow_custom_text: z.boolean().optional(),
})
const EditInputStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  input_step_id: InputStepIdField.uuid(),
  name: z.string().optional(),
  description: z.string().optional(),
  type: ValueTypeEnum.optional(),
  order: z.number().int().optional(),
  style: InputStyleEnum.optional(),
  choices: z.array(InputStepChoiceData).optional(),
  template: ValueIdField.nullish(),
  required: z.boolean().optional(),
  pinned: z.boolean().optional(),
  section: SectionStepIdField.nullish(),
  calculated_value: ExpressionIdField.nullish(),
})
const LocationStepIdField = z.string()
const EditLocationStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  location_step_id: LocationStepIdField.uuid(),
  request_address: z.boolean().optional(),
  request_coordinates: z.boolean().optional(),
  overlay: GISLayerSourceIdField.nullish(),
})
const NumberStepIdField = z.string()
const EditNumberStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  number_step_id: NumberStepIdField.uuid(),
  allow_negative_numbers: z.boolean().optional(),
  allow_zero: z.boolean().optional(),
  decimal_places: z.number().int().nullish(),
  prefix_unit: z.string().nullish(),
  suffix_unit: z.string().nullish(),
})
const EditProcessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  owner: IdentityIdField.uuid().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  status: ProcessStatusEnum.optional(),
  appliable: z.boolean().optional(),
  copyable: z.boolean().optional(),
  review_steps_enabled: z.boolean().optional(),
  voice_apply_enabled: z.boolean().optional(),
  case_open_email_enabled: z.boolean().optional(),
  case_close_email_enabled: z.boolean().optional(),
  inbound_email_notify_enabled: z.boolean().optional(),
  constituent_inquiry_email: z.string().email().nullish(),
  ai_enabled: z.boolean().optional(),
  apply_without_verification: ProcessVerificationEnum.nullish(),
})
const EditProcessViewResponseData = z.object({ workflow: Workflow })
const EditProcessStageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_stage_id: ProcessStageIdField.uuid(),
  name: z.string().optional(),
  description: z.string().optional(),
  order: z.number().int().optional(),
})
const EditSectionSubStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  section_step_id: SectionStepIdField.uuid(),
  section: SectionStepIdField.nullish(),
  name: z.string().optional(),
  description: z.string().optional(),
  order: z.number().int().optional(),
  button_name: z.string().nullish(),
  min: z.number().int().optional(),
  max: z.number().int().optional(),
  default_grid_view: z.boolean().optional(),
  role_section: z.boolean().optional(),
  render: ExpressionIdField.nullish(),
})
const SignatureStepIdField = z.string()
const EditSignatureStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  signature_step_id: SignatureStepIdField.uuid(),
  check_outdated: z.boolean(),
})
const EditSignatureStepViewResponseData = z.object({ signature_step: SignatureStep })
const EditStepViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  step_id: StepIdField.uuid(),
  process_stage_id: ProcessStageIdField.nullish(),
  set_status: StatusEnum.nullish(),
  calculated_tags: z.array(CalculatedTag).optional(),
  ai_review_prompt: z.string().nullish(),
  ai_review_input_step_ids: z.array(InputStepIdField).optional(),
  ai_assign_case_prompt: z.string().nullish(),
  ai_assign_case_input_step_ids: z.array(InputStepIdField).optional(),
})
const EditTaskViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  task_id: TaskIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  due: z.string().datetime({ offset: true }).optional(),
  completed: z.boolean().optional(),
  assignees: z.array(IdentityIdField).optional(),
})
const ExportProcessJsonViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
})
const ExportProcessJsonViewResponseData = z.object({
  process_json: z.object({}).partial().passthrough(),
})
const GenerateAICaseSummaryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const GenerateAICaseSummaryViewResponseData = z.object({ summary: z.string() })
const GenerateAIEmailSummaryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  message_id: MessageIdField.uuid(),
})
const GenerateAIEmailSummaryViewResponseData = z.object({ summary: z.string() })
const GenerateDocumentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  document_step_id: DocumentStepIdField.uuid(),
})
const GenerateDocumentViewResponseData = z.object({ document_url: z.string() })
const GetCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.nullish(),
  case_id: CaseIdField.uuid(),
})
const GetCaseViewResponseData = z.object({ case: Case2 })
const GetCaseCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  filter: CaseFilter,
})
const GetCaseCountViewResponseData = z.object({ total: z.number().int(), unread: z.number().int() })
const GetCaseIdViewRequestData = z.object({ user_id: EndUserIdField.uuid(), handle: z.string() })
const CaseAccessModeEnum = z.enum(['feed', 'apply'])
const GetCaseIdViewResponseData = z.object({
  case_id: CaseIdField.uuid(),
  access_mode: CaseAccessModeEnum,
  owner_identity_id: IdentityIdField.uuid(),
})
const GetCaseOwnerViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const GetCaseOwnerViewResponseData = z.object({ owner: Identity0 }).partial()
const CaseSortEnum = z.enum([
  'by_last_action_asc',
  'by_last_action_desc',
  'by_next_due',
  'by_last_message',
  'by_created_asc',
  'by_created_desc',
])
const GetCasesTableViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  workspace_id: WorkspaceIdField.nullish(),
  filter: CaseFilter,
  sort: CaseSortEnum,
  offset: z.number().int(),
  limit: z.number().int(),
})
const CasesTableIdentity = z.object({
  id: IdentityIdField.uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  image: z.string().nullable(),
})
const CasesTableTag = z.object({ id: TagIdField.uuid(), name: z.string(), color: z.string() })
const CasesTableRow = z.object({
  id: CaseIdField.uuid(),
  handle: z.string(),
  title: z.string(),
  status: StatusEnum.nullable(),
  owner_identity_id: IdentityIdField.uuid(),
  created_time: z.string().datetime({ offset: true }),
  last_action_time: z.string().datetime({ offset: true }).nullable(),
  next_due: z.string().datetime({ offset: true }).nullable(),
  requester: CasesTableIdentity.nullable(),
  assignees: z.array(CasesTableIdentity),
  tags: z.array(CasesTableTag),
})
const GetCasesTableViewResponseData = z.object({ cases: z.array(CasesTableRow) })
const GetFormStepCountViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  step_id: FormStepIdField.uuid(),
  complete: z.boolean().optional().default(false),
  after: z.string().datetime({ offset: true }).nullish(),
  before: z.string().datetime({ offset: true }).nullish(),
  completion_time: z.string().nullish(),
})
const GetFormStepCountViewResponseData = z.object({ count: z.number().int() })
const FormStepTimeFilter = z.object({
  step_id: FormStepIdField.uuid(),
  after_created: z.string().datetime({ offset: true }).nullish(),
  before_created: z.string().datetime({ offset: true }).nullish(),
  after_completed: z.string().datetime({ offset: true }).nullish(),
  before_completed: z.string().datetime({ offset: true }).nullish(),
})
const GetFormStepTimeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_time_filter: FormStepTimeFilter,
})
const GetFormStepTimeViewResponseData = z
  .object({ average_duration: z.string().nullable() })
  .partial()
const GetOrCreateAgenticImplementationLogViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
})
const GetOrCreateAgenticImplementationLogViewResponseData = z.object({
  log_id: z.string(),
  messages: z.array(AgenticImplementationMessageSerializerAgenticImplementationMessage),
  status: z.string(),
  status_message: z.string().nullish(),
})
const ProcessInfoArgs = z
  .object({
    process_id: ProcessIdField.nullable(),
    owner_handle: z.string().nullable(),
    process_code: z.string().nullable(),
  })
  .partial()
const GetProcessViewRequestData = z.object({ process_info_args: ProcessInfoArgs })
const Process3 = z.object({
  bill_steps: z.array(BillStep1),
  form_steps: z.array(FormStep0),
  process_stages: z.array(ProcessStage),
  inputs: z.array(InputSubstep2),
  subsections: z.array(SectionSubstep0),
  owner: Identity0,
  page_breaks: z.array(FormStyleElement),
  blocks: z.array(Block),
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  status: ProcessStatusEnum,
  appliable: z.boolean(),
  copyable: z.boolean(),
  apply_without_verification: ProcessVerificationEnum.nullable(),
  icon: z.string().nullable(),
  button_name: z.string().nullable(),
  pinned: z.boolean().nullable(),
  review_steps_enabled: z.boolean().optional(),
  voice_apply_enabled: z.boolean().optional(),
  case_open_email_enabled: z.boolean().optional(),
  case_close_email_enabled: z.boolean().optional(),
  inbound_email_notify_enabled: z.boolean().optional(),
  constituent_inquiry_email: z.string().nullish(),
})
const GetProcessViewResponseData = z.object({ process: Process3 })
const GetWorkflowViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_code: z.string(),
})
const PhoneNumberIdField = z.string()
const Process = z.object({
  id: ProcessIdField.uuid(),
  metadata: z.unknown().nullish(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  description: z.string(),
  status: ProcessStatusEnum,
  appliable: z.boolean(),
  copyable: z.boolean(),
  apply_without_verification: ProcessVerificationEnum.nullish(),
  icon: z.string().nullish(),
  pinned: z.boolean().nullish(),
  button_name: z.string().nullish(),
  version_number: z
    .number()
    .int()
    .gte(-9223372036854776000)
    .lte(9223372036854776000)
    .optional()
    .default(1),
  owner: IdentityIdField.uuid(),
  phone_line: PhoneNumberIdField.nullish(),
  ad_hoc_target: AdHocTargetIdField.uuid(),
  workflow: WorkflowIdField.uuid(),
})
const GetWorkflowViewResponseData = z.object({
  workflow: Workflow,
  workflow_version: Process,
  workflow_version_ids: z.array(ProcessIdField),
})
const GetWorkflowSummaryViewRequestData = z.object({
  owner_handle: z.string(),
  workflow_code: z.string(),
})
const GetWorkflowSummaryViewResponseData = z.object({
  workflow: Workflow,
  workflow_version: Process,
  process_stages: z.array(ProcessStage),
  charges: z.record(z.array(ChargeSubstep)),
  charge_dollars: z.record(z.string().regex(/^-?\d{0,18}(?:\.\d{0,12})?$/)),
})
const ImportEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  internet_message_id: z.string(),
  subject: z.string(),
  sender: z.string(),
  recipients: z.array(z.string()),
  message: z.string(),
})
const ImportEmailViewResponseData = z.object({ message_id: MessageIdField.uuid() })
const InviteCaseMemberByEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  email: z.string().email(),
})
const ContactBookIdField = z.string()
const ProcessFilter = z
  .object({
    owners: z.array(z.string().uuid()).nullable(),
    governing_units: z.array(z.string().uuid()).nullable(),
    has_bill: z.boolean(),
    code: z.string().nullable(),
    owner_handle: z.string().nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    managed_only: z.boolean(),
    appliable_only: z.boolean(),
    ai_enabled_only: z.boolean(),
    status: z.array(ProcessStatusEnum).nullable(),
    query: z.string().nullable(),
    license_template_ids: z.array(z.string().uuid()).nullable(),
    contact_book_id: ContactBookIdField.nullable(),
  })
  .partial()
const ListBaseProcessesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  process_filter: ProcessFilter,
})
const ListBaseProcessesViewResponseData = z.object({ processes: z.array(Process) })
const ListCalculatedInputsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  input_step_ids: z.array(InputStepIdField),
})
const InputCreate = z.object({
  value: ValueCreate.nullish(),
  form_id: FormIdField.uuid(),
  step: InputStepIdField.uuid(),
  type: ValueTypeEnum,
  input_id: InputIdField.nullish(),
})
const ListCalculatedInputsViewResponseData = z.object({ calculated_inputs: z.array(InputCreate) })
const ListCaseExportFilesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const SubmissionEnum = z.enum(['submission', 'generated', 'other'])
const CaseExportFileSerializerCaseExportFile = z.object({
  key: z.string(),
  name: z.string(),
  category: SubmissionEnum,
  size_bytes: z.number().int().nullable(),
  url: z.string(),
})
const ListCaseExportFilesViewResponseData = z.object({
  files: z.array(CaseExportFileSerializerCaseExportFile),
})
const ListCaseMembersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const ListCaseMembersViewResponseData = z.object({ members: z.array(Identity2) })
const ListCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).optional(),
  workspace_id: WorkspaceIdField.nullish(),
  filter: CaseFilter,
  sort: CaseSortEnum,
  offset: z.number().int(),
  limit: z.number().int(),
})
const ListCasesViewResponseData = z.object({ data: z.array(Case2) })
const ListCasesTabsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  tabs: z.array(CasesTabIdField).optional(),
  workspace_id: WorkspaceIdField.nullish(),
})
const CasesTab = z.object({
  id: z.string().uuid().optional(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000),
  page_size: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  custom_filters: z.unknown().nullish(),
  owner: EndUserIdField.uuid(),
  workspace: WorkspaceIdField.uuid(),
})
const ConfigTypeEnum = z.literal('saved')
const CasesConfig = z.object({
  id: z.string().uuid().optional(),
  type: ConfigTypeEnum.optional(),
  created_after: z.string().datetime({ offset: true }).nullish(),
  created_before: z.string().datetime({ offset: true }).nullish(),
  next_due_after: z.string().datetime({ offset: true }).nullish(),
  next_due_before: z.string().datetime({ offset: true }).nullish(),
  closed_after: z.string().datetime({ offset: true }).nullish(),
  closed_before: z.string().datetime({ offset: true }).nullish(),
  no_manager: z.boolean().nullish(),
  tab: CasesTabIdField.uuid(),
})
const CasesConfig1 = z.object({
  config: CasesConfig,
  assignee: z.array(z.string().uuid()).nullable(),
  manager: z.array(z.string().uuid()).nullable(),
  completed_by: z.array(z.string().uuid()).nullable(),
  owner: z.array(z.string().uuid()).nullable(),
  process: z.array(z.string().uuid()).nullable(),
  status: z.array(StatusEnum).nullable(),
  tag: z.array(z.string().uuid()).nullable(),
})
const CasesTab1 = z.object({
  tab: CasesTab,
  config: CasesConfig1.nullish(),
  process_step_ids: z.array(z.string().uuid().nullable()).nullish(),
  input_choices: z.record(z.string()).nullish(),
  input_text: z.record(z.string()).nullish(),
  input_bool: z.record(z.boolean()).nullish(),
  input_number: z.record(NumberComparison).nullish(),
})
const ListCasesTabsViewResponseData = z.object({ tabs: z.array(CasesTab1) })
const ListCasesWithLocationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  workspace_id: WorkspaceIdField.nullish(),
  filter: CaseFilter,
})
const CaseWithLocation = z.object({
  id: z.string().uuid(),
  title: z.string(),
  handle: z.string(),
  owner_identity_id: z.string().uuid(),
  location: Location0,
  status: StatusEnum.nullable(),
  created_time: z.string().datetime({ offset: true }),
  process_name: z.string().nullable(),
})
const ListCasesWithLocationViewResponseData = z.object({ cases: z.array(CaseWithLocation) })
const ListDocumentTemplatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListDocumentTemplatesViewResponseData = z.object({
  document_templates: z.array(DocumentTemplate),
})
const ListExpressionConstantsViewRequestData = z.object({
  expression_ids: z.array(ExpressionIdField),
})
const ListExpressionConstantsViewResponseData = z.object({ values: z.array(Value0) })
const ListExpressionsViewRequestData = z
  .object({
    expression_ids: z.array(ExpressionIdField),
    process_id: ProcessIdField.uuid(),
    input_step_ids: z.array(InputStepIdField),
  })
  .partial()
const ListExpressionsViewResponseData = z.object({ data: z.array(Expression) })
const ListFormStepsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  form_step_id: FormStepIdField.uuid().optional(),
  has_sla: z.boolean().optional().default(false),
})
const ListFormStepsViewResponseData = z.object({ form_steps: z.array(FormStep0) })
const ListFormsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const Form0 = z.object({
  block_instance: BlockInstance.nullable(),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  created: z.string().datetime({ offset: true }),
  completed: z.string().datetime({ offset: true }).nullable(),
  completer: z.string().uuid().nullable(),
  completer_name: z.string().nullable(),
  process_id: z.string().uuid(),
  case_id: z.string().uuid(),
  form_step_id: z.string().uuid(),
  step_id: z.string().uuid(),
  order: z.number().int(),
  due: z.string().datetime({ offset: true }).nullable(),
  item: z.string().uuid(),
  action: z.string().uuid(),
  in_review: z.boolean(),
})
const ListFormsViewResponseData = z.object({ forms: z.array(Form0) })
const InputStepFilter = z
  .object({
    input_step_ids: z.array(z.string().uuid()).nullable(),
    process_ids: z.array(z.string().uuid()).nullable(),
    form_step_ids: z.array(z.string().uuid()).nullable(),
  })
  .partial()
const ListInputSubStepsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  input_step_filter: InputStepFilter,
})
const ListInputSubStepsViewResponseData = z.object({ input_substeps: z.array(InputSubstep2) })
const InputFilter = z.object({
  case_ids: z.array(CaseIdField),
  input_id: InputIdField.nullish(),
  form_ids: z.array(z.string().uuid()).nullish(),
  input_step_ids: z.array(z.string().uuid()).nullish(),
  complete: z.boolean().nullish(),
  external_only: z.boolean().optional(),
  pinned_only: z.boolean().optional(),
})
const ListInputsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).optional(),
  input_filter: InputFilter,
})
const Input1 = z.object({
  value: Value0.nullish(),
  template: Value0.nullish(),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  type: ValueTypeEnum,
  value_id: z.string().uuid().nullable(),
  template_id: z.string().uuid().nullable(),
  setter: z.string().uuid().nullable(),
  complete: z.boolean(),
  created: z.string().datetime({ offset: true }),
  completed: z.string().datetime({ offset: true }).nullable(),
  process_id: z.string().uuid(),
  case_id: z.string().uuid(),
  step_id: z.string().uuid(),
  form_id: z.string().uuid(),
  item_id: z.string().uuid(),
  order: z.number().int().nullable(),
  due: z.string().datetime({ offset: true }).nullable(),
  section: z.string().uuid().nullable(),
})
const ListInputsViewResponseData = z.object({ inputs: z.array(Input1) })
const NotificationFilter = z
  .object({
    notification_id: NotificationIdField.nullable(),
    item_ids: z.array(z.string().uuid()).nullable(),
    block_instance_ids: z.array(z.string().uuid()).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
  })
  .partial()
const ListNotificationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  case_id: CaseIdField.uuid().optional(),
  notification_filter: NotificationFilter,
})
const Notification1 = z.object({
  assignee: Identity0,
  id: z.string().uuid(),
  item_id: z.string().uuid().nullable(),
  block_instance_id: z.string().uuid().nullable(),
  after: z.string().datetime({ offset: true }),
  assigned: z.boolean(),
  read: z.boolean(),
})
const ListNotificationsViewResponseData = z.object({ notifications: z.array(Notification1) })
const ListProcessCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_ids: z.array(CaseIdField),
})
const Bill0 = z.object({
  id: z.string().uuid(),
  receiver_id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  payer: z.string().uuid().nullable(),
  installments: z.number().int(),
  editable: z.boolean(),
  complete: z.boolean(),
  created: z.string().datetime({ offset: true }),
  due: z.string().datetime({ offset: true }).nullable(),
  completed: z.string().datetime({ offset: true }).nullable(),
  process_id: z.string().uuid(),
  bill_step_id: z.string().uuid(),
  allow_ach: z.boolean(),
  step_id: z.string().uuid(),
  case_id: z.string().uuid(),
  order: z.number().int().nullable(),
  item_id: z.string().uuid(),
  block_instance_id: z.string().uuid(),
  block_id: z.string().uuid(),
})
const ProcessCase3 = z.object({
  process: Process3,
  case: Case2,
  completed_block_instances: z.array(BlockInstance),
  form_actions: z.array(Form0),
  restricted_form_step_ids: z.array(FormStepIdField),
  restricted_bill_step_ids: z.array(BillStepIdField),
  case_id: z.string().uuid(),
  name: z.string(),
  bill_actions: z.array(Bill0),
  page_breaks: z.array(z.string().uuid()),
  inputs: z.array(Input1),
})
const ListProcessCasesViewResponseData = z.object({ process_case_3s: z.array(ProcessCase3) })
const ListProcessSummariesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_ids: z.array(CaseIdField),
})
const ProcessSummary = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  name: z.string(),
  status: ProcessStatusEnum,
  current_step_name: z.string().nullable(),
  total_stages: z.number().int(),
  completed_stages: z.number().int(),
  in_progress_stages: z.number().int(),
  incomplete_stages: z.number().int(),
  unverified_apply: z.boolean().optional(),
})
const ListProcessSummariesViewResponseData = z.object({ processes: z.array(ProcessSummary) })
const ListProcessesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  process_filter: ProcessFilter,
})
const Process0 = z.object({
  description: z.string(),
  id: z.string().uuid(),
  owner: Identity0,
  code: z.string(),
  name: z.string(),
  status: ProcessStatusEnum,
  appliable: z.boolean(),
  copyable: z.boolean(),
  apply_without_verification: ProcessVerificationEnum.nullable(),
  icon: z.string().nullable(),
  button_name: z.string().nullable(),
  pinned: z.boolean().nullable(),
  phone_line: z.string().nullable(),
  workflow_id: z.string().uuid(),
})
const ListProcessesViewResponseData = z.object({ processes: z.array(Process0) })
const ListProcessesByIdViewRequestData = z.object({ process_ids: z.array(ProcessIdField) })
const ListProcessesByIdViewResponseData = z.object({ processes: z.array(Process0) })
const ListProcessesRestrictedViewRequestData = z.object({
  governing_unit_ids: z.array(OrganizationIdField),
})
const ListProcessesRestrictedViewResponseData = z.object({ processes: z.array(Process0) })
const ListProcessesStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  process_filter: ProcessFilter,
})
const Process1 = z.object({
  description: z.string(),
  id: z.string().uuid(),
  owner: Identity0,
  code: z.string(),
  name: z.string(),
  status: ProcessStatusEnum,
  appliable: z.boolean(),
  copyable: z.boolean(),
  apply_without_verification: ProcessVerificationEnum.nullable(),
  icon: z.string().nullable(),
  button_name: z.string().nullable(),
  pinned: z.boolean().nullable(),
  total: z.number().int(),
  completed: z.number().int(),
})
const ListProcessesStatsViewResponseData = z.object({ processes: z.array(Process1) })
const ListPublicReviewCommentThreadsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const ListPublicReviewCommentThreadsViewResponseData = z.object({
  review_comment_threads: z.array(ReviewCommentThread),
})
const ListRelationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const RelationIdField = z.string()
const Relation = z.object({
  id: RelationIdField.uuid(),
  from_field: CaseIdField.uuid(),
  to: CaseIdField.uuid(),
})
const ListRelationsViewResponseData = z.object({ relations: z.array(Relation) })
const ListReviewCommentThreadsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.uuid(),
})
const ListReviewCommentThreadsViewResponseData = z.object({
  review_comment_threads: z.array(ReviewCommentThread),
})
const ListReviewsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  is_completed: z.boolean().optional(),
})
const ListReviewsViewResponseData = z.object({ reviews_by_case_id: z.record(z.array(Review)) })
const ListSectionSubStepsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  section_step_ids: z.array(SectionStepIdField).optional(),
  process_ids: z.array(ProcessIdField).optional(),
  expression_ids: z.array(ExpressionIdField).optional(),
})
const ListSectionSubStepsViewResponseData = z.object({ section_substeps: z.array(SectionSubstep0) })
const ListSectionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  section_ids: z.array(SectionIdField).optional(),
  section_step_ids: z.array(SectionStepIdField).optional(),
})
const Section = z.object({
  id: SectionIdField.uuid(),
  form: FormIdField.uuid(),
  substep: SectionStepIdField.uuid(),
  form_step: FormStepIdField.uuid(),
  section: SectionIdField.nullish(),
  created: z.string().datetime({ offset: true }).optional(),
  order: z.number().int().gte(-9223372036854776000).lte(9223372036854776000).optional(),
})
const ListSectionsViewResponseData = z.object({ sections: z.array(Section) })
const ListStepAssignmentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_ids: z.array(BlockIdField).optional(),
  expression_ids: z.array(ExpressionIdField).optional(),
})
const StepNotificationIdField = z.string()
const StepNotification = z.object({
  id: StepNotificationIdField.uuid(),
  step: StepIdField.nullish(),
  assigned: z.boolean(),
  user: EndUserIdField.uuid(),
  user_type: ValueTypeEnum,
  block: BlockIdField.uuid(),
  step_name: z.string(),
})
const ListStepAssignmentsViewResponseData = z.object({
  step_assignments: z.array(StepNotification),
})
const ListTasksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  task_id: TaskIdField.uuid().optional(),
  item_ids: z.array(ItemIdField).optional(),
  case_ids: z.array(CaseIdField).optional(),
})
const Task1 = z.object({
  id: z.string().uuid(),
  owner: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  created: z.string().datetime({ offset: true }),
  creator: Identity0.nullable(),
  complete: z.boolean(),
  completed: z.string().datetime({ offset: true }).nullable(),
  due: z.string().datetime({ offset: true }).nullable(),
  case_id: z.string().uuid().nullable(),
  item_id: z.string().uuid(),
})
const ListTasksViewResponseData = z.object({ tasks: z.array(Task1) })
const ListTemplatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  template_id: MessageTemplateIdField.uuid().optional(),
  organization_identity_id_list: z.array(IdentityIdField),
  organization_code: z.string().optional(),
  template_code: z.string().optional(),
  query: z.string().optional(),
  limit: z.number().int().optional(),
})
const MessageTemplate0 = z.object({
  owner: Identity0,
  attachments: z.array(Attachment),
  name: z.string(),
  body: z.string(),
  id: z.string().uuid(),
  code: z.string(),
  modified: z.string().datetime({ offset: true }),
})
const ListTemplatesViewResponseData = z.object({ templates: z.array(MessageTemplate0) })
const ListValuesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  value_ids: z.array(ValueIdField),
  type: ValueTypeEnum.optional(),
})
const ListValuesViewResponseData = z.object({ values: z.array(Value0) })
const ListWorkflowStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  status: z.array(Active1Enum).optional(),
  has_live_version: z.boolean().nullish(),
  ai_enabled_only: z.boolean().optional().default(false),
  query: z.string().optional(),
})
const WorkflowStats = z.object({
  workflow: Workflow,
  has_live_version: z.boolean(),
  total: z.number().int(),
  completed: z.number().int(),
})
const ListWorkflowStatsViewResponseData = z.object({ workflows: z.array(WorkflowStats) })
const ListWorkflowVersionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
})
const ListWorkflowVersionsViewResponseData = z.object({ workflow_versions: z.array(Process) })
const ListWorkflowsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  has_bill: z.boolean().optional().default(false),
  status: z.array(Active1Enum).optional(),
  has_live_version: z.boolean().nullish(),
  contact_book_id: ContactBookIdField.nullish(),
  license_template_ids: z.array(LicenseTemplateIdField).optional(),
})
const ListWorkflowsViewResponseData = z.object({ workflows: z.array(Workflow) })
const NotifyArgs = z.object({
  assignee: IdentityIdField.uuid(),
  assigned: z.boolean(),
  send: z.boolean(),
  item_id: ItemIdField.nullish(),
  block_instance_id: BlockInstanceIdField.nullish(),
  after: z.string().datetime({ offset: true }).nullish(),
})
const NotifyViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  notify_args: NotifyArgs,
})
const PerformAIPreSubmissionReviewViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  form_id: FormIdField.uuid(),
  current_local_date: z.string(),
})
const AIReviewComment = z.object({ input_id: InputIdField.uuid(), comment: z.string() })
const PerformAIPreSubmissionReviewViewResponseData = z.object({
  comments: z.array(AIReviewComment),
})
const PhonicVoiceCreateCaseViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  process_id: ProcessIdField.uuid(),
})
const PhonicVoiceCreateCaseViewResponseData = z.object({
  case_handle: z.string(),
  log_id: z.string(),
  time_taken_ms: z.number().int(),
})
const PhonicVoiceGetNextQuestionsViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough().optional(),
  case_handle: z.string(),
  last_question_checkpoint: InputStepIdField.nullish(),
})
const PhonicVoiceGetNextQuestionsViewResponseData = z.object({ time_taken_ms: z.number().int() })
const PhonicVoiceRecordAnswersViewRequestData = z.object({
  call_info: z.object({}).partial().passthrough(),
  case_handle: z.string(),
  section_blocks: z.unknown().optional(),
  input_blocks: z.unknown().optional(),
  add_an_item: z.boolean().optional().default(false),
})
const PhonicVoiceRecordAnswersViewResponseData = z.object({
  form_id: FormIdField.nullable(),
  error_message: z.string().nullable(),
  invalid_input_step_id: InputStepIdField.nullable(),
  time_taken_ms: z.number().int(),
})
const Variable = z.object({ key: z.string(), label: z.string().optional() })
const AutoMapDocumentTemplateFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  variables: z.array(Variable),
})
const Mapping = z.object({
  variable_key: z.string(),
  input_step_id: InputStepIdField.uuid(),
  field: z.string().optional(),
})
const AutoMapDocumentTemplateFieldsViewResponseData = z.object({ mappings: z.array(Mapping) })
const APIEndpointIdField = z.string()
const CommandEnum = z.enum(['command', 'listener'])
const CreateAPIAutomationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
  api_endpoint_id: APIEndpointIdField.nullish(),
  connection_type: ConnectionTypeEnum,
  endpoint_tool_name: z.string().max(100),
  kind: CommandEnum.optional(),
})
const APIAutomationIdField = z.string()
const IntegrationParameterMappingIdField = z.string()
const IntegrationParameterMapping = z.object({
  id: IntegrationParameterMappingIdField.uuid(),
  automation: APIAutomationIdField.uuid(),
  expression: ExpressionIdField.uuid(),
  metadata: z.unknown().nullish(),
  target: z.string().max(200),
  expression_sub_field: z.string().max(200).nullish(),
})
const IntegrationBlockMetadata = z.object({
  connection_type: ConnectionTypeEnum,
  endpoint_tool_name: z.string(),
})
const APIAutomation = z.object({
  id: APIAutomationIdField.uuid(),
  block: BlockIdField.nullable(),
  api_endpoint: APIEndpointIdField.nullable(),
  input_mappings: z.array(IntegrationParameterMapping).optional(),
  metadata: IntegrationBlockMetadata.nullish(),
  kind: CommandEnum.optional(),
})
const CreateAPIAutomationBlockViewResponseData = z.object({
  block: Block,
  api_automation: APIAutomation,
})
const CreateCommunicationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateCommunicationBlockViewResponseData = z.object({
  block: Block,
  communication_automation: CommunicationAutomation,
})
const CreateConnectorViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  from_block: BlockIdField.nullable(),
  to_block: BlockIdField.nullable(),
  gate: ExpressionIdField.nullish(),
  review_outcome: AcceptEnum.optional(),
  force_replay: z.boolean().optional().default(false),
})
const CreateConnectorViewResponseData = z.object({
  connector: Connector,
  gate_expressions: z.array(Expression),
})
const CreateDemoBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateDemoBlockViewResponseData = z.object({ block: Block, demo_block: DemoBlock })
const CreateDocumentGenerationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateDocumentGenerationBlockViewResponseData = z.object({
  block: Block,
  document_step: DocumentStep,
})
const CreateFormBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateFormBlockViewResponseData = z.object({ block: Block, form_step: FormStep })
const CreateLicenseBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateLicenseBlockViewResponseData = z.object({ block: Block, license_block: LicenseBlock })
const CreatePaymentBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreatePaymentBlockViewResponseData = z.object({ block: Block, bill_step: BillStep })
const CreateRedactionBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateRedactionBlockViewResponseData = z.object({
  block: Block,
  redaction_block: RedactionBlock,
})
const ReviewFocusFieldIdField = z.string()
const ReviewFocusFieldInput = z
  .object({
    id: ReviewFocusFieldIdField.nullable(),
    focus_block_id: BlockIdField.nullable(),
    focus_section_step_id: SectionStepIdField.nullable(),
    focus_input_step_id: InputStepIdField.nullable(),
  })
  .partial()
const ReviewerRulesetIdField = z.string()
const ReviewerRulesetInput = z.object({
  id: ReviewerRulesetIdField.nullish(),
  review_group_id: GroupIdField.uuid(),
  quorum: z.number().int(),
})
const CreateReviewBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
  allowed_outcomes: z.array(AcceptEnum).optional(),
  automated_review_prompt: z.string().optional(),
  due: z.string().nullish(),
  sla: z.string().nullish(),
  focus_fields: z.array(ReviewFocusFieldInput).optional(),
  reviewer_rulesets: z.array(ReviewerRulesetInput).optional(),
})
const ReviewFocusField = z.object({
  id: ReviewFocusFieldIdField.uuid(),
  review_block: ReviewBlockIdField.uuid(),
  focus_block: BlockIdField.nullable(),
  focus_section_step: SectionStepIdField.nullable(),
  focus_input_step: InputStepIdField.nullable(),
  metadata: z.unknown().nullish(),
})
const ReviewerRuleset = z.object({
  id: ReviewerRulesetIdField.uuid(),
  review_block: ReviewBlockIdField.uuid(),
  review_group: GroupIdField.uuid(),
  metadata: z.unknown().nullish(),
  quorum: z.number().int().gte(-2147483648).lte(2147483647),
})
const ReviewBlock = z.object({
  id: ReviewBlockIdField.uuid(),
  block: BlockIdField.uuid(),
  allowed_outcomes: z.array(AcceptEnum),
  focus_fields: z.array(ReviewFocusField),
  reviewer_rulesets: z.array(ReviewerRuleset),
  metadata: z.unknown().nullish(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
  automated_review_prompt: z.string().optional(),
  due: z.string().nullish(),
  sla: z.string().nullish(),
})
const CreateReviewBlockViewResponseData = z.object({
  block: Block,
  review_block: ReviewBlock,
  connectors: z.array(Connector),
  gate_expressions: z.array(Expression),
})
const CreateRoleBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateRoleBlockViewResponseData = z.object({
  block: Block,
  role_automation: CaseRoleAutomation,
})
const CreateStatusBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
})
const CreateStatusBlockViewResponseData = z.object({ block: Block, status: StatusEnum.nullable() })
const CreateTagBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_version_id: ProcessIdField.uuid(),
  step_id: StepIdField.uuid().optional(),
  tag_id: TagIdField.uuid().optional(),
  apply_id: ExpressionIdField.uuid().optional(),
  action: TagAutomationActionEnum.optional(),
})
const CreateTagBlockViewResponseData = z.object({ block: Block, tag_automation: TagAutomation })
const DeleteBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
})
const DeleteConnectorViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connector: ConnectorIdField.uuid(),
})
const DetectDocumentTemplateFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  file: z.string().url(),
})
const DetectDocumentTemplateFieldsViewResponseData = z.object({
  docx: z.string(),
  field_count: z.number().int(),
})
const ParameterMappingPayload = z.object({
  target: z.string().max(200),
  expression: ExpressionIdField.uuid(),
  expression_sub_field: z.string().max(200).nullish(),
})
const EditAPIAutomationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  api_endpoint_id: APIEndpointIdField.nullish(),
  kind: CommandEnum.optional(),
  parameter_mappings: z.array(ParameterMappingPayload).optional(),
})
const EditAPIAutomationBlockViewResponseData = z.object({ api_automation: APIAutomation })
const EditBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  process_stage_id: ProcessStageIdField.nullish(),
  control_flow_replay: AlwaysEnum.optional(),
  data_dependency_replay: AlwaysEnum.optional(),
  gate_type: AnyInboundEnum.optional(),
  entry_criteria: ExpressionIdField.nullish(),
})
const EditBlockViewResponseData = z.object({ block: Block })
const CommunicationRecipientPayload = z.object({
  recipient_expression: ExpressionIdField.uuid(),
  recipient_type: ToEnum,
})
const EditCommunicationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  name: z.string().max(200).optional(),
  subject: z.string().max(500).optional(),
  staff_review_required: z.boolean().optional(),
  due: z.string().nullish(),
  from_email: ContactEmailIdField.nullish(),
  form_letter_template: MessageTemplateIdField.nullish(),
  ai_prompt: z.string().optional(),
  body: z.string().optional(),
  input_step_ids: z.array(InputStepIdField).optional(),
  attachments: z.array(Attachment).optional(),
  case_document_step_ids: z.array(DocumentStepIdField).optional(),
  case_file_input_step_ids: z.array(InputStepIdField).optional(),
  case_redaction_block_ids: z.array(BlockIdField).optional(),
  case_document_ai_enabled: z.boolean().optional(),
  recipients: z.array(CommunicationRecipientPayload).optional(),
})
const EditCommunicationBlockViewResponseData = z.object({
  communication_automation: CommunicationAutomation,
})
const EditConnectorViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connector: ConnectorIdField.uuid(),
  from_block: BlockIdField.nullish(),
  to_block: BlockIdField.nullish(),
  gate: ExpressionIdField.nullish(),
  review_outcome: AcceptEnum.optional(),
  force_replay: z.boolean().optional(),
})
const EditConnectorViewResponseData = z.object({
  connector: Connector,
  gate_expressions: z.array(Expression),
})
const EditDemoBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
  icon: z.string().max(100).optional(),
  color: z.string().max(50).optional(),
})
const EditDemoBlockViewResponseData = z.object({ demo_block: DemoBlock })
const EditDocumentGenerationBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  document_template: DocumentTemplateIdField.nullish(),
  field_values: z.unknown().nullish(),
  output_filename: ExpressionIdField.nullish(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
})
const EditDocumentGenerationBlockViewResponseData = z.object({ document_step: DocumentStep })
const EditDocumentTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  document_template_id: DocumentTemplateIdField.uuid(),
  name: z.string(),
  file: z.string().url().optional(),
})
const EditDocumentTemplateViewResponseData = z.object({ document_template: DocumentTemplate })
const EditLicenseBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  license_template_id: LicenseTemplateIdField.nullish(),
  application_type: NewEnum.optional(),
})
const EditLicenseBlockViewResponseData = z.object({ license_block: LicenseBlock })
const EditRedactionBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  name: z.string().max(200),
})
const EditRedactionBlockViewResponseData = z.object({ redaction_block: RedactionBlock })
const EditReviewBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  name: z.string().max(200).optional(),
  description: z.string().optional(),
  allowed_outcomes: z.array(AcceptEnum).optional(),
  automated_review_prompt: z.string().optional(),
  due: z.string().nullish(),
  sla: z.string().nullish(),
  focus_fields: z.array(ReviewFocusFieldInput).optional(),
  reviewer_rulesets: z.array(ReviewerRulesetInput).optional(),
})
const EditReviewBlockViewResponseData = z.object({
  review_block: ReviewBlock,
  connectors: z.array(Connector),
  gate_expressions: z.array(Expression),
  deleted_connector_ids: z.array(ConnectorIdField),
})
const EditRoleBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  case_role_action: SetRequesterEnum.optional(),
  identity_expression: ExpressionIdField.uuid().optional(),
})
const EditRoleBlockViewResponseData = z.object({ case_role_automation: CaseRoleAutomation })
const EditStatusBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  status: StatusEnum.nullable(),
})
const EditStatusBlockViewResponseData = z.object({ status: StatusEnum.nullable() })
const EditTagBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  tag_id: TagIdField.uuid().optional(),
  apply_id: ExpressionIdField.nullish(),
  action: TagAutomationActionEnum.optional(),
  applies_to: TagAutomationTargetEnum.optional(),
  identity_expression: ExpressionIdField.nullish(),
})
const EditTagBlockViewResponseData = z.object({ tag_automation: TagAutomation })
const GetOrganizationSourceEmailsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const CommunicationSenderOption = z.object({
  contact_email_id: ContactEmailIdField.uuid(),
  address: z.string(),
  name: z.string(),
  ses_verified: z.boolean(),
  is_org: z.boolean(),
})
const GetOrganizationSourceEmailsViewResponseData = z.object({
  has_email: z.boolean(),
  from_email: z.string().nullish(),
  from_name: z.string().nullish(),
  ses_verified: z.boolean(),
  senders: z.array(CommunicationSenderOption),
})
const ListBlockConfigurationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_ids: z.array(BlockIdField),
})
const ListBlockConfigurationsViewResponseData = z.object({
  form_steps: z.record(FormStep),
  bill_steps: z.record(BillStep),
  status_automations: z.record(CaseStatusAutomation),
  tag_automations: z.record(TagAutomation),
  role_automations: z.record(CaseRoleAutomation),
  communication_automations: z.record(CommunicationAutomation),
  document_steps: z.record(DocumentStep),
  license_blocks: z.record(LicenseBlock),
  demo_blocks: z.record(DemoBlock),
  redaction_blocks: z.record(RedactionBlock),
  api_automations: z.record(APIAutomation),
  review_blocks: z.record(ReviewBlock),
})
const ListBlocksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_version: ProcessIdField.uuid(),
})
const ListBlocksViewResponseData = z.object({ blocks: z.array(Block) })
const ListConnectorsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_version_id: ProcessIdField.uuid(),
})
const ListConnectorsViewResponseData = z.object({ connectors: z.array(Connector) })
const PreviewCommunicationBlockEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  block_id: BlockIdField.uuid(),
  subject: z.string().max(500),
  body: z.string(),
  send_test: z.boolean().optional().default(false),
})
const PreviewCommunicationBlockEmailViewResponseData = z.object({ html: z.string() })
const UploadDocumentTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  file: z.string().url(),
  detect_fields: z.boolean().optional().default(false),
})
const UploadDocumentTemplateViewResponseData = z.object({ document_template: DocumentTemplate })
const PublishLatestWorkflowVersionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  draft_version: ProcessIdField.uuid(),
})
const PublishLatestWorkflowVersionViewResponseData = z.object({
  draft_version: ProcessIdField.uuid(),
})
const ResetDraftVersionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
})
const ResetDraftVersionViewResponseData = z.object({ workflow_version_id: ProcessIdField.uuid() })
const ResolveThreadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  thread_id: CommentThreadIdField.uuid(),
  is_resolved: z.boolean().optional().default(true),
})
const RestartAgenticImplementationLogViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
})
const RestartAgenticImplementationLogViewResponseData = z.object({
  log_id: z.string(),
  messages: z.array(AgenticImplementationMessageSerializerAgenticImplementationMessage),
  status: z.string(),
  status_message: z.string().nullish(),
})
const SaveOrSubmitFormViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  mark_form_complete: z.boolean(),
  form_nodes: z.array(FormNodeCreate).optional(),
  form_id: FormIdField.uuid().optional(),
})
const SearchCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  query: z.string(),
  offset: z.number().int(),
  limit: z.number().int(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  workspace_id: WorkspaceIdField.nullish(),
  filter: CaseFilter.optional(),
})
const SearchCasesViewResponseData = z.object({
  cases: z.array(Case2),
  total: z.number().int(),
  has_more: z.boolean(),
})
const SetActionInReviewViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  action_id: ActionIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  in_review: z.boolean(),
})
const SetCaseStatusViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  status: StatusEnum.nullable(),
})
const StepAssignmentCreate = z.object({
  block_id: BlockIdField.uuid(),
  user_expression_id: ExpressionIdField.uuid(),
  assigned: z.boolean(),
})
const SetStepAssignmentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  step_assignments: z.array(StepAssignmentCreate),
})
const SetStepAssignmentsViewResponseData = z.object({ step_assignments: z.array(StepNotification) })
const StartProcessViewRequestData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  email: z.string().email().optional(),
  process_id: ProcessIdField.uuid(),
  token: z.string().optional(),
})
const StartProcessViewResponseData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  case_id: CaseIdField.uuid(),
  handle: z.string(),
})
const ToggleApplicantIdentitySectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  form_step_id: FormStepIdField.uuid(),
  enabled: z.boolean(),
})
const UploadAgenticImplementationAttachmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  document: z.string().url(),
})
const UploadAgenticImplementationAttachmentViewResponseData = z.object({
  attachment_id: z.string(),
  filename: z.string(),
  content_type: z.string().nullable(),
  s3_url: z.string(),
  download_url: z.string(),
})
const CasesConfigUpsert = z.object({
  tab_id: CasesTabIdField.uuid(),
  created_after: z.string().datetime({ offset: true }).nullish(),
  created_before: z.string().datetime({ offset: true }).nullish(),
  next_due_after: z.string().datetime({ offset: true }).nullish(),
  next_due_before: z.string().datetime({ offset: true }).nullish(),
  closed_after: z.string().datetime({ offset: true }).nullish(),
  closed_before: z.string().datetime({ offset: true }).nullish(),
  no_manager: z.boolean().nullish(),
  assignee: z.array(z.string().uuid()).nullish(),
  manager: z.array(z.string().uuid()).nullish(),
  owner: z.array(z.string().uuid()).nullish(),
  process: z.array(z.string().uuid()).nullish(),
  status: z.array(StatusEnum).nullish(),
  tag: z.array(z.string().uuid()).nullish(),
  count: z.number().int().nullish(),
  process_step_ids: z.array(z.string().uuid().nullable()).nullish(),
  completed_by: z.array(z.string().uuid()).nullish(),
  input_choices: z.record(z.string()).optional(),
  input_text: z.record(z.string()).optional(),
  input_bool: z.record(z.boolean()).optional(),
  input_number: z.record(NumberComparison).optional(),
})
const UpsertCasesConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  config: CasesConfigUpsert,
})
const UpsertTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  template_id: MessageTemplateIdField.uuid().optional(),
  name: z.string(),
  body: z.string(),
  attachments: z.array(Attachment),
})
const UpsertTemplateViewResponseData = z.object({ template_id: MessageTemplateIdField.uuid() })
const ElevenLabsVoiceCreateCaseViewRequestData = z.object({
  organization_id: OrganizationIdField.uuid(),
  process_id: ProcessIdField.uuid(),
  phone_number: z.string(),
})
const LogIdField = z.string()
const ElevenLabsVoiceCreateCaseViewResponseData = z.object({
  user_id: EndUserIdField.uuid(),
  case_handle: z.string(),
  log_id: LogIdField.uuid(),
})
const ElevenLabsVoiceGetNextQuestionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_handle: z.string(),
  last_question_checkpoint: InputStepIdField.nullish(),
})
const SectionResponseBlock = z.object({
  id: z.string().uuid(),
  temporary_id: z.string(),
  parent_section_temporary_id: z.string().nullish(),
})
const VoiceResponseData = z.object({ type: ValueTypeEnum, value: z.unknown().nullable() })
const QuestionResponseBlock = z.object({
  id: z.string(),
  instance: VoiceResponseData,
  section_temporary_id: z.string().nullish(),
})
const ElevenLabsVoiceRecordAnswersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_handle: z.string(),
  caller_id: z.string().optional(),
  section_blocks: z.array(SectionResponseBlock).optional(),
  input_blocks: z.array(QuestionResponseBlock).optional(),
  add_an_item: z.boolean().optional().default(false),
})
const ElevenLabsVoiceRecordAnswersViewResponseData = z.object({
  form_id: FormIdField.nullable(),
  error_message: z.string().nullable(),
  invalid_input_step_id: InputStepIdField.nullable(),
})
const EmptyRequestSerializerEmptyRequest = z.object({ _body: z.string() }).partial()
const ChangelogEntry = z.object({ week: z.string(), content: z.string() })
const GetChangelogViewResponseData = z.object({ entries: z.array(ChangelogEntry) })
const AddAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  address_id: AddressIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  skip_contact_record_write: z.boolean().optional().default(false),
})
const AddAddressToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  address_id: AddressIdField.uuid(),
})
const AddAttributionToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  name: z.string(),
  value: z.string(),
})
const AddEmailToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  email: z.string().email(),
})
const AddNoteToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  note: z.string(),
})
const AddPhoneNumberViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  contact_id: ContactIdField.uuid(),
  phone_number: z.string(),
  skip_contact_record_write: z.boolean().optional().default(false),
})
const AddPhoneToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  phone: z.string(),
})
const AddTagToContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  tag_id: TagIdField.uuid(),
})
const AddressImportConfig = z.object({
  street: z.string(),
  unit: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  longitude: z.string().nullable(),
  latitude: z.string().nullable(),
})
const ImportContactRecordsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  first_name: z.string(),
  last_name: z.string(),
  date_of_birth: z.string(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  one_line_addresses: z.array(z.string()),
  structured_addresses: z.array(AddressImportConfig),
  attributions: z.array(z.array(z.string()).min(2).max(2)),
  tags: z.array(z.string()),
  make_title_case: z.boolean(),
  url: z.string(),
  file_name: z.string().optional().default(''),
})
const ContactImportJobIdField = z.string()
const ImportContactRecordsViewResponseData = z.object({ job_id: ContactImportJobIdField.uuid() })
const GetContactRecordImportHeaderViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  file_url: z.string(),
})
const GetContactRecordImportHeaderViewResponseData = z.object({
  contacts_header: z.array(z.string()),
})
const ListContactImportJobsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
})
const ContactImportJob = z.object({
  id: ContactImportJobIdField.uuid(),
  status: z.string(),
  file_name: z.string(),
  total_rows: z.number().int().nullable(),
  processed_rows: z.number().int(),
  error_count: z.number().int(),
  error_message: z.string().nullable(),
  created: z.string().datetime({ offset: true }),
  finished: z.string().datetime({ offset: true }).nullable(),
})
const ListContactImportJobsViewResponseData = z.object({ jobs: z.array(ContactImportJob) })
const GISLayerPropertyPredicate = z.object({
  layer_id: GISLayerSourceIdField.uuid(),
  property_key: z.string(),
  values: z.array(z.string()),
})
const ContactFilter = z
  .object({
    gis_layer_property_filters: z.array(GISLayerPropertyPredicate).nullable(),
    book: IdentityIdField.nullable(),
    tags: z.array(z.string().uuid()).nullable(),
    exclude_tags: z.array(z.string().uuid()).nullable(),
    points: z.array(GeoPoint).nullable(),
    gis_layer_ids: z.array(z.string().uuid()).nullable(),
    name: z.string().nullable(),
    entries: z.array(z.string().uuid()).nullable(),
  })
  .partial()
const CountContactsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_filter: ContactFilter,
})
const CountContactsViewResponseData = z.object({ num_contacts: z.number().int() })
const CreateAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  location: GeoPoint,
})
const CreateAddressViewResponseData = z.object({ address: Address0 })
const AttributeIdField = z.string()
const AttributionCreate = z.object({
  name: z.string(),
  value: z.string(),
  attribute_id: AttributeIdField.nullable(),
})
const CreateContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  title: z.string().optional(),
  first_name: z.string().optional(),
  middle_name: z.string().optional(),
  last_name: z.string().optional(),
  suffix: z.string().optional(),
  date_of_birth: z.string().nullish(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  addresses: z.array(AddressIdField),
  tags: z.array(TagIdField),
  attributions: z.array(AttributionCreate),
  is_organization: z.boolean().optional(),
})
const CreateContactViewResponseData = z.object({
  contact_entry_id: ContactEntryIdField.uuid().optional(),
  identity_id: IdentityIdField.uuid().optional(),
  target_id: TargetIdField.uuid().optional(),
  email_address_conflict: z.boolean(),
  existing_contact_handle: z.string().optional(),
  existing_contact_name: z.string().nullish(),
})
const CreateContactFromCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const CreateContactFromCaseViewResponseData = z.object({
  contact_entry_id: ContactEntryIdField.uuid(),
})
const CreateContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  date_of_birth: z.string().nullish(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  addresses: z.array(AddressIdField),
  tags: z.array(TagIdField),
  attributions: z.array(AttributionCreate),
  is_organization: z.boolean().optional(),
})
const CreateContactRecordViewResponseData = z
  .object({
    contact_record_id: ContactRecordIdField.uuid(),
    contact_record_handle: z.string(),
    existing_contact_handle: z.string().nullable(),
  })
  .partial()
const CreateContactRelationshipViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  from_contact_record_id: ContactRecordIdField.uuid(),
  to_contact_record_id: ContactRecordIdField.uuid(),
  relationship_label: z.string(),
  create_reverse: z.boolean().optional().default(false),
  reverse_relationship_label: z.string().optional(),
})
const CreateEmailAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.nullish(),
  address: z.string(),
  verify: z.boolean(),
  contact_id: ContactIdField.uuid().optional(),
  with_force: z.boolean().optional().default(false),
  skip_contact_record_write: z.boolean().optional().default(false),
})
const CreateLightweightIdentityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const CreateLightweightIdentityViewResponseData = z.object({ identity_id: IdentityIdField.uuid() })
const CreateRelatedContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  from_contact_entry_id: ContactEntryIdField.uuid(),
  to_contact_entry_id: ContactEntryIdField.uuid(),
  name: z.string(),
  create_reverse: z.boolean().optional().default(false),
  reverse_name: z.string().optional(),
})
const DeleteContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_entry_id: ContactEntryIdField.uuid(),
})
const DeleteContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
})
const DeleteContactRelationshipViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  from_contact_record_id: ContactRecordIdField.uuid(),
  to_contact_record_id: ContactRecordIdField.uuid(),
  relationship_label: z.string(),
})
const DeleteRelatedContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  from_contact_entry_id: ContactEntryIdField.uuid(),
  to_contact_entry_id: ContactEntryIdField.uuid(),
})
const DownloadContactBookViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  query: z.string().nullish(),
  tag_names: z.array(z.string()).optional(),
  polygon: z.array(z.record(z.number())).optional(),
})
const DownloadContactBookViewResponseData = z.object({ email_recipient: z.string().email() })
const DownloadContactsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_filter: ContactFilter,
  excludes: z.array(ContactEntryIdField).optional(),
  query: z.string().optional(),
})
const DownloadContactsViewResponseData = z.object({ email_recipient: z.string().email() })
const VerifyAdmin = z.object({ identity: Identity0 })
const VerifyApply = z.object({ workflow: WorkflowIdField.uuid() })
const VerifyStandard = z.object({ uri: z.string().nullable() })
const VerifyThread = z.object({ identity: Identity0, title: z.string(), handle: z.string() })
const AccessResourceTypeEnum = z.enum(['case', 'workflow'])
const AccessActionEnum = z.enum(['read', 'write', 'delete', 'export'])
const VerifyAccessGrant = z.object({
  resource_type: AccessResourceTypeEnum,
  access_level: AccessActionEnum,
  resource_name: z.string(),
  uri: z.string().nullable(),
})
const VerifyTypeEnum = z.enum([
  'apply',
  'admin',
  'signup',
  'standard',
  'thread',
  'login',
  'access_grant',
])
const VerifyLogin = z.object({ redirect_uri: z.string().nullable() })
const VerifyOptions = z.object({
  admin: VerifyAdmin.nullish(),
  apply: VerifyApply.nullish(),
  standard: VerifyStandard.nullish(),
  thread: VerifyThread.nullish(),
  access_grant: VerifyAccessGrant.nullish(),
  type: VerifyTypeEnum,
  login: VerifyLogin.nullish(),
})
const EmailSignupViewRequestData = z.object({ email: z.string(), verify_options: VerifyOptions })
const EmailSignupViewResponseData = z
  .object({ contact_email_id: ContactEmailIdField.uuid() })
  .partial()
const GenerateAIContactSummaryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_code: z.string(),
  contact_handle: z.string(),
  workspace_id: WorkspaceIdField.nullish(),
  contact_first_name: z.string().nullish(),
})
const ContactInteractionTitle = z.object({
  ai_interaction_id: AIInteractionIdField.uuid(),
  title: z.string(),
})
const GenerateAIContactSummaryViewResponseData = z.object({
  summary: z.string(),
  interaction_titles: z.array(ContactInteractionTitle),
})
const GetContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_code: z.string(),
  contact_handle: z.string(),
})
const ContactView = z.object({
  id: z.string(),
  contact_book_id: ContactBookIdField.uuid(),
  handle: z.string().nullable(),
  contact_type: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  addresses: z.array(AddressBase),
  tags: z.array(z.string()),
  attributions: z.array(z.array(z.string())),
  interaction_count: z.number().int(),
  last_interaction_at: z.string().datetime({ offset: true }).nullish(),
  notes: z.array(z.string()).optional(),
})
const GetContactViewResponseData = z.object({ contact: ContactView })
const GetContactCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_code: z.string(),
  contact_handle: z.string(),
  workspace_id: WorkspaceIdField.nullish(),
})
const CaseCard = z.object({
  id: CaseIdField.uuid(),
  handle: z.string(),
  name: z.string(),
  status: StatusEnum.nullable(),
  created: z.string().datetime({ offset: true }).optional(),
  last_action_time: z.string().datetime({ offset: true }).nullish(),
})
const GetContactCasesViewResponseData = z.object({ cases: z.array(CaseCard) })
const GetContactEntryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_entry_id: ContactEntryIdField.uuid(),
})
const ContactEntry0 = z.object({
  id: ContactEntryIdField.uuid(),
  owner: z.string(),
  owner_id: IdentityIdField.uuid(),
  handle: z.string(),
  target: TargetIdField.uuid(),
  name: z.string(),
  image: z.string().nullable(),
  contact: ContactIdField.uuid(),
  is_organization: z.boolean(),
})
const GetContactEntryViewResponseData = z.object({ contact_entry: ContactEntry0 })
const GetContactEntryExpandedViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  identity_handle: z.string(),
})
const PhoneNumber0 = z.object({
  phone_number_id: PhoneNumberIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  phone_number: z.string(),
})
const Attribute = z.object({
  id: z.string().uuid().optional(),
  owner_id: IdentityIdField.uuid(),
  code: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  info: z.string(),
})
const Attribution = z.object({
  id: z.string().uuid().optional(),
  target: TargetIdField.uuid(),
  attribute: Attribute,
  value: z.string(),
})
const ContactEntry2 = z.object({
  emails: z.array(ContactEmail0),
  phones: z.array(PhoneNumber0),
  addresses: z.array(Address0),
  tags: z.array(Tag),
  attributions: z.array(Attribution),
  id: ContactEntryIdField.uuid(),
  owner: z.string(),
  owner_identity_id: IdentityIdField.uuid(),
  handle: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  is_organization: z.boolean(),
})
const Person = z.object({
  first_name: z.string(),
  middle_name: z.string().nullish(),
  last_name: z.string(),
  date_of_birth: z.string().nullish(),
  title: z.string().nullish(),
  suffix: z.string().nullish(),
})
const GetContactEntryExpandedViewResponseData = z.object({
  contact_entry: ContactEntry2,
  notes: z.array(Note0),
  person: Person.nullish(),
})
const GetContactEntryIdentityIdsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  identity_handle: z.string(),
})
const GetContactEntryIdentityIdsViewResponseData = z.object({
  identity_id: IdentityIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  target_id: TargetIdField.uuid(),
})
const GetContactOutboundCommsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_code: z.string(),
  contact_handle: z.string(),
  workspace_id: WorkspaceIdField.nullish(),
})
const MassCommunicationReceiptIdField = z.string()
const CommunicationChannelEnum = z.union([z.literal(0), z.literal(1)])
const ReceiptStatusEnum = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
])
const ContactOutboundComm = z.object({
  id: MassCommunicationReceiptIdField.uuid(),
  mass_communication: MassCommunicationIdField.uuid(),
  channel: CommunicationChannelEnum,
  channel_display: z.string().optional(),
  phone_number: PhoneNumberIdField.nullish(),
  phone_number_display: z.string().nullish(),
  email_address: EmailAddressIdField.nullish(),
  email_address_display: z.string().nullish(),
  subject: z.string().nullish(),
  body: z.string().nullish(),
  status: ReceiptStatusEnum.optional(),
  status_display: z.string().optional(),
  created: z.string().datetime({ offset: true }).optional(),
  updated: z.string().datetime({ offset: true }).optional(),
  sent_at: z.string().datetime({ offset: true }).nullish(),
  error_message: z.string().nullish(),
  external_id: z.string().nullish(),
  opened_at: z.string().datetime({ offset: true }).nullish(),
  clicked_at: z.string().datetime({ offset: true }).nullish(),
  bounced_at: z.string().datetime({ offset: true }).nullish(),
  complaint_at: z.string().datetime({ offset: true }).nullish(),
  organization_handle: z.string().nullish(),
  reply_ai_interaction_id: AIInteractionIdField.nullish(),
  reply_at: z.string().datetime({ offset: true }).nullish(),
  mass_communication_name: z.string().nullish(),
  from_address: z.string().nullish(),
})
const GetContactOutboundCommsViewResponseData = z.object({
  outbound_comms: z.array(ContactOutboundComm),
})
const GetContactRecordIdentityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
})
const GetContactRecordIdentityViewResponseData = z.object({ identity_id: IdentityIdField.uuid() })
const GetContactRouteByIdentityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
})
const GetContactRouteByIdentityViewResponseData = z
  .object({ contact_book_code: z.string().nullable(), contact_handle: z.string().nullable() })
  .partial()
const GetContactsHeaderViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  file_url: z.string(),
})
const GetContactsHeaderViewResponseData = z.object({ contacts_header: z.array(z.string()) })
const GetMyLocationViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetMyLocationViewResponseData = z.object({ point: GeoPoint.nullable() })
const GetOrganizationLocationViewRequestData = z.object({
  user_id: EndUserIdField.uuid().optional(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetOrganizationLocationViewResponseData = z.object({ point: GeoPoint.nullable() })
const GetUserAccountAddressViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetUserAccountAddressViewResponseData = z.object({ addresses: z.array(Address0) })
const GetUserEmailAddressesViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetUserEmailAddressesViewResponseData = z.object({ contact_emails: z.array(ContactEmail0) })
const GetUserPhoneViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetUserPhoneViewResponseData = z.object({ phone_numbers: z.array(PhoneNumber0) })
const HandleInboundEmailViewRequestData = z.object({
  token: z.string(),
  Records: z.array(z.object({}).partial().passthrough()).optional(),
})
const NameImportConfig = z.object({
  title: z.string().nullable(),
  first_name: z.string(),
  middle_name: z.string().nullable(),
  last_name: z.string(),
  suffix: z.string().nullable(),
})
const ImportContactsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: NameImportConfig,
  date_of_birth: z.string(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  one_line_addresses: z.array(z.string()),
  structured_addresses: z.array(AddressImportConfig),
  attributions: z.array(z.array(z.string()).min(2).max(2)),
  tags: z.array(z.string()),
  title_flag: z.boolean(),
  update_contact_via_email_address_flag: z.boolean(),
  url: z.string(),
})
const ImportContactsViewResponseData = z.object({
  email_address_conflict: z.boolean(),
  errors: z.number().int(),
  total: z.number().int(),
})
const ListAddressesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  address_ids: z.array(AddressIdField).optional(),
  contact_ids: z.array(ContactIdField).optional(),
  location_ids: z.array(LocationIdField).optional(),
})
const ListAddressesViewResponseData = z.object({ addresses: z.array(Address0) })
const ListCaseAddressesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  location_ids: z.array(LocationIdField).min(1),
})
const ListCaseAddressesViewResponseData = z.object({ addresses: z.array(Address0) })
const ListCaseEmailAddressesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid(),
  identity_ids: z.array(IdentityIdField).min(1),
})
const ListCaseEmailAddressesViewResponseData = z.object({ contact_emails: z.array(ContactEmail0) })
const ListContactAddressesForMapViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  tag_names: z.array(z.string()).optional(),
})
const AddressLocation = z.object({ address_string: z.string(), location: z.record(z.number()) })
const ListContactAddressesForMapViewResponseData = z.object({ addresses: z.array(AddressLocation) })
const ListContactBookTagsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
})
const ListContactBookTagsViewResponseData = z.object({ tags: z.array(Tag) })
const ListContactEntriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_filter: ContactFilter,
  excludes: z.array(ContactEntryIdField).optional(),
  limit: z.number().int().gte(0).optional(),
  offset: z.number().int().gte(0).optional(),
})
const ListContactEntriesViewResponseData = z.object({ contact_entries: z.array(ContactEntry2) })
const ListContactRelationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_entry_id: ContactEntryIdField.uuid(),
})
const RelatedContactEntry2 = z.object({
  emails: z.array(ContactEmail0),
  phones: z.array(PhoneNumber0),
  addresses: z.array(Address0),
  tags: z.array(Tag),
  attributions: z.array(Attribution),
  id: ContactEntryIdField.uuid(),
  owner: z.string(),
  owner_identity_id: IdentityIdField.uuid(),
  handle: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  is_organization: z.boolean(),
  relation: z.string(),
})
const ListContactRelationsViewResponseData = z.object({
  related_contacts: z.array(RelatedContactEntry2),
  reverse_related_contacts: z.array(RelatedContactEntry2),
})
const ListContactRelationshipsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
})
const RelatedContact = z.object({
  contact_record_id: ContactRecordIdField.uuid(),
  handle: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  relationship_label: z.string(),
})
const ListContactRelationshipsViewResponseData = z.object({
  related_contacts: z.array(RelatedContact),
  reverse_related_contacts: z.array(RelatedContact),
})
const ListEmailAddressesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_ids: z.array(ContactIdField).optional(),
  identity_ids: z.array(IdentityIdField).optional(),
  ses_verified: z.boolean().optional().default(false),
})
const ListEmailAddressesViewResponseData = z.object({ contact_emails: z.array(ContactEmail0) })
const EmailFilter = z.object({
  only_my_mailbox: z.boolean(),
  message_id: z.array(z.string()).nullable(),
  internal_message_id: z.array(z.string().uuid()).nullable(),
})
const ListEmailsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  email_filter: EmailFilter,
})
const EmailAddress0 = z.object({ id: EmailAddressIdField.uuid(), address: z.string() })
const EmailParticipant0 = z.object({
  address: EmailAddress0,
  email: EmailIdField.uuid(),
  type: EmailParticipantTypeEnum,
  name: z.string(),
  message_id: MessageIdField.uuid(),
})
const Email1 = z.object({
  participants: z.array(EmailParticipant0),
  case: Case.nullish(),
  attachments: z.array(Attachment),
  body: z.string(),
  text: z.string(),
  time: z.string().datetime({ offset: true }),
  medium: ViasiteEnum,
  subject: z.string(),
  html: z.string().nullable(),
  email_type: z.string().nullable(),
  completed: z.string().datetime({ offset: true }).nullable(),
  item_id: ItemIdField.uuid(),
  id: MessageIdField.uuid(),
  from_communication_block: z.boolean().optional(),
})
const ListEmailsViewResponseData = z.object({ emails: z.array(Email1), has_more: z.boolean() })
const ListOrganizationPhoneNumbersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListOrganizationPhoneNumbersViewResponseData = z.object({
  phone_numbers: z.array(PhoneNumber0),
})
const ListPermittedContactBooksViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  workspace_id: WorkspaceIdField.nullish(),
})
const ContactBook = z.object({ id: ContactBookIdField.uuid(), name: z.string(), code: z.string() })
const ListPermittedContactBooksViewResponseData = z.object({ contact_books: z.array(ContactBook) })
const MaterializeVirtualContactViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  virtual_handle: z.string(),
})
const MaterializeVirtualContactViewResponseData = z.object({
  contact_record_id: ContactRecordIdField.uuid(),
  handle: z.string(),
})
const ObtainEmailAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  email: z.string(),
})
const ObtainEmailAddressViewResponseData = z.object({
  contact_email_id: ContactEmailIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
  email_address_id: EmailAddressIdField.uuid(),
})
const ObtainEmailAddressWithUserViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  email: z.string(),
})
const ObtainEmailAddressWithUserViewResponseData = z.object({
  contact_email_id: ContactEmailIdField.uuid(),
  user_id: EndUserIdField.uuid(),
  email_address_id: EmailAddressIdField.uuid(),
})
const QuickCreateAddressViewRequestData = z.object({ address: z.string() })
const QuickCreateAddressViewResponseData = z.object({ address: Address0 })
const RemoveAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  address_id: AddressIdField.uuid(),
  contact_id: ContactIdField.uuid(),
})
const RemoveAddressFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
})
const RemoveAttributionFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  name: z.string(),
  value: z.string(),
})
const RemoveEmailAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.nullish(),
  contact_email_id: ContactEmailIdField.uuid(),
})
const RemoveEmailFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  email: z.string().email(),
})
const RemoveNoteFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  note: z.string(),
})
const RemovePhoneFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  phone: z.string(),
})
const RemovePhoneNumberViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  phone_number_id: PhoneNumberIdField.uuid(),
  contact_id: ContactIdField.uuid(),
})
const RemoveTagFromContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  tag_id: TagIdField.uuid(),
})
const ReverseSearchAddressesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  location: GeoPoint,
})
const ReverseSearchAddressesViewResponseData = z.object({ addresses: z.array(z.string()) })
const SearchAddressesViewRequestData = z.object({
  address: z.string(),
  location: GeoPoint.optional(),
})
const SearchAddressesViewResponseData = z.object({ addresses: z.array(z.string()) })
const NameEnum = z.enum([
  'name',
  'emails',
  'phones',
  'addresses',
  'tags',
  'interaction_count',
  'last_interaction_at',
])
const SearchContactDocumentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  query: z.string().nullish(),
  tag_names: z.array(z.string()).optional(),
  polygon: z.array(z.record(z.number())).optional(),
  limit: z.number().int().gte(1).lte(100).optional(),
  offset: z.number().int().gte(0).optional(),
  sort_by: NameEnum.nullish(),
  sort_direction: SortDirectionEnum.nullish(),
})
const SearchContactDocumentsViewResponseData = z.object({
  contacts: z.array(ContactView),
  total: z.number().int(),
  has_more: z.boolean(),
})
const SearchContactEntriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string(),
  contact_filter: ContactFilter,
  limit: z.number().int().optional(),
  excludes: z.array(ContactEntryIdField).optional(),
})
const SearchContactEntriesViewResponseData = z.object({
  contact_entries: z.array(ContactEntry2),
  has_more: z.boolean(),
})
const SearchContactsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  query: z.string(),
  book_identity_id: IdentityIdField.uuid().optional(),
  limit: z.number().int().optional(),
})
const SearchContactsViewResponseData = z.object({ contacts: z.array(Identity2) })
const SearchEmailsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  query: z.string().min(2),
  limit: z.number().int().gte(1).lte(100).optional().default(20),
})
const EmailAddressResult = z.object({
  id: EmailAddressIdField.uuid(),
  address: z.string(),
  contact_name: z.string().nullable(),
})
const SearchEmailsViewResponseData = z.object({ results: z.array(EmailAddressResult) })
const SendConstituentEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  email_address: z.string().email(),
  body: z.string(),
})
const SendConstituentEmailViewResponseData = z.object({
  message_id: MessageIdField.uuid(),
  email_id: EmailIdField.nullish(),
})
const SendEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  from_id: EmailAddressIdField.uuid(),
  to_ids: z.array(EmailAddressIdField),
  cc_ids: z.array(EmailAddressIdField),
  bcc_ids: z.array(EmailAddressIdField),
  subject: z.string(),
  body: z.string(),
  attachments: z.array(Attachment),
  case_id: CaseIdField.uuid(),
  block_instance_id: BlockInstanceIdField.nullish(),
})
const SendEmailViewResponseData = z.object({ email_id: EmailIdField.uuid() })
const SendVerificationEmailViewRequestData = z
  .object({
    contact_email_id: ContactEmailIdField.uuid(),
    email: z.string().email(),
    redirect_url: z.string(),
    verify_options: VerifyOptions,
    manual: z.boolean().default(true),
  })
  .partial()
const SendVerificationEmailViewResponseData = z.object({ success: z.boolean() })
const SetEmailSignatureViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  contact_email_id: ContactEmailIdField.uuid(),
  signature: z.string(),
})
const SetPrimaryEmailAddressViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.nullish(),
  contact_email_id: ContactEmailIdField.uuid(),
})
const UpdateContactRecordPersonProfileViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  date_of_birth: z.string().nullish(),
})
const PersonEnum = z.enum(['person', 'business'])
const UpdateContactRecordTypeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  contact_type: PersonEnum,
})
const UpdateNoteOnContactRecordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  contact_book_id: ContactBookIdField.uuid(),
  contact_record_id: ContactRecordIdField.uuid(),
  old_note: z.string(),
  new_note: z.string(),
})
const VerifyEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  contact_email_id: ContactEmailIdField.uuid(),
  verify_options: VerifyOptions,
})
const VerifyLoginViewRequestData = z.object({
  contact_email_id: ContactEmailIdField.uuid(),
  verification_code: z.string(),
})
const VerifyLoginViewResponseData = z.object({
  user_id: EndUserIdField.uuid(),
  send_to_invite_flow: z.boolean(),
  default_organization_handle: z.string().nullish(),
})
const AbortMultipartUploadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  key: z.string(),
  upload_id: z.string(),
  is_secure: z.boolean().optional().default(true),
})
const AbortMultipartUploadViewResponseData = z.object({ aborted: z.boolean() })
const AddEmailDomainViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  domain_name: z
    .string()
    .max(253)
    .regex(/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/),
})
const EmailDomain = z.object({
  id: DomainIdField.uuid(),
  name: z.string(),
  ses_verified: z.boolean(),
})
const AddEmailDomainViewResponseData = z.object({ domain: EmailDomain })
const BreakdownContactTagsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const Tag0 = z.object({ tag: Tag, count: z.number().int() })
const BreakdownContactTagsViewResponseData = z.object({ tags: z.array(Tag0) })
const ChangePasswordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  current_password: z.string(),
  new_password: z.string(),
})
const CheckEmailDomainVerificationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  domain_id: DomainIdField.uuid(),
})
const NotStartedEnum = z.enum(['not_started', 'pending', 'verified', 'failed'])
const CnameEnum = z.literal('CNAME')
const DnsRecord = z.object({ name: z.string(), record_type: CnameEnum, value: z.string() })
const CheckEmailDomainVerificationViewResponseData = z.object({
  status: NotStartedEnum,
  ses_verified: z.boolean(),
  dns_records: z.array(DnsRecord),
})
const MultipartPart = z.object({
  part_number: z.number().int(),
  etag: z.string(),
  size: z.number().int().nullish(),
})
const CompleteMultipartUploadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  key: z.string(),
  upload_id: z.string(),
  is_secure: z.boolean().optional().default(true),
  parts: z.array(MultipartPart).min(1),
})
const CompleteMultipartUploadViewResponseData = z.object({
  key: z.string(),
  url: z.string(),
  is_secure: z.boolean(),
  accessible_url: z.string(),
})
const CreateAttributionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_id: TargetIdField.uuid(),
  attribute_id: AttributeIdField.uuid().optional(),
  name: z.string(),
  value: z.string(),
})
const CreateDepartmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  suffix: z.string(),
  name: z.string(),
})
const CreateDepartmentViewResponseData = z.object({ organization_id: OrganizationIdField.uuid() })
const CreateNoteViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_id: TargetIdField.uuid(),
  note: z.string(),
})
const CreateNoteViewResponseData = z.object({ note_id: NoteIdField.uuid() })
const CreateOrganizationContactBookViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
})
const CreateOrganizationContactBookViewResponseData = z.object({ contact_book: ContactBook })
const AssetTypeEnum = z.enum([
  'Logo',
  'Banner',
  'EmailAttachment',
  'EmailContent',
  'CaseFile',
  'Bill',
  'FormLetterAttachment',
  'LogFile',
  'AIDocument',
  'AIFigure',
  'AIVoiceAudio',
  'DataExport',
  'GISLayer',
  'Misc',
  'CaseFileZip',
  'RedactionDocument',
])
const CreatePresignedUploadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  owner_id: IdentityIdField.uuid(),
  file_name: z.string().max(512),
  file_size: z.number().int().gte(1).lte(104857600000),
  content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  content_type: z.string().nullish(),
  is_secure: z.boolean().optional().default(true),
  asset_type: AssetTypeEnum.optional(),
  case_handle: z.string().nullish(),
  case_id: z.string().nullish(),
  multipart: z.boolean().optional().default(false),
})
const CreatePresignedUploadViewResponseData = z.object({
  key: z.string(),
  url: z.string(),
  is_secure: z.boolean(),
  accessible_url: z.string(),
  expires_in: z.number().int(),
  upload_url: z.string().nullable(),
  upload_headers: z.record(z.string()),
  upload_id: z.string().nullable(),
  part_size: z.number().int().nullable(),
  part_urls: z.array(z.string()).nullable(),
})
const CreateTagViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  color: z.string(),
  description: z.string().optional(),
})
const CreateTagViewResponseData = z.object({ tag: Tag })
const DeleteNoteViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  note_id: NoteIdField.uuid(),
})
const DeleteTagViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  tag_id: TagIdField.uuid(),
})
const EditContactBirthdayViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  contact_id: ContactIdField.uuid(),
  birthday: z.string().nullable(),
  skip_contact_record_write: z.boolean().optional().default(false),
})
const EditContactImageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  image: z.string().nullable(),
})
const EditContactImageViewResponseData = z.object({ image: z.string().nullable() })
const EditContactNameViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid().optional(),
  contact_id: ContactIdField.uuid(),
  name: z.string(),
  title: z.string().optional(),
  first: z.string().optional(),
  middle: z.string().optional(),
  last: z.string().optional(),
  suffix: z.string().optional(),
  skip_contact_record_write: z.boolean().optional().default(false),
})
const AIStatusEnum = z.enum(['purchased', 'live'])
const AISearchStatusEnum = z.enum(['purchased', 'live_main', 'live_backup', 'live_link'])
const EmailStatusEnum = z.enum(['main', 'add_in', 'both'])
const EmailProviderEnum = z.enum(['outlook', 'gmail'])
const CustomerTypeEnum = z.enum(['customer', 'prospect', 'churned'])
const EditCustomerDataViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  chatbot_status: AIStatusEnum.nullish(),
  search_status: AISearchStatusEnum.nullish(),
  sms_status: AIStatusEnum.nullish(),
  voice_status: AIStatusEnum.nullish(),
  voice_status_main: z.boolean().nullish(),
  voice_status_after_hours: z.boolean().nullish(),
  voice_status_overflow: z.boolean().nullish(),
  email_status: EmailStatusEnum.nullish(),
  email_provider: EmailProviderEnum.nullish(),
  crm_seats: z.number().int().nullish(),
  customer_type: CustomerTypeEnum.nullish(),
  customer_tier: PricingTierEnum.optional(),
  salesforce_id: z.string().max(18).nullish(),
})
const PaymentsErpEnum = z.enum([
  'southern',
  'tyler_incode',
  'springbrook',
  'caselle',
  'tyler_munis',
])
const EditPaymentSettingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  auto_payout: z.boolean().optional(),
  payments_erp: PaymentsErpEnum.nullish(),
  fee_burden: PayerEnum.optional(),
})
const EditTagViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  tag_id: TagIdField.uuid(),
  name: z.string().optional(),
  color: z.string().optional(),
  description: z.string().optional(),
})
const GetAccountViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const DigestFrequencyEnum = z.enum(['Never', 'Daily', 'Weekly'])
const GetAccountViewResponseData = z.object({
  title: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  profile: z.string().nullable(),
  notify: z.boolean(),
  digest_frequency: DigestFrequencyEnum.nullable(),
  auto_cc_case_emails: z.boolean(),
})
const GetCustomerPurchasedStatusesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetCustomerPurchasedStatusesViewResponseData = z.object({
  is_purchased_for_search: z.boolean(),
  is_purchased_for_chatbot: z.boolean(),
  is_purchased_for_voice: z.boolean(),
  is_purchased_for_sms: z.boolean(),
})
const GetIdentityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  identity_id: IdentityIdField.uuid().optional(),
  handle: z.string().optional(),
})
const OrganizationTypeEnum = z.enum([
  'OrganizationCity',
  'OrganizationCountry',
  'OrganizationCounty',
  'OrganizationDepartment',
  'OrganizationNGO',
  'OrganizationPoli',
  'OrganizationState',
])
const Identity1 = z.object({
  id: IdentityIdField.uuid(),
  contact_id: ContactIdField.uuid(),
  handle: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  stripe_id: z.string().nullable(),
  email: z.string().nullable(),
  contacts_owner_identity_id: IdentityIdField.uuid(),
  phone: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  birthday: z.string().nullable(),
  auto_payout: z.boolean().nullable(),
  organization_type: OrganizationTypeEnum.nullable(),
  payments_erp: PaymentsErpEnum.nullish(),
  fee_burden: PayerEnum.nullish(),
})
const GetIdentityViewResponseData = z.object({ identity: Identity1 })
const GetOrganizationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const GetOrganizationViewResponseData = z.object({ organization: Organization })
const GetOrganizationContactBookInfoViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetOrganizationContactBookInfoViewResponseData = z.object({
  current_contact_book: ContactBook,
  using_default: z.boolean(),
})
const GetOrganizationIdentityViewRequestData = z
  .object({ identity_id: IdentityIdField.uuid(), handle: z.string() })
  .partial()
const GetOrganizationIdentityViewResponseData = z.object({ identity: Identity1 })
const GetPasswordPolicyViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const GetPasswordPolicyViewResponseData = z.object({
  min_length: z.number().int(),
  require_uppercase: z.boolean(),
  require_lowercase: z.boolean(),
  require_number: z.boolean(),
  require_symbol: z.boolean(),
})
const GetUserViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const SkinEnum = z.enum(['Casual', 'Professional'])
const CityManagerEnum = z.enum(['city_manager', 'staff_manager'])
const GetUserViewResponseData = z.object({
  id: EndUserIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
  logged_in: z.boolean(),
  verified: z.boolean().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  super: z.boolean().nullable(),
  skin: SkinEnum.nullable(),
  beta: z.boolean().nullable(),
  has_password: z.boolean(),
  contact_id: ContactIdField.nullable(),
  session_expires_at: z.string().datetime({ offset: true }).nullable(),
  is_internal: z.boolean(),
  persona: CityManagerEnum.nullable(),
})
const GetUserFromSessionViewResponseData = z.object({ data: EndUserIdField.nullable() })
const GetUserIdsViewRequestData = z.object({ user_ids: z.array(EndUserIdField) })
const UserIds = z.object({
  user_id: EndUserIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
  contact_id: ContactIdField.uuid(),
})
const GetUserIdsViewResponseData = z.object({ users: z.array(UserIds) })
const IdentityToUserViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
})
const IdentityToUserViewResponseData = z.object({ user_id: EndUserIdField.uuid() })
const ListAttributionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_ids: z.array(TargetIdField),
})
const AttributionIdField = z.string()
const Attribution0 = z.object({
  attribute: Attribute,
  id: AttributionIdField.uuid(),
  value: z.string(),
})
const ListAttributionsViewResponseData = z.object({ attributions: z.array(Attribution0) })
const ListContactEntryNotesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  contact_entry_id: ContactEntryIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListContactEntryNotesViewResponseData = z.object({ notes: z.array(Note0) })
const ListDistinctEmailIdentitiesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  u_first: z.boolean().optional(),
})
const EmailIdentity = z.object({
  id: IdentityIdField.uuid(),
  handle: z.string().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  email: z.string().nullable(),
  email_address_id: EmailAddressIdField.nullable(),
  email_id: EmailIdField.nullable(),
  email_ses_verified: z.boolean().nullable(),
  email_signature: z.string().nullable(),
  email_stripe_id: z.string().nullable(),
  stripe_account_id: z.string().nullable(),
  organization_type: z.string().nullable(),
  organization_id: OrganizationIdField.nullable(),
  is_direct_member: z.boolean(),
  contacts_owner_identity_id: IdentityIdField.uuid(),
  workspace_id: WorkspaceIdField.nullable(),
  workspace_name: z.string().nullable(),
  workspace_icon: z.string().nullable(),
  workspace_root_org_id: IdentityIdField.nullable(),
})
const ListDistinctEmailIdentitiesViewResponseData = z.object({ data: z.array(EmailIdentity) })
const ListEmailDomainsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListEmailDomainsViewResponseData = z.object({ domains: z.array(EmailDomain) })
const ListIdentitiesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  identity_ids: z.array(IdentityIdField),
})
const ListIdentitiesViewResponseData = z.object({ identities: z.array(Identity1) })
const ListIdentitiesByIdsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  book_identity_id: IdentityIdField.uuid().optional(),
  identity_ids: z.array(IdentityIdField),
})
const ListIdentitiesByIdsViewResponseData = z.object({ identities: z.array(Identity2) })
const ListOrgIdentitiesTierViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_ids: z.array(IdentityIdField),
})
const AccessTierEnum = z.enum(['ActivateAccess', 'ConnectAccess', 'DeliverAccess', 'UnifyAccess'])
const OrgIdentityTier = z.object({
  identity_id: IdentityIdField.uuid(),
  tier: PricingTierEnum,
  included_tiers: z.array(AccessTierEnum),
})
const ListOrgIdentitiesTierViewResponseData = z.object({ tiers: z.array(OrgIdentityTier) })
const ListOrganizationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_ids: z.array(OrganizationIdField).optional(),
  identity_ids: z.array(IdentityIdField).optional(),
  parent_id: OrganizationIdField.uuid().optional(),
  types: z.array(OrganizationTypeEnum).optional(),
  query: z.string().optional(),
})
const EnrichedOrganization = z.object({
  id: OrganizationIdField.uuid(),
  identity_id: IdentityIdField.uuid(),
  handle: z.string(),
  name: z.string(),
  type: z.string(),
  active: z.boolean(),
  full_hierarchical_name: z.string(),
  bg_color: z.string(),
  banner: z.string().nullable(),
  image: z.string().nullable(),
  logo: z.string().nullable(),
  information: z.string().nullable(),
  governing_unit: OrganizationIdField.nullable(),
  state: z.string().nullish(),
})
const ListOrganizationsViewResponseData = z.object({ organizations: z.array(EnrichedOrganization) })
const ListOrganizationsCustomerDataViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  parent_id: OrganizationIdField.uuid().optional(),
  types: z.array(OrganizationTypeEnum).optional(),
  query: z.string().optional(),
  customer_type: CustomerTypeEnum.nullish(),
  active: z.boolean().optional(),
})
const OrganizationCustomerIdField = z.string()
const OrganizationCustomerData = z.object({
  id: OrganizationCustomerIdField.uuid(),
  site_types: z.array(z.string()),
  internal_assistant_status: AIStatusEnum.nullish(),
  chatbot_status: AIStatusEnum.nullish(),
  search_status: AISearchStatusEnum.nullish(),
  sms_status: AIStatusEnum.nullish(),
  voice_status: AIStatusEnum.nullish(),
  voice_status_main: z.boolean().nullish(),
  voice_status_after_hours: z.boolean().nullish(),
  voice_status_overflow: z.boolean().nullish(),
  email_status: EmailStatusEnum.nullish(),
  email_provider: EmailProviderEnum.nullish(),
  crm_seats: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
  customer_type: CustomerTypeEnum.nullish(),
  organization: OrganizationIdField.uuid(),
})
const OrganizationAndCustomerData = z.object({
  organization: EnrichedOrganization,
  customer_data: OrganizationCustomerData,
  customer_tier: z.string().nullish(),
  salesforce_id: z.string().nullish(),
  workspace_id: WorkspaceIdField.nullish(),
  workspace_name: z.string().nullish(),
})
const ListOrganizationsCustomerDataViewResponseData = z.object({
  organizations: z.array(OrganizationAndCustomerData),
})
const ListQuillDashboardsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
})
const QuillDashboardIdField = z.string()
const QuillDashboard = z.object({
  id: QuillDashboardIdField.uuid(),
  quill_name: z.string(),
  title: z.string(),
  created: z.string().datetime({ offset: true }).optional(),
  organization: OrganizationIdField.uuid(),
  processes: z.array(z.string().uuid()).optional(),
  workflows: z.array(z.string().uuid()).optional(),
})
const ListQuillDashboardsViewResponseData = z.object({ dashboards: z.array(QuillDashboard) })
const ListStaffersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  limit: z.number().int().optional(),
  query: z.string().optional(),
  workspace: z.boolean().optional().default(false),
})
const ListStaffersViewResponseData = z.object({ identities: z.array(Identity0) })
const ListTaggingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  case_id: CaseIdField.uuid().optional(),
  target_ids: z.array(TargetIdField),
  calculated: z.boolean().optional(),
})
const ListTaggingsViewResponseData = z.object({ taggings: z.array(Tagging) })
const ListTagsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  tag_ids: z.array(TagIdField).optional(),
  owner_ids: z.array(IdentityIdField).min(1),
})
const ListTagsViewResponseData = z.object({ tags: z.array(Tag) })
const UserLoginViewRequestData = z.object({
  email: z.string().email(),
  password: z.string(),
  mfa_code: z.string().optional(),
})
const UserLoginViewResponseData = z.object({
  user_id: EndUserIdField.nullable(),
  require_mfa: z.boolean(),
})
const UserLogoutViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const QuickSearchViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  query: z.string(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  workspace_id: WorkspaceIdField.nullish(),
  limit: z.number().int().gte(1).lte(20).optional(),
})
const QuickSearchContact = z.object({
  id: z.string(),
  name: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  image: z.string().nullish(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  addresses: z.array(z.string()),
  path: z.string(),
})
const LicenseSummary = z.object({
  id: LicenseIdField.uuid(),
  number: z.string(),
  status: ActiveEnum,
})
const LicenseTemplateSummary = z.object({
  licenses: z.array(LicenseSummary),
  id: LicenseTemplateIdField.uuid(),
  code: z.string(),
  name: z.string(),
  organization_identity_id: IdentityIdField.uuid(),
  organization_handle: z.string(),
  status: LiveEnum,
  active: z.number().int(),
  total: z.number().int(),
})
const LicenseParty = z.object({ name: z.string(), image: z.string().nullable() })
const QuickSearchViewResponseData = z.object({
  contacts: z.array(QuickSearchContact),
  cases: z.array(Case2),
  processes: z.array(Process0),
  license_templates: z.array(LicenseTemplateSummary),
  license_parties: z.record(z.array(LicenseParty)),
})
const ResetPasswordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  reset_code: z.string(),
  new_password: z.string(),
})
const ResetPasswordRequestViewRequestData = z.object({ email: z.string().email() })
const ResumeMultipartUploadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  key: z.string(),
  upload_id: z.string(),
  is_secure: z.boolean().optional().default(true),
  part_count: z.number().int().gte(1).lte(10000),
})
const ResumeMultipartUploadViewResponseData = z.object({
  expires_in: z.number().int(),
  part_urls: z.array(z.string()),
  uploaded_parts: z.array(MultipartPart),
})
const SendTestDigestEmailViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  digest_frequency: DigestFrequencyEnum.nullable(),
})
const SetAutoCcCaseEmailsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  auto_cc_case_emails: z.boolean(),
})
const SetDigestFrequencyViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  digest_frequency: DigestFrequencyEnum.nullable(),
})
const SetNotifyViewRequestData = z.object({ user_id: EndUserIdField.uuid(), notify: z.boolean() })
const SetPasswordViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  new_password: z.string(),
})
const SetPersonBirthdayRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  day: z.string().nullish(),
})
const SetPersonNameRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  title: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
})
const SetPersonaViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  target_user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  persona: CityManagerEnum.nullable(),
})
const SSOConfirmLinkViewRequestData = z.object({ link_token: z.string(), password: z.string() })
const SSOConfirmLinkViewResponseData = z.object({
  user_id: EndUserIdField.nullable(),
  success: z.boolean(),
})
const GetSSOConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const IdPProviderEnum = z.enum(['microsoft', 'okta', 'other'])
const PasswordEnum = z.enum(['password', 'dual', 'sso_only'])
const SSOConfig = z.object({
  idp_provider: IdPProviderEnum,
  active: z.boolean(),
  transition_mode: PasswordEnum,
  tenant_id: z.string(),
  client_id: z.string(),
  login_url: z.string(),
  idp_entity_id: z.string(),
  has_certificate: z.boolean(),
  has_graph_credential: z.boolean(),
  idp_groups_synced_at: z.string().datetime({ offset: true }).nullable(),
  idp_groups_autosync: z.boolean(),
  session_ttl_hours: z.number().int(),
  break_glass_user_ids: z.array(EndUserIdField),
  default_group_id: GroupIdField.nullable(),
  auto_create_unmapped_groups: z.boolean(),
  claim_mapping: z.record(z.string()),
  sso_domains: z.array(z.string()),
})
const SSOGroupMapping = z.object({
  saml_group_id: z.string(),
  saml_group_name: z.string(),
  group_id: GroupIdField.uuid(),
})
const GetSSOConfigViewResponseData = z.object({
  config: SSOConfig.nullable(),
  group_mappings: z.array(SSOGroupMapping),
  sp_entity_id: z.string(),
  sp_acs_url: z.string(),
  sp_login_url: z.string(),
  sp_logout_url: z.string(),
  verified_domains: z.array(z.string()),
})
const ListIdPGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  refresh: z.boolean().optional().default(false),
})
const ListIdPGroupsViewIdPGroupItem = z.object({ id: z.string(), name: z.string() })
const ListIdPGroupsViewResponseData = z.object({
  idp_groups: z.array(ListIdPGroupsViewIdPGroupItem),
  synced_at: z.string().datetime({ offset: true }).nullable(),
})
const SAMLLookupViewRequestData = z
  .object({ domain: z.string(), email: z.string().email() })
  .partial()
const SAMLLookupViewResponseData = z.object({
  org_handle: z.string(),
  org_name: z.string(),
  sso_available: z.boolean(),
  idp_provider: IdPProviderEnum,
})
const SetupSSOConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  idp_provider: IdPProviderEnum,
  tenant_id: z.string().optional(),
  client_id: z.string().optional(),
  login_url: z.string().url(),
  idp_entity_id: z.string(),
  certificate: z.string(),
  graph_client_secret: z.string().optional(),
})
const SetupSSOConfigViewResponseData = z.object({ config: SSOConfig })
const TestSSOConnectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const TestSSOConnectionViewResponseData = z.object({
  success: z.boolean(),
  issues: z.array(z.string()),
})
const UpdateSSOGroupMappingsViewMappingItem = z.object({
  saml_group_id: z.string().max(500),
  saml_group_name: z.string().max(255).optional().default(''),
  group_id: GroupIdField.uuid(),
})
const UpdateSSOGroupMappingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mappings: z.array(UpdateSSOGroupMappingsViewMappingItem),
})
const UpdateSSOGroupMappingsViewResponseData = z.object({
  group_mappings: z.array(SSOGroupMapping),
})
const UpdateSSOSettingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  break_glass_user_ids: z.array(EndUserIdField).optional(),
  session_ttl_hours: z.number().int().gte(1).lte(8760).optional(),
  default_group_id: GroupIdField.nullish(),
  active: z.boolean().optional(),
  auto_create_unmapped_groups: z.boolean().optional(),
  idp_groups_autosync: z.boolean().optional(),
  claim_mapping: z.record(z.string()).optional(),
  transition_mode: PasswordEnum.optional(),
  graph_client_secret: z.string().optional(),
  sso_domains: z.array(z.string()).optional(),
})
const UpdateSSOSettingsViewResponseData = z.object({ config: SSOConfig })
const StartEmailDomainVerificationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  domain_id: DomainIdField.uuid(),
})
const StartEmailDomainVerificationViewResponseData = z.object({
  status: NotStartedEnum,
  dns_records: z.array(DnsRecord),
})
const SuperOrgAccessAddViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid(),
  include_sub_organizations: z.boolean().optional().default(false),
})
const SuperOrgAccessGetCurrentViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const SuperOrgAccessGetCurrentViewResponseData = z.object({
  organization_ids: z.array(OrganizationIdField),
})
const SuperOrgAccessRemoveViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_id: OrganizationIdField.uuid().optional(),
  remove_all: z.boolean().optional().default(false),
  include_sub_organizations: z.boolean().optional().default(false),
})
const SuperOrgAccessSearchViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  query: z.string().optional(),
})
const OrganizationHierarchy = z.object({
  organization: EnrichedOrganization,
  sub_organizations: z.array(EnrichedOrganization),
})
const SuperOrgAccessSearchViewResponseData = z.object({
  governing_units: z.array(OrganizationHierarchy),
  standalone_organizations: z.array(EnrichedOrganization),
})
const TagViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  target_id: TargetIdField.uuid(),
  tag_id: TagIdField.uuid(),
})
const UnattributeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  attribution_id: AttributionIdField.uuid(),
})
const UntagViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  target_id: TargetIdField.uuid(),
  tag_id: TagIdField.uuid(),
})
const UpdateNoteViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  note_id: NoteIdField.uuid(),
  note: z.string(),
})
const UploadAssetViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  owner_id: IdentityIdField.uuid(),
  file: z.string().url(),
  is_secure: z.boolean().optional().default(true),
  asset_type: AssetTypeEnum.optional(),
  case_handle: z.string().nullish(),
  case_id: z.string().nullish(),
})
const UploadAssetViewResponseData = z.object({
  key: z.string(),
  url: z.string(),
  is_secure: z.boolean(),
  accessible_url: z.string(),
})
const LocalDBCheckViewResponseData = z.object({ using_local_db: z.boolean() })
const AddTwilioAccountConfigViewRequestData = z.object({
  name: z.string().max(255),
  account_sid: z.string().regex(/^AC[0-9a-zA-Z]{32}$/),
  messaging_service_sid: z
    .string()
    .regex(/^MG[0-9a-zA-Z]{32}$/)
    .nullish(),
  phonic_trunk_sid: z
    .string()
    .regex(/^TK[0-9a-zA-Z]{32}$/)
    .nullish(),
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const AddTwilioAccountConfigViewResponseData = z.object({ id: TwilioAccountConfigIdField.uuid() })
const AppendOpenAPIDraftMessageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid().optional(),
  content: z.string().max(20000),
  display_content: z.string().optional(),
  attachments: z
    .array(AgenticImplementationAttachmentSerializerAgenticImplementationAttachment)
    .optional(),
})
const OpenAPIDraftSerializerOpenAPIDraft = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  log_id: z.string(),
  messages: z.array(AgenticImplementationMessageSerializerAgenticImplementationMessage),
  status: z.string(),
  status_message: z.string().nullish(),
  openapi_document: z.unknown().nullable(),
  integration_preview: z.unknown().nullable(),
  devin_session_url: z.string().nullish(),
})
const CreateAPIAuthenticationTokenViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  token_name: z.string().max(255),
  token_value: z.string().max(1000),
})
const APIAuthenticationTokenIdField = z.string()
const CreateAPIAuthenticationTokenViewResponseData = z.object({
  api_authentication_token_id: APIAuthenticationTokenIdField.uuid(),
})
const CreateAPIConnectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().max(255),
  base_url: z.string().max(500).url(),
  connection_type: z.string().max(50).optional().default('rest'),
  auth_type: z.string().max(50).optional().default('none'),
  auth_config: z.unknown().optional(),
  default_headers: z.unknown().optional(),
  config: z.unknown().optional(),
  use_ip_proxy: z.boolean().optional().default(false),
})
const CreateAPIConnectionViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
})
const HTTPMethodEnum = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
const DataFormatEnum = z.enum(['json', 'xml', 'form_urlencoded'])
const CreateAPIEndpointViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().max(255),
  endpoint_url: z.string().max(2048),
  http_method: HTTPMethodEnum.optional(),
  description: z.string().optional().default(''),
  active: z.boolean().optional().default(true),
  tool_name: z.string().max(255).optional().default(''),
  tool_description: z.string().optional().default(''),
  tool_enabled: z.boolean().optional().default(false),
  custom_headers: z.unknown().optional(),
  request_body_schema: z.unknown().optional(),
  response_mapping: z.unknown().optional(),
  external_id_path: z.string().max(255).nullish(),
  validation_rules: z.unknown().optional(),
  timeout: z.number().int().optional().default(30),
  rate_limit: z.number().int().nullish(),
  request_format: DataFormatEnum.optional(),
  response_format: DataFormatEnum.optional(),
})
const CreateAPIEndpointViewResponseData = z.object({ api_endpoint_id: APIEndpointIdField.uuid() })
const IntegrationSurfaceEnum = z.enum(['SEARCH', 'CHAT', 'WORKFLOW', 'ASSISTANT', 'VOICE'])
const CreateAPIEndpointConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_endpoint_id: APIEndpointIdField.uuid(),
  integration_surface: IntegrationSurfaceEnum,
})
const APIEndpointConfigIdField = z.string()
const CreateAPIEndpointConfigViewResponseData = z.object({
  api_endpoint_config_id: APIEndpointConfigIdField.uuid(),
})
const QueryEnum = z.enum(['query', 'path', 'header', 'body'])
const StringEnum = z.enum(['string', 'integer', 'boolean', 'array', 'object'])
const CreateAPIEndpointParameterViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  endpoint_id: APIEndpointIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().max(255),
  location: QueryEnum.optional(),
  parameter_type: StringEnum.optional(),
  description: z.string().optional().default(''),
  required: z.boolean().optional().default(false),
  default_value: z.string().nullish(),
  validation_rules: z.unknown().optional(),
  json_schema: z.unknown().optional(),
  tool_name: z.string().max(255).optional().default(''),
  tool_description: z.string().optional().default(''),
})
const APIEndpointParameterIdField = z.string()
const CreateAPIEndpointParameterViewResponseData = z.object({
  api_endpoint_parameter_id: APIEndpointParameterIdField.uuid(),
})
const DeleteAPIConnectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
})
const DeleteAPIConnectionViewResponseData = z.object({ success: z.boolean() })
const DeleteAPIEndpointConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_endpoint_config_id: APIEndpointConfigIdField.uuid(),
})
const DeleteTwilioAccountConfigViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  twilio_account_config_id: TwilioAccountConfigIdField.uuid(),
})
const DiscardOpenAPIDraftViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
})
const DiscardOpenAPIDraftViewResponseData = z.object({ success: z.boolean() })
const EditTwilioAccountConfigViewRequestData = z.object({
  name: z.string().max(255),
  account_sid: z.string().regex(/^AC[0-9a-zA-Z]{32}$/),
  messaging_service_sid: z
    .string()
    .regex(/^MG[0-9a-zA-Z]{32}$/)
    .nullish(),
  phonic_trunk_sid: z
    .string()
    .regex(/^TK[0-9a-zA-Z]{32}$/)
    .nullish(),
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  twilio_account_config_id: TwilioAccountConfigIdField.uuid(),
})
const BrowserActionInputVariableTypeEnum = z.enum(['string', 'integer'])
const BrowserActionVariable = z.object({
  name: z.string().max(100),
  type: BrowserActionInputVariableTypeEnum.optional(),
  description: z.string().optional().default(''),
  pattern: z.string().max(200).optional().default(''),
})
const BrowserRegionEnum = z.enum(['us_east', 'us_west', 'eu_central', 'asia_southeast'])
const ExecuteBrowserTestActionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  session_id: z.string(),
  start_url: z.string().max(500),
  action_name: z.string().max(100),
  agent_instructions: z.string(),
  output_schema: z.unknown().nullish(),
  agent_max_steps: z.number().int().gte(1).lte(50).optional().default(10),
  input_variables: z.array(BrowserActionVariable).optional(),
  input_values: z.unknown().optional(),
  region: BrowserRegionEnum.nullish(),
})
const ExecuteBrowserTestActionViewResponseData = z.object({
  success: z.boolean(),
  result: z.unknown().nullable(),
  error: z.string().nullable(),
  execution_time: z.number().int(),
})
const CayentaUtilityBillingEndpointEnum = z.enum([
  'find_locations',
  'check_balance_by_account',
  'check_balance_by_location',
  'check_bill_due_date_by_account',
  'check_bill_due_date_by_location',
  'check_pay_arrangements_by_account',
  'check_pay_arrangements_by_location',
  'check_budget_bill_by_account',
  'check_budget_bill_by_account_and_location',
  'check_budget_bill_by_location',
  'find_accounts_from_address',
  'check_billing_address_by_account',
  'check_billing_address_by_location',
  'check_person_details_by_account',
  'check_person_details_by_location',
  'check_transactions_by_account',
  'check_transactions_by_location',
])
const GenerateCayentaUtilityBillingIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  host: z.string().max(500),
  connection_name: z.string().max(255).optional().default('Cayenta Utility Billing'),
  excluded_endpoints: z.array(CayentaUtilityBillingEndpointEnum).optional().default([]),
})
const GenerateCayentaUtilityBillingIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const GenerateCentralSquareIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  base_url: z.string().max(500),
  connection_name: z.string().max(255).optional().default('Central Square'),
  trakit_url: z.string().max(500).optional().default(''),
  default_group: z.string().max(100).optional().default('PERMIT'),
  default_record_type: z.string().max(100).optional().default('NEWPERMIT'),
  excluded_endpoints: z.array(z.string()).optional().default([]),
})
const GenerateCentralSquareIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const GenerateCityworksIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  base_url: z.string().max(500),
  connection_name: z.string().max(255).optional().default('Cityworks'),
  excluded_endpoints: z.array(z.string()).optional(),
})
const GenerateCityworksIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const AuthTypeEnum = z.enum([
  'no_auth',
  'api_key',
  'bearer_token',
  'basic',
  'oauth2',
  'custom_header',
  'query_param',
  'cookie_session',
  'soap_login',
  'ws_security',
  'xml_credentials',
])
const ApiEnum = z.enum(['api', 'browser'])
const BrowserActionSchema = z.object({
  action_name: z.string().max(100),
  description: z.string().optional().default(''),
  agent_instructions: z.string(),
  output_schema: z.unknown().nullish(),
  cache_ttl: z.number().int().gte(0).optional().default(3600),
  region: BrowserRegionEnum.nullish(),
  input_variables: z.array(BrowserActionVariable).optional(),
})
const ParameterLocationEnum = z.enum(['path', 'query', 'header', 'body'])
const ParameterTypeEnum = z.enum(['string', 'integer', 'boolean', 'number', 'array', 'object'])
const ParameterSchema = z.object({
  name: z.string().max(200),
  description: z.string().optional().default(''),
  location: ParameterLocationEnum.optional(),
  parameter_type: ParameterTypeEnum.optional(),
  required: z.boolean().optional().default(false),
  default_value: z.unknown().nullish(),
  validation_rules: z.unknown().optional(),
  json_schema: z.unknown().optional(),
  required_params: z.array(z.string()).optional(),
  tool_name: z.string().max(100).optional().default(''),
  tool_description: z.string().optional().default(''),
})
const EndpointSchema = z.object({
  name: z.string().max(200),
  description: z.string().optional().default(''),
  http_method: HTTPMethodEnum.optional(),
  endpoint_url: z.string().max(2048),
  active: z.boolean().optional().default(true),
  tool_name: z.string().max(100).optional().default(''),
  tool_description: z.string().optional().default(''),
  tool_enabled: z.boolean().optional().default(false),
  custom_headers: z.unknown().optional(),
  request_body_schema: z.unknown().optional(),
  response_mapping: z.unknown().optional(),
  validation_rules: z.unknown().optional(),
  timeout: z.number().int().gte(1).lte(300).optional().default(30),
  rate_limit: z.number().int().nullish(),
  request_format: DataFormatEnum.optional(),
  response_format: DataFormatEnum.optional(),
  parameters: z.array(ParameterSchema).optional(),
})
const GenerateCustomIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().max(255),
  description: z.string().optional().default(''),
  base_url: z.string().max(500),
  auth_type: AuthTypeEnum.optional(),
  auth_config: z.unknown().optional(),
  default_headers: z.unknown().optional(),
  use_ip_proxy: z.boolean().optional().default(false),
  internal_only: z.boolean().optional().default(false),
  integration_type: ApiEnum.optional(),
  browser_actions: z.array(BrowserActionSchema).optional(),
  browser_region: BrowserRegionEnum.nullish(),
  endpoints: z.array(EndpointSchema).optional(),
  draft_api_connection_id: APIConnectionIdField.uuid().optional(),
})
const GenerateCustomIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const GenerateEnergovEplIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  host: z.string().max(500),
  civic_access_host: z.string().max(500).optional().default(''),
  civic_access_path: z.string().max(500).optional().default(''),
  connection_name: z.string().max(255).optional().default('EnerGov EPL'),
  excluded_endpoints: z.array(z.string()).optional(),
})
const GenerateEnergovEplIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const FastTrackEndpointEnum = z.enum([
  'get_permit_details',
  'get_permit_info',
  'get_permit_reviews',
  'get_permit_holds',
  'get_permit_inspections',
  'search_permits',
  'get_staff_info',
])
const GenerateFastTrackIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_key: z.string().max(500),
  base_url: z.string().max(500),
  connection_name: z.string().max(255).optional().default('FastTrack Permits'),
  sensitive_field_mapping: z
    .record(z.string())
    .optional()
    .default({ applicant_name: 'applicantName', property_address: 'propertyAddress' }),
  excluded_endpoints: z.array(FastTrackEndpointEnum).optional().default([]),
})
const GenerateFastTrackIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const GenerateMunisArwsIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  host: z.string().max(500),
  connection_name: z.string().max(255).optional().default('Munis ARWS'),
  default_category: z.number().int().optional().default(60),
  excluded_endpoints: z.array(z.string()).optional(),
})
const GenerateMunisArwsIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const CreateWorkOrderEnum = z.enum([
  'create_work_order',
  'update_work_order',
  'get_work_order',
  'list_work_orders',
])
const GenerateOpenGovIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  username: z.string().max(500),
  password: z.string().max(500),
  base_url: z.string().max(500),
  connection_name: z.string().max(255).optional().default('OpenGov EAM'),
  excluded_endpoints: z.array(CreateWorkOrderEnum).optional().default([]),
})
const GenerateOpenGovIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const Request311EndpointEnum = z.enum([
  'get_prompt',
  'create_case',
  'get_types',
  'get_knowledge_articles',
  'get_case',
])
const GenerateRequest311IntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_key: z.string().max(500),
  host: z.string().max(500),
  connection_name: z.string().max(255).optional().default('Catalis Request311'),
  required_submitter_fields: z.array(z.string()).optional().default(['firstName', 'email']),
  excluded_endpoints: z.array(Request311EndpointEnum).optional().default([]),
  fallback_type_id: z.number().int().nullish(),
})
const GenerateRequest311IntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_chatbot: z.boolean(),
  is_live_for_voice: z.boolean(),
})
const SharedEnum = z.enum(['shared', 'delegated'])
const GenerateSharePointIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  tenant_id: z.string().max(255),
  client_id: z.string().max(255),
  client_secret: z.string().max(500),
  connection_name: z.string().max(255).optional().default('SharePoint'),
  auth_mode: SharedEnum.optional(),
})
const GenerateSharePointIntegrationViewResponseData = z.object({
  api_connection_id: APIConnectionIdField.uuid(),
  endpoints_created: z.number().int(),
  is_live_for_voice: z.boolean(),
})
const GetOpenAPIDraftViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetOpenAPIDraftViewResponseData = z.object({
  draft: OpenAPIDraftSerializerOpenAPIDraft.nullable(),
})
const ListAPIConnectionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  include_drafts: z.boolean().optional().default(false),
})
const APIEndpointParameter = z.object({
  id: APIEndpointParameterIdField.uuid(),
  name: z.string().max(200),
  description: z.string().nullish(),
  location: ParameterLocationEnum,
  parameter_type: ParameterTypeEnum,
  required: z.boolean().optional(),
  default_value: z.unknown().nullish(),
  validation_rules: z.unknown().nullish(),
  json_schema: z.unknown().nullish(),
  required_params: z.unknown().nullish(),
  tool_name: z.string().max(100).nullish(),
  tool_description: z.string().nullish(),
})
const EndpointStatusEnum = z.enum(['DRAFT', 'LIVE'])
const APIEndpointConfig = z.object({
  id: APIEndpointConfigIdField.uuid(),
  active: z.boolean().optional(),
  integration_surface: IntegrationSurfaceEnum,
  status: EndpointStatusEnum.optional(),
})
const APIEndpoint = z.object({
  id: APIEndpointIdField.uuid(),
  name: z.string().max(200),
  description: z.string().nullish(),
  active: z.boolean().optional(),
  tool_name: z.string().max(100).nullish(),
  tool_description: z.string().nullish(),
  tool_enabled: z.boolean().optional(),
  http_method: HTTPMethodEnum.optional(),
  endpoint_url: z.string().max(2048),
  parameters: z.array(APIEndpointParameter),
  configs: z.array(APIEndpointConfig),
  request_format: DataFormatEnum.optional(),
  response_format: DataFormatEnum.optional(),
  custom_headers: z.unknown().nullish(),
  request_body_schema: z.unknown().nullish(),
  response_mapping: z.unknown().nullish(),
  external_id_path: z.string().max(255).nullish(),
  validation_rules: z.unknown().nullish(),
  timeout: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  rate_limit: z.number().int().gte(-2147483648).lte(2147483647).nullish(),
})
const BrowserActionInputVariable = z.object({
  name: z.string(),
  type: BrowserActionInputVariableTypeEnum,
  description: z.string().optional(),
  pattern: z.string().optional(),
})
const BrowserAction = z.object({
  id: z.string().uuid(),
  action_name: z.string().max(100),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  agent_instructions: z.string().nullish(),
  agent_max_steps: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  output_schema: z.unknown().nullish(),
  cache_ttl: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  region: BrowserRegionEnum.nullish(),
  input_variables: z.array(BrowserActionInputVariable).optional(),
})
const APIConnection = z.object({
  id: APIConnectionIdField.uuid(),
  name: z.string().max(200),
  description: z.string().nullish(),
  connection_type: ConnectionTypeEnum.optional(),
  base_url: z.string().max(500).url(),
  auth_type: AuthTypeEnum.optional(),
  config: z.unknown().nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  endpoints: z.array(APIEndpoint),
  browser_actions: z.array(BrowserAction),
  default_headers: z.unknown().nullish(),
  use_ip_proxy: z.boolean().optional(),
  internal_only: z.boolean().optional(),
  integration_type: ApiEnum.optional(),
  status: EndpointStatusEnum,
})
const ListAPIConnectionsViewResponseData = z.object({ connections: z.array(APIConnection) })
const ListAPIEndpointParametersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  endpoint_id: APIEndpointIdField.uuid(),
})
const ListAPIEndpointParametersViewResponseData = z.object({ parameters: z.array(z.unknown()) })
const ListAPIEndpointsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
})
const ListAPIEndpointsViewResponseData = z.object({ endpoints: z.array(APIEndpoint) })
const ListIntegrationEndpointDefinitionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connection_type: ConnectionTypeEnum,
})
const ParameterDefinition = z.object({
  name: z.string(),
  description: z.string(),
  required: z.boolean(),
  default_value: z.unknown().nullable(),
})
const EndpointDefinition = z.object({
  tool_name: z.string(),
  name: z.string(),
  description: z.string(),
  parameters: z.array(ParameterDefinition),
})
const ListIntegrationEndpointDefinitionsViewResponseData = z.object({
  endpoints: z.array(EndpointDefinition),
})
const ListTwilioAccountConfigsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const TwilioAccountConfigPhoneLine = z.object({
  id: AIPhoneLineIdField.uuid(),
  number: z.string(),
  name: z.string().nullish(),
})
const TwilioAccountConfig = z.object({
  id: TwilioAccountConfigIdField.uuid(),
  name: z.string().max(255),
  account_sid: z.string().max(34),
  messaging_service_sid: z.string().max(34).nullish(),
  phonic_trunk_sid: z.string().max(34).nullish(),
  phone_lines: z.array(TwilioAccountConfigPhoneLine).optional().default([]),
})
const ListTwilioAccountConfigsViewResponseData = z.object({
  twilio_account_configs: z.array(TwilioAccountConfig),
})
const DisconnectMicrosoftViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connection_id: APIConnectionIdField.uuid(),
})
const DisconnectMicrosoftViewResponseData = z.object({ disconnected: z.boolean() })
const StartMicrosoftConnectViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connection_id: APIConnectionIdField.uuid(),
})
const StartMicrosoftConnectViewResponseData = z.object({ authorize_url: z.string() })
const MicrosoftConnectionStatusViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  connection_id: APIConnectionIdField.uuid(),
})
const MicrosoftConnectionStatusViewResponseData = z.object({ connected: z.boolean() })
const StartBrowserTestSessionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  start_url: z.string().max(500),
  region: BrowserRegionEnum.nullish(),
})
const StartBrowserTestSessionViewResponseData = z.object({
  session_id: z.string(),
  live_view_url: z.string(),
})
const StopBrowserTestSessionViewRequestData = z.object({
  user_id: EndUserIdField.nullish(),
  organization_identity_id: IdentityIdField.nullish(),
  session_id: z.string(),
  region: BrowserRegionEnum.nullish(),
})
const StopBrowserTestSessionViewResponseData = z.object({ success: z.boolean() })
const TestCustomEndpointViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.nullish(),
  base_url: z.string().max(500).optional().default(''),
  auth_type: AuthTypeEnum.optional(),
  auth_config: z.unknown().optional(),
  default_headers: z.unknown().optional(),
  use_proxy: z.boolean().optional().default(false),
  endpoint_url: z.string().max(2048),
  http_method: HTTPMethodEnum.optional(),
  endpoint_headers: z.unknown().optional(),
  parameters: z.unknown().optional(),
  body: z.unknown().nullish(),
  timeout: z.number().int().gte(1).lte(300).optional().default(30),
  request_format: DataFormatEnum.optional(),
  response_format: DataFormatEnum.optional(),
})
const TestCustomEndpointViewResponseData = z.object({
  success: z.boolean(),
  status_code: z.number().int().nullable(),
  status_text: z.string(),
  body: z.string(),
  time: z.number().int(),
  error: z.string().nullable(),
})
const Request311Enum = z.enum([
  'request311',
  'fasttrack',
  'munis_arws',
  'cityworks',
  'sharepoint',
  'cayenta_utility_billing',
  'opengov',
  'central_square',
  'energov_epl',
])
const TestHostedIntegrationCredentialsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  integration_type: Request311Enum,
  credentials: z.unknown(),
})
const TestHostedIntegrationCredentialsViewResponseData = z.object({
  success: z.boolean(),
  message: z.string(),
  error_details: z.string().nullish(),
})
const TestIntegrationEndpointsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  integration_type: Request311Enum,
  credentials: z.unknown(),
})
const TestIntegrationEndpointsViewResponseData = z.object({ results: z.array(z.unknown()) })
const UpdateAPIConnectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  config: z.unknown().optional(),
})
const UpdateAPIConnectionViewResponseData = z.object({ success: z.boolean() })
const EndpointConfigUpdate = z.object({
  endpoint_id: APIEndpointIdField.uuid(),
  integration_surface: IntegrationSurfaceEnum,
  active: z.boolean(),
  status: EndpointStatusEnum.optional(),
})
const UpdateEndpointConfigsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  endpoint_config_updates: z.array(EndpointConfigUpdate),
})
const UpdateEndpointConfigsViewResponseData = z.object({
  success: z.boolean(),
  updated_count: z.number().int(),
})
const UpdateAPIEndpointsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  active: z.boolean().optional(),
})
const UpdateAPIEndpointsViewResponseData = z.object({
  success: z.boolean(),
  updated_count: z.number().int(),
})
const UpdateParameterSchema = z.object({
  name: z.string().max(200),
  description: z.string().optional(),
  location: ParameterLocationEnum.optional(),
  parameter_type: ParameterTypeEnum.optional(),
  required: z.boolean().optional(),
  default_value: z.unknown().nullish(),
  validation_rules: z.unknown().optional(),
  json_schema: z.unknown().optional(),
  required_params: z.array(z.string()).optional(),
  tool_name: z.string().max(100).optional(),
  tool_description: z.string().optional(),
  id: APIEndpointParameterIdField.nullish(),
})
const UpdateEndpointSchema = z.object({
  name: z.string().max(200),
  description: z.string().optional(),
  http_method: HTTPMethodEnum.optional(),
  endpoint_url: z.string().max(2048),
  active: z.boolean().optional(),
  tool_name: z.string().max(100).optional(),
  tool_description: z.string().optional(),
  tool_enabled: z.boolean().optional(),
  custom_headers: z.unknown().optional(),
  request_body_schema: z.unknown().optional(),
  response_mapping: z.unknown().optional(),
  validation_rules: z.unknown().optional(),
  timeout: z.number().int().gte(1).lte(300).optional(),
  rate_limit: z.number().int().nullish(),
  request_format: DataFormatEnum.optional(),
  response_format: DataFormatEnum.optional(),
  parameters: z.array(UpdateParameterSchema).optional(),
  id: APIEndpointIdField.nullish(),
})
const UpdateCustomIntegrationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  name: z.string().max(255).optional(),
  description: z.string().optional(),
  base_url: z.string().max(500).optional(),
  default_headers: z.unknown().optional(),
  use_ip_proxy: z.boolean().optional(),
  internal_only: z.boolean().optional(),
  auth_config: z.unknown().optional(),
  auth_type: AuthTypeEnum.optional(),
  publish_draft: z.boolean().optional().default(false),
  endpoints: z.array(UpdateEndpointSchema).optional(),
  browser_actions: z.array(BrowserActionSchema).optional(),
})
const UpdateCustomIntegrationViewResponseData = z.object({
  success: z.boolean(),
  endpoints_created: z.number().int(),
  endpoints_updated: z.number().int(),
  endpoints_deleted: z.number().int(),
})
const EndpointUpdate = z.object({ endpoint_id: APIEndpointIdField.uuid(), active: z.boolean() })
const UpdateEndpointActiveViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  api_connection_id: APIConnectionIdField.uuid(),
  endpoint_updates: z.array(EndpointUpdate),
})
const UpdateEndpointActiveViewResponseData = z.object({
  success: z.boolean(),
  updated_count: z.number().int(),
})
const UploadOpenAPIDraftAttachmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  document: z.string().url(),
})
const UploadOpenAPIDraftAttachmentViewResponseData = z.object({
  attachment_id: z.string(),
  filename: z.string(),
  content_type: z.string().nullable(),
  s3_url: z.string(),
  download_url: z.string(),
})
const GetDomainSitemapViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  crawl_task_id: CrawlTaskIdField.uuid(),
})
const DomainSitemapNode = z.object({
  item_id: z.string().uuid().nullish(),
  path: z.string(),
  title: z.string().optional(),
  parent_path: z.string().nullish(),
  url: z.string().nullish(),
  page_type: z.string().nullish(),
  external: z.boolean().optional(),
  link_count: z.number().int().optional(),
})
const GetDomainSitemapViewResponseData = z.object({
  nodes: z.array(DomainSitemapNode),
  truncated: z.boolean(),
})
const GetKnowledgeBaseItemUsageViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  source_ids: z.array(SourceIdField).max(100),
  usage_window_days: z.number().int().gte(1).lte(30).optional().default(7),
})
const KnowledgeBaseItemUsage = z.object({
  source_id: SourceIdField.uuid(),
  count: z.number().int(),
  daily_counts: z.array(z.number().int()).optional(),
  top_percent: z.number().int().nullish(),
  last_used_at: z.string().datetime({ offset: true }).nullish(),
})
const GetKnowledgeBaseItemUsageViewResponseData = z.object({
  usages: z.array(KnowledgeBaseItemUsage),
})
const KnowledgeBaseItemTypeEnum = z.enum(['domain', 'link', 'document', 'faq'])
const KnowledgeBaseItemSortFieldEnum = z.enum(['title', 'updated', 'usage'])
const ListKnowledgeBaseItemsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter_item_types: z.array(KnowledgeBaseItemTypeEnum).min(1).optional(),
  filter_query: z.string().optional(),
  filter_title: z.string().optional(),
  filter_url: z.string().optional(),
  filter_access_type: SourceAccessTypeEnum.optional(),
  filter_page_types: z.array(z.string()).min(1).optional(),
  filter_archived: z.boolean().optional().default(false),
  filter_folder_id: FolderIdField.uuid().optional(),
  filter_unfiled_pages: z.boolean().optional().default(false),
  filter_crawl_task_id: CrawlTaskIdField.uuid().optional(),
  sort_field: KnowledgeBaseItemSortFieldEnum.optional(),
  sort_direction: SortDirectionEnum.nullish(),
  usage_window_days: z.number().int().gte(1).lte(30).optional().default(7),
  limit: z.number().int().gte(1).lte(100).optional().default(25),
  offset: z.number().int().gte(0).optional().default(0),
})
const KnowledgeBaseItemSummary = z.object({
  item_id: z.string().uuid(),
  item_type: KnowledgeBaseItemTypeEnum,
  source_id: SourceIdField.nullish(),
  folder_id: FolderIdField.nullish(),
  title: z.string(),
  archived: z.boolean(),
  description: z.string().nullish(),
  url: z.string().nullish(),
  gui_url: z.string().nullish(),
  access_type: SourceAccessTypeEnum.nullish(),
  updated_at: z.string().datetime({ offset: true }).nullish(),
  folder_name: z.string().nullish(),
  page_type: z.string().nullish(),
  source_count: z.number().int().nullish(),
  sitemap_enabled: z.boolean().nullish(),
})
const ListKnowledgeBaseItemsViewResponseData = z.object({
  items: z.array(KnowledgeBaseItemSummary),
  total_count: z.number().int(),
})
const KnowledgeFindingTypeEnum = z.enum(['STALE', 'GAP', 'CONFLICT'])
const KnowledgeFindingStatusEnum = z.enum(['OPEN', 'DISMISSED', 'RESOLVED'])
const ListKnowledgeFindingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  filter_finding_type: KnowledgeFindingTypeEnum.optional(),
  filter_status: KnowledgeFindingStatusEnum.optional(),
  limit: z.number().int().gte(1).lte(500).nullish(),
  offset: z.number().int().gte(0).nullish(),
})
const KnowledgeFindingIdField = z.string()
const ExtractedEntityConfigIdField = z.string()
const MatchedEntityAttribute = z.object({
  value: z.unknown().nullish(),
  name: z.string(),
  type: z.string(),
  required: z.boolean(),
  description: z.string().nullish(),
})
const MatchedEntitySummary = z.object({
  config_id: ExtractedEntityConfigIdField.uuid(),
  attributes: z.record(MatchedEntityAttribute),
  name: z.string(),
  type: z.string(),
  config_version: z.number().int(),
})
const KnowledgeFindingSummary = z.object({
  finding_id: KnowledgeFindingIdField.uuid(),
  page_source_id: PageSourceIdField.nullish(),
  matched_entity: MatchedEntitySummary.nullish(),
  evidence: z.unknown().optional(),
  organization_id: OrganizationIdField.uuid(),
  finding_type: KnowledgeFindingTypeEnum,
  status: KnowledgeFindingStatusEnum,
  impact_score: z.number(),
  occurrences: z.number().int(),
  first_detected_at: z.string().datetime({ offset: true }),
  last_seen_at: z.string().datetime({ offset: true }).nullable(),
  page_title: z.string().nullish(),
  page_url: z.string().nullish(),
  bucket_label: z.string().nullish(),
  dismissed_at: z.string().datetime({ offset: true }).nullish(),
  addressed_at: z.string().datetime({ offset: true }).nullish(),
  resolved_at: z.string().datetime({ offset: true }).nullish(),
})
const ListKnowledgeFindingsViewResponseData = z.object({
  findings: z.array(KnowledgeFindingSummary),
})
const BulkEditLicenseTemplatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  license_template_ids: z.array(LicenseTemplateIdField).min(1),
  status: LiveEnum,
})
const BulkEditLicenseTemplatesViewResponseData = z.object({
  license_templates: z.array(LicenseTemplate),
})
const BulkEditLicensesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_ids: z.array(LicenseIdField).min(1),
  status: ActiveEnum,
  reason: z.string().optional(),
})
const BulkEditLicensesViewResponseData = z.object({ licenses: z.array(License) })
const LicenseTemplateFieldIdField = z.string()
const LicenseFieldInput = z.object({
  license_template_field_id: LicenseTemplateFieldIdField.uuid(),
  value_id: ValueIdField.nullish(),
})
const CreateLicenseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  case_id: CaseIdField.nullish(),
  status: ActiveEnum.optional(),
  issued_on: z.string().optional(),
  expires_at: z.string().nullish(),
  license_fields: z.array(LicenseFieldInput).optional(),
})
const LicenseFieldIdField = z.string()
const LicenseField = z.object({
  id: LicenseFieldIdField.uuid(),
  license_template_field: LicenseTemplateFieldIdField.uuid(),
  license: LicenseIdField.uuid(),
  value: ValueIdField.nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const CreateLicenseViewResponseData = z.object({
  license: License,
  license_fields: z.array(LicenseField),
})
const CreateLicenseFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid(),
  license_fields: z.array(LicenseFieldInput),
})
const CreateLicenseFieldsViewResponseData = z.object({ license_fields: z.array(LicenseField) })
const CreateLicenseTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  prefix: z.string().regex(/^[A-Z0-9]+$/),
  expiration_config: z.unknown(),
  status: LiveEnum.optional(),
})
const CreateLicenseTemplateViewResponseData = z.object({ license_template: LicenseTemplate })
const CreateLicenseTemplateFieldViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  name: z.string(),
  type: ValueTypeEnum,
})
const LicenseTemplateField = z.object({
  id: LicenseTemplateFieldIdField.uuid(),
  license_template: LicenseTemplateIdField.uuid(),
  name: z.string(),
  type: ValueTypeEnum,
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  archived: z.string().datetime({ offset: true }).nullish(),
})
const CreateLicenseTemplateFieldViewResponseData = z.object({
  license_template_field: LicenseTemplateField,
})
const EditLicenseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid(),
  status: ActiveEnum.optional(),
  issued_on: z.string().optional(),
  expires_at: z.string().nullish(),
  reason: z.string().optional(),
  case_id: CaseIdField.nullish(),
  notify_license_field_ids: z.array(LicenseFieldIdField).optional(),
})
const EditLicenseViewResponseData = z.object({ license: License })
const LicenseFieldUpdate = z.object({
  license_field_id: LicenseFieldIdField.uuid(),
  value: ValueCreate.nullish(),
})
const EditLicenseFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid(),
  license_fields: z.array(LicenseFieldUpdate),
  reason: z.string().optional(),
  notify_license_field_ids: z.array(LicenseFieldIdField).optional(),
})
const EditLicenseFieldsViewResponseData = z.object({ license_fields: z.array(LicenseField) })
const EditLicenseTemplateViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  name: z.string().optional(),
  expiration_config: z.unknown().optional(),
  status: LiveEnum.optional(),
})
const EditLicenseTemplateViewResponseData = z.object({ license_template: LicenseTemplate })
const EditLicenseTemplateFieldViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_field_id: LicenseTemplateFieldIdField.uuid(),
  name: z.string().optional(),
  archived: z.boolean().optional(),
})
const EditLicenseTemplateFieldViewResponseData = z.object({
  license_template_field: LicenseTemplateField,
})
const ListConstituentLicensesViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const ListConstituentLicensesViewResponseData = z.object({
  licenses: z.array(License),
  license_template_names: z.record(z.string()),
  license_organization_handles: z.record(z.string()),
  license_process_codes: z.record(z.string()),
})
const ListContactLicensesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  contact_identity_id: IdentityIdField.uuid(),
})
const ListContactLicensesViewResponseData = z.object({
  licenses: z.array(License),
  license_templates: z.array(LicenseTemplate),
})
const ListLicenseCasesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  license_id: LicenseIdField.uuid(),
})
const ListLicenseCasesViewResponseData = z.object({ cases: z.array(CaseCard) })
const ListLicenseDocumentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid(),
})
const ListLicenseDocumentsViewResponseData = z.object({
  license_documents: z.array(LicenseDocument),
})
const ListLicenseFieldMappingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_field_id: LicenseTemplateFieldIdField.uuid(),
})
const LicenseFieldMapping = z.object({
  id: z.string().uuid().optional(),
  process_input: ExpressionIdField.uuid(),
  license_template_field: LicenseTemplateFieldIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const ListLicenseFieldMappingsViewResponseData = z.object({
  license_field_mappings: z.array(LicenseFieldMapping),
})
const ListLicenseFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid().optional(),
  license_template_id: LicenseTemplateIdField.uuid().optional(),
})
const ListLicenseFieldsViewResponseData = z.object({
  license_fields: z.array(LicenseField),
  values: z.array(Value0),
})
const ListLicenseHistoryViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_id: LicenseIdField.uuid(),
})
const LicenseHistoryIdField = z.string()
const LicenseHistory = z.object({
  id: LicenseHistoryIdField.uuid(),
  license: LicenseIdField.uuid(),
  field: z.string(),
  old_value: ValueIdField.nullish(),
  new_value: ValueIdField.nullish(),
  reason: z.string().nullish(),
  case: CaseIdField.nullish(),
  staffer: EndUserIdField.nullish(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const ListLicenseHistoryViewResponseData = z.object({
  license_history: z.array(LicenseHistory),
  values: z.array(Value0),
})
const ListLicenseTemplateFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  type: ValueTypeEnum.optional(),
  archived: z.boolean().optional().default(false),
})
const ListLicenseTemplateFieldsViewResponseData = z.object({
  license_template_fields: z.array(LicenseTemplateField),
})
const ListLicenseTemplatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  license_template_id: LicenseTemplateIdField.uuid().optional(),
  code: z.string().optional(),
  status: LiveEnum.optional(),
  name: z.string().optional(),
  include_fields: z.boolean().optional().default(false),
  include_document_template: z.boolean().optional().default(false),
})
const ListLicenseTemplatesViewResponseData = z.object({
  license_templates: z.array(LicenseTemplate),
  license_template_fields: z.array(LicenseTemplateField),
  license_document_templates: z.array(LicenseDocumentTemplate),
})
const ListLicensesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField).min(1),
  license_template_id: LicenseTemplateIdField.uuid().optional(),
  number: z.string().optional(),
  status: ActiveEnum.optional(),
})
const ListLicensesViewResponseData = z.object({ licenses: z.array(License) })
const ListLicensesWithLocationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  status_list: z.array(ActiveEnum).optional(),
  issued_on_from: z.string().optional(),
  issued_on_to: z.string().optional(),
  expires_on_from: z.string().optional(),
  expires_on_to: z.string().optional(),
})
const ListLicensesWithLocationsViewResponseData = z.object({
  licenses: z.array(License),
  license_locations: z.record(z.array(AddressBase)),
})
const LicenseFieldMappingEntry = z.object({
  license_template_field_id: LicenseTemplateFieldIdField.uuid(),
  process_input_ids: z.array(ExpressionIdField),
})
const SetLicenseFieldMappingsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  process_version: ProcessIdField.uuid(),
  mappings: z.array(LicenseFieldMappingEntry),
})
const SetLicenseFieldMappingsViewResponseData = z.object({
  license_field_mappings: z.array(LicenseFieldMapping),
})
const LicenseTemplateFieldEntry = z.object({
  license_template_field_id: LicenseTemplateFieldIdField.uuid().optional(),
  key: z.string(),
  name: z.string(),
  type: ValueTypeEnum,
})
const SetLicenseTemplateFieldsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  license_template_id: LicenseTemplateIdField.uuid(),
  license_template_fields: z.array(LicenseTemplateFieldEntry).optional(),
  document_template_id: DocumentTemplateIdField.uuid().optional(),
  field_values: z.unknown().optional(),
})
const SetLicenseTemplateFieldsViewResponseData = z.object({
  license_template_fields: z.array(LicenseTemplateField),
  license_template_field_ids_by_key: z.record(LicenseTemplateFieldIdField),
  license_document_template: LicenseDocumentTemplate.nullable(),
})
const CancelScheduledMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
})
const MassCommunicationStatusEnum = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
])
const MassCommunication = z.object({
  id: MassCommunicationIdField.uuid(),
  organization: OrganizationIdField.uuid(),
  created_by: EndUserIdField.nullish(),
  sent_by: EndUserIdField.nullish(),
  name: z.string().nullish(),
  subject: z.string().nullish(),
  body: z.string().nullish(),
  channel: CommunicationChannelEnum,
  channel_display: z.string().optional(),
  status: MassCommunicationStatusEnum.optional(),
  status_display: z.string().optional(),
  contact_filter: z.unknown().nullish(),
  personalization_fallbacks: z.object({}).partial().passthrough().nullable(),
  from_email: z.string().nullable(),
  recipient_count: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  created: z.string().datetime({ offset: true }).optional(),
  updated: z.string().datetime({ offset: true }).optional(),
  sent_at: z.string().datetime({ offset: true }).nullish(),
  scheduled_send_at: z.string().datetime({ offset: true }).nullish(),
})
const CancelScheduledMassCommunicationViewResponseData = z.object({
  mass_communication: MassCommunication,
})
const ListMassCommunicationContactBookAddressPointsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  tag_names: z.array(z.string()).optional(),
  exclude_tag_names: z.array(z.string()).optional(),
  points: z.array(GeoPoint).optional(),
  gis_layer_ids: z.array(GISLayerSourceIdField).optional(),
  gis_layer_property_filters: z.array(GISLayerPropertyPredicate).optional(),
  workflow_ids: z.array(WorkflowIdField).optional(),
  channel: z.number().int().optional().default(1),
  excluded_contact_ids: z.array(z.string()).optional(),
  limit: z.number().int().gte(0).optional().default(10000),
})
const ContactBookAddressPoint = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lon: z.number(),
  lat: z.number(),
  contact_id: z.string(),
  name: z.string(),
})
const ListMassCommunicationContactBookAddressPointsViewResponseData = z.object({
  address_points: z.array(ContactBookAddressPoint),
})
const CreateMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string().optional().default(''),
  channel: CommunicationChannelEnum,
})
const CreateMassCommunicationViewResponseData = z.object({ mass_communication: MassCommunication })
const DeleteMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
})
const GetOrganizationEmailCapabilityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetOrganizationEmailCapabilityViewResponseData = z.object({
  has_email: z.boolean(),
  from_email: z.string().nullish(),
  from_name: z.string().nullish(),
  ses_verified: z.boolean(),
})
const GetMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
})
const GetMassCommunicationViewResponseData = z.object({
  mass_communication: MassCommunication,
  receipt_summary: z.object({}).partial().passthrough(),
})
const ListMassCommunicationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  offset: z.number().int().optional().default(0),
  limit: z.number().int().optional().default(25),
})
const ListMassCommunicationsViewResponseData = z.object({
  mass_communications: z.array(MassCommunication),
  total_count: z.number().int(),
})
const ListMassCommunicationReceiptsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
  status_filter: z.array(z.number().int()).optional(),
  offset: z.number().int().optional().default(0),
  limit: z.number().int().optional().default(50),
})
const MassCommunicationReceipt = z.object({
  id: MassCommunicationReceiptIdField.uuid(),
  mass_communication: MassCommunicationIdField.uuid(),
  channel: CommunicationChannelEnum,
  channel_display: z.string().optional(),
  phone_number: PhoneNumberIdField.nullish(),
  phone_number_display: z.string().nullish(),
  email_address: EmailAddressIdField.nullish(),
  email_address_display: z.string().nullish(),
  subject: z.string().nullish(),
  body: z.string().nullish(),
  status: ReceiptStatusEnum.optional(),
  status_display: z.string().optional(),
  created: z.string().datetime({ offset: true }).optional(),
  updated: z.string().datetime({ offset: true }).optional(),
  sent_at: z.string().datetime({ offset: true }).nullish(),
  error_message: z.string().nullish(),
  external_id: z.string().nullish(),
  opened_at: z.string().datetime({ offset: true }).nullish(),
  clicked_at: z.string().datetime({ offset: true }).nullish(),
  bounced_at: z.string().datetime({ offset: true }).nullish(),
  complaint_at: z.string().datetime({ offset: true }).nullish(),
})
const ListMassCommunicationReceiptsViewResponseData = z.object({
  receipts: z.array(MassCommunicationReceipt),
  total_count: z.number().int(),
})
const ListMassCommunicationResponsesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
  offset: z.number().int().optional().default(0),
  limit: z.number().int().optional().default(50),
})
const MassCommResponse = z.object({
  receipt_id: MassCommunicationReceiptIdField.nullable(),
  phone_number_display: z.string().nullable(),
  email_address_display: z.string().nullable(),
  receipt_status: z.number().int().nullable(),
  receipt_status_display: z.string().nullable(),
  ai_interaction_id: AIInteractionIdField.uuid(),
  ai_interaction_created: z.string(),
  first_user_message: z.string().nullable(),
  case_handle: z.string().nullable(),
  case_id: CaseIdField.nullable(),
})
const ListMassCommunicationResponsesViewResponseData = z.object({
  responses: z.array(MassCommResponse),
  total_count: z.number().int(),
})
const PreviewMassCommunicationContactBookRecipientsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  tag_names: z.array(z.string()).optional(),
  exclude_tag_names: z.array(z.string()).optional(),
  points: z.array(GeoPoint).optional(),
  gis_layer_ids: z.array(GISLayerSourceIdField).optional(),
  gis_layer_property_filters: z.array(GISLayerPropertyPredicate).optional(),
  workflow_ids: z.array(WorkflowIdField).optional(),
  channel: z.number().int().optional().default(1),
  count_only: z.boolean().optional().default(false),
  excluded_contact_ids: z.array(z.string()).optional(),
})
const PreviewMassCommunicationContactBookRecipientsViewResponseData = z.object({
  recipient_count: z.number().int(),
  contacts: z.array(z.object({}).partial().passthrough()),
})
const SendMassCommunicationContactBookViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
  excluded_contact_ids: z.array(z.string()).optional(),
  scheduled_send_at: z.string().datetime({ offset: true }).nullish(),
})
const SendMassCommunicationContactBookViewResponseData = z.object({
  mass_communication: MassCommunication,
})
const SendTestMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
})
const GetOrganizationSmsCapabilityViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const GetOrganizationSmsCapabilityViewResponseData = z.object({
  has_phone_line: z.boolean(),
  phone_number: z.string().nullish(),
  enabled_for_outbound_sms: z.boolean(),
  enabled_for_outbound_calls: z.boolean(),
  has_twilio_sub_account: z.boolean(),
})
const UpdateMassCommunicationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  mass_communication_id: MassCommunicationIdField.uuid(),
  name: z.string().nullish(),
  subject: z.string().nullish(),
  body: z.string().nullish(),
  contact_filter: z.unknown().nullish(),
  personalization_fallbacks: z.unknown().nullish(),
  from_email: ContactEmailIdField.nullish(),
})
const UpdateMassCommunicationViewResponseData = z.object({ mass_communication: MassCommunication })
const AddChargeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  name: z.string(),
  description: z.string(),
  amount: z.number().int(),
  account_receivable_code: z.string().optional(),
})
const AddChargeViewResponseData = z.object({ charge_id: ChargeIdField.uuid() })
const AddPaymentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  amount: z.number().int().optional(),
  method: PaymentMethodEnum,
  payer_id: EndUserIdField.uuid().optional(),
})
const CancelTerminalPaymentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  reader_id: z.string(),
})
const CompleteBillViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  complete: z.boolean(),
  completed: z.string().datetime({ offset: true }).optional(),
  waived: z.boolean().optional().default(false),
})
const StripeSourceIdField = z.string()
const ConfirmPaymentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  source_id: StripeSourceIdField.uuid().optional(),
  reader_id: z.string().optional(),
  manual: z.number().int().optional(),
  is_installment: z.boolean(),
  is_bank: z.boolean(),
  is_phone: z.boolean(),
  payer_id: EndUserIdField.uuid().optional(),
})
const ConfirmPaymentViewResponseData = z.object({ payment_id: PaymentIdField.uuid() })
const ConnectStripeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ConnectStripeViewResponseData = z.object({ url: z.string().url(), stripe_id: z.string() })
const LookupKeyTypeEnum = z.enum(['id', 'code', 'handle'])
const LookupIdField = z.string()
const LookupKey = z.object({
  type: LookupKeyTypeEnum,
  lookup_id: LookupIdField.nullish(),
  receiver_identity_id: IdentityIdField.nullish(),
  code: z.string().nullish(),
  handle: z.string().nullish(),
})
const PaymentFilter = z
  .object({
    reconciled: z.boolean().nullable(),
    status: PaymentStatusEnum.nullable(),
    manual: z.boolean().nullable(),
  })
  .partial()
const BillFilter = z
  .object({
    ids: z.array(z.string().uuid()).nullable(),
    item_ids: z.array(z.string().uuid()).nullable(),
    case_ids: z.array(z.string().uuid()).nullable(),
    step_id: StepIdField.nullable(),
    block_id: BlockIdField.nullable(),
    receiver_ids: z.array(z.string().uuid()).nullable(),
    complete: z.boolean().nullable(),
    completed_after: z.string().datetime({ offset: true }).nullable(),
    completed_before: z.string().datetime({ offset: true }).nullable(),
    payment_filter: PaymentFilter.nullable(),
  })
  .partial()
const CountLookupEntriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  lookup_key: LookupKey,
  bill_filter: BillFilter,
  similar_key: z.string().nullish(),
  digits_key: z.string().optional(),
  exact_key: z.string().optional(),
})
const CountLookupEntriesViewResponseData = z.object({ count: z.number().int() })
const PaymentListArgs = z
  .object({
    bill_ids: z.array(z.string().uuid()).nullable(),
    start: z.string().datetime({ offset: true }).nullable(),
    end: z.string().datetime({ offset: true }).nullable(),
    methods: z.array(PaymentMethodEnum).nullable(),
    is_stripe: z.boolean().nullable(),
    payment_intent: z.string().nullable(),
    reconciled: z.boolean().nullable(),
    key: z.string().nullable(),
    offset: z.number().int().nullable(),
    limit: z.number().int().nullable(),
    complete: z.boolean().nullable(),
    payout_ids: z.array(z.string().uuid()).nullable(),
    workflow_ids: z.array(z.string().uuid()).nullable(),
    lookup: z.array(z.string().uuid()).nullable(),
    bill_receivers: z.array(z.string().uuid()).nullable(),
    attempted: z.boolean(),
    no_payout: z.boolean(),
    status: z.array(PaymentStatusEnum).nullable(),
    payment: PaymentIdField.nullable(),
    payment_code: z.string().nullable(),
    bill_code: z.string().nullable(),
  })
  .partial()
const CountPaymentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  payment_id: PaymentIdField.uuid().optional(),
  block_id: BlockIdField.uuid().optional(),
  payment_list_args: PaymentListArgs,
})
const CountPaymentsViewResponseData = z.object({ count: z.number().int() })
const CreateLocationViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  display_name: z.string(),
  address_line1: z.string(),
  address_city: z.string(),
  address_state: z.string(),
  address_postal_code: z.string(),
})
const PaymentTerminalLocationIdField = z.string()
const PaymentTerminalLocation = z.object({
  id: PaymentTerminalLocationIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  stripe_location_id: z.string(),
  display_name: z.string(),
  address_line1: z.string(),
  address_city: z.string(),
  address_state: z.string(),
  address_postal_code: z.string(),
  owner: IdentityIdField.uuid(),
})
const CreateLocationViewResponseData = z.object({ location: PaymentTerminalLocation })
const CreateLookupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  name: z.string(),
  key_name: z.string(),
  key_description: z.string(),
  process_name: z.string(),
  process_description: z.string(),
  bill_step_name: z.string(),
  bill_step_description: z.string(),
  allow_ach: z.boolean(),
})
const CreateLookupViewResponseData = z.object({ lookup_id: LookupIdField.uuid() })
const CreatePayoutViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  payment_ids: z.array(PaymentIdField),
  descriptor: z.string(),
})
const CreatePayoutViewResponseData = z.object({ payout_id: PayoutIdField.uuid() })
const DeleteChargeViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  charge_id: ChargeIdField.uuid(),
})
const DownloadSpringbrookCSVViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  payment_ids: z.array(PaymentIdField).min(1),
})
const DownloadSpringbrookCSVViewResponseData = z.object({ email_recipient: z.string().email() })
const FTPAuthViewRequestData = z.object({
  username: z.string(),
  password: z.string(),
  token: z.string(),
})
const FTPAuthViewResponseData = z.object({
  Role: z.string(),
  Policy: z.unknown(),
  HomeDirectory: z.string(),
})
const GetLookupViewRequestData = z.object({ user_id: EndUserIdField.uuid(), lookup_key: LookupKey })
const Lookup0 = z.object({
  id: LookupIdField.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  process_id: ProcessIdField.uuid(),
  block_id: BlockIdField.uuid(),
  receiver_identity_id: IdentityIdField.uuid(),
})
const GetLookupViewResponseData = z.object({ lookup: Lookup0 })
const GetPaymentForReceiptViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  payment_code: z.string(),
  bill_code: z.string(),
})
const Contact0 = z.object({
  email: z.string(),
  identity_id: IdentityIdField.uuid(),
  name: z.string(),
  image: z.string().nullable(),
  address: Address0.nullable(),
})
const Payment1 = z.object({
  payer: Contact0.nullish(),
  charges: z.array(Charge),
  id: PaymentIdField.uuid(),
  code: z.string(),
  bill_id: BillIdField.uuid(),
  method: PaymentMethodEnum,
  amount: z.number().int(),
  complete: z.boolean(),
  reconciled: z.boolean(),
  created: z.string().datetime({ offset: true }),
  completed: z.string().datetime({ offset: true }).nullable(),
  fee: z.number().int(),
  revenue_object_name: z.string(),
  revenue_object_code: z.string().nullable(),
  revenue_object_description: z.string(),
  bill_name: z.string(),
  bill_code: z.string(),
  bill_account: z.string().nullable(),
  bill_distribution: z.string().nullable(),
  is_stripe: z.boolean(),
  key: z.string().nullable(),
  payout: PayoutIdField.nullable(),
  status: PaymentStatusEnum,
  attempted: z.string().datetime({ offset: true }).nullable(),
  case_handle: z.string(),
  receiver: Identity1.nullable(),
})
const GetPaymentForReceiptViewResponseData = z.object({ data: Payment1.nullable() }).partial()
const GetPaymentInfoViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  bill_code: z.string(),
  organization_identity_id: IdentityIdField.uuid(),
  skip_permission_check: z.boolean().optional().default(false),
})
const Payment0 = z.object({
  id: PaymentIdField.uuid(),
  code: z.string(),
  bill_id: BillIdField.uuid(),
  method: PaymentMethodEnum,
  amount: z.number().int(),
  complete: z.boolean(),
  reconciled: z.boolean(),
  created: z.string().datetime({ offset: true }),
  completed: z.string().datetime({ offset: true }).nullable(),
  fee: z.number().int(),
  revenue_object_name: z.string(),
  revenue_object_code: z.string().nullable(),
  revenue_object_description: z.string(),
  bill_name: z.string(),
  bill_code: z.string(),
  bill_account: z.string().nullable(),
  bill_distribution: z.string().nullable(),
  is_stripe: z.boolean(),
  key: z.string().nullable(),
  payout: PayoutIdField.nullable(),
  status: PaymentStatusEnum,
  attempted: z.string().datetime({ offset: true }).nullable(),
  case_handle: z.string(),
  payer: EndUserIdField.nullable(),
  receiver: IdentityIdField.nullable(),
})
const Bill2 = z.object({
  charges: z.array(Charge),
  payments: z.array(Payment0),
  payer: Identity0.nullable(),
  receiver: Identity0,
  process: Process0,
  description: z.string(),
  id: BillIdField.uuid(),
  handle: z.string(),
  created: z.string().datetime({ offset: true }),
  due: z.string().datetime({ offset: true }).nullable(),
  complete: z.boolean(),
  completion_time: z.string().datetime({ offset: true }).nullable(),
  editable: z.boolean(),
  installments: z.number().int(),
  name: z.string(),
  is_stripe: z.boolean(),
  step_id: StepIdField.uuid(),
  bill_step_id: BillStepIdField.uuid(),
  allow_ach: z.boolean(),
  case_id: CaseIdField.uuid(),
  order: z.number().int().nullable(),
  key: z.string().nullable(),
  item: ItemIdField.uuid(),
  org_covers_fee: z.boolean(),
})
const GetPaymentInfoViewResponseData = z.object({ bill: Bill2 })
const GetPaymentTotalViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  bill_filter: BillFilter,
})
const GetPaymentTotalViewResponseData = z.object({ total: z.number().int() })
const GranularityEnum = z.enum(['hour', 'day', 'week', 'month'])
const GetPaymentsStatsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  granularity: GranularityEnum,
  bill_filter: BillFilter,
})
const PaymentInterval = z.object({
  time: z.string().datetime({ offset: true }),
  total: z.number().int(),
})
const GetPaymentsStatsViewResponseData = z.object({ data: z.array(PaymentInterval) })
const GetRevenueBreakdownViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  active: z.boolean(),
})
const RevenueStat = z.object({
  process_id: ProcessIdField.nullable(),
  name: z.string(),
  amount: z.number().int(),
})
const GetRevenueBreakdownViewResponseData = z.object({ data: z.array(RevenueStat) })
const GetRevenueTypesBreakdownViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  block_id: BlockIdField.uuid().optional(),
  active: z.boolean().optional().default(false),
})
const RevenueTypeBreakdown = z.object({ method: PaymentMethodEnum, total: z.number().int() })
const GetRevenueTypesBreakdownViewResponseData = z.object({ data: z.array(RevenueTypeBreakdown) })
const BillImportFormatEnum = z.enum(['nemrc', 'southern-auto', 'southern-manual', 'stormwater'])
const ImportBillsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  lookup_id: LookupIdField.uuid(),
  format: BillImportFormatEnum,
  url: z.string(),
})
const ListBillsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  bill_filter: BillFilter,
})
const ListBillsViewResponseData = z.object({ bills: z.array(Bill2) })
const ListChargeSubstepsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  charge_substep_ids: z.array(ChargeSubstepIdField).optional(),
  expression_ids: z.array(ExpressionIdField).optional(),
})
const ListChargeSubstepsViewResponseData = z.object({ charge_substeps: z.array(ChargeSubstep) })
const ListConstituentCaseBillViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
  step_id: StepIdField.uuid(),
})
const ListConstituentCaseBillViewResponseData = z.object({ bill: Bill2 })
const ListLocationsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListLocationsViewResponseData = z.object({ locations: z.array(PaymentTerminalLocation) })
const ListLookupEntriesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  lookup_key: LookupKey,
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  bill_filter: BillFilter,
  similar_key: z.string().nullish(),
  digits_key: z.string().optional(),
  exact_key: z.string().optional(),
})
const LookupEntryIdField = z.string()
const LookupEntry2 = z.object({ payment: Bill2, id: LookupEntryIdField.uuid(), key: z.string() })
const ListLookupEntriesViewResponseData = z.object({ lookup_entries: z.array(LookupEntry2) })
const ListLookupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  active: z.boolean(),
})
const LookupStats = z.object({
  total: z.number().int(),
  paid: z.number().int(),
  receipts: z.number().int(),
})
const Lookup2 = z.object({
  stats: LookupStats,
  id: LookupIdField.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  process_id: ProcessIdField.uuid(),
  block_id: BlockIdField.uuid(),
  owner: z.string(),
})
const ListLookupsViewResponseData = z.object({ lookups: z.array(Lookup2) })
const ListPaymentExportRulesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const PaymentExportRuleIdField = z.string()
const PaymentExportRule = z.object({
  id: PaymentExportRuleIdField.uuid(),
  method: PaymentMethodEnum,
  is_stripe: z.boolean(),
  code: z.string(),
  organization: OrganizationIdField.uuid(),
})
const ListPaymentExportRulesViewResponseData = z.object({
  payment_export_rules: z.array(PaymentExportRule),
})
const ListPaymentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  payment_list_args: PaymentListArgs,
})
const ListPaymentsViewResponseData = z.object({ data: z.array(Payment1) })
const ListPayoutsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  limit: z.number().int().gte(1).optional(),
  offset: z.number().int().gte(0).optional(),
  search: z.string().optional(),
  descriptors: z.array(z.string()).optional(),
  start: z.string().datetime({ offset: true }).optional(),
  end: z.string().datetime({ offset: true }).optional(),
  include_descriptors: z.boolean().optional().default(false),
})
const Payout = z.object({
  id: PayoutIdField.uuid(),
  descriptor: z.string(),
  created: z.string().datetime({ offset: true }),
  stripe_id: z.string().nullish(),
  initiated: z.boolean(),
  locked: z.string().datetime({ offset: true }).nullish(),
})
const ListPayoutsViewResponseData = z.object({
  payouts: z.array(Payout),
  descriptors: z.array(z.string()),
  total_payouts: z.number().int(),
})
const StripeSourceTypeEnum = z.enum(['StripeSourceBank', 'StripeSourceCard'])
const ListStripeSourcesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  type: StripeSourceTypeEnum.optional(),
})
const StripeSource = z.object({
  id: StripeSourceIdField.uuid(),
  type: StripeSourceTypeEnum,
  stripe_id: z.string(),
  provider: z.string(),
  last4: z.string(),
  verified: z.boolean(),
  owner: IdentityIdField.uuid(),
})
const ListStripeSourcesViewResponseData = z.object({ sources: z.array(StripeSource) })
const ListTerminalsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const PaymentTerminalIdField = z.string()
const PaymentTerminal = z.object({
  id: PaymentTerminalIdField.uuid(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
  metadata: z.unknown().nullish(),
  stripe_terminal_id: z.string(),
  label: z.string(),
  stripe_location_id: z.string(),
  owner: IdentityIdField.uuid(),
  location: PaymentTerminalLocationIdField.nullish(),
})
const ListTerminalsViewResponseData = z.object({ terminals: z.array(PaymentTerminal) })
const ObtainPaymentIntentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  manual: z.number().int().optional(),
  is_installment: z.boolean(),
  is_phone: z.boolean(),
  is_bank: z.boolean(),
  reader_id: z.string().optional(),
  save: z.boolean(),
  payer_id: EndUserIdField.uuid().optional(),
})
const Intent = z.object({ destination: z.string(), secret: z.string(), intent_id: z.string() })
const ObtainPaymentIntentViewResponseData = z.object({ intent: Intent })
const ReconcileViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  payment_ids: z.array(PaymentIdField),
  reconcile: z.boolean(),
})
const RegisterTerminalViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  location_id: z.string(),
  registration_code: z.string(),
  label: z.string().optional(),
})
const RegisterTerminalViewResponseData = z.object({ terminal: PaymentTerminal })
const SearchBillsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id_list: z.array(IdentityIdField),
  start: z.string().datetime({ offset: true }).optional(),
  end: z.string().datetime({ offset: true }).optional(),
  lookup_entry_key: z.string().optional(),
  bill_name: z.string().optional(),
  bill_description: z.string().optional(),
  payment_type: WorkflowIdField.uuid().optional(),
  active: z.boolean(),
  has_balance: z.boolean(),
})
const SearchBillsViewResponseData = z.object({ bills: z.array(Bill2) })
const SendBillRequestViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  bill_id: BillIdField.uuid(),
  email: z.string(),
  url: z.string().optional(),
})
const UpsertPaymentExportRuleViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  method: PaymentMethodEnum,
  is_stripe: z.boolean(),
  code: z.string(),
})
const VerifyStripeSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  stripe_source_id: StripeSourceIdField.uuid(),
})
const AddAdHocBlockInstancePermissionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_ids: z.array(EndUserIdField).optional(),
  target_group_ids: z.array(GroupIdField).optional(),
  target_model_id: BlockInstanceIdField.uuid(),
  actions: z.array(AccessActionEnum).min(1),
})
const AdHocPermissionIdField = z.string()
const AddAdHocBlockInstancePermissionsViewResponseData = z.object({
  ad_hoc_permission_ids: z.array(AdHocPermissionIdField),
})
const AddAdHocBlockPermissionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_ids: z.array(EndUserIdField).optional(),
  target_group_ids: z.array(GroupIdField).optional(),
  target_model_id: BlockIdField.uuid(),
  actions: z.array(AccessActionEnum).min(1),
})
const AddAdHocBlockPermissionsViewResponseData = z.object({
  ad_hoc_permission_ids: z.array(AdHocPermissionIdField),
})
const TargetResourceEnum = z.enum([
  'case',
  'case_block_instance',
  'case_item',
  'process',
  'process_case',
  'process_case_block',
])
const AddAdHocCasePermissionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_ids: z.array(EndUserIdField).optional(),
  target_group_ids: z.array(GroupIdField).optional(),
  target_model_id: CaseIdField.uuid(),
  resource_type: TargetResourceEnum,
  actions: z.array(AccessActionEnum).min(1),
})
const AddAdHocCasePermissionsViewResponseData = z.object({
  ad_hoc_permission_ids: z.array(AdHocPermissionIdField),
})
const AddAdHocWorkflowPermissionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_ids: z.array(EndUserIdField).optional(),
  target_group_ids: z.array(GroupIdField).optional(),
  workflow_id: WorkflowIdField.uuid(),
  actions: z.array(AccessActionEnum).min(1),
})
const AddAdHocWorkflowPermissionsViewResponseData = z.object({
  ad_hoc_permission_ids: z.array(AdHocPermissionIdField),
})
const AddGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_name: z.string(),
})
const AddGroupViewResponseData = z.object({ group_id: GroupIdField.uuid() })
const AddGroupMembersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_ids: z.array(GroupIdField),
  member_list: z.array(EndUserIdField),
})
const GroupMemberIdField = z.string()
const AddGroupMembersViewResponseData = z.object({ group_members: z.array(GroupMemberIdField) })
const PolicyIdField = z.string()
const AdminEnum = z.enum(['admin', 'staff'])
const AddPermissionsRoleViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  role_name: z.string(),
  policies: z.array(PolicyIdField).optional(),
  template_policy: AdminEnum.optional(),
})
const PermissionsRoleIdField = z.string()
const AddPermissionsRoleViewResponseData = z.object({
  permissions_role_id: PermissionsRoleIdField.uuid(),
})
const GeneralResourceEnum = z.enum([
  'cases',
  'processes',
  'licenses',
  'contacts',
  'payments',
  'lookup_payments',
  'payouts',
  'assets',
  'assistant_logs',
  'interaction_logs',
  'interaction_logs_pii',
  'interaction_analytics',
  'interaction_customization',
  'knowledge_base',
  'organization_settings',
  'user_management_settings',
  'assistant_settings',
  'payment_settings',
  'outbound_communications',
])
const RolePolicyTuple = z.object({
  general_resource: GeneralResourceEnum,
  action: AccessActionEnum,
})
const AddRolePoliciesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  role_id: PermissionsRoleIdField.uuid(),
  policies: z.array(RolePolicyTuple),
})
const AddSingletonGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  group_name: z.string().min(1),
  owner_ids: z.array(EndUserIdField).min(1).max(1),
})
const AddSingletonGroupViewResponseData = z.object({ group_id: GroupIdField.uuid() })
const AddWorkspaceAdministratorViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  administrator_user_id: EndUserIdField.uuid(),
})
const WorkspaceAdministratorIdField = z.string()
const AddWorkspaceAdministratorViewResponseData = z.object({
  workspace_administrator_id: WorkspaceAdministratorIdField.uuid(),
})
const AddWorkspaceGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  group_name: z.string().min(1),
  owner_ids: z.array(EndUserIdField).min(1).optional(),
  member_ids: z.array(EndUserIdField).optional(),
})
const AddWorkspaceGroupViewResponseData = z.object({ group_id: GroupIdField.uuid() })
const AssignGroupRolesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
  role_list: z.array(PermissionsRoleIdField),
})
const RoleAssignmentIdField = z.string()
const AssignGroupRolesViewResponseData = z.object({
  role_assignments: z.array(RoleAssignmentIdField),
})
const CheckUserBlockAccessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid(),
  block_id: BlockIdField.uuid(),
})
const CheckUserBlockAccessViewResponseData = z.object({ has_access: z.boolean() })
const CheckUserCaseAccessViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid(),
  case_id: CaseIdField.uuid(),
})
const CheckUserCaseAccessViewResponseData = z.object({ has_access: z.boolean() })
const CopyOrganizationGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
  group_name: z.string().min(1),
  workspace_group_owner_ids: z.array(EndUserIdField).min(1).optional(),
  include_sso_mapping: z.boolean().optional().default(false),
})
const Group = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  organization: OrganizationIdField.nullish(),
  organization_name: z.string().optional().default(''),
  workspace: WorkspaceIdField.nullish(),
  is_singleton: z.boolean().optional(),
})
const EndUser = z.object({
  id: z.string().uuid().optional(),
  identity: Identity0,
  persona: CityManagerEnum.nullish(),
})
const GroupMember = z.object({
  id: z.string().uuid().optional(),
  user: EndUser,
  group: GroupIdField.uuid(),
})
const CopyOrganizationGroupViewResponseData = z.object({
  group: Group,
  group_members: z.array(GroupMember),
})
const CopyWorkspaceGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  group_id: GroupIdField.uuid(),
  group_name: z.string().min(1),
  organization_identity_id: IdentityIdField.nullish(),
  include_sso_mapping: z.boolean().optional().default(false),
})
const CopyWorkspaceGroupViewResponseData = z.object({
  group: Group,
  group_members: z.array(GroupMember),
})
const CreateRoleAssignmentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  role_name: z.string(),
  group_organization_identity_id: IdentityIdField.uuid(),
  group_name: z.string().optional(),
  group_id: GroupIdField.uuid().optional(),
  member_list: z.array(EndUserIdField).optional(),
  policies: z.array(PolicyIdField).optional(),
  policy_tuples: z.array(RolePolicyTuple).optional(),
  template_policy: AdminEnum.optional(),
})
const CreateRoleAssignmentViewResponseData = z.object({
  role_assignment_id: RoleAssignmentIdField.uuid(),
})
const EnsureSingletonGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  target_user_ids: z.array(EndUserIdField).optional(),
  emails: z.array(z.string().email()).optional(),
})
const EnsureSingletonGroupsViewResponseData = z.object({
  groups: z.array(Group),
  singleton_user_ids: z.record(EndUserIdField),
})
const ListAdHocPermittedUsersForBlockViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_model_id: BlockIdField.uuid(),
})
const ListAdHocPermittedUsersForBlockViewResponseData = z.object({
  permitted_users: z.record(z.array(z.enum(['read', 'write', 'delete', 'export']))),
  users: z.array(EndUser),
})
const ListAdHocPermittedUsersForBlockInstanceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_model_id: BlockInstanceIdField.uuid(),
})
const ListAdHocPermittedUsersForBlockInstanceViewResponseData = z.object({
  permitted_users: z.record(z.array(z.enum(['read', 'write', 'delete', 'export']))),
  users: z.array(EndUser),
})
const ListAdHocPermittedUsersForCaseViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_model_id: CaseIdField.uuid(),
})
const ListAdHocPermittedUsersForCaseViewResponseData = z.object({
  permitted_users: z.record(z.array(z.enum(['read', 'write', 'delete', 'export']))),
  users: z.array(EndUser),
})
const ListAdHocPermittedUsersForWorkflowViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  workflow_id: WorkflowIdField.uuid(),
})
const ListAdHocPermittedUsersForWorkflowViewResponseData = z.object({
  permitted_users: z.record(z.array(z.enum(['read', 'write', 'delete', 'export']))),
  users: z.array(EndUser),
})
const ListExternalRoleAssignmentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const RoleIdField = z.string()
const ExternalRoleAssignment = z.object({
  role_id: RoleIdField.uuid(),
  group_id: GroupIdField.uuid(),
  group_name: z.string(),
  group_organization_name: z.string(),
})
const ListExternalRoleAssignmentsViewResponseData = z.object({
  external_assignments: z.array(ExternalRoleAssignment),
})
const ListGeneralPermissionsForGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
})
const Policy = z.object({
  id: z.string().uuid().optional(),
  action: AccessActionEnum,
  general_resource: GeneralResourceEnum,
})
const ListGeneralPermissionsForGroupViewResponseData = z.object({
  org_policies: z.record(z.array(Policy)),
})
const ListGeneralPermissionsForUserViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  member_user_id: EndUserIdField.uuid(),
})
const ListGeneralPermissionsForUserViewResponseData = z.object({
  org_policies: z.record(z.array(Policy)),
})
const ListGroupMembersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid().optional(),
})
const ListGroupMembersViewResponseData = z.object({ group_members: z.array(GroupMember) })
const ListGroupRolesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
})
const PermissionsRole = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  organization: OrganizationIdField.uuid(),
  policies: z.array(Policy),
})
const ListGroupRolesViewResponseData = z.object({ roles: z.array(PermissionsRole) })
const ListGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_ids: z.array(GroupIdField).optional(),
  workspace: z.boolean().optional().default(false),
})
const ListGroupsViewResponseData = z.object({
  groups: z.array(Group),
  saml_managed_group_ids: z.array(GroupIdField),
})
const ListInboundExternalRolesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const InboundExternalRole = z.object({
  role_id: RoleIdField.uuid(),
  role_name: z.string(),
  role_organization_name: z.string(),
  group_ids: z.array(GroupIdField),
})
const ListInboundExternalRolesViewResponseData = z.object({
  inbound_roles: z.array(InboundExternalRole),
})
const ListPermissionsRolesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  role_ids: z.array(RoleIdField).optional(),
})
const ListPermissionsRolesViewResponseData = z.object({ roles: z.array(PermissionsRole) })
const ListRoleAssignmentsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const ListRoleAssignmentsViewResponseData = z.object({
  assignments: z.record(z.array(PermissionsRole)),
})
const ListUserAdHocPermissionsRequestData = z.object({ user_id: EndUserIdField.uuid() })
const ListUserAdHocPermissionsResponseData = z.object({
  permitted_for_organization: z.record(z.array(IdentityIdField)),
  permitted_for_resource: z.record(z.array(z.string())),
})
const ListUserGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  member_user_id: EndUserIdField.uuid().optional(),
  group_ids: z.array(GroupIdField).optional(),
})
const ListUserGroupsViewResponseData = z.object({
  groups: z.array(Group),
  saml_managed_group_ids: z.array(GroupIdField),
})
const ListUserPermittedOrgIdentitiesViewRequestData = z.object({ user_id: EndUserIdField.uuid() })
const ListUserPermittedOrgIdentitiesViewResponseData = z.object({
  organizations_for_policies: z.record(z.array(IdentityIdField)),
})
const ListWorkspaceAdministratorsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
})
const WorkspaceAdministrator = z.object({
  id: z.string().uuid().optional(),
  user: EndUserIdField.uuid(),
  workspace: WorkspaceIdField.uuid(),
})
const ListWorkspaceAdministratorsViewResponseData = z.object({
  administrators: z.array(WorkspaceAdministrator),
})
const ListWorkspaceGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  include_singletons: z.boolean().optional().default(false),
})
const WorkspaceUser = z.object({
  id: EndUserIdField.uuid(),
  name: z.string(),
  email: z.string().nullable(),
})
const ListWorkspaceGroupsViewResponseData = z.object({
  groups: z.array(Group),
  saml_managed_group_ids: z.array(GroupIdField),
  singleton_user_ids: z.record(EndUserIdField),
  users: z.array(WorkspaceUser),
})
const RoleGroupReassignment = z.object({
  role_id: PermissionsRoleIdField.uuid(),
  current_group_id: GroupIdField.uuid(),
  new_group_id: GroupIdField.uuid(),
})
const ReassignRoleGroupsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  reassignments: z.array(RoleGroupReassignment),
})
const ReassignRoleGroupsViewResponseData = z.object({
  role_assignment_ids: z.array(RoleAssignmentIdField),
})
const RemoveBlockAdHocPermissionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid().optional(),
  target_group_id: GroupIdField.uuid().optional(),
  target_model_id: BlockIdField.uuid(),
  action: AccessActionEnum,
})
const RemoveBlockInstanceAdHocPermissionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid().optional(),
  target_group_id: GroupIdField.uuid().optional(),
  target_model_id: BlockInstanceIdField.uuid(),
  action: AccessActionEnum,
})
const RemoveCaseAdHocPermissionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid().optional(),
  target_group_id: GroupIdField.uuid().optional(),
  target_model_id: CaseIdField.uuid(),
  resource_type: TargetResourceEnum,
  action: AccessActionEnum,
})
const RemoveGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
})
const RemoveGroupMembersViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_ids: z.array(GroupIdField),
  member_list: z.array(EndUserIdField),
})
const RemoveGroupRolesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
  role_list: z.array(PermissionsRoleIdField),
})
const RemovePermissionsRoleViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  permissions_role_id: PermissionsRoleIdField.uuid(),
})
const RemoveRolePoliciesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  role_id: PermissionsRoleIdField.uuid(),
  policies: z.array(PolicyIdField),
})
const RemoveWorkflowAdHocPermissionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  target_user_id: EndUserIdField.uuid().optional(),
  target_group_id: GroupIdField.uuid().optional(),
  workflow_id: WorkflowIdField.uuid(),
  action: AccessActionEnum,
})
const RemoveWorkspaceAdministratorViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  administrator_user_id: EndUserIdField.uuid(),
})
const RemoveWorkspaceGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  group_id: GroupIdField.uuid(),
})
const RenameGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  group_id: GroupIdField.uuid(),
  group_name: z.string(),
})
const RenamePermissionsRoleViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  permissions_role_id: PermissionsRoleIdField.uuid(),
  role_name: z.string(),
})
const RenameWorkspaceGroupViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  workspace_id: WorkspaceIdField.uuid(),
  group_id: GroupIdField.uuid(),
  group_name: z.string().min(1),
})
const ApplyRedactionsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
})
const ApplyRedactionsViewResponseData = z.object({
  filename: z.string(),
  content_type: z.string(),
  download_url: z.string().url(),
  expires_in: z.number().int(),
})
const DeleteRedactionDocumentViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
})
const RedactionJobIdField = z.string()
const PdfEnum = z.enum(['pdf', 'txt', 'docx', 'eml', 'pst'])
const Pending1Enum = z.enum([
  'pending',
  'processing',
  'ready',
  'failed',
  'approved',
  'exported',
  'deleted',
])
const UnsupportedTypeEnum = z.enum([
  'unsupported_type',
  'nested_archive_not_reviewable',
  'extraction_failed',
  'no_content',
])
const SkippedAttachment = z.object({
  name: z.string(),
  content_type: z.string(),
  reason: UnsupportedTypeEnum,
})
const RedactionDocument = z.object({
  id: RedactionDocumentIdField.uuid(),
  redaction_job: RedactionJobIdField.uuid(),
  expanded_from: RedactionDocumentIdField.nullish(),
  file_name: z.string().max(255),
  file_type: PdfEnum,
  size_bytes: z.number().int().gte(0).lte(9223372036854776000).nullish(),
  status: Pending1Enum.optional(),
  skipped_attachments: z.array(SkippedAttachment),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const DeleteRedactionDocumentViewResponseData = z.object({ document: RedactionDocument })
const GetActiveRedactionRequestExportViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_id: RedactionRequestIdField.uuid(),
})
const GetActiveRedactionRequestExportViewResponseData = z.object({
  export: RedactionRequestExport.nullable(),
})
const GetRedactionDocumentSourceViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
})
const RenderModeEnum = z.enum(['canvas', 'text'])
const RedactionSourceTypeEnum = z.enum(['pdf', 'txt', 'docx', 'eml_body'])
const RedactionSourcePage = z.object({
  page: z.number().int(),
  width: z.number(),
  height: z.number(),
  render_mode: RenderModeEnum,
  source_type: RedactionSourceTypeEnum,
  section_label: z.string().nullable(),
  text: z.string(),
})
const GetRedactionDocumentSourceViewResponseData = z.object({
  pages: z.array(RedactionSourcePage),
  skipped_attachments: z.array(SkippedAttachment),
  pdf_attachments: z.record(z.string()),
  source_file: z.string().url(),
  expires_in: z.number().int(),
})
const GetRedactionJobViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_job_id: RedactionJobIdField.uuid(),
})
const Pending2Enum = z.enum(['pending', 'started', 'complete', 'failed'])
const RedactionJob = z.object({
  id: RedactionJobIdField.uuid(),
  redaction_request: RedactionRequestIdField.uuid(),
  status: Pending2Enum.optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const RedactionCandidateCounts = z.object({
  redaction_document: RedactionDocumentIdField.uuid(),
  visible: z.number().int(),
  pending: z.number().int(),
})
const GetRedactionJobViewResponseData = z.object({
  job: RedactionJob,
  documents: z.array(RedactionDocument),
  candidate_counts: z.array(RedactionCandidateCounts),
})
const GetRedactionRequestViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_id: RedactionRequestIdField.uuid(),
})
const GetRedactionRequestViewResponseData = z.object({
  request: RedactionRequest,
  block_instance: BlockInstance.nullable(),
  redaction_block: RedactionBlock.nullable(),
  case: Case.nullable(),
  documents: z.array(RedactionDocument),
  candidate_counts: z.array(RedactionCandidateCounts),
})
const GetRedactionRequestExportViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_export_id: RedactionRequestExportIdField.uuid(),
})
const GetRedactionRequestExportViewResponseData = z.object({ export: RedactionRequestExport })
const GetRedactionRequestExportDownloadViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_export_id: RedactionRequestExportIdField.uuid(),
})
const GetRedactionRequestExportDownloadViewResponseData = z.object({
  download_url: z.string().url(),
  expires_in: z.number().int(),
})
const GetRedactionSizeLimitsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
})
const RedactionSizeLimit = z.object({ file_type: PdfEnum, max_size_bytes: z.number().int() })
const GetRedactionSizeLimitsViewResponseData = z.object({ limits: z.array(RedactionSizeLimit) })
const ListRedactionCandidatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
})
const RedactionCandidateIdField = z.string()
const Bbox = z.object({ x0: z.number(), y0: z.number(), x1: z.number(), y1: z.number() })
const DetectedEnum = z.enum(['detected', 'manual'])
const CandidateSourceEnum = z.enum(['detected', 'llm', 'llm_suppressed', 'superseded', 'manual'])
const CharRange = z.object({ start: z.number().int(), end: z.number().int() })
const PendingEnum = z.enum(['pending', 'approved', 'rejected'])
const RedactionCandidate = z.object({
  id: RedactionCandidateIdField.uuid(),
  redaction_document: RedactionDocumentIdField.uuid(),
  statute_code: z.string().nullish(),
  statute_description: z.string().nullish(),
  entity: z.string().max(100),
  page: z.number().int().gte(-2147483648).lte(2147483647),
  section_label: z.string().nullish(),
  char_start: z.number().int().gte(-2147483648).lte(2147483647),
  char_end: z.number().int().gte(-2147483648).lte(2147483647),
  x0: z.number().nullish(),
  y0: z.number().nullish(),
  x1: z.number().nullish(),
  y1: z.number().nullish(),
  line_bboxes: z.array(Bbox),
  confidence: z.number().nullish(),
  origin: DetectedEnum.optional(),
  detection_source: CandidateSourceEnum,
  superseded_by: CharRange.nullable(),
  bbox_override: Bbox.nullable(),
  char_range_override: CharRange.nullable(),
  status: PendingEnum.optional(),
  created: z.string().datetime({ offset: true }),
  updated: z.string().datetime({ offset: true }),
})
const ListRedactionCandidatesViewResponseData = z.object({
  candidates: z.array(RedactionCandidate),
})
const ListRedactionRequestsViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  limit: z.number().int().gte(1).lte(200).optional().default(50),
  offset: z.number().int().gte(0).optional().default(0),
})
const ListRedactionRequestsViewResponseData = z.object({
  requests: z.array(RedactionRequest),
  total_count: z.number().int(),
})
const JurisdictionEnum = z.enum([
  'FL',
  'CA',
  'GA',
  'NJ',
  'NC',
  'TX',
  'UT',
  'WA',
  'AR',
  'MI',
  'MO',
  'CT',
  'PA',
  'CO',
  'FED',
])
const RedactionLevelEnum = z.enum(['FINANCIAL', 'PERSONAL_ID', 'FULL'])
const RetryRedactionDetectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_job_id: RedactionJobIdField.uuid(),
  jurisdiction: JurisdictionEnum.optional(),
  level: RedactionLevelEnum,
  add_entities: z.array(z.string()).optional(),
  remove_entities: z.array(z.string()).optional(),
  confidence_threshold: z.number().gte(0).lte(1).optional().default(0.3),
})
const RetryRedactionDetectionViewResponseData = z.object({
  job: RedactionJob,
  documents: z.array(RedactionDocument),
})
const SetRedactionDocumentCompleteViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
  complete: z.boolean(),
})
const SetRedactionDocumentCompleteViewResponseData = z.object({ document: RedactionDocument })
const Statute = z.object({
  code: z.string().nullable(),
  description: z.string().nullable(),
  is_categorical: z.boolean(),
})
const ManualRedactionCandidate = z.object({
  page: z.number().int().gte(1),
  section_label: z.string().nullable(),
  entity: z.string().max(100),
  matched_text: z.string(),
  char_range: CharRange,
  statute: Statute,
  bbox: Bbox.nullable(),
})
const SetRedactionManualCandidatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_document_id: RedactionDocumentIdField.uuid(),
  manual_candidates: z.array(ManualRedactionCandidate),
})
const SetRedactionManualCandidatesViewResponseData = z.object({
  candidates: z.array(RedactionCandidate),
})
const SetRedactionRequestNameViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_id: RedactionRequestIdField.uuid(),
  name: z.string().max(120),
})
const SetRedactionRequestNameViewResponseData = z.object({ request: RedactionRequest })
const UploadedRedactionDocument = z.object({
  key: z.string(),
  file_name: z.string().max(255),
  content_type: z.string().nullish(),
})
const StartRedactionDetectionViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  documents: z.array(UploadedRedactionDocument),
  jurisdiction: JurisdictionEnum.optional(),
  level: RedactionLevelEnum,
  add_entities: z.array(z.string()).optional(),
  remove_entities: z.array(z.string()).optional(),
  confidence_threshold: z.number().gte(0).lte(1).optional().default(0.3),
  redaction_request_id: RedactionRequestIdField.nullish(),
})
const FailedDocumentUpload = z.object({ file_name: z.string(), reason: z.string() })
const StartRedactionDetectionViewResponseData = z.object({
  redaction_request_id: RedactionRequestIdField.nullable(),
  redaction_job_id: RedactionJobIdField.nullable(),
  documents: z.array(RedactionDocument),
  failures: z.array(FailedDocumentUpload),
})
const StartRedactionRequestExportViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  redaction_request_id: RedactionRequestIdField.uuid(),
  redaction_document_ids: z.array(RedactionDocumentIdField).min(1).optional(),
  include_exemption_summary: z.boolean().optional().default(false),
})
const StartRedactionRequestExportViewResponseData = z.object({
  redaction_request_export_id: RedactionRequestExportIdField.uuid(),
})
const RedactionCandidateStatusUpdate = z.object({
  redaction_candidate_id: RedactionCandidateIdField.uuid(),
  status: PendingEnum,
  bbox_override: Bbox.nullish(),
  char_range_override: CharRange.nullish(),
})
const UpdateRedactionCandidatesViewRequestData = z.object({
  user_id: EndUserIdField.uuid(),
  organization_identity_id: IdentityIdField.uuid(),
  candidate_status_updates: z.array(RedactionCandidateStatusUpdate),
})
const UpdateRedactionCandidatesViewResponseData = z.object({
  candidates: z.array(RedactionCandidate),
})

export const schemas = {
  EndUserIdField,
  IdentityIdField,
  AIPhoneLineTypeEnum,
  AIPhoneLineOwnerEnum,
  AIPhoneLineConfigIdField,
  AIPhoneLineIdField,
  EnrichedAIPhoneLineConfig,
  TwilioAccountConfigIdField,
  PhonicIntelligenceLevelEnum,
  PhonicMultilingualModeEnum,
  PhonicBackgroundNoiseEnum,
  PhonicPronunciationDictionaryEntry,
  AddAIPhoneLineViewRequestData,
  EmptyResponseSerializerEmptyResponse,
  WorkflowIdField,
  AddAIProcessSourceViewRequestData,
  AIInteractionTypeEnum,
  DocumentTemplateIdField,
  AddAISkillViewRequestData,
  AISkillIdField,
  DraftEnum,
  OrganizationIdField,
  AISkill,
  AddAISkillViewResponseData,
  AIUsagePolicySnapshotIdField,
  InteractionDirectionEnum,
  BlockEnum,
  AddAIUsagePolicyViewRequestData,
  AIUsagePolicyIdField,
  AIUsagePolicy,
  AddAIUsagePolicyViewResponseData,
  AddAIUsagePolicySnapshotViewRequestData,
  AIUsagePolicySnapshot,
  AddAIUsagePolicySnapshotViewResponseData,
  SourceAccessTypeEnum,
  FolderIdField,
  AddFAQSourceViewRequestData,
  AddFolderViewRequestData,
  Folder,
  AddFolderViewResponseData,
  AddGISLayerSourceViewRequestData,
  GISLayerSourceIdField,
  AddGISLayerSourceViewResponseData,
  ExpectedAnswerSourceEnum,
  ScenarioQuery,
  AddScenarioViewRequestData,
  AITestScenarioIdField,
  AddScenarioViewResponseData,
  TransferLineSourceConfigIdField,
  TransferLineSourceConfig,
  AddTransferLineSourceViewRequestData,
  AITestRunIdField,
  ArchiveTestRunViewRequestData,
  AIInteractionIdField,
  CaseIdField,
  AssociateAIInteractionCaseViewRequestData,
  AIScenarioCreate,
  BulkAddScenariosViewRequestData,
  AITestQueryIdField,
  AITestQueryInfo,
  AITestScenarioInfo,
  BulkAddScenariosViewResponseData,
  PageSourceIdField,
  BulkArchivePageSourcesViewRequestData,
  BulkArchivePageSourcesViewResponseData,
  FAQSourceIdField,
  BulkMovePageSourcesViewRequestData,
  BulkMovePageSourcesViewResponseData,
  FormStepIdField,
  CancelFormGenerationViewRequestData,
  CustomCategory,
  CheckCategoryEvaluationConfigViewRequestData,
  CheckCategoryEvaluationConfigViewResponseData,
  ConnectArcGISViewRequestData,
  ElevenLabsConversationInitiationViewRequestData,
  VoiceDynamicVariables,
  ELPromptConfig,
  ELVoiceConfig,
  ELAgentConfig,
  ELConversationConfigOverride,
  ElevenLabsConversationInitiationViewResponseData,
  ConvertDocumentToAIUsagePoliciesViewRequestData,
  ConvertedAIUsagePolicy,
  ConvertDocumentToAIUsagePoliciesViewResponseData,
  WebsiteTypeEnum,
  CrawlDomainViewRequestData,
  CrawlTaskIdField,
  CrawlTaskLogsViewRequestData,
  SourceIdField,
  SourceActionEnum,
  CrawlStatusEnum,
  QuerySourcesLog,
  CrawlTaskLogsViewResponseData,
  CreateCustomCategoryEvaluationConfigViewRequestData,
  EvaluationConfigIdField,
  CreateCustomCategoryEvaluationConfigViewResponseData,
  DeleteAIAssistantInteractionViewRequestData,
  AIEmailIdField,
  DeleteAIEmailViewRequestData,
  DeleteAIPhoneLineViewRequestData,
  DeleteAISkillViewRequestData,
  DeleteAllAISkillsViewRequestData,
  DeleteAllAIUsagePoliciesViewRequestData,
  DeleteAllAssistantChatsViewRequestData,
  ChunkIdField,
  DeleteChunkViewRequestData,
  DeleteCrawlTaskViewRequestData,
  DeleteFAQSourceViewRequestData,
  DeleteFolderViewRequestData,
  DeleteFolderViewResponseData,
  DeleteGISLayerSourceViewRequestData,
  TransferLineSourceIdField,
  DeleteTransferLineSourceViewRequestData,
  DevinAskViewRequestData,
  DevinAskViewResponseData,
  DomainSanityCheckViewRequestData,
  DomainSanityCheckViewResponseData,
  OnlyEnum,
  AgentToolEnum,
  WorkspaceIdField,
  AIInteractionFilter,
  DownloadAIInteractionsViewRequestData,
  DownloadAIInteractionsViewResponseData,
  DownloadAssistantInteractionsViewRequestData,
  DownloadAssistantInteractionsViewResponseData,
  DownloadFAQSourcesViewRequestData,
  DownloadFAQSourcesViewResponseData,
  CsvEnum,
  DownloadTestRunViewRequestData,
  DownloadTestRunViewResponseData,
  DownloadTransferLineSourcesViewRequestData,
  DownloadTransferLineSourcesViewResponseData,
  EditAIPhoneLineViewRequestData,
  AIProcessSourceIdField,
  EditAIProcessSourceViewRequestData,
  Live1Enum,
  EditAISkillViewRequestData,
  EditAISkillViewResponseData,
  EditAIUsagePolicyViewRequestData,
  EditAIUsagePolicyViewResponseData,
  EditAIUsagePolicySnapshotViewRequestData,
  EditAIUsagePolicySnapshotViewResponseData,
  EditChunkViewRequestData,
  EditCrawlTaskViewRequestData,
  EditFAQSourceViewRequestData,
  EditFolderViewRequestData,
  EditFolderViewResponseData,
  EditGISLayerSourceViewRequestData,
  EditOrganizationAIRetentionSettingsViewRequestData,
  OrganizationAISettingsIdField,
  GeoPoint,
  AIChatSizeEnum,
  SearchSuggestion,
  ProcessIdField,
  Active1Enum,
  ProcessVerificationEnum,
  AdHocTargetIdField,
  Workflow,
  EnrichedOrganizationAISettings,
  EditOrganizationAIRetentionSettingsViewResponseData,
  SearchLayoutTypeEnum,
  SearchSuggestionStyleEnum,
  EditOrganizationAISettingsViewRequestData,
  EditOrganizationAISettingsViewResponseData,
  EditPageSourceViewRequestData,
  EditScenarioViewRequestData,
  EditTransferLineSourceViewRequestData,
  APIConnectionIdField,
  ElevenLabsExternalAPICallViewRequestData,
  ExternalAPICallResult,
  ElevenLabsExternalAPICallViewResponseData,
  GenerateAssistantResponseViewRequestData,
  ResponseStreamIdField,
  GenerateAssistantResponseViewResponseData,
  GenerateFormFromPDFViewRequestData,
  GenerateFormFromPDFViewResponseData,
  ConstituentAIInteractionTypeEnum,
  GenerateResponseViewRequestData,
  GenerateResponseViewResponseData,
  GetAIInteractionCountViewRequestData,
  GetAIInteractionCountViewResponseData,
  AccessReasonEnum,
  GetAIInteractionPIITranscriptViewRequestData,
  EnrichedAttachment,
  QueryIdField,
  AppliedUsagePolicy,
  AppliedUsagePolicies,
  EnrichedQuery,
  TransferLineSourceConfigInfo,
  TransferLineSourceHolidayInfo,
  SourceDisplayLink,
  CaseDisplayLink,
  ContactDisplayLink,
  CreatedSkill,
  ResponseIdField,
  ResponseTypeEnum,
  EnrichedResponse,
  AgentActionTypeEnum,
  AgentAction,
  ToolCallIdField,
  ToolCallStatusEnum,
  EnrichedToolCall,
  VoiceCallStatusEnum,
  EnrichedVoiceCallLog,
  TextChannelEnum,
  EnrichedSmsTextLog,
  MassCommunicationIdField,
  MassCommContext,
  AgentTraceNodeTypeEnum,
  AgentTraceNode,
  AIInteractionEvaluation,
  AIInteractionTranscript,
  GetAIInteractionPIITranscriptViewResponseData,
  GetAIInteractionTranscriptViewRequestData,
  GetAIInteractionTranscriptViewResponseData,
  GetAIProcessSourceViewRequestData,
  AIProcessSource,
  GetAIProcessSourceViewResponseData,
  GetAIProcessSourcesCountViewRequestData,
  GetAIProcessSourcesCountViewResponseData,
  GetArcGISLayersViewRequestData,
  GISLayerOption,
  GetArcGISLayersViewResponseData,
  GetAssistantInteractionCountViewRequestData,
  GetAssistantInteractionCountViewResponseData,
  GetCivicPlusFoldersViewRequestData,
  GetCivicPlusFoldersViewResponseData,
  GetConnectedResourceCountsViewRequestData,
  GetConnectedResourceCountsViewResponseData,
  GetEvaluationCategoriesViewRequestData,
  GetEvaluationCategoriesViewResponseData,
  GetFAQSourcesCountViewRequestData,
  GetFAQSourcesCountViewResponseData,
  GetFormGenerationStatusViewRequestData,
  GetFormGenerationStatusViewResponseData,
  GetGISLayerPolygonsViewRequestData,
  GISFeatureData,
  GetGISLayerPolygonsViewResponseData,
  GetGISLayerPropertiesViewRequestData,
  GISProperty,
  GetGISLayerPropertiesViewResponseData,
  GetGISLayerSourceViewRequestData,
  EnrichedGISLayerSource,
  GetGISLayerSourceViewResponseData,
  GetGISLayerSourcesCountViewRequestData,
  GetGISLayerSourcesCountViewResponseData,
  GISPolygonIdField,
  GetGISPolygonsViewRequestData,
  GetGISPolygonsViewResponseData,
  GetGpkgLayersViewRequestData,
  GetGpkgLayersViewResponseData,
  GetInteractionCategoryUpdateStatusViewRequestData,
  GetInteractionCategoryUpdateStatusViewResponseData,
  GetPageSourceViewRequestData,
  EnrichedChunk,
  ExtractedEntityIdField,
  EnrichedExtractedEntity,
  EnrichedPageSourceTag,
  EnrichedPageSource,
  GetPageSourceViewResponseData,
  GetPageSourcesCountViewRequestData,
  GetPageSourcesCountViewResponseData,
  GetSupportedDocumentTypesViewRequestData,
  SupportedDocumentTypeEnum,
  GetSupportedDocumentTypesViewResponseData,
  GetTransferLineSourcesCountViewRequestData,
  GetTransferLineSourcesCountViewResponseData,
  GetWebsiteTypeViewRequestData,
  GetWebsiteTypeViewResponseData,
  ElevenLabsGISLookupViewRequestData,
  SourceTypeEnum,
  GISFeatureInfo,
  NoMatchingPointEnum,
  GISLookupResult,
  ElevenLabsGISLookupViewResponseData,
  ImportGISPolygonsViewRequestData,
  ImportGISPolygonsViewResponseData,
  UploadedDocument,
  IngestUploadedDocumentsViewRequestData,
  IngestUploadedDocumentsViewResponseData,
  InitializeAIViewResponseData,
  InitializeAIViewRequestData,
  ListActiveCallsViewRequestData,
  WarmHandoffContact,
  Pending5Enum,
  WarmHandoffData,
  ActiveCallContactRecord,
  ActiveCall,
  ListActiveCallsViewResponseData,
  ListAIEmailsViewRequestData,
  MessageTemplateIdField,
  AIEmail,
  ListAIEmailsViewResponseData,
  ListAIInteractionEvaluationEnumsViewRequestData,
  InteractionCategoryTypeEnum,
  UserSentimentEnum,
  InteractionRatingTypeEnum,
  ResponseOutcomeEnum,
  AIInteractionEvaluationEnums,
  ListAIInteractionEvaluationEnumsViewResponseData,
  ListAIInteractionSummariesViewRequestData,
  AIInteractionRoleEnum,
  TranscriptMessage,
  AIInteractionLogStats,
  AIInteractionSummary,
  ListAIInteractionSummariesViewResponseData,
  ListAIPhoneLinesViewRequestData,
  AIPhoneLine,
  ListAIPhoneLinesViewResponseData,
  ListAIProcessSourcesViewRequestData,
  ListAIProcessSourcesViewResponseData,
  ListAISkillsViewRequestData,
  ListAISkillsViewResponseData,
  ListAIUsagePoliciesViewRequestData,
  ListAIUsagePoliciesViewResponseData,
  ListAIUsagePolicySnapshotsViewRequestData,
  ListAIUsagePolicySnapshotsViewResponseData,
  ListAssistantInteractionsViewRequestData,
  AssistantInteractionContact,
  ListAssistantInteractionsViewResponseData,
  ListAssistantToolsViewRequestData,
  ListAssistantToolsViewResponseData,
  CrawlTaskSortTypeEnum,
  SortDirectionEnum,
  ListCrawlTasksViewRequestData,
  CrawlTask,
  ListCrawlTasksViewResponseData,
  ListFAQSourcesViewRequestData,
  FAQSource,
  ListFAQSourcesViewResponseData,
  SourcewebsiteEnum,
  ListFoldersViewRequestData,
  ListFoldersViewResponseData,
  ListGISLayerSourcesViewRequestData,
  ListGISLayerSourcesViewResponseData,
  PageSortTypeEnum,
  ListPageSourcesViewRequestData,
  PageSourceSummary,
  ListPageSourcesViewResponseData,
  HealthCheckOutcomeEnum,
  ListRunResponsesViewRequestData,
  AITestResponseIdField,
  EvaluationIssueLabelEnum,
  EvaluationIssue,
  EvaluationMetricResult,
  AITestResponseInfo,
  ListRunResponsesViewResponseData,
  ListScenarioTemplatesViewRequestData,
  Organization,
  OrganizationTemplate,
  ListScenarioTemplatesViewResponseData,
  ListSuggestionsViewRequestData,
  SuggestionIdField,
  Suggestion,
  ListSuggestionsViewResponseData,
  ListTestRunsViewRequestData,
  AITestRunConfig,
  AITestRunTriggerTypeEnum,
  AITestRun,
  AITestRunWithStats,
  ListTestRunsViewResponseData,
  ListScenariosViewRequestData,
  ListScenariosViewResponseData,
  ListTransferLineSourcesViewRequestData,
  TransferLineSource,
  ListTransferLineSourcesViewResponseData,
  ListTransferredNumbersViewRequestData,
  TransferredNumber,
  ListTransferredNumbersViewResponseData,
  ListVoicesViewRequestData,
  ELVoice,
  ListVoicesViewResponseData,
  LoadScenariosFromTemplateViewRequestData,
  LoadScenariosFromTemplateViewResponseData,
  ELToolCall,
  ELToolResult,
  ELConversationTurn,
  ELTranscriptData,
  ElevenLabsLogConversationViewRequestData,
  MarkAIInteractionAsTestViewRequestData,
  PhonicConversationInitiationViewRequestData,
  PhonicConversationInitiationViewResponseData,
  PhonicEventsViewRequestData,
  PhonicExternalAPICallViewRequestData,
  PhonicExternalAPICallViewResponseData,
  PhonicGISLookupViewRequestData,
  PhonicGISLookupViewResponseData,
  ListPhonicVoicesViewRequestData,
  PhonicVoice,
  ListPhonicVoicesViewResponseData,
  PhonicRetrieveSourcesViewRequestData,
  VoiceChunkInfo,
  VoicePageInfo,
  VoiceTransferLineInfo,
  VoiceProcessInfo,
  ChunkFilters,
  RetrievalLogMetrics,
  PhonicRetrieveSourcesViewResponseData,
  PhonicSendSMSViewRequestData,
  PhonicSendSMSViewResponseData,
  PhonicTransferCallViewRequestData,
  PhonicTransferCallViewResponseData,
  PreviewBulkArchivePageSourcesViewRequestData,
  PreviewBulkArchivePageSourcesViewResponseData,
  PreviewVoiceRequest,
  RechunkPageSourceViewRequestData,
  RechunkPageSourceViewResponseData,
  ReextractPageSourceViewRequestData,
  ReextractPageSourceViewResponseData,
  RemoveGISPolygonsViewRequestData,
  RemoveScenarioViewRequestData,
  ResolveAIInteractionOrganizationViewRequestData,
  ResolveAIInteractionOrganizationViewResponseData,
  ElevenLabsRetrieveSourcesViewRequestData,
  ElevenLabsRetrieveSourcesViewResponseData,
  ScrapePagesViewRequestData,
  ElevenLabsSendSMSViewRequestData,
  ElevenLabsSendSMSViewResponseData,
  SetResponseFeedbackImpressionViewRequestData,
  SetSuggestionsViewRequestData,
  SetSuggestionsViewResponseData,
  StartTestRunViewRequestData,
  StartTestRunViewResponseData,
  SubmitResponseFeedbackViewRequestData,
  SuggestCrawlConfigViewRequestData,
  SuggestCrawlConfigViewResponseData,
  SuperGetAIInteractionSystemPromptViewRequestData,
  SuperGetAIInteractionSystemPromptViewResponseData,
  SuperGetAIInteractionTranscriptViewRequestData,
  SuperGetAIInteractionTranscriptViewResponseData,
  TranscribeAssistantAudioViewRequestData,
  TranscribeAssistantAudioViewResponseData,
  ElevenLabsTransferCallViewRequestData,
  ElevenLabsTransferCallViewResponseData,
  TranslateTextViewRequestData,
  TranslateTextViewResponseData,
  UpdateAIInteractionTitleViewRequestData,
  UpdateAIInteractionTitleViewResponseData,
  UpdateCrawlTaskViewRequestData,
  UpdateCustomCategoryEvaluationConfigViewRequestData,
  UpdateCustomCategoryEvaluationConfigViewResponseData,
  UpdateInteractionCategoriesViewRequestData,
  TaskIdField,
  UpdateInteractionCategoriesViewResponseData,
  UpdateTestRunViewRequestData,
  UploadAssistantAttachmentViewRequestData,
  UploadAssistantAttachmentViewResponseData,
  UpsertAIEmailViewRequestData,
  UpsertAIEmailViewResponseData,
  IdentifiedWorkflowRowIndexSerializerIdentifiedWorkflowRowIndex,
  BuildIdentifiedWorkflowsViewRequestData,
  BuildIdentifiedWorkflowsViewResponseData,
  ClearIdentifiedWorkflowsViewRequestData,
  ClearIdentifiedWorkflowsViewResponseData,
  PricingTierEnum,
  CostDriverEnum,
  ExportLLMCostViewRequestData,
  ExportLLMCostViewResponseData,
  GetChannelVolumeViewRequestData,
  ChannelVolumeSeries,
  GetChannelVolumeViewResponseData,
  GetCrawlTaskDashboardViewRequestData,
  LightweightQuerySourcesLog,
  CrawlTaskDailyCounts,
  CrawlTaskSummaryStats,
  GetCrawlTaskDashboardViewResponseData,
  GetFirecrawlConcurrencyViewRequestData,
  GetFirecrawlConcurrencyViewResponseData,
  GetFirecrawlConcurrencyHistoryViewRequestData,
  FirecrawlConcurrencyLogIdField,
  FirecrawlConcurrencyLog,
  GetFirecrawlConcurrencyHistoryViewResponseData,
  FrontDeskInteractionTypeEnum,
  GetFrontDeskDataRequestData,
  HandledInteractions,
  GetFrontDeskDataResponseData,
  GetHealthCheckSummaryViewRequestData,
  GetHealthCheckSummaryViewResponseData,
  GetIdentifiedWorkflowsViewRequestData,
  IdentifiedWorkflowRowSerializerIdentifiedWorkflowRow,
  IdentifiedWorkflowTabSerializerIdentifiedWorkflowTab,
  GetIdentifiedWorkflowsViewResponseData,
  GetIntegrationsViewRequestData,
  ConnectionTypeEnum,
  ActiveIntegration,
  SuggestedIntegration,
  GetIntegrationsViewResponseData,
  GetInteractionDashboardStatsViewRequestData,
  InteractionTypeBreakdown,
  InteractionBucket,
  OrgInteractionSummary,
  DurationBucket,
  DurationStats,
  HourOfDayRow,
  InteractionDashboardStats,
  GetInteractionDashboardStatsViewResponseData,
  GetKnowledgeBaseQualityViewRequestData,
  GetKnowledgeBaseQualityViewResponseData,
  GetKPIsViewRequestData,
  GetKPIsViewResponseData,
  GetLLMCostDashboardStatsViewRequestData,
  LLMTokenTotals,
  LLMCostBucket,
  LLMCostTimeRow,
  LLMCostOrganizationRow,
  LLMCostModelRow,
  LLMCostInteractionRow,
  QuerySourcesLogIdField,
  LLMCostCrawlRow,
  AgenticImplementationLogIdField,
  LLMCostDevinSessionRow,
  LLMCostFilterOptions,
  LLMCostDashboardStats,
  GetLLMCostDashboardStatsViewResponseData,
  GetOrgWorkflowScorecardViewRequestData,
  GetOrgWorkflowScorecardViewResponseData,
  SankeyInteractionTypeEnum,
  ViewTypeEnum,
  GetOrganizationAIInteractionFlowsRequestData,
  AIInteractionFlow,
  GetOrganizationAIInteractionFlowsResponseData,
  GetOrganizationAIInteractionStatsRequestData,
  GetOrganizationAIInteractionStatsResponseData,
  CaseTaskEnum,
  OverdueEnum,
  AssignedToYouEnum,
  GetTasksViewRequestData,
  BlockInstanceIdField,
  ItemIdField,
  StepFormEnum,
  HomePageTask,
  GetTasksViewResponseData,
  GetToolCallDashboardStatsViewRequestData,
  ToolCallBucket,
  ToolCallDashboardStats,
  GetToolCallDashboardStatsViewResponseData,
  GetTopCategoriesViewRequestData,
  TopCategory,
  GetTopCategoriesViewResponseData,
  GetTopDomainsViewRequestData,
  GetTopDomainsViewResponseData,
  GetWorkflowPerformanceViewRequestData,
  BlockIdField,
  WorkflowPerformanceItem,
  GetWorkflowPerformanceViewResponseData,
  GetWorkflowUsageDashboardStatsViewRequestData,
  MonthlyCaseCountSerializerMonthlyCaseCount,
  WorkspaceWorkflowUsageSerializerWorkspaceWorkflowUsage,
  OrgWorkflowUsageSerializerOrgWorkflowUsage,
  ProcessStatusEnum,
  WorkflowComplexitySerializerWorkflowComplexity,
  WorkflowUsageStatsSerializerWorkflowUsageStats,
  GetWorkflowUsageDashboardStatsViewResponseData,
  ListCrawlTasksMetricsViewRequestData,
  ListCrawlTasksMetricsViewResponseData,
  ListToolCallSummariesViewRequestData,
  ToolCallSummary,
  ListToolCallSummariesViewResponseData,
  RequestAIAnalyticsReportViewRequestData,
  RequestAIAnalyticsReportViewResponseData,
  SetWorkflowGeneralIntakeViewRequestData,
  SetWorkflowGeneralIntakeViewResponseData,
  UploadIdentifiedWorkflowsViewRequestData,
  UploadIdentifiedWorkflowsViewResponseData,
  AddCaseMemberViewRequestData,
  FormIdField,
  InputIdField,
  CommentThreadIdField,
  AddCommentViewRequestData,
  CommentIdField,
  Comment,
  AddCommentViewResponseData,
  ReviewCommentThreadIdField,
  AddReviewCommentViewRequestData,
  ReviewCommentIdField,
  ReviewComment,
  AddReviewCommentViewResponseData,
  AgenticImplementationAttachmentSerializerAgenticImplementationAttachment,
  AppendAgenticImplementationMessageViewRequestData,
  AgenticImplementationMessageKindEnum,
  AgenticImplementationMessageSerializerAgenticImplementationMessage,
  AppendAgenticImplementationMessageViewResponseData,
  AttachProcessViewRequestData,
  BeginReviewViewRequestData,
  ReviewIdField,
  AcceptEnum,
  Review,
  BeginReviewViewResponseData,
  InputStepIdField,
  BulkCreateInputExpressionsViewRequestData,
  ExpressionIdField,
  ValueTypeEnum,
  CaseIdentifierTypeEnum,
  CustomExpressionNameEnum,
  OperatorTypeEnum,
  ValueIdField,
  ReviewBlockIdField,
  Expression,
  BulkCreateInputExpressionsViewResponseData,
  StatusEnum,
  BulkEditCasesViewRequestData,
  BulkEditWorkflowsViewRequestData,
  DatetimeStep,
  IdentityStep,
  NumberStep,
  FileStep,
  SignatureStep,
  LocationStep,
  InputStepSub0,
  BoolValueIdField,
  BoolValue0,
  DatetimeValueIdField,
  DatetimeValue0,
  FileValueIdField,
  FileValue0,
  IdentityValueIdField,
  IdentityValue0,
  LocationValueIdField,
  LocationIdField,
  AddressIdField,
  LocationValue0,
  NumberValueIdField,
  NumberValue0,
  SignatureValueIdField,
  SignatureValue0,
  TextValueIdField,
  TextValue0,
  Value0,
  InputStepChoice1,
  InputStyleEnum,
  InputSubstep2,
  SectionSubstep0,
  TagIdField,
  CalculatedTag,
  FormStep0,
  IdentitySnapshot,
  ValueCreate,
  FormNodeCreateTypeEnum,
  FormDataNode,
  FormNodeCreate,
  CalculateFormDataHashViewRequestData,
  CalculateFormDataHashViewResponseData,
  GetCaseApplyStepperStageStatusesRequestData,
  ProcessStageIdField,
  ProcessStage,
  CompleteEnum,
  GetCaseApplyStepperStageStatusesResponseData,
  GetActiveTasksViewRequestData,
  EagerEnum,
  BlockInstance,
  ActiveTask,
  GetActiveTasksViewResponseData,
  GetCaseFeedActivityViewRequestData,
  NotificationIdField,
  GroupIdField,
  Notification,
  CaseStatusChangeIdField,
  CaseStatusChange,
  CaseAutomationEventLogIdField,
  AddcasememberEnum,
  CaseAutomationEventLog,
  CaseEventLogIdField,
  AddCaseLocationEnum,
  CaseEventLog,
  ActionIdField,
  Form,
  BillIdField,
  ContactIdentifierIdField,
  BillStepIdField,
  Bill,
  ChargeIdField,
  Charge,
  Task,
  Item,
  LicenseIdField,
  LicenseTemplateIdField,
  ActiveEnum,
  License,
  LiveEnum,
  LicenseTemplate,
  Tag,
  GetCaseFeedActivityViewResponseData,
  GetCaseFeedContactRecordInfoViewRequestData,
  HandleIdField,
  TargetIdField,
  Case,
  CaseIdentity,
  ContactIdField,
  Contact,
  Address0,
  TaggingIdField,
  Tagging,
  GetCaseFeedContactRecordInfoViewResponseData,
  GetCaseFeedContactRecordPartiesViewRequestData,
  AddressBase,
  Party,
  GetCaseFeedContactRecordPartiesViewResponseData,
  GetCaseFeedDocumentsViewRequestData,
  AnnotatedDocumentIdField,
  DocumentStepIdField,
  AnnotatedDocument,
  StepIdField,
  HarbourEnum,
  DocumentTemplate,
  DocumentStep,
  SectionStepIdField,
  InputStep,
  SectionIdField,
  Input,
  Value,
  FileValue,
  LicenseDocumentIdField,
  LicenseDocumentTemplateIdField,
  LicenseDocument,
  LicenseDocumentTemplate,
  RedactionRequestExportIdField,
  RedactionRequestIdField,
  Pending4Enum,
  RedactionDocumentIdField,
  RedactionRequestExport,
  Pending3Enum,
  RedactionRequest,
  RedactionBlockIdField,
  RedactionBlock,
  GetCaseFeedDocumentsViewResponseData,
  GetCaseFeedInfoViewRequestData,
  GetCaseFeedInfoViewResponseData,
  GetCaseFeedInteractionsViewRequestData,
  ViasiteEnum,
  Message,
  GetCaseFeedInteractionsViewAudioMessage,
  MessageIdField,
  Log,
  MessageFile,
  EmailTypeEnum,
  Email,
  EmailParticipantTypeEnum,
  EmailIdField,
  EmailAddressIdField,
  EmailParticipant,
  DomainIdField,
  EmailAddress,
  GetCaseFeedInteractionsViewResponseData,
  GetCaseFeedPartiesViewRequestData,
  GetCaseFeedPartiesViewResponseData,
  GetCaseFeedWorkflowViewRequestData,
  ConnectorInstanceIdField,
  ConnectorIdField,
  ConnectorInstance,
  FormEnum,
  AlwaysEnum,
  AnyInboundEnum,
  Block,
  Connector,
  FormStep,
  BillStep,
  CaseStatusAutomationIdField,
  CaseStatusAutomation,
  TagAutomationIdField,
  TagAutomationActionEnum,
  TagAutomationTargetEnum,
  TagAutomation,
  CaseRoleAutomationIdField,
  SetRequesterEnum,
  CaseRoleAutomation,
  CommunicationAutomationIdField,
  ContactEmailIdField,
  CommunicationAutomationRecipientIdField,
  ToEnum,
  CommunicationAutomationRecipient,
  CommunicationAutomation,
  DemoBlockIdField,
  DemoBlock,
  LicenseBlockIdField,
  NewEnum,
  LicenseBlock,
  LicenseBlockInstanceIdField,
  LicenseBlockInstance,
  PaymentIdField,
  PaymentMethodEnum,
  PaymentStatusEnum,
  PaymentMediumEnum,
  PayerEnum,
  PayoutIdField,
  Payment,
  BlockInstanceMemberIdField,
  InteractionIdField,
  BlockInstanceMember,
  GetCaseFeedWorkflowViewResponseData,
  CompleteRedactionBlockViewRequestData,
  CompleteRedactionBlockViewResponseData,
  CompleteReviewViewRequestData,
  CompleteReviewViewResponseData,
  CopyExpressionViewRequestData,
  CopyExpressionViewResponseData,
  CopyProcessViewRequestData,
  CopyProcessViewResponseData,
  CopySectionSubStepViewRequestData,
  CopySectionSubStepViewResponseData,
  CopyWorkflowViewRequestData,
  CopyWorkflowViewResponseData,
  ContactRecordIdField,
  CreateCaseViewRequestData,
  Identity0,
  Location0,
  ContactEntryIdField,
  ContactEmail0,
  NoteIdField,
  Note0,
  Identity2,
  Case2,
  CreateCaseViewResponseData,
  CreateCasesTabViewRequestData,
  CasesTabIdField,
  CreateCasesTabViewResponseData,
  CreateDraftVersionViewRequestData,
  CreateDraftVersionViewResponseData,
  ExpressionTypeEnum,
  CreateExpressionViewRequestData,
  CreateExpressionViewResponseData,
  FormStyleElementTypeEnum,
  CreateFormStyleElementViewRequestData,
  FormStyleElementIdField,
  FormStyleElement,
  CreateFormStyleElementViewResponseData,
  CreateInputStepViewRequestData,
  CreateInputStepViewResponseData,
  Attachment,
  CreateMessageViewRequestData,
  CreateMessageViewResponseData,
  CreateProcessStageViewRequestData,
  CreateProcessStageViewResponseData,
  CreateRelationViewRequestData,
  CreateReviewCommentThreadViewRequestData,
  ReviewCommentThread,
  CreateReviewCommentThreadViewResponseData,
  CreateSectionStepViewRequestData,
  CreateSectionStepViewResponseData,
  CreateTaskViewRequestData,
  CreateTaskViewResponseData,
  CreateTestCaseViewRequestData,
  CreateTestCaseViewResponseData,
  CreateValueViewRequestData,
  CreateValueViewResponseData,
  CreateVoicemailProcessViewRequestData,
  CreateVoicemailProcessViewResponseData,
  CreateWorkflowViewRequestData,
  CreateWorkflowViewResponseData,
  DeleteAssignmentViewRequestData,
  DeleteBillStepViewRequestData,
  DeleteCaseViewRequestData,
  DeleteCasesTabViewRequestData,
  DeleteCommentViewRequestData,
  DeleteExpressionsViewRequestData,
  DeleteFormStepViewRequestData,
  DeleteFormStyleElementViewRequestData,
  DeleteInputSubstepViewRequestData,
  DeleteProcessStageViewRequestData,
  DeleteSectionSubstepViewRequestData,
  DeleteStepAssignmentViewRequestData,
  DeleteTemplateViewRequestData,
  NumberInputComparisonOperatorEnum,
  NumberComparison,
  ShortTextEnum,
  CaseFilter,
  DownloadCasesViewRequestData,
  DownloadCasesViewResponseData,
  DraftCommunicationMessageViewRequestData,
  DraftCommunicationRecipient,
  DraftCommunicationMessageViewResponseData,
  ChargeSubStepCreate,
  EditBillStepViewRequestData,
  ChargeSubstepIdField,
  ChargeSubstep,
  BillStep1,
  EditBillStepViewResponseData,
  EditCaseViewRequestData,
  EditCasesTabViewRequestData,
  DatetimeStepIdField,
  EditDateTimeStepViewRequestData,
  EditDueDateViewRequestData,
  EditExpressionViewRequestData,
  FileStepIdField,
  EditFileStepViewRequestData,
  EditFormStepViewRequestData,
  EditFormStyleElementViewRequestData,
  EditFormStyleElementViewResponseData,
  IdentityStepIdField,
  EditIdentityStepViewRequestData,
  InputStepChoiceData,
  EditInputStepViewRequestData,
  LocationStepIdField,
  EditLocationStepViewRequestData,
  NumberStepIdField,
  EditNumberStepViewRequestData,
  EditProcessViewRequestData,
  EditProcessViewResponseData,
  EditProcessStageViewRequestData,
  EditSectionSubStepViewRequestData,
  SignatureStepIdField,
  EditSignatureStepViewRequestData,
  EditSignatureStepViewResponseData,
  EditStepViewRequestData,
  EditTaskViewRequestData,
  ExportProcessJsonViewRequestData,
  ExportProcessJsonViewResponseData,
  GenerateAICaseSummaryViewRequestData,
  GenerateAICaseSummaryViewResponseData,
  GenerateAIEmailSummaryViewRequestData,
  GenerateAIEmailSummaryViewResponseData,
  GenerateDocumentViewRequestData,
  GenerateDocumentViewResponseData,
  GetCaseViewRequestData,
  GetCaseViewResponseData,
  GetCaseCountViewRequestData,
  GetCaseCountViewResponseData,
  GetCaseIdViewRequestData,
  CaseAccessModeEnum,
  GetCaseIdViewResponseData,
  GetCaseOwnerViewRequestData,
  GetCaseOwnerViewResponseData,
  CaseSortEnum,
  GetCasesTableViewRequestData,
  CasesTableIdentity,
  CasesTableTag,
  CasesTableRow,
  GetCasesTableViewResponseData,
  GetFormStepCountViewRequestData,
  GetFormStepCountViewResponseData,
  FormStepTimeFilter,
  GetFormStepTimeViewRequestData,
  GetFormStepTimeViewResponseData,
  GetOrCreateAgenticImplementationLogViewRequestData,
  GetOrCreateAgenticImplementationLogViewResponseData,
  ProcessInfoArgs,
  GetProcessViewRequestData,
  Process3,
  GetProcessViewResponseData,
  GetWorkflowViewRequestData,
  PhoneNumberIdField,
  Process,
  GetWorkflowViewResponseData,
  GetWorkflowSummaryViewRequestData,
  GetWorkflowSummaryViewResponseData,
  ImportEmailViewRequestData,
  ImportEmailViewResponseData,
  InviteCaseMemberByEmailViewRequestData,
  ContactBookIdField,
  ProcessFilter,
  ListBaseProcessesViewRequestData,
  ListBaseProcessesViewResponseData,
  ListCalculatedInputsViewRequestData,
  InputCreate,
  ListCalculatedInputsViewResponseData,
  ListCaseExportFilesViewRequestData,
  SubmissionEnum,
  CaseExportFileSerializerCaseExportFile,
  ListCaseExportFilesViewResponseData,
  ListCaseMembersViewRequestData,
  ListCaseMembersViewResponseData,
  ListCasesViewRequestData,
  ListCasesViewResponseData,
  ListCasesTabsViewRequestData,
  CasesTab,
  ConfigTypeEnum,
  CasesConfig,
  CasesConfig1,
  CasesTab1,
  ListCasesTabsViewResponseData,
  ListCasesWithLocationViewRequestData,
  CaseWithLocation,
  ListCasesWithLocationViewResponseData,
  ListDocumentTemplatesViewRequestData,
  ListDocumentTemplatesViewResponseData,
  ListExpressionConstantsViewRequestData,
  ListExpressionConstantsViewResponseData,
  ListExpressionsViewRequestData,
  ListExpressionsViewResponseData,
  ListFormStepsViewRequestData,
  ListFormStepsViewResponseData,
  ListFormsViewRequestData,
  Form0,
  ListFormsViewResponseData,
  InputStepFilter,
  ListInputSubStepsViewRequestData,
  ListInputSubStepsViewResponseData,
  InputFilter,
  ListInputsViewRequestData,
  Input1,
  ListInputsViewResponseData,
  NotificationFilter,
  ListNotificationsViewRequestData,
  Notification1,
  ListNotificationsViewResponseData,
  ListProcessCasesViewRequestData,
  Bill0,
  ProcessCase3,
  ListProcessCasesViewResponseData,
  ListProcessSummariesViewRequestData,
  ProcessSummary,
  ListProcessSummariesViewResponseData,
  ListProcessesViewRequestData,
  Process0,
  ListProcessesViewResponseData,
  ListProcessesByIdViewRequestData,
  ListProcessesByIdViewResponseData,
  ListProcessesRestrictedViewRequestData,
  ListProcessesRestrictedViewResponseData,
  ListProcessesStatsViewRequestData,
  Process1,
  ListProcessesStatsViewResponseData,
  ListPublicReviewCommentThreadsViewRequestData,
  ListPublicReviewCommentThreadsViewResponseData,
  ListRelationsViewRequestData,
  RelationIdField,
  Relation,
  ListRelationsViewResponseData,
  ListReviewCommentThreadsViewRequestData,
  ListReviewCommentThreadsViewResponseData,
  ListReviewsViewRequestData,
  ListReviewsViewResponseData,
  ListSectionSubStepsViewRequestData,
  ListSectionSubStepsViewResponseData,
  ListSectionsViewRequestData,
  Section,
  ListSectionsViewResponseData,
  ListStepAssignmentsViewRequestData,
  StepNotificationIdField,
  StepNotification,
  ListStepAssignmentsViewResponseData,
  ListTasksViewRequestData,
  Task1,
  ListTasksViewResponseData,
  ListTemplatesViewRequestData,
  MessageTemplate0,
  ListTemplatesViewResponseData,
  ListValuesViewRequestData,
  ListValuesViewResponseData,
  ListWorkflowStatsViewRequestData,
  WorkflowStats,
  ListWorkflowStatsViewResponseData,
  ListWorkflowVersionsViewRequestData,
  ListWorkflowVersionsViewResponseData,
  ListWorkflowsViewRequestData,
  ListWorkflowsViewResponseData,
  NotifyArgs,
  NotifyViewRequestData,
  PerformAIPreSubmissionReviewViewRequestData,
  AIReviewComment,
  PerformAIPreSubmissionReviewViewResponseData,
  PhonicVoiceCreateCaseViewRequestData,
  PhonicVoiceCreateCaseViewResponseData,
  PhonicVoiceGetNextQuestionsViewRequestData,
  PhonicVoiceGetNextQuestionsViewResponseData,
  PhonicVoiceRecordAnswersViewRequestData,
  PhonicVoiceRecordAnswersViewResponseData,
  Variable,
  AutoMapDocumentTemplateFieldsViewRequestData,
  Mapping,
  AutoMapDocumentTemplateFieldsViewResponseData,
  APIEndpointIdField,
  CommandEnum,
  CreateAPIAutomationBlockViewRequestData,
  APIAutomationIdField,
  IntegrationParameterMappingIdField,
  IntegrationParameterMapping,
  IntegrationBlockMetadata,
  APIAutomation,
  CreateAPIAutomationBlockViewResponseData,
  CreateCommunicationBlockViewRequestData,
  CreateCommunicationBlockViewResponseData,
  CreateConnectorViewRequestData,
  CreateConnectorViewResponseData,
  CreateDemoBlockViewRequestData,
  CreateDemoBlockViewResponseData,
  CreateDocumentGenerationBlockViewRequestData,
  CreateDocumentGenerationBlockViewResponseData,
  CreateFormBlockViewRequestData,
  CreateFormBlockViewResponseData,
  CreateLicenseBlockViewRequestData,
  CreateLicenseBlockViewResponseData,
  CreatePaymentBlockViewRequestData,
  CreatePaymentBlockViewResponseData,
  CreateRedactionBlockViewRequestData,
  CreateRedactionBlockViewResponseData,
  ReviewFocusFieldIdField,
  ReviewFocusFieldInput,
  ReviewerRulesetIdField,
  ReviewerRulesetInput,
  CreateReviewBlockViewRequestData,
  ReviewFocusField,
  ReviewerRuleset,
  ReviewBlock,
  CreateReviewBlockViewResponseData,
  CreateRoleBlockViewRequestData,
  CreateRoleBlockViewResponseData,
  CreateStatusBlockViewRequestData,
  CreateStatusBlockViewResponseData,
  CreateTagBlockViewRequestData,
  CreateTagBlockViewResponseData,
  DeleteBlockViewRequestData,
  DeleteConnectorViewRequestData,
  DetectDocumentTemplateFieldsViewRequestData,
  DetectDocumentTemplateFieldsViewResponseData,
  ParameterMappingPayload,
  EditAPIAutomationBlockViewRequestData,
  EditAPIAutomationBlockViewResponseData,
  EditBlockViewRequestData,
  EditBlockViewResponseData,
  CommunicationRecipientPayload,
  EditCommunicationBlockViewRequestData,
  EditCommunicationBlockViewResponseData,
  EditConnectorViewRequestData,
  EditConnectorViewResponseData,
  EditDemoBlockViewRequestData,
  EditDemoBlockViewResponseData,
  EditDocumentGenerationBlockViewRequestData,
  EditDocumentGenerationBlockViewResponseData,
  EditDocumentTemplateViewRequestData,
  EditDocumentTemplateViewResponseData,
  EditLicenseBlockViewRequestData,
  EditLicenseBlockViewResponseData,
  EditRedactionBlockViewRequestData,
  EditRedactionBlockViewResponseData,
  EditReviewBlockViewRequestData,
  EditReviewBlockViewResponseData,
  EditRoleBlockViewRequestData,
  EditRoleBlockViewResponseData,
  EditStatusBlockViewRequestData,
  EditStatusBlockViewResponseData,
  EditTagBlockViewRequestData,
  EditTagBlockViewResponseData,
  GetOrganizationSourceEmailsViewRequestData,
  CommunicationSenderOption,
  GetOrganizationSourceEmailsViewResponseData,
  ListBlockConfigurationsViewRequestData,
  ListBlockConfigurationsViewResponseData,
  ListBlocksViewRequestData,
  ListBlocksViewResponseData,
  ListConnectorsViewRequestData,
  ListConnectorsViewResponseData,
  PreviewCommunicationBlockEmailViewRequestData,
  PreviewCommunicationBlockEmailViewResponseData,
  UploadDocumentTemplateViewRequestData,
  UploadDocumentTemplateViewResponseData,
  PublishLatestWorkflowVersionViewRequestData,
  PublishLatestWorkflowVersionViewResponseData,
  ResetDraftVersionViewRequestData,
  ResetDraftVersionViewResponseData,
  ResolveThreadViewRequestData,
  RestartAgenticImplementationLogViewRequestData,
  RestartAgenticImplementationLogViewResponseData,
  SaveOrSubmitFormViewRequestData,
  SearchCasesViewRequestData,
  SearchCasesViewResponseData,
  SetActionInReviewViewRequestData,
  SetCaseStatusViewRequestData,
  StepAssignmentCreate,
  SetStepAssignmentsViewRequestData,
  SetStepAssignmentsViewResponseData,
  StartProcessViewRequestData,
  StartProcessViewResponseData,
  ToggleApplicantIdentitySectionViewRequestData,
  UploadAgenticImplementationAttachmentViewRequestData,
  UploadAgenticImplementationAttachmentViewResponseData,
  CasesConfigUpsert,
  UpsertCasesConfigViewRequestData,
  UpsertTemplateViewRequestData,
  UpsertTemplateViewResponseData,
  ElevenLabsVoiceCreateCaseViewRequestData,
  LogIdField,
  ElevenLabsVoiceCreateCaseViewResponseData,
  ElevenLabsVoiceGetNextQuestionsViewRequestData,
  SectionResponseBlock,
  VoiceResponseData,
  QuestionResponseBlock,
  ElevenLabsVoiceRecordAnswersViewRequestData,
  ElevenLabsVoiceRecordAnswersViewResponseData,
  EmptyRequestSerializerEmptyRequest,
  ChangelogEntry,
  GetChangelogViewResponseData,
  AddAddressViewRequestData,
  AddAddressToContactRecordViewRequestData,
  AddAttributionToContactRecordViewRequestData,
  AddEmailToContactRecordViewRequestData,
  AddNoteToContactRecordViewRequestData,
  AddPhoneNumberViewRequestData,
  AddPhoneToContactRecordViewRequestData,
  AddTagToContactRecordViewRequestData,
  AddressImportConfig,
  ImportContactRecordsViewRequestData,
  ContactImportJobIdField,
  ImportContactRecordsViewResponseData,
  GetContactRecordImportHeaderViewRequestData,
  GetContactRecordImportHeaderViewResponseData,
  ListContactImportJobsViewRequestData,
  ContactImportJob,
  ListContactImportJobsViewResponseData,
  GISLayerPropertyPredicate,
  ContactFilter,
  CountContactsViewRequestData,
  CountContactsViewResponseData,
  CreateAddressViewRequestData,
  CreateAddressViewResponseData,
  AttributeIdField,
  AttributionCreate,
  CreateContactViewRequestData,
  CreateContactViewResponseData,
  CreateContactFromCaseViewRequestData,
  CreateContactFromCaseViewResponseData,
  CreateContactRecordViewRequestData,
  CreateContactRecordViewResponseData,
  CreateContactRelationshipViewRequestData,
  CreateEmailAddressViewRequestData,
  CreateLightweightIdentityViewRequestData,
  CreateLightweightIdentityViewResponseData,
  CreateRelatedContactViewRequestData,
  DeleteContactViewRequestData,
  DeleteContactRecordViewRequestData,
  DeleteContactRelationshipViewRequestData,
  DeleteRelatedContactViewRequestData,
  DownloadContactBookViewRequestData,
  DownloadContactBookViewResponseData,
  DownloadContactsViewRequestData,
  DownloadContactsViewResponseData,
  VerifyAdmin,
  VerifyApply,
  VerifyStandard,
  VerifyThread,
  AccessResourceTypeEnum,
  AccessActionEnum,
  VerifyAccessGrant,
  VerifyTypeEnum,
  VerifyLogin,
  VerifyOptions,
  EmailSignupViewRequestData,
  EmailSignupViewResponseData,
  GenerateAIContactSummaryViewRequestData,
  ContactInteractionTitle,
  GenerateAIContactSummaryViewResponseData,
  GetContactViewRequestData,
  ContactView,
  GetContactViewResponseData,
  GetContactCasesViewRequestData,
  CaseCard,
  GetContactCasesViewResponseData,
  GetContactEntryViewRequestData,
  ContactEntry0,
  GetContactEntryViewResponseData,
  GetContactEntryExpandedViewRequestData,
  PhoneNumber0,
  Attribute,
  Attribution,
  ContactEntry2,
  Person,
  GetContactEntryExpandedViewResponseData,
  GetContactEntryIdentityIdsViewRequestData,
  GetContactEntryIdentityIdsViewResponseData,
  GetContactOutboundCommsViewRequestData,
  MassCommunicationReceiptIdField,
  CommunicationChannelEnum,
  ReceiptStatusEnum,
  ContactOutboundComm,
  GetContactOutboundCommsViewResponseData,
  GetContactRecordIdentityViewRequestData,
  GetContactRecordIdentityViewResponseData,
  GetContactRouteByIdentityViewRequestData,
  GetContactRouteByIdentityViewResponseData,
  GetContactsHeaderViewRequestData,
  GetContactsHeaderViewResponseData,
  GetMyLocationViewRequestData,
  GetMyLocationViewResponseData,
  GetOrganizationLocationViewRequestData,
  GetOrganizationLocationViewResponseData,
  GetUserAccountAddressViewRequestData,
  GetUserAccountAddressViewResponseData,
  GetUserEmailAddressesViewRequestData,
  GetUserEmailAddressesViewResponseData,
  GetUserPhoneViewRequestData,
  GetUserPhoneViewResponseData,
  HandleInboundEmailViewRequestData,
  NameImportConfig,
  ImportContactsViewRequestData,
  ImportContactsViewResponseData,
  ListAddressesViewRequestData,
  ListAddressesViewResponseData,
  ListCaseAddressesViewRequestData,
  ListCaseAddressesViewResponseData,
  ListCaseEmailAddressesViewRequestData,
  ListCaseEmailAddressesViewResponseData,
  ListContactAddressesForMapViewRequestData,
  AddressLocation,
  ListContactAddressesForMapViewResponseData,
  ListContactBookTagsViewRequestData,
  ListContactBookTagsViewResponseData,
  ListContactEntriesViewRequestData,
  ListContactEntriesViewResponseData,
  ListContactRelationsViewRequestData,
  RelatedContactEntry2,
  ListContactRelationsViewResponseData,
  ListContactRelationshipsViewRequestData,
  RelatedContact,
  ListContactRelationshipsViewResponseData,
  ListEmailAddressesViewRequestData,
  ListEmailAddressesViewResponseData,
  EmailFilter,
  ListEmailsViewRequestData,
  EmailAddress0,
  EmailParticipant0,
  Email1,
  ListEmailsViewResponseData,
  ListOrganizationPhoneNumbersViewRequestData,
  ListOrganizationPhoneNumbersViewResponseData,
  ListPermittedContactBooksViewRequestData,
  ContactBook,
  ListPermittedContactBooksViewResponseData,
  MaterializeVirtualContactViewRequestData,
  MaterializeVirtualContactViewResponseData,
  ObtainEmailAddressViewRequestData,
  ObtainEmailAddressViewResponseData,
  ObtainEmailAddressWithUserViewRequestData,
  ObtainEmailAddressWithUserViewResponseData,
  QuickCreateAddressViewRequestData,
  QuickCreateAddressViewResponseData,
  RemoveAddressViewRequestData,
  RemoveAddressFromContactRecordViewRequestData,
  RemoveAttributionFromContactRecordViewRequestData,
  RemoveEmailAddressViewRequestData,
  RemoveEmailFromContactRecordViewRequestData,
  RemoveNoteFromContactRecordViewRequestData,
  RemovePhoneFromContactRecordViewRequestData,
  RemovePhoneNumberViewRequestData,
  RemoveTagFromContactRecordViewRequestData,
  ReverseSearchAddressesViewRequestData,
  ReverseSearchAddressesViewResponseData,
  SearchAddressesViewRequestData,
  SearchAddressesViewResponseData,
  NameEnum,
  SearchContactDocumentsViewRequestData,
  SearchContactDocumentsViewResponseData,
  SearchContactEntriesViewRequestData,
  SearchContactEntriesViewResponseData,
  SearchContactsViewRequestData,
  SearchContactsViewResponseData,
  SearchEmailsViewRequestData,
  EmailAddressResult,
  SearchEmailsViewResponseData,
  SendConstituentEmailViewRequestData,
  SendConstituentEmailViewResponseData,
  SendEmailViewRequestData,
  SendEmailViewResponseData,
  SendVerificationEmailViewRequestData,
  SendVerificationEmailViewResponseData,
  SetEmailSignatureViewRequestData,
  SetPrimaryEmailAddressViewRequestData,
  UpdateContactRecordPersonProfileViewRequestData,
  PersonEnum,
  UpdateContactRecordTypeViewRequestData,
  UpdateNoteOnContactRecordViewRequestData,
  VerifyEmailViewRequestData,
  VerifyLoginViewRequestData,
  VerifyLoginViewResponseData,
  AbortMultipartUploadViewRequestData,
  AbortMultipartUploadViewResponseData,
  AddEmailDomainViewRequestData,
  EmailDomain,
  AddEmailDomainViewResponseData,
  BreakdownContactTagsViewRequestData,
  Tag0,
  BreakdownContactTagsViewResponseData,
  ChangePasswordViewRequestData,
  CheckEmailDomainVerificationViewRequestData,
  NotStartedEnum,
  CnameEnum,
  DnsRecord,
  CheckEmailDomainVerificationViewResponseData,
  MultipartPart,
  CompleteMultipartUploadViewRequestData,
  CompleteMultipartUploadViewResponseData,
  CreateAttributionViewRequestData,
  CreateDepartmentViewRequestData,
  CreateDepartmentViewResponseData,
  CreateNoteViewRequestData,
  CreateNoteViewResponseData,
  CreateOrganizationContactBookViewRequestData,
  CreateOrganizationContactBookViewResponseData,
  AssetTypeEnum,
  CreatePresignedUploadViewRequestData,
  CreatePresignedUploadViewResponseData,
  CreateTagViewRequestData,
  CreateTagViewResponseData,
  DeleteNoteViewRequestData,
  DeleteTagViewRequestData,
  EditContactBirthdayViewRequestData,
  EditContactImageViewRequestData,
  EditContactImageViewResponseData,
  EditContactNameViewRequestData,
  AIStatusEnum,
  AISearchStatusEnum,
  EmailStatusEnum,
  EmailProviderEnum,
  CustomerTypeEnum,
  EditCustomerDataViewRequestData,
  PaymentsErpEnum,
  EditPaymentSettingsViewRequestData,
  EditTagViewRequestData,
  GetAccountViewRequestData,
  DigestFrequencyEnum,
  GetAccountViewResponseData,
  GetCustomerPurchasedStatusesViewRequestData,
  GetCustomerPurchasedStatusesViewResponseData,
  GetIdentityViewRequestData,
  OrganizationTypeEnum,
  Identity1,
  GetIdentityViewResponseData,
  GetOrganizationViewRequestData,
  GetOrganizationViewResponseData,
  GetOrganizationContactBookInfoViewRequestData,
  GetOrganizationContactBookInfoViewResponseData,
  GetOrganizationIdentityViewRequestData,
  GetOrganizationIdentityViewResponseData,
  GetPasswordPolicyViewRequestData,
  GetPasswordPolicyViewResponseData,
  GetUserViewRequestData,
  SkinEnum,
  CityManagerEnum,
  GetUserViewResponseData,
  GetUserFromSessionViewResponseData,
  GetUserIdsViewRequestData,
  UserIds,
  GetUserIdsViewResponseData,
  IdentityToUserViewRequestData,
  IdentityToUserViewResponseData,
  ListAttributionsViewRequestData,
  AttributionIdField,
  Attribution0,
  ListAttributionsViewResponseData,
  ListContactEntryNotesViewRequestData,
  ListContactEntryNotesViewResponseData,
  ListDistinctEmailIdentitiesViewRequestData,
  EmailIdentity,
  ListDistinctEmailIdentitiesViewResponseData,
  ListEmailDomainsViewRequestData,
  ListEmailDomainsViewResponseData,
  ListIdentitiesViewRequestData,
  ListIdentitiesViewResponseData,
  ListIdentitiesByIdsViewRequestData,
  ListIdentitiesByIdsViewResponseData,
  ListOrgIdentitiesTierViewRequestData,
  AccessTierEnum,
  OrgIdentityTier,
  ListOrgIdentitiesTierViewResponseData,
  ListOrganizationsViewRequestData,
  EnrichedOrganization,
  ListOrganizationsViewResponseData,
  ListOrganizationsCustomerDataViewRequestData,
  OrganizationCustomerIdField,
  OrganizationCustomerData,
  OrganizationAndCustomerData,
  ListOrganizationsCustomerDataViewResponseData,
  ListQuillDashboardsViewRequestData,
  QuillDashboardIdField,
  QuillDashboard,
  ListQuillDashboardsViewResponseData,
  ListStaffersViewRequestData,
  ListStaffersViewResponseData,
  ListTaggingsViewRequestData,
  ListTaggingsViewResponseData,
  ListTagsViewRequestData,
  ListTagsViewResponseData,
  UserLoginViewRequestData,
  UserLoginViewResponseData,
  UserLogoutViewRequestData,
  QuickSearchViewRequestData,
  QuickSearchContact,
  LicenseSummary,
  LicenseTemplateSummary,
  LicenseParty,
  QuickSearchViewResponseData,
  ResetPasswordViewRequestData,
  ResetPasswordRequestViewRequestData,
  ResumeMultipartUploadViewRequestData,
  ResumeMultipartUploadViewResponseData,
  SendTestDigestEmailViewRequestData,
  SetAutoCcCaseEmailsViewRequestData,
  SetDigestFrequencyViewRequestData,
  SetNotifyViewRequestData,
  SetPasswordViewRequestData,
  SetPersonBirthdayRequestData,
  SetPersonNameRequestData,
  SetPersonaViewRequestData,
  SSOConfirmLinkViewRequestData,
  SSOConfirmLinkViewResponseData,
  GetSSOConfigViewRequestData,
  IdPProviderEnum,
  PasswordEnum,
  SSOConfig,
  SSOGroupMapping,
  GetSSOConfigViewResponseData,
  ListIdPGroupsViewRequestData,
  ListIdPGroupsViewIdPGroupItem,
  ListIdPGroupsViewResponseData,
  SAMLLookupViewRequestData,
  SAMLLookupViewResponseData,
  SetupSSOConfigViewRequestData,
  SetupSSOConfigViewResponseData,
  TestSSOConnectionViewRequestData,
  TestSSOConnectionViewResponseData,
  UpdateSSOGroupMappingsViewMappingItem,
  UpdateSSOGroupMappingsViewRequestData,
  UpdateSSOGroupMappingsViewResponseData,
  UpdateSSOSettingsViewRequestData,
  UpdateSSOSettingsViewResponseData,
  StartEmailDomainVerificationViewRequestData,
  StartEmailDomainVerificationViewResponseData,
  SuperOrgAccessAddViewRequestData,
  SuperOrgAccessGetCurrentViewRequestData,
  SuperOrgAccessGetCurrentViewResponseData,
  SuperOrgAccessRemoveViewRequestData,
  SuperOrgAccessSearchViewRequestData,
  OrganizationHierarchy,
  SuperOrgAccessSearchViewResponseData,
  TagViewRequestData,
  UnattributeViewRequestData,
  UntagViewRequestData,
  UpdateNoteViewRequestData,
  UploadAssetViewRequestData,
  UploadAssetViewResponseData,
  LocalDBCheckViewResponseData,
  AddTwilioAccountConfigViewRequestData,
  AddTwilioAccountConfigViewResponseData,
  AppendOpenAPIDraftMessageViewRequestData,
  OpenAPIDraftSerializerOpenAPIDraft,
  CreateAPIAuthenticationTokenViewRequestData,
  APIAuthenticationTokenIdField,
  CreateAPIAuthenticationTokenViewResponseData,
  CreateAPIConnectionViewRequestData,
  CreateAPIConnectionViewResponseData,
  HTTPMethodEnum,
  DataFormatEnum,
  CreateAPIEndpointViewRequestData,
  CreateAPIEndpointViewResponseData,
  IntegrationSurfaceEnum,
  CreateAPIEndpointConfigViewRequestData,
  APIEndpointConfigIdField,
  CreateAPIEndpointConfigViewResponseData,
  QueryEnum,
  StringEnum,
  CreateAPIEndpointParameterViewRequestData,
  APIEndpointParameterIdField,
  CreateAPIEndpointParameterViewResponseData,
  DeleteAPIConnectionViewRequestData,
  DeleteAPIConnectionViewResponseData,
  DeleteAPIEndpointConfigViewRequestData,
  DeleteTwilioAccountConfigViewRequestData,
  DiscardOpenAPIDraftViewRequestData,
  DiscardOpenAPIDraftViewResponseData,
  EditTwilioAccountConfigViewRequestData,
  BrowserActionInputVariableTypeEnum,
  BrowserActionVariable,
  BrowserRegionEnum,
  ExecuteBrowserTestActionViewRequestData,
  ExecuteBrowserTestActionViewResponseData,
  CayentaUtilityBillingEndpointEnum,
  GenerateCayentaUtilityBillingIntegrationViewRequestData,
  GenerateCayentaUtilityBillingIntegrationViewResponseData,
  GenerateCentralSquareIntegrationViewRequestData,
  GenerateCentralSquareIntegrationViewResponseData,
  GenerateCityworksIntegrationViewRequestData,
  GenerateCityworksIntegrationViewResponseData,
  AuthTypeEnum,
  ApiEnum,
  BrowserActionSchema,
  ParameterLocationEnum,
  ParameterTypeEnum,
  ParameterSchema,
  EndpointSchema,
  GenerateCustomIntegrationViewRequestData,
  GenerateCustomIntegrationViewResponseData,
  GenerateEnergovEplIntegrationViewRequestData,
  GenerateEnergovEplIntegrationViewResponseData,
  FastTrackEndpointEnum,
  GenerateFastTrackIntegrationViewRequestData,
  GenerateFastTrackIntegrationViewResponseData,
  GenerateMunisArwsIntegrationViewRequestData,
  GenerateMunisArwsIntegrationViewResponseData,
  CreateWorkOrderEnum,
  GenerateOpenGovIntegrationViewRequestData,
  GenerateOpenGovIntegrationViewResponseData,
  Request311EndpointEnum,
  GenerateRequest311IntegrationViewRequestData,
  GenerateRequest311IntegrationViewResponseData,
  SharedEnum,
  GenerateSharePointIntegrationViewRequestData,
  GenerateSharePointIntegrationViewResponseData,
  GetOpenAPIDraftViewRequestData,
  GetOpenAPIDraftViewResponseData,
  ListAPIConnectionsViewRequestData,
  APIEndpointParameter,
  EndpointStatusEnum,
  APIEndpointConfig,
  APIEndpoint,
  BrowserActionInputVariable,
  BrowserAction,
  APIConnection,
  ListAPIConnectionsViewResponseData,
  ListAPIEndpointParametersViewRequestData,
  ListAPIEndpointParametersViewResponseData,
  ListAPIEndpointsViewRequestData,
  ListAPIEndpointsViewResponseData,
  ListIntegrationEndpointDefinitionsViewRequestData,
  ParameterDefinition,
  EndpointDefinition,
  ListIntegrationEndpointDefinitionsViewResponseData,
  ListTwilioAccountConfigsViewRequestData,
  TwilioAccountConfigPhoneLine,
  TwilioAccountConfig,
  ListTwilioAccountConfigsViewResponseData,
  DisconnectMicrosoftViewRequestData,
  DisconnectMicrosoftViewResponseData,
  StartMicrosoftConnectViewRequestData,
  StartMicrosoftConnectViewResponseData,
  MicrosoftConnectionStatusViewRequestData,
  MicrosoftConnectionStatusViewResponseData,
  StartBrowserTestSessionViewRequestData,
  StartBrowserTestSessionViewResponseData,
  StopBrowserTestSessionViewRequestData,
  StopBrowserTestSessionViewResponseData,
  TestCustomEndpointViewRequestData,
  TestCustomEndpointViewResponseData,
  Request311Enum,
  TestHostedIntegrationCredentialsViewRequestData,
  TestHostedIntegrationCredentialsViewResponseData,
  TestIntegrationEndpointsViewRequestData,
  TestIntegrationEndpointsViewResponseData,
  UpdateAPIConnectionViewRequestData,
  UpdateAPIConnectionViewResponseData,
  EndpointConfigUpdate,
  UpdateEndpointConfigsViewRequestData,
  UpdateEndpointConfigsViewResponseData,
  UpdateAPIEndpointsViewRequestData,
  UpdateAPIEndpointsViewResponseData,
  UpdateParameterSchema,
  UpdateEndpointSchema,
  UpdateCustomIntegrationViewRequestData,
  UpdateCustomIntegrationViewResponseData,
  EndpointUpdate,
  UpdateEndpointActiveViewRequestData,
  UpdateEndpointActiveViewResponseData,
  UploadOpenAPIDraftAttachmentViewRequestData,
  UploadOpenAPIDraftAttachmentViewResponseData,
  GetDomainSitemapViewRequestData,
  DomainSitemapNode,
  GetDomainSitemapViewResponseData,
  GetKnowledgeBaseItemUsageViewRequestData,
  KnowledgeBaseItemUsage,
  GetKnowledgeBaseItemUsageViewResponseData,
  KnowledgeBaseItemTypeEnum,
  KnowledgeBaseItemSortFieldEnum,
  ListKnowledgeBaseItemsViewRequestData,
  KnowledgeBaseItemSummary,
  ListKnowledgeBaseItemsViewResponseData,
  KnowledgeFindingTypeEnum,
  KnowledgeFindingStatusEnum,
  ListKnowledgeFindingsViewRequestData,
  KnowledgeFindingIdField,
  ExtractedEntityConfigIdField,
  MatchedEntityAttribute,
  MatchedEntitySummary,
  KnowledgeFindingSummary,
  ListKnowledgeFindingsViewResponseData,
  BulkEditLicenseTemplatesViewRequestData,
  BulkEditLicenseTemplatesViewResponseData,
  BulkEditLicensesViewRequestData,
  BulkEditLicensesViewResponseData,
  LicenseTemplateFieldIdField,
  LicenseFieldInput,
  CreateLicenseViewRequestData,
  LicenseFieldIdField,
  LicenseField,
  CreateLicenseViewResponseData,
  CreateLicenseFieldsViewRequestData,
  CreateLicenseFieldsViewResponseData,
  CreateLicenseTemplateViewRequestData,
  CreateLicenseTemplateViewResponseData,
  CreateLicenseTemplateFieldViewRequestData,
  LicenseTemplateField,
  CreateLicenseTemplateFieldViewResponseData,
  EditLicenseViewRequestData,
  EditLicenseViewResponseData,
  LicenseFieldUpdate,
  EditLicenseFieldsViewRequestData,
  EditLicenseFieldsViewResponseData,
  EditLicenseTemplateViewRequestData,
  EditLicenseTemplateViewResponseData,
  EditLicenseTemplateFieldViewRequestData,
  EditLicenseTemplateFieldViewResponseData,
  ListConstituentLicensesViewRequestData,
  ListConstituentLicensesViewResponseData,
  ListContactLicensesViewRequestData,
  ListContactLicensesViewResponseData,
  ListLicenseCasesViewRequestData,
  ListLicenseCasesViewResponseData,
  ListLicenseDocumentsViewRequestData,
  ListLicenseDocumentsViewResponseData,
  ListLicenseFieldMappingsViewRequestData,
  LicenseFieldMapping,
  ListLicenseFieldMappingsViewResponseData,
  ListLicenseFieldsViewRequestData,
  ListLicenseFieldsViewResponseData,
  ListLicenseHistoryViewRequestData,
  LicenseHistoryIdField,
  LicenseHistory,
  ListLicenseHistoryViewResponseData,
  ListLicenseTemplateFieldsViewRequestData,
  ListLicenseTemplateFieldsViewResponseData,
  ListLicenseTemplatesViewRequestData,
  ListLicenseTemplatesViewResponseData,
  ListLicensesViewRequestData,
  ListLicensesViewResponseData,
  ListLicensesWithLocationsViewRequestData,
  ListLicensesWithLocationsViewResponseData,
  LicenseFieldMappingEntry,
  SetLicenseFieldMappingsViewRequestData,
  SetLicenseFieldMappingsViewResponseData,
  LicenseTemplateFieldEntry,
  SetLicenseTemplateFieldsViewRequestData,
  SetLicenseTemplateFieldsViewResponseData,
  CancelScheduledMassCommunicationViewRequestData,
  MassCommunicationStatusEnum,
  MassCommunication,
  CancelScheduledMassCommunicationViewResponseData,
  ListMassCommunicationContactBookAddressPointsViewRequestData,
  ContactBookAddressPoint,
  ListMassCommunicationContactBookAddressPointsViewResponseData,
  CreateMassCommunicationViewRequestData,
  CreateMassCommunicationViewResponseData,
  DeleteMassCommunicationViewRequestData,
  GetOrganizationEmailCapabilityViewRequestData,
  GetOrganizationEmailCapabilityViewResponseData,
  GetMassCommunicationViewRequestData,
  GetMassCommunicationViewResponseData,
  ListMassCommunicationsViewRequestData,
  ListMassCommunicationsViewResponseData,
  ListMassCommunicationReceiptsViewRequestData,
  MassCommunicationReceipt,
  ListMassCommunicationReceiptsViewResponseData,
  ListMassCommunicationResponsesViewRequestData,
  MassCommResponse,
  ListMassCommunicationResponsesViewResponseData,
  PreviewMassCommunicationContactBookRecipientsViewRequestData,
  PreviewMassCommunicationContactBookRecipientsViewResponseData,
  SendMassCommunicationContactBookViewRequestData,
  SendMassCommunicationContactBookViewResponseData,
  SendTestMassCommunicationViewRequestData,
  GetOrganizationSmsCapabilityViewRequestData,
  GetOrganizationSmsCapabilityViewResponseData,
  UpdateMassCommunicationViewRequestData,
  UpdateMassCommunicationViewResponseData,
  AddChargeViewRequestData,
  AddChargeViewResponseData,
  AddPaymentViewRequestData,
  CancelTerminalPaymentViewRequestData,
  CompleteBillViewRequestData,
  StripeSourceIdField,
  ConfirmPaymentViewRequestData,
  ConfirmPaymentViewResponseData,
  ConnectStripeViewRequestData,
  ConnectStripeViewResponseData,
  LookupKeyTypeEnum,
  LookupIdField,
  LookupKey,
  PaymentFilter,
  BillFilter,
  CountLookupEntriesViewRequestData,
  CountLookupEntriesViewResponseData,
  PaymentListArgs,
  CountPaymentsViewRequestData,
  CountPaymentsViewResponseData,
  CreateLocationViewRequestData,
  PaymentTerminalLocationIdField,
  PaymentTerminalLocation,
  CreateLocationViewResponseData,
  CreateLookupViewRequestData,
  CreateLookupViewResponseData,
  CreatePayoutViewRequestData,
  CreatePayoutViewResponseData,
  DeleteChargeViewRequestData,
  DownloadSpringbrookCSVViewRequestData,
  DownloadSpringbrookCSVViewResponseData,
  FTPAuthViewRequestData,
  FTPAuthViewResponseData,
  GetLookupViewRequestData,
  Lookup0,
  GetLookupViewResponseData,
  GetPaymentForReceiptViewRequestData,
  Contact0,
  Payment1,
  GetPaymentForReceiptViewResponseData,
  GetPaymentInfoViewRequestData,
  Payment0,
  Bill2,
  GetPaymentInfoViewResponseData,
  GetPaymentTotalViewRequestData,
  GetPaymentTotalViewResponseData,
  GranularityEnum,
  GetPaymentsStatsViewRequestData,
  PaymentInterval,
  GetPaymentsStatsViewResponseData,
  GetRevenueBreakdownViewRequestData,
  RevenueStat,
  GetRevenueBreakdownViewResponseData,
  GetRevenueTypesBreakdownViewRequestData,
  RevenueTypeBreakdown,
  GetRevenueTypesBreakdownViewResponseData,
  BillImportFormatEnum,
  ImportBillsViewRequestData,
  ListBillsViewRequestData,
  ListBillsViewResponseData,
  ListChargeSubstepsViewRequestData,
  ListChargeSubstepsViewResponseData,
  ListConstituentCaseBillViewRequestData,
  ListConstituentCaseBillViewResponseData,
  ListLocationsViewRequestData,
  ListLocationsViewResponseData,
  ListLookupEntriesViewRequestData,
  LookupEntryIdField,
  LookupEntry2,
  ListLookupEntriesViewResponseData,
  ListLookupsViewRequestData,
  LookupStats,
  Lookup2,
  ListLookupsViewResponseData,
  ListPaymentExportRulesViewRequestData,
  PaymentExportRuleIdField,
  PaymentExportRule,
  ListPaymentExportRulesViewResponseData,
  ListPaymentsViewRequestData,
  ListPaymentsViewResponseData,
  ListPayoutsViewRequestData,
  Payout,
  ListPayoutsViewResponseData,
  StripeSourceTypeEnum,
  ListStripeSourcesViewRequestData,
  StripeSource,
  ListStripeSourcesViewResponseData,
  ListTerminalsViewRequestData,
  PaymentTerminalIdField,
  PaymentTerminal,
  ListTerminalsViewResponseData,
  ObtainPaymentIntentViewRequestData,
  Intent,
  ObtainPaymentIntentViewResponseData,
  ReconcileViewRequestData,
  RegisterTerminalViewRequestData,
  RegisterTerminalViewResponseData,
  SearchBillsViewRequestData,
  SearchBillsViewResponseData,
  SendBillRequestViewRequestData,
  UpsertPaymentExportRuleViewRequestData,
  VerifyStripeSourceViewRequestData,
  AddAdHocBlockInstancePermissionsViewRequestData,
  AdHocPermissionIdField,
  AddAdHocBlockInstancePermissionsViewResponseData,
  AddAdHocBlockPermissionsViewRequestData,
  AddAdHocBlockPermissionsViewResponseData,
  TargetResourceEnum,
  AddAdHocCasePermissionsViewRequestData,
  AddAdHocCasePermissionsViewResponseData,
  AddAdHocWorkflowPermissionsViewRequestData,
  AddAdHocWorkflowPermissionsViewResponseData,
  AddGroupViewRequestData,
  AddGroupViewResponseData,
  AddGroupMembersViewRequestData,
  GroupMemberIdField,
  AddGroupMembersViewResponseData,
  PolicyIdField,
  AdminEnum,
  AddPermissionsRoleViewRequestData,
  PermissionsRoleIdField,
  AddPermissionsRoleViewResponseData,
  GeneralResourceEnum,
  RolePolicyTuple,
  AddRolePoliciesViewRequestData,
  AddSingletonGroupViewRequestData,
  AddSingletonGroupViewResponseData,
  AddWorkspaceAdministratorViewRequestData,
  WorkspaceAdministratorIdField,
  AddWorkspaceAdministratorViewResponseData,
  AddWorkspaceGroupViewRequestData,
  AddWorkspaceGroupViewResponseData,
  AssignGroupRolesViewRequestData,
  RoleAssignmentIdField,
  AssignGroupRolesViewResponseData,
  CheckUserBlockAccessViewRequestData,
  CheckUserBlockAccessViewResponseData,
  CheckUserCaseAccessViewRequestData,
  CheckUserCaseAccessViewResponseData,
  CopyOrganizationGroupViewRequestData,
  Group,
  EndUser,
  GroupMember,
  CopyOrganizationGroupViewResponseData,
  CopyWorkspaceGroupViewRequestData,
  CopyWorkspaceGroupViewResponseData,
  CreateRoleAssignmentViewRequestData,
  CreateRoleAssignmentViewResponseData,
  EnsureSingletonGroupsViewRequestData,
  EnsureSingletonGroupsViewResponseData,
  ListAdHocPermittedUsersForBlockViewRequestData,
  ListAdHocPermittedUsersForBlockViewResponseData,
  ListAdHocPermittedUsersForBlockInstanceViewRequestData,
  ListAdHocPermittedUsersForBlockInstanceViewResponseData,
  ListAdHocPermittedUsersForCaseViewRequestData,
  ListAdHocPermittedUsersForCaseViewResponseData,
  ListAdHocPermittedUsersForWorkflowViewRequestData,
  ListAdHocPermittedUsersForWorkflowViewResponseData,
  ListExternalRoleAssignmentsViewRequestData,
  RoleIdField,
  ExternalRoleAssignment,
  ListExternalRoleAssignmentsViewResponseData,
  ListGeneralPermissionsForGroupViewRequestData,
  Policy,
  ListGeneralPermissionsForGroupViewResponseData,
  ListGeneralPermissionsForUserViewRequestData,
  ListGeneralPermissionsForUserViewResponseData,
  ListGroupMembersViewRequestData,
  ListGroupMembersViewResponseData,
  ListGroupRolesViewRequestData,
  PermissionsRole,
  ListGroupRolesViewResponseData,
  ListGroupsViewRequestData,
  ListGroupsViewResponseData,
  ListInboundExternalRolesViewRequestData,
  InboundExternalRole,
  ListInboundExternalRolesViewResponseData,
  ListPermissionsRolesViewRequestData,
  ListPermissionsRolesViewResponseData,
  ListRoleAssignmentsViewRequestData,
  ListRoleAssignmentsViewResponseData,
  ListUserAdHocPermissionsRequestData,
  ListUserAdHocPermissionsResponseData,
  ListUserGroupsViewRequestData,
  ListUserGroupsViewResponseData,
  ListUserPermittedOrgIdentitiesViewRequestData,
  ListUserPermittedOrgIdentitiesViewResponseData,
  ListWorkspaceAdministratorsViewRequestData,
  WorkspaceAdministrator,
  ListWorkspaceAdministratorsViewResponseData,
  ListWorkspaceGroupsViewRequestData,
  WorkspaceUser,
  ListWorkspaceGroupsViewResponseData,
  RoleGroupReassignment,
  ReassignRoleGroupsViewRequestData,
  ReassignRoleGroupsViewResponseData,
  RemoveBlockAdHocPermissionViewRequestData,
  RemoveBlockInstanceAdHocPermissionViewRequestData,
  RemoveCaseAdHocPermissionViewRequestData,
  RemoveGroupViewRequestData,
  RemoveGroupMembersViewRequestData,
  RemoveGroupRolesViewRequestData,
  RemovePermissionsRoleViewRequestData,
  RemoveRolePoliciesViewRequestData,
  RemoveWorkflowAdHocPermissionViewRequestData,
  RemoveWorkspaceAdministratorViewRequestData,
  RemoveWorkspaceGroupViewRequestData,
  RenameGroupViewRequestData,
  RenamePermissionsRoleViewRequestData,
  RenameWorkspaceGroupViewRequestData,
  ApplyRedactionsViewRequestData,
  ApplyRedactionsViewResponseData,
  DeleteRedactionDocumentViewRequestData,
  RedactionJobIdField,
  PdfEnum,
  Pending1Enum,
  UnsupportedTypeEnum,
  SkippedAttachment,
  RedactionDocument,
  DeleteRedactionDocumentViewResponseData,
  GetActiveRedactionRequestExportViewRequestData,
  GetActiveRedactionRequestExportViewResponseData,
  GetRedactionDocumentSourceViewRequestData,
  RenderModeEnum,
  RedactionSourceTypeEnum,
  RedactionSourcePage,
  GetRedactionDocumentSourceViewResponseData,
  GetRedactionJobViewRequestData,
  Pending2Enum,
  RedactionJob,
  RedactionCandidateCounts,
  GetRedactionJobViewResponseData,
  GetRedactionRequestViewRequestData,
  GetRedactionRequestViewResponseData,
  GetRedactionRequestExportViewRequestData,
  GetRedactionRequestExportViewResponseData,
  GetRedactionRequestExportDownloadViewRequestData,
  GetRedactionRequestExportDownloadViewResponseData,
  GetRedactionSizeLimitsViewRequestData,
  RedactionSizeLimit,
  GetRedactionSizeLimitsViewResponseData,
  ListRedactionCandidatesViewRequestData,
  RedactionCandidateIdField,
  Bbox,
  DetectedEnum,
  CandidateSourceEnum,
  CharRange,
  PendingEnum,
  RedactionCandidate,
  ListRedactionCandidatesViewResponseData,
  ListRedactionRequestsViewRequestData,
  ListRedactionRequestsViewResponseData,
  JurisdictionEnum,
  RedactionLevelEnum,
  RetryRedactionDetectionViewRequestData,
  RetryRedactionDetectionViewResponseData,
  SetRedactionDocumentCompleteViewRequestData,
  SetRedactionDocumentCompleteViewResponseData,
  Statute,
  ManualRedactionCandidate,
  SetRedactionManualCandidatesViewRequestData,
  SetRedactionManualCandidatesViewResponseData,
  SetRedactionRequestNameViewRequestData,
  SetRedactionRequestNameViewResponseData,
  UploadedRedactionDocument,
  StartRedactionDetectionViewRequestData,
  FailedDocumentUpload,
  StartRedactionDetectionViewResponseData,
  StartRedactionRequestExportViewRequestData,
  StartRedactionRequestExportViewResponseData,
  RedactionCandidateStatusUpdate,
  UpdateRedactionCandidatesViewRequestData,
  UpdateRedactionCandidatesViewResponseData,
}

const endpoints = makeApi([
  {
    method: 'post',
    path: '/ai/add-ai-phone-line/',
    alias: 'ai_addAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIPhoneLineViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-ai-process-source/',
    alias: 'ai_addAIProcessSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIProcessSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-ai-skill/',
    alias: 'ai_addAISkill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAISkillViewRequestData,
      },
    ],
    response: AddAISkillViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-ai-usage-policy-snapshot/',
    alias: 'ai_addAIUsagePolicySnapshot',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIUsagePolicySnapshotViewRequestData,
      },
    ],
    response: AddAIUsagePolicySnapshotViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-ai-usage-policy/',
    alias: 'ai_addAIUsagePolicy',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAIUsagePolicyViewRequestData,
      },
    ],
    response: AddAIUsagePolicyViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-faq-source/',
    alias: 'ai_addFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddFAQSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-folder/',
    alias: 'ai_addFolder',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddFolderViewRequestData,
      },
    ],
    response: AddFolderViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-gis-layer-source/',
    alias: 'ai_addGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddGISLayerSourceViewRequestData,
      },
    ],
    response: AddGISLayerSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-test-scenario/',
    alias: 'ai_addScenario',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddScenarioViewRequestData,
      },
    ],
    response: AddScenarioViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/add-transfer-line-source/',
    alias: 'ai_addTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddTransferLineSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/archive-test-run/',
    alias: 'ai_archiveTestRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ArchiveTestRunViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/associate-ai-interaction-case/',
    alias: 'ai_associateAIInteractionCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AssociateAIInteractionCaseViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/bulk-add-test-scenarios/',
    alias: 'ai_bulkAddScenarios',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkAddScenariosViewRequestData,
      },
    ],
    response: BulkAddScenariosViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/bulk-archive-page-sources/',
    alias: 'ai_bulkArchivePageSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkArchivePageSourcesViewRequestData,
      },
    ],
    response: BulkArchivePageSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/bulk-move-page-sources/',
    alias: 'ai_bulkMovePageSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkMovePageSourcesViewRequestData,
      },
    ],
    response: BulkMovePageSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/cancel-form-generation/',
    alias: 'ai_cancelFormGeneration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelFormGenerationViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/check-category-evaluation-config/',
    alias: 'ai_checkCategoryEvaluationConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CheckCategoryEvaluationConfigViewRequestData,
      },
    ],
    response: CheckCategoryEvaluationConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/connect-arcgis/',
    alias: 'ai_connectArcGIS',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConnectArcGISViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/conversation-initiation/',
    alias: 'ai_elevenLabsConversationInitiation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsConversationInitiationViewRequestData,
      },
    ],
    response: ElevenLabsConversationInitiationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/convert-document-to-ai-usage-policies/',
    alias: 'ai_convertDocumentToAIUsagePolicies',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConvertDocumentToAIUsagePoliciesViewRequestData,
      },
    ],
    response: ConvertDocumentToAIUsagePoliciesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/crawl-domain/',
    alias: 'ai_crawlDomain',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CrawlDomainViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/crawl-task-logs/',
    alias: 'ai_crawlTaskLogs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CrawlTaskLogsViewRequestData,
      },
    ],
    response: CrawlTaskLogsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/create-custom-category-evaluation-config/',
    alias: 'ai_createCustomCategoryEvaluationConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCustomCategoryEvaluationConfigViewRequestData,
      },
    ],
    response: CreateCustomCategoryEvaluationConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-ai-assistant-interaction/',
    alias: 'ai_deleteAIAssistantInteraction',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAIAssistantInteractionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-ai-email/',
    alias: 'ai_deleteAIEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAIEmailViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-ai-phone-line/',
    alias: 'ai_deleteAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAIPhoneLineViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-ai-skill/',
    alias: 'ai_deleteAISkill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAISkillViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-all-ai-skills/',
    alias: 'ai_deleteAllAISkills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAllAISkillsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-all-ai-usage-policies/',
    alias: 'ai_deleteAllAIUsagePolicies',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAllAIUsagePoliciesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-all-assistant-chats/',
    alias: 'ai_deleteAllAssistantChats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAllAssistantChatsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-chunk/',
    alias: 'ai_deleteChunk',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteChunkViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-crawl-task/',
    alias: 'ai_deleteCrawlTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCrawlTaskViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-faq-source/',
    alias: 'ai_deleteFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFAQSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-folder/',
    alias: 'ai_deleteFolder',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFolderViewRequestData,
      },
    ],
    response: z.object({ affected_source_count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-gis-layer-source/',
    alias: 'ai_deleteGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteGISLayerSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/delete-transfer-line-source/',
    alias: 'ai_deleteTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTransferLineSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/devin/ask/',
    alias: 'agentic_implementation_devinAsk',
    description: `Simple CS-facing endpoint that proxies a question to Devin MCP.

This is intentionally minimal for V1: one question in, one answer out.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DevinAskViewRequestData,
      },
    ],
    response: DevinAskViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/domain-sanity-check/',
    alias: 'ai_domainSanityCheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DomainSanityCheckViewRequestData,
      },
    ],
    response: z.object({ is_compatible: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-ai-interactions/',
    alias: 'ai_downloadAIInteractions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadAIInteractionsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-assistant-interactions/',
    alias: 'ai_downloadAssistantInteractions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadAssistantInteractionsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-faq-sources/',
    alias: 'ai_downloadFAQSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadFAQSourcesViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-test-run/',
    alias: 'ai_downloadTestRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadTestRunViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/download-transfer-line-sources/',
    alias: 'ai_downloadTransferLineSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadTransferLineSourcesViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-phone-line/',
    alias: 'ai_editAIPhoneLine',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIPhoneLineViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-process-source/',
    alias: 'ai_editAIProcessSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIProcessSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-skill/',
    alias: 'ai_editAISkill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAISkillViewRequestData,
      },
    ],
    response: EditAISkillViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-usage-policy-snapshot/',
    alias: 'ai_editAIUsagePolicySnapshot',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIUsagePolicySnapshotViewRequestData,
      },
    ],
    response: EditAIUsagePolicySnapshotViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-ai-usage-policy/',
    alias: 'ai_editAIUsagePolicy',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAIUsagePolicyViewRequestData,
      },
    ],
    response: EditAIUsagePolicyViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-chunk/',
    alias: 'ai_editChunk',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditChunkViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-crawl-task/',
    alias: 'ai_editCrawlTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCrawlTaskViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-faq-source/',
    alias: 'ai_editFAQSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFAQSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-folder/',
    alias: 'ai_editFolder',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFolderViewRequestData,
      },
    ],
    response: EditFolderViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-gis-layer-source/',
    alias: 'ai_editGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditGISLayerSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-organization-ai-retention-settings/',
    alias: 'ai_editOrganizationAIRetentionSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditOrganizationAIRetentionSettingsViewRequestData,
      },
    ],
    response: EditOrganizationAIRetentionSettingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-organization-ai-settings/',
    alias: 'ai_editOrganizationAISettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditOrganizationAISettingsViewRequestData,
      },
    ],
    response: EditOrganizationAISettingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-page-source/',
    alias: 'ai_editPageSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditPageSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-test-scenario/',
    alias: 'ai_editScenario',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditScenarioViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/edit-transfer-line-source/',
    alias: 'ai_editTransferLineSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTransferLineSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/external-api-call/',
    alias: 'ai_elevenLabsExternalAPICall',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsExternalAPICallViewRequestData,
      },
    ],
    response: ElevenLabsExternalAPICallViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/generate-assistant-response/',
    alias: 'ai_generateAssistantResponse',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateAssistantResponseViewRequestData,
      },
    ],
    response: GenerateAssistantResponseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/generate-form-from-pdf/',
    alias: 'ai_generateFormFromPDF',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateFormFromPDFViewRequestData,
      },
    ],
    response: z.object({ task_id: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/generate-response/',
    alias: 'ai_generateResponse',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateResponseViewRequestData,
      },
    ],
    response: GenerateResponseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-interaction-count/',
    alias: 'ai_getAIInteractionCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIInteractionCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-interaction-pii-transcript/',
    alias: 'ai_getAIInteractionPIITranscript',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIInteractionPIITranscriptViewRequestData,
      },
    ],
    response: GetAIInteractionPIITranscriptViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-interaction-transcript/',
    alias: 'ai_getAIInteractionTranscript',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIInteractionTranscriptViewRequestData,
      },
    ],
    response: GetAIInteractionTranscriptViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-process-source/',
    alias: 'ai_getAIProcessSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIProcessSourceViewRequestData,
      },
    ],
    response: GetAIProcessSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-ai-process-sources-count/',
    alias: 'ai_getAIProcessSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAIProcessSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-arcgis-layers/',
    alias: 'ai_getArcGISLayers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetArcGISLayersViewRequestData,
      },
    ],
    response: GetArcGISLayersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-assistant-interaction-count/',
    alias: 'ai_getAssistantInteractionCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAssistantInteractionCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-civicplus-folders/',
    alias: 'ai_getCivicPlusFolders',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCivicPlusFoldersViewRequestData,
      },
    ],
    response: GetCivicPlusFoldersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-connected-resource-counts/',
    alias: 'ai_getConnectedResourceCounts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetConnectedResourceCountsViewRequestData,
      },
    ],
    response: GetConnectedResourceCountsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-evaluation-categories/',
    alias: 'ai_getEvaluationCategories',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetEvaluationCategoriesViewRequestData,
      },
    ],
    response: GetEvaluationCategoriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-faq-sources-count/',
    alias: 'ai_getFAQSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFAQSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-form-generation-status/',
    alias: 'ai_getFormGenerationStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFormGenerationStatusViewRequestData,
      },
    ],
    response: z.object({ status: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-polygons/',
    alias: 'ai_getGISLayerPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerPolygonsViewRequestData,
      },
    ],
    response: GetGISLayerPolygonsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-properties/',
    alias: 'ai_getGISLayerProperties',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerPropertiesViewRequestData,
      },
    ],
    response: GetGISLayerPropertiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-source/',
    alias: 'ai_getGISLayerSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerSourceViewRequestData,
      },
    ],
    response: GetGISLayerSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-layer-sources-count/',
    alias: 'ai_getGISLayerSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISLayerSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gis-polygons/',
    alias: 'ai_getGISPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGISPolygonsViewRequestData,
      },
    ],
    response: GetGISPolygonsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-gpkg-layers/',
    alias: 'ai_getGpkgLayers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetGpkgLayersViewRequestData,
      },
    ],
    response: GetGpkgLayersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-interaction-category-update-status/',
    alias: 'ai_getInteractionCategoryUpdateStatus',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetInteractionCategoryUpdateStatusViewRequestData,
      },
    ],
    response: z.object({ in_progress: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-page-source/',
    alias: 'ai_getPageSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPageSourceViewRequestData,
      },
    ],
    response: GetPageSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-page-sources-count/',
    alias: 'ai_getPageSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPageSourcesCountViewRequestData,
      },
    ],
    response: z.object({ page_count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-supported-document-types/',
    alias: 'ai_getSupportedDocumentTypes',
    description: `Exists to emit SupportedDocumentTypeEnum into the generated zod schema.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetSupportedDocumentTypesViewRequestData,
      },
    ],
    response: GetSupportedDocumentTypesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-transfer-line-sources-count/',
    alias: 'ai_getTransferLineSourcesCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTransferLineSourcesCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/get-website-type/',
    alias: 'ai_getWebsiteType',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetWebsiteTypeViewRequestData,
      },
    ],
    response: GetWebsiteTypeViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/gis-lookup/',
    alias: 'ai_elevenLabsGISLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsGISLookupViewRequestData,
      },
    ],
    response: ElevenLabsGISLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/hooks/backup-inbound-call-router/',
    alias: 'ai_hooks_backup_inbound_call_router_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/call-status-change/',
    alias: 'ai_hooks_call_status_change_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/firecrawl/monitor/',
    alias: 'ai_hooks_firecrawl_monitor_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/inbound-call-router/',
    alias: 'ai_hooks_inbound_call_router_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/inbound-sms/',
    alias: 'ai_hooks_inbound_sms_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable',
    alias: 'ai_hooks_voice_stable_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-action-or-play/:ai_interaction_id/:org_id/:current_language/:turn_key',
    alias: 'ai_hooks_voice_stable_action_or_play_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ai_interaction_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'turn_key',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-collect/:ai_interaction_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_collect_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ai_interaction_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-gather/:ai_interaction_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_gather_create',
    description: `Sets up the Gather for the next user input`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'ai_interaction_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/hooks/voice-stable-no-input/:ai_interaction_id/:org_id/:current_language',
    alias: 'ai_hooks_voice_stable_no_input_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'ai_interaction_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
      {
        name: 'current_language',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'org_id',
        type: 'Path',
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/import-gis-polygons/',
    alias: 'ai_importGISPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportGISPolygonsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/ingest-uploaded-documents/',
    alias: 'ai_ingestUploadedDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IngestUploadedDocumentsViewRequestData,
      },
    ],
    response: IngestUploadedDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/ai/initialize-ai/',
    alias: 'ai_initialize_ai_retrieve',
    requestFormat: 'json',
    parameters: [
      {
        name: 'device_id',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'organization_handle',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'organization_id',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'user_id',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: InitializeAIViewResponseData,
  },
  {
    method: 'post',
    path: '/ai/initialize-ai/',
    alias: 'ai_initializeAI',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InitializeAIViewRequestData,
      },
    ],
    response: InitializeAIViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-active-calls/',
    alias: 'ai_listActiveCalls',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListActiveCallsViewRequestData,
      },
    ],
    response: ListActiveCallsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-emails/',
    alias: 'ai_listAIEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIEmailsViewRequestData,
      },
    ],
    response: ListAIEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-interaction-evaluation-enums/',
    alias: 'ai_listAIInteractionEvaluationEnums',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIInteractionEvaluationEnumsViewRequestData,
      },
    ],
    response: ListAIInteractionEvaluationEnumsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-interaction-summaries/',
    alias: 'ai_listAIInteractionSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIInteractionSummariesViewRequestData,
      },
    ],
    response: ListAIInteractionSummariesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-phone-lines/',
    alias: 'ai_listAIPhoneLines',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIPhoneLinesViewRequestData,
      },
    ],
    response: ListAIPhoneLinesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-process-sources/',
    alias: 'ai_listAIProcessSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIProcessSourcesViewRequestData,
      },
    ],
    response: ListAIProcessSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-skills/',
    alias: 'ai_listAISkills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAISkillsViewRequestData,
      },
    ],
    response: ListAISkillsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-usage-policies/',
    alias: 'ai_listAIUsagePolicies',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIUsagePoliciesViewRequestData,
      },
    ],
    response: ListAIUsagePoliciesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-ai-usage-policy-snapshots/',
    alias: 'ai_listAIUsagePolicySnapshots',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAIUsagePolicySnapshotsViewRequestData,
      },
    ],
    response: ListAIUsagePolicySnapshotsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-assistant-interactions/',
    alias: 'ai_listAssistantInteractions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAssistantInteractionsViewRequestData,
      },
    ],
    response: ListAssistantInteractionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-assistant-tools/',
    alias: 'ai_listAssistantTools',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAssistantToolsViewRequestData,
      },
    ],
    response: ListAssistantToolsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-crawl-tasks/',
    alias: 'ai_listCrawlTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCrawlTasksViewRequestData,
      },
    ],
    response: ListCrawlTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-faq-sources/',
    alias: 'ai_listFAQSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFAQSourcesViewRequestData,
      },
    ],
    response: ListFAQSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-folders/',
    alias: 'ai_listFolders',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFoldersViewRequestData,
      },
    ],
    response: ListFoldersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-gis-layer-sources/',
    alias: 'ai_listGISLayerSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGISLayerSourcesViewRequestData,
      },
    ],
    response: ListGISLayerSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-page-sources/',
    alias: 'ai_listPageSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPageSourcesViewRequestData,
      },
    ],
    response: ListPageSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-run-responses/',
    alias: 'ai_listRunResponses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRunResponsesViewRequestData,
      },
    ],
    response: ListRunResponsesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-scenario-templates/',
    alias: 'ai_listScenarioTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListScenarioTemplatesViewRequestData,
      },
    ],
    response: ListScenarioTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-suggestions/',
    alias: 'ai_listSuggestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSuggestionsViewRequestData,
      },
    ],
    response: ListSuggestionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-test-runs/',
    alias: 'ai_listTestRuns',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTestRunsViewRequestData,
      },
    ],
    response: ListTestRunsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-test-scenarios/',
    alias: 'ai_listScenarios',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListScenariosViewRequestData,
      },
    ],
    response: ListScenariosViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-transfer-line-sources/',
    alias: 'ai_listTransferLineSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTransferLineSourcesViewRequestData,
      },
    ],
    response: ListTransferLineSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-transferred-numbers/',
    alias: 'ai_listTransferredNumbers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTransferredNumbersViewRequestData,
      },
    ],
    response: ListTransferredNumbersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/list-voices/',
    alias: 'ai_listVoices',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListVoicesViewRequestData,
      },
    ],
    response: ListVoicesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/load-test-scenarios-from-template/',
    alias: 'ai_loadScenariosFromTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoadScenariosFromTemplateViewRequestData,
      },
    ],
    response: LoadScenariosFromTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/log-conversation/',
    alias: 'ai_elevenLabsLogConversation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsLogConversationViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/mark-ai-interaction-as-test/',
    alias: 'ai_markAIInteractionAsTest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MarkAIInteractionAsTestViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/conversation-initiation/',
    alias: 'ai_phonicConversationInitiation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicConversationInitiationViewRequestData,
      },
    ],
    response: PhonicConversationInitiationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/events/',
    alias: 'ai_phonicEvents',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicEventsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/external-api-call/',
    alias: 'ai_phonicExternalAPICall',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicExternalAPICallViewRequestData,
      },
    ],
    response: PhonicExternalAPICallViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/gis-lookup/',
    alias: 'ai_phonicGISLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicGISLookupViewRequestData,
      },
    ],
    response: PhonicGISLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/list-voices/',
    alias: 'ai_listPhonicVoices',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPhonicVoicesViewRequestData,
      },
    ],
    response: ListPhonicVoicesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/retrieve-sources/',
    alias: 'ai_phonicRetrieveSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicRetrieveSourcesViewRequestData,
      },
    ],
    response: PhonicRetrieveSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/send-sms/',
    alias: 'ai_phonicSendSMS',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicSendSMSViewRequestData,
      },
    ],
    response: PhonicSendSMSViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/phonic/transfer-call/',
    alias: 'ai_phonicTransferCall',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicTransferCallViewRequestData,
      },
    ],
    response: PhonicTransferCallViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/preview-bulk-archive-page-sources/',
    alias: 'ai_previewBulkArchivePageSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PreviewBulkArchivePageSourcesViewRequestData,
      },
    ],
    response: PreviewBulkArchivePageSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/preview-voice/',
    alias: 'ai_preview_voice_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PreviewVoiceRequest,
      },
    ],
    response: z.instanceof(File),
    errors: [
      {
        status: 400,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 403,
        schema: z.object({}).partial().passthrough(),
      },
      {
        status: 502,
        schema: z.object({}).partial().passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/print-ai-interaction-transcript/',
    alias: 'ai_print_ai_interaction_transcript_create',
    description: `Generate a PDF of an AI interaction transcript.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/ai/rechunk-page-source/',
    alias: 'ai_rechunkPageSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RechunkPageSourceViewRequestData,
      },
    ],
    response: RechunkPageSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/reextract-page-source/',
    alias: 'ai_reextractPageSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReextractPageSourceViewRequestData,
      },
    ],
    response: ReextractPageSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/remove-gis-polygons/',
    alias: 'ai_removeGISPolygons',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveGISPolygonsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/remove-test-scenario/',
    alias: 'ai_removeScenario',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveScenarioViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/resolve-ai-interaction-organization/',
    alias: 'ai_resolveAIInteractionOrganization',
    description: `Resolve which organization an AI interaction belongs to.

Used to redirect users with old Super-scoped links to the correct org-scoped URL.
Returns the organization handle if the requesting user has RBAC read access to that org&#x27;s
interaction logs, otherwise 403.

Note: no required_policies here because validate_permissions() requires organization_identity_id
in the request body, which we don&#x27;t have — that&#x27;s what we&#x27;re resolving. Authentication is still
enforced by UserAndSessionValid; the org-specific RBAC check is done inline in post_handler.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResolveAIInteractionOrganizationViewRequestData,
      },
    ],
    response: z.object({ organization_handle: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/retrieve-sources/',
    alias: 'ai_elevenLabsRetrieveSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsRetrieveSourcesViewRequestData,
      },
    ],
    response: ElevenLabsRetrieveSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/scrape-pages/',
    alias: 'ai_scrapePages',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ScrapePagesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/send-sms/',
    alias: 'ai_elevenLabsSendSMS',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsSendSMSViewRequestData,
      },
    ],
    response: ElevenLabsSendSMSViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/set-response-feedback-impression/',
    alias: 'ai_setResponseFeedbackImpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetResponseFeedbackImpressionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/set-suggestions/',
    alias: 'ai_setSuggestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetSuggestionsViewRequestData,
      },
    ],
    response: SetSuggestionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/start-test-run/',
    alias: 'ai_startTestRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartTestRunViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/submit-response-feedback/',
    alias: 'ai_submitResponseFeedback',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SubmitResponseFeedbackViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/suggest-crawl-config/',
    alias: 'ai_suggestCrawlConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuggestCrawlConfigViewRequestData,
      },
    ],
    response: z.object({ config: z.unknown() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/super-get-ai-interaction-system-prompt/',
    alias: 'ai_superGetAIInteractionSystemPrompt',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperGetAIInteractionSystemPromptViewRequestData,
      },
    ],
    response: z.object({ system_prompt: z.string().nullable() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/super-get-ai-interaction-transcript/',
    alias: 'ai_superGetAIInteractionTranscript',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperGetAIInteractionTranscriptViewRequestData,
      },
    ],
    response: SuperGetAIInteractionTranscriptViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/transcribe-assistant-audio/',
    alias: 'ai_transcribeAssistantAudio',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TranscribeAssistantAudioViewRequestData,
      },
    ],
    response: z.object({ text: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/transfer-call/',
    alias: 'ai_elevenLabsTransferCall',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsTransferCallViewRequestData,
      },
    ],
    response: ElevenLabsTransferCallViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/translate-text/',
    alias: 'ai_translateText',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TranslateTextViewRequestData,
      },
    ],
    response: z.object({ translated_text: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-ai-interaction-title/',
    alias: 'ai_updateAIInteractionTitle',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateAIInteractionTitleViewRequestData,
      },
    ],
    response: z.object({ title: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-crawl-task/',
    alias: 'ai_updateCrawlTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCrawlTaskViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-custom-category-evaluation-config/',
    alias: 'ai_updateCustomCategoryEvaluationConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCustomCategoryEvaluationConfigViewRequestData,
      },
    ],
    response: z.object({ prompt_template: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-interaction-categories/',
    alias: 'ai_updateInteractionCategories',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateInteractionCategoriesViewRequestData,
      },
    ],
    response: UpdateInteractionCategoriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/update-test-run/',
    alias: 'ai_updateTestRun',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateTestRunViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/upload-assistant-attachment/',
    alias: 'ai_uploadAssistantAttachment',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadAssistantAttachmentViewRequestData,
      },
    ],
    response: UploadAssistantAttachmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/ai/upsert-ai-email/',
    alias: 'ai_upsertAIEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertAIEmailViewRequestData,
      },
    ],
    response: UpsertAIEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/build-identified-workflows/',
    alias: 'analytics_buildIdentifiedWorkflows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BuildIdentifiedWorkflowsViewRequestData,
      },
    ],
    response: BuildIdentifiedWorkflowsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/clear-identified-workflows/',
    alias: 'analytics_clearIdentifiedWorkflows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ClearIdentifiedWorkflowsViewRequestData,
      },
    ],
    response: z.object({ cleared: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/export-llm-cost/',
    alias: 'analytics_exportLLMCost',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ExportLLMCostViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-channel-volume',
    alias: 'analytics_getChannelVolume',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetChannelVolumeViewRequestData,
      },
    ],
    response: GetChannelVolumeViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-crawl-task-dashboard/',
    alias: 'analytics_getCrawlTaskDashboard',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCrawlTaskDashboardViewRequestData,
      },
    ],
    response: GetCrawlTaskDashboardViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-firecrawl-concurrency-history/',
    alias: 'analytics_getFirecrawlConcurrencyHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFirecrawlConcurrencyHistoryViewRequestData,
      },
    ],
    response: GetFirecrawlConcurrencyHistoryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-firecrawl-concurrency/',
    alias: 'analytics_getFirecrawlConcurrency',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFirecrawlConcurrencyViewRequestData,
      },
    ],
    response: GetFirecrawlConcurrencyViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-front-desk-data',
    alias: 'analytics_getFrontDeskData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFrontDeskDataRequestData,
      },
    ],
    response: GetFrontDeskDataResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-health-check-summary',
    alias: 'analytics_getHealthCheckSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetHealthCheckSummaryViewRequestData,
      },
    ],
    response: GetHealthCheckSummaryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-identified-workflows/',
    alias: 'analytics_getIdentifiedWorkflows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetIdentifiedWorkflowsViewRequestData,
      },
    ],
    response: GetIdentifiedWorkflowsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-integrations',
    alias: 'analytics_getIntegrations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetIntegrationsViewRequestData,
      },
    ],
    response: GetIntegrationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-interaction-dashboard-stats/',
    alias: 'analytics_getInteractionDashboardStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetInteractionDashboardStatsViewRequestData,
      },
    ],
    response: GetInteractionDashboardStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-knowledge-base-quality',
    alias: 'analytics_getKnowledgeBaseQuality',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetKnowledgeBaseQualityViewRequestData,
      },
    ],
    response: GetKnowledgeBaseQualityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-kpis',
    alias: 'analytics_getKPIs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetKPIsViewRequestData,
      },
    ],
    response: GetKPIsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-llm-cost-dashboard-stats/',
    alias: 'analytics_getLLMCostDashboardStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetLLMCostDashboardStatsViewRequestData,
      },
    ],
    response: GetLLMCostDashboardStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-org-workflow-scorecard/',
    alias: 'analytics_getOrgWorkflowScorecard',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrgWorkflowScorecardViewRequestData,
      },
    ],
    response: GetOrgWorkflowScorecardViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-organization-ai-interaction-flows',
    alias: 'analytics_getOrganizationAIInteractionFlows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationAIInteractionFlowsRequestData,
      },
    ],
    response: GetOrganizationAIInteractionFlowsResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-organization-ai-interaction-stats',
    alias: 'analytics_getOrganizationAIInteractionStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationAIInteractionStatsRequestData,
      },
    ],
    response: GetOrganizationAIInteractionStatsResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-tasks',
    alias: 'analytics_getTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTasksViewRequestData,
      },
    ],
    response: GetTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-tool-call-dashboard-stats/',
    alias: 'analytics_getToolCallDashboardStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetToolCallDashboardStatsViewRequestData,
      },
    ],
    response: GetToolCallDashboardStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-top-categories',
    alias: 'analytics_getTopCategories',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTopCategoriesViewRequestData,
      },
    ],
    response: GetTopCategoriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-top-domains',
    alias: 'analytics_getTopDomains',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetTopDomainsViewRequestData,
      },
    ],
    response: GetTopDomainsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-workflow-performance',
    alias: 'analytics_getWorkflowPerformance',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetWorkflowPerformanceViewRequestData,
      },
    ],
    response: GetWorkflowPerformanceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/get-workflow-usage-dashboard-stats/',
    alias: 'analytics_getWorkflowUsageDashboardStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetWorkflowUsageDashboardStatsViewRequestData,
      },
    ],
    response: GetWorkflowUsageDashboardStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/list-crawl-tasks-dashboard/',
    alias: 'analytics_listCrawlTasksMetrics',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCrawlTasksMetricsViewRequestData,
      },
    ],
    response: ListCrawlTasksMetricsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/list-tool-call-summaries',
    alias: 'analytics_listToolCallSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListToolCallSummariesViewRequestData,
      },
    ],
    response: ListToolCallSummariesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/request-ai-analytics-report/',
    alias: 'analytics_requestAIAnalyticsReport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RequestAIAnalyticsReportViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/set-workflow-general-intake/',
    alias: 'analytics_setWorkflowGeneralIntake',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetWorkflowGeneralIntakeViewRequestData,
      },
    ],
    response: z.object({ general_intake: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/analytics/upload-identified-workflows/',
    alias: 'analytics_uploadIdentifiedWorkflows',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadIdentifiedWorkflowsViewRequestData,
      },
    ],
    response: z.object({ file_name: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-case-member/',
    alias: 'cases_addCaseMember',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCaseMemberViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-comment/',
    alias: 'cases_addComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddCommentViewRequestData,
      },
    ],
    response: AddCommentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/add-review-comment/',
    alias: 'cases_addReviewComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddReviewCommentViewRequestData,
      },
    ],
    response: AddReviewCommentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/append-agentic-implementation-message/',
    alias: 'agentic_implementation_appendAgenticImplementationMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AppendAgenticImplementationMessageViewRequestData,
      },
    ],
    response: AppendAgenticImplementationMessageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/attach-process/',
    alias: 'cases_attachProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AttachProcessViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/begin-review/',
    alias: 'cases_beginReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BeginReviewViewRequestData,
      },
    ],
    response: BeginReviewViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/bulk-create-input-expressions/',
    alias: 'cases_bulkCreateInputExpressions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkCreateInputExpressionsViewRequestData,
      },
    ],
    response: BulkCreateInputExpressionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/bulk-edit-cases/',
    alias: 'cases_bulkEditCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkEditCasesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/bulk-edit-workflows/',
    alias: 'cases_bulkEditWorkflows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkEditWorkflowsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/calculate-form-data-hash/',
    alias: 'cases_calculateFormDataHash',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CalculateFormDataHashViewRequestData,
      },
    ],
    response: z.object({ hash_value: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-apply/get-case-apply-stepper-stage-statuses/',
    alias: 'cases_getCaseApplyStepperStageStatuses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseApplyStepperStageStatusesRequestData,
      },
    ],
    response: GetCaseApplyStepperStageStatusesResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-active-tasks/',
    alias: 'cases_getActiveTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetActiveTasksViewRequestData,
      },
    ],
    response: GetActiveTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-activity/',
    alias: 'cases_getCaseFeedActivity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedActivityViewRequestData,
      },
    ],
    response: GetCaseFeedActivityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-contact-record-info/',
    alias: 'cases_getCaseFeedContactRecordInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedContactRecordInfoViewRequestData,
      },
    ],
    response: GetCaseFeedContactRecordInfoViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-contact-record-parties/',
    alias: 'cases_getCaseFeedContactRecordParties',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedContactRecordPartiesViewRequestData,
      },
    ],
    response: GetCaseFeedContactRecordPartiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-documents/',
    alias: 'cases_getCaseFeedDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedDocumentsViewRequestData,
      },
    ],
    response: GetCaseFeedDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-info/',
    alias: 'cases_getCaseFeedInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedInfoViewRequestData,
      },
    ],
    response: GetCaseFeedInfoViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-interactions/',
    alias: 'cases_getCaseFeedInteractions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedInteractionsViewRequestData,
      },
    ],
    response: GetCaseFeedInteractionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-parties/',
    alias: 'cases_getCaseFeedParties',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedPartiesViewRequestData,
      },
    ],
    response: GetCaseFeedPartiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/case-feed/get-case-feed-workflow/',
    alias: 'cases_getCaseFeedWorkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseFeedWorkflowViewRequestData,
      },
    ],
    response: GetCaseFeedWorkflowViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/complete-redaction-block/',
    alias: 'cases_completeRedactionBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CompleteRedactionBlockViewRequestData,
      },
    ],
    response: CompleteRedactionBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/complete-review/',
    alias: 'cases_completeReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CompleteReviewViewRequestData,
      },
    ],
    response: CompleteReviewViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-expression/',
    alias: 'cases_copyExpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyExpressionViewRequestData,
      },
    ],
    response: CopyExpressionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-process/',
    alias: 'cases_copyProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyProcessViewRequestData,
      },
    ],
    response: CopyProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-section-substep/',
    alias: 'cases_copySectionSubStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopySectionSubStepViewRequestData,
      },
    ],
    response: CopySectionSubStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/copy-workflow/',
    alias: 'cases_copyWorkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyWorkflowViewRequestData,
      },
    ],
    response: CopyWorkflowViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-case/',
    alias: 'cases_createCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCaseViewRequestData,
      },
    ],
    response: CreateCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-cases-tab/',
    alias: 'cases_createCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCasesTabViewRequestData,
      },
    ],
    response: CreateCasesTabViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-draft-version/',
    alias: 'cases_createDraftVersion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDraftVersionViewRequestData,
      },
    ],
    response: CreateDraftVersionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-expression/',
    alias: 'cases_createExpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateExpressionViewRequestData,
      },
    ],
    response: CreateExpressionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-form-style-element',
    alias: 'cases_createFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateFormStyleElementViewRequestData,
      },
    ],
    response: CreateFormStyleElementViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-input-step/',
    alias: 'cases_createInputStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateInputStepViewRequestData,
      },
    ],
    response: CreateInputStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-message/',
    alias: 'cases_createMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateMessageViewRequestData,
      },
    ],
    response: CreateMessageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-process-stage/',
    alias: 'cases_createProcessStage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateProcessStageViewRequestData,
      },
    ],
    response: CreateProcessStageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-relation/',
    alias: 'cases_createRelation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRelationViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-review-comment-thread/',
    alias: 'cases_createReviewCommentThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateReviewCommentThreadViewRequestData,
      },
    ],
    response: CreateReviewCommentThreadViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-section-step/',
    alias: 'cases_createSectionStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateSectionStepViewRequestData,
      },
    ],
    response: CreateSectionStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-task/',
    alias: 'cases_createTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTaskViewRequestData,
      },
    ],
    response: CreateTaskViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-test-case/',
    alias: 'cases_createTestCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTestCaseViewRequestData,
      },
    ],
    response: CreateTestCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-value/',
    alias: 'cases_createValue',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateValueViewRequestData,
      },
    ],
    response: CreateValueViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-voicemail-process/',
    alias: 'cases_createVoicemailProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateVoicemailProcessViewRequestData,
      },
    ],
    response: CreateVoicemailProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/create-workflow/',
    alias: 'cases_createWorkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateWorkflowViewRequestData,
      },
    ],
    response: CreateWorkflowViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-assignment/',
    alias: 'cases_deleteAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAssignmentViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-bill-step/',
    alias: 'cases_deleteBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteBillStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-case/',
    alias: 'cases_deleteCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCaseViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-cases-tab/',
    alias: 'cases_deleteCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCasesTabViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-comment/',
    alias: 'cases_deleteComment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteCommentViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-expressions/',
    alias: 'cases_deleteExpressions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteExpressionsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-form-step/',
    alias: 'cases_deleteFormStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFormStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-form-style-element/',
    alias: 'cases_deleteFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteFormStyleElementViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-input-substep/',
    alias: 'cases_deleteInputSubstep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteInputSubstepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-process-stage/',
    alias: 'cases_deleteProcessStage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteProcessStageViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-section-substep/',
    alias: 'cases_deleteSectionSubstep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteSectionSubstepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-step-assignment/',
    alias: 'cases_deleteStepAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteStepAssignmentViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/delete-template/',
    alias: 'cases_deleteTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTemplateViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/download-cases/',
    alias: 'cases_downloadCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadCasesViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/draft-communication-message/',
    alias: 'cases_draftCommunicationMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DraftCommunicationMessageViewRequestData,
      },
    ],
    response: DraftCommunicationMessageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-bill-step/',
    alias: 'cases_editBillStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditBillStepViewRequestData,
      },
    ],
    response: EditBillStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-case/',
    alias: 'cases_editCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCaseViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-cases-tab/',
    alias: 'cases_editCasesTab',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCasesTabViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-datetime-step/',
    alias: 'cases_editDateTimeStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDateTimeStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-due-date/',
    alias: 'cases_editDueDate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDueDateViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-expression/',
    alias: 'cases_editExpression',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditExpressionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-file-step/',
    alias: 'cases_editFileStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFileStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-form-step/',
    alias: 'cases_editFormStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFormStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-form-style-element/',
    alias: 'cases_editFormStyleElement',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditFormStyleElementViewRequestData,
      },
    ],
    response: EditFormStyleElementViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-identity-step/',
    alias: 'cases_editIdentityStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditIdentityStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-input-step/',
    alias: 'cases_editInputStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditInputStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-location-step/',
    alias: 'cases_editLocationStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLocationStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-number-step/',
    alias: 'cases_editNumberStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditNumberStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-process-stage/',
    alias: 'cases_editProcessStage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditProcessStageViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-process/',
    alias: 'cases_editProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditProcessViewRequestData,
      },
    ],
    response: EditProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-section-substep/',
    alias: 'cases_editSectionSubStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditSectionSubStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-signature-step/',
    alias: 'cases_editSignatureStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditSignatureStepViewRequestData,
      },
    ],
    response: EditSignatureStepViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-step/',
    alias: 'cases_editStep',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditStepViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/edit-task/',
    alias: 'cases_editTask',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTaskViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/export-process-json/',
    alias: 'cases_exportProcessJson',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ExportProcessJsonViewRequestData,
      },
    ],
    response: z.object({ process_json: z.object({}).partial().passthrough() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/generate-ai-case-summary/',
    alias: 'cases_generateAICaseSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateAICaseSummaryViewRequestData,
      },
    ],
    response: z.object({ summary: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/generate-ai-email-summary/',
    alias: 'cases_generateAIEmailSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateAIEmailSummaryViewRequestData,
      },
    ],
    response: z.object({ summary: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/generate-document/',
    alias: 'cases_generateDocument',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateDocumentViewRequestData,
      },
    ],
    response: z.object({ document_url: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-count/',
    alias: 'cases_getCaseCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseCountViewRequestData,
      },
    ],
    response: GetCaseCountViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-id/',
    alias: 'cases_getCaseId',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseIdViewRequestData,
      },
    ],
    response: GetCaseIdViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case-owner/',
    alias: 'cases_getCaseOwner',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseOwnerViewRequestData,
      },
    ],
    response: GetCaseOwnerViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-case/',
    alias: 'cases_getCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseViewRequestData,
      },
    ],
    response: GetCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-cases-table/',
    alias: 'cases_getCasesTable',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCasesTableViewRequestData,
      },
    ],
    response: GetCasesTableViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-form-step-count/',
    alias: 'cases_getFormStepCount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFormStepCountViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-form-step-time/',
    alias: 'cases_getFormStepTime',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetFormStepTimeViewRequestData,
      },
    ],
    response: z.object({ average_duration: z.string().nullable() }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-or-create-agentic-implementation-log/',
    alias: 'agentic_implementation_getOrCreateAgenticImplementationLog',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrCreateAgenticImplementationLogViewRequestData,
      },
    ],
    response: GetOrCreateAgenticImplementationLogViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-process/',
    alias: 'cases_getProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetProcessViewRequestData,
      },
    ],
    response: GetProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-workflow-summary/',
    alias: 'cases_getWorkflowSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetWorkflowSummaryViewRequestData,
      },
    ],
    response: GetWorkflowSummaryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/get-workflow/',
    alias: 'cases_getWorkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetWorkflowViewRequestData,
      },
    ],
    response: GetWorkflowViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/import-email/',
    alias: 'cases_importEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportEmailViewRequestData,
      },
    ],
    response: ImportEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/invite-case-member-by-email/',
    alias: 'cases_inviteCaseMemberByEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InviteCaseMemberByEmailViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-base-processes/',
    alias: 'cases_listBaseProcesses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListBaseProcessesViewRequestData,
      },
    ],
    response: ListBaseProcessesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-calculated-inputs/',
    alias: 'cases_listCalculatedInputs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCalculatedInputsViewRequestData,
      },
    ],
    response: ListCalculatedInputsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-case-export-files/',
    alias: 'cases_listCaseExportFiles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseExportFilesViewRequestData,
      },
    ],
    response: ListCaseExportFilesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-case-members/',
    alias: 'cases_listCaseMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseMembersViewRequestData,
      },
    ],
    response: ListCaseMembersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases-tabs/',
    alias: 'cases_listCasesTabs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesTabsViewRequestData,
      },
    ],
    response: ListCasesTabsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases-with-locations/',
    alias: 'cases_listCasesWithLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesWithLocationViewRequestData,
      },
    ],
    response: ListCasesWithLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-cases/',
    alias: 'cases_listCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCasesViewRequestData,
      },
    ],
    response: ListCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-document-templates/',
    alias: 'cases_listDocumentTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListDocumentTemplatesViewRequestData,
      },
    ],
    response: ListDocumentTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-expression-constants/',
    alias: 'cases_listExpressionConstants',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListExpressionConstantsViewRequestData,
      },
    ],
    response: ListExpressionConstantsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-expressions/',
    alias: 'cases_listExpressions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListExpressionsViewRequestData,
      },
    ],
    response: ListExpressionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-form-steps/',
    alias: 'cases_listFormSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFormStepsViewRequestData,
      },
    ],
    response: ListFormStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-forms/',
    alias: 'cases_listForms',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListFormsViewRequestData,
      },
    ],
    response: ListFormsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-input-substeps/',
    alias: 'cases_listInputSubSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListInputSubStepsViewRequestData,
      },
    ],
    response: ListInputSubStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-inputs/',
    alias: 'cases_listInputs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListInputsViewRequestData,
      },
    ],
    response: ListInputsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-notifications/',
    alias: 'cases_listNotifications',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListNotificationsViewRequestData,
      },
    ],
    response: ListNotificationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-process-cases/',
    alias: 'cases_listProcessCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessCasesViewRequestData,
      },
    ],
    response: ListProcessCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-process-summaries/',
    alias: 'cases_listProcessSummaries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessSummariesViewRequestData,
      },
    ],
    response: ListProcessSummariesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes-by-id/',
    alias: 'cases_listProcessesById',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesByIdViewRequestData,
      },
    ],
    response: ListProcessesByIdViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes-restricted/',
    alias: 'cases_listProcessesRestricted',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesRestrictedViewRequestData,
      },
    ],
    response: ListProcessesRestrictedViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes-stats/',
    alias: 'cases_listProcessesStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesStatsViewRequestData,
      },
    ],
    response: ListProcessesStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-processes/',
    alias: 'cases_listProcesses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListProcessesViewRequestData,
      },
    ],
    response: ListProcessesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-public-review-comment-threads/',
    alias: 'cases_listPublicReviewCommentThreads',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPublicReviewCommentThreadsViewRequestData,
      },
    ],
    response: ListPublicReviewCommentThreadsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-relations/',
    alias: 'cases_listRelations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRelationsViewRequestData,
      },
    ],
    response: ListRelationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-review-comment-threads/',
    alias: 'cases_listReviewCommentThreads',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListReviewCommentThreadsViewRequestData,
      },
    ],
    response: ListReviewCommentThreadsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-reviews/',
    alias: 'cases_listReviews',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListReviewsViewRequestData,
      },
    ],
    response: ListReviewsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-section-substeps/',
    alias: 'cases_listSectionSubSteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSectionSubStepsViewRequestData,
      },
    ],
    response: ListSectionSubStepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-sections/',
    alias: 'cases_listSections',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListSectionsViewRequestData,
      },
    ],
    response: ListSectionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-step-assignments',
    alias: 'cases_listStepAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStepAssignmentsViewRequestData,
      },
    ],
    response: ListStepAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-tasks/',
    alias: 'cases_listTasks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTasksViewRequestData,
      },
    ],
    response: ListTasksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-templates/',
    alias: 'cases_listTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTemplatesViewRequestData,
      },
    ],
    response: ListTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-values/',
    alias: 'cases_listValues',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListValuesViewRequestData,
      },
    ],
    response: ListValuesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-workflow-stats/',
    alias: 'cases_listWorkflowStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListWorkflowStatsViewRequestData,
      },
    ],
    response: ListWorkflowStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-workflow-versions/',
    alias: 'cases_listWorkflowVersions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListWorkflowVersionsViewRequestData,
      },
    ],
    response: ListWorkflowVersionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/list-workflows/',
    alias: 'cases_listWorkflows',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListWorkflowsViewRequestData,
      },
    ],
    response: ListWorkflowsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/notify/',
    alias: 'cases_notify',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: NotifyViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/perform-ai-presubmission-review/',
    alias: 'cases_performAIPreSubmissionReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PerformAIPreSubmissionReviewViewRequestData,
      },
    ],
    response: PerformAIPreSubmissionReviewViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/phonic/voice-create-case/',
    alias: 'cases_phonicVoiceCreateCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicVoiceCreateCaseViewRequestData,
      },
    ],
    response: PhonicVoiceCreateCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/phonic/voice-get-next-questions/',
    alias: 'cases_phonicVoiceGetNextQuestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicVoiceGetNextQuestionsViewRequestData,
      },
    ],
    response: z.object({ time_taken_ms: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/phonic/voice-record-answers/',
    alias: 'cases_phonicVoiceRecordAnswers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PhonicVoiceRecordAnswersViewRequestData,
      },
    ],
    response: PhonicVoiceRecordAnswersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/print-case/',
    alias: 'cases_print_case_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/cases/process-editor/auto-map-document-template-fields/',
    alias: 'cases_autoMapDocumentTemplateFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AutoMapDocumentTemplateFieldsViewRequestData,
      },
    ],
    response: AutoMapDocumentTemplateFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-api-automation-block/',
    alias: 'cases_createAPIAutomationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIAutomationBlockViewRequestData,
      },
    ],
    response: CreateAPIAutomationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-communication-block/',
    alias: 'cases_createCommunicationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateCommunicationBlockViewRequestData,
      },
    ],
    response: CreateCommunicationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-connector/',
    alias: 'cases_createConnector',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateConnectorViewRequestData,
      },
    ],
    response: CreateConnectorViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-demo-block/',
    alias: 'cases_createDemoBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDemoBlockViewRequestData,
      },
    ],
    response: CreateDemoBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-document-generation-block/',
    alias: 'cases_createDocumentGenerationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDocumentGenerationBlockViewRequestData,
      },
    ],
    response: CreateDocumentGenerationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-form-block/',
    alias: 'cases_createFormBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateFormBlockViewRequestData,
      },
    ],
    response: CreateFormBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-license-block/',
    alias: 'cases_createLicenseBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLicenseBlockViewRequestData,
      },
    ],
    response: CreateLicenseBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-payment-block/',
    alias: 'cases_createPaymentBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePaymentBlockViewRequestData,
      },
    ],
    response: CreatePaymentBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-redaction-block/',
    alias: 'cases_createRedactionBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRedactionBlockViewRequestData,
      },
    ],
    response: CreateRedactionBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-review-block/',
    alias: 'cases_createReviewBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateReviewBlockViewRequestData,
      },
    ],
    response: CreateReviewBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-role-block/',
    alias: 'cases_createRoleBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRoleBlockViewRequestData,
      },
    ],
    response: CreateRoleBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-status-block/',
    alias: 'cases_createStatusBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateStatusBlockViewRequestData,
      },
    ],
    response: CreateStatusBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/create-tag-block/',
    alias: 'cases_createTagBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTagBlockViewRequestData,
      },
    ],
    response: CreateTagBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/delete-block/',
    alias: 'cases_deleteBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteBlockViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/delete-connector/',
    alias: 'cases_deleteConnector',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteConnectorViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/detect-document-template-fields/',
    alias: 'cases_detectDocumentTemplateFields',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DetectDocumentTemplateFieldsViewRequestData,
      },
    ],
    response: DetectDocumentTemplateFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-api-automation-block/',
    alias: 'cases_editAPIAutomationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditAPIAutomationBlockViewRequestData,
      },
    ],
    response: EditAPIAutomationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-block/',
    alias: 'cases_editBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditBlockViewRequestData,
      },
    ],
    response: EditBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-communication-block/',
    alias: 'cases_editCommunicationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCommunicationBlockViewRequestData,
      },
    ],
    response: EditCommunicationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-connector/',
    alias: 'cases_editConnector',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditConnectorViewRequestData,
      },
    ],
    response: EditConnectorViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-demo-block/',
    alias: 'cases_editDemoBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDemoBlockViewRequestData,
      },
    ],
    response: EditDemoBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-document-generation-block/',
    alias: 'cases_editDocumentGenerationBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDocumentGenerationBlockViewRequestData,
      },
    ],
    response: EditDocumentGenerationBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-document-template/',
    alias: 'cases_editDocumentTemplate',
    description: `Edit an organization&#x27;s document template: its name, and optionally the
Docx file itself along with the fields parsed from it.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditDocumentTemplateViewRequestData,
      },
    ],
    response: EditDocumentTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-license-block/',
    alias: 'cases_editLicenseBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLicenseBlockViewRequestData,
      },
    ],
    response: EditLicenseBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-redaction-block/',
    alias: 'cases_editRedactionBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditRedactionBlockViewRequestData,
      },
    ],
    response: EditRedactionBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-review-block/',
    alias: 'cases_editReviewBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditReviewBlockViewRequestData,
      },
    ],
    response: EditReviewBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-role-block/',
    alias: 'cases_editRoleBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditRoleBlockViewRequestData,
      },
    ],
    response: EditRoleBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-status-block/',
    alias: 'cases_editStatusBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditStatusBlockViewRequestData,
      },
    ],
    response: EditStatusBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/edit-tag-block/',
    alias: 'cases_editTagBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTagBlockViewRequestData,
      },
    ],
    response: EditTagBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/get-organization-source-emails/',
    alias: 'cases_getOrganizationSourceEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationSourceEmailsViewRequestData,
      },
    ],
    response: GetOrganizationSourceEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/list-block-configurations/',
    alias: 'cases_listBlockConfigurations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListBlockConfigurationsViewRequestData,
      },
    ],
    response: ListBlockConfigurationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/list-blocks/',
    alias: 'cases_listBlocks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListBlocksViewRequestData,
      },
    ],
    response: ListBlocksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/list-connectors/',
    alias: 'cases_listConnectors',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListConnectorsViewRequestData,
      },
    ],
    response: ListConnectorsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/preview-communication-block-email/',
    alias: 'cases_previewCommunicationBlockEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PreviewCommunicationBlockEmailViewRequestData,
      },
    ],
    response: z.object({ html: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/process-editor/upload-document-template/',
    alias: 'cases_uploadDocumentTemplate',
    description: `Upload a Docx file, parse its SDT fields, and store it as a
SuperDoc-sourced DocumentTemplate owned by the organization.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadDocumentTemplateViewRequestData,
      },
    ],
    response: UploadDocumentTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/publish-latest-workflow-version/',
    alias: 'cases_publishLatestWorkflowVersion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PublishLatestWorkflowVersionViewRequestData,
      },
    ],
    response: PublishLatestWorkflowVersionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/reset-draft-version/',
    alias: 'cases_resetDraftVersion',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResetDraftVersionViewRequestData,
      },
    ],
    response: ResetDraftVersionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/resolve-thread/',
    alias: 'cases_resolveThread',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResolveThreadViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/restart-agentic-implementation-log/',
    alias: 'agentic_implementation_restartAgenticImplementationLog',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RestartAgenticImplementationLogViewRequestData,
      },
    ],
    response: RestartAgenticImplementationLogViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/save-or-submit-form/',
    alias: 'cases_saveOrSubmitForm',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveOrSubmitFormViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/search-cases/',
    alias: 'cases_searchCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchCasesViewRequestData,
      },
    ],
    response: SearchCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-action-in-review/',
    alias: 'cases_setActionInReview',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetActionInReviewViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-case-status/',
    alias: 'cases_setCaseStatus',
    description: `Uses can_user_change_case_status (same as PrintCaseView pattern).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetCaseStatusViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/set-step-assignments/',
    alias: 'cases_setStepAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetStepAssignmentsViewRequestData,
      },
    ],
    response: SetStepAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/start-process/',
    alias: 'cases_startProcess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartProcessViewRequestData,
      },
    ],
    response: StartProcessViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/toggle-applicant-identity-section-view/',
    alias: 'cases_toggleApplicantIdentitySection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ToggleApplicantIdentitySectionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/upload-agentic-implementation-attachment/',
    alias: 'agentic_implementation_uploadAgenticImplementationAttachment',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadAgenticImplementationAttachmentViewRequestData,
      },
    ],
    response: UploadAgenticImplementationAttachmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/upsert-cases-config/',
    alias: 'cases_upsertCasesConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertCasesConfigViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/upsert-template/',
    alias: 'cases_upsertTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertTemplateViewRequestData,
      },
    ],
    response: UpsertTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/voice-create-case/',
    alias: 'cases_elevenLabsVoiceCreateCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsVoiceCreateCaseViewRequestData,
      },
    ],
    response: ElevenLabsVoiceCreateCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/voice-get-next-questions/',
    alias: 'cases_elevenLabsVoiceGetNextQuestions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsVoiceGetNextQuestionsViewRequestData,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/cases/voice-record-answers/',
    alias: 'cases_elevenLabsVoiceRecordAnswers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ElevenLabsVoiceRecordAnswersViewRequestData,
      },
    ],
    response: ElevenLabsVoiceRecordAnswersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/changelog/',
    alias: 'api_app_getChangelog',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ _body: z.string() }).partial(),
      },
    ],
    response: GetChangelogViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-address-to-contact-record/',
    alias: 'contacts_addAddressToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAddressToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-address/',
    alias: 'contacts_addAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAddressViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-attribution-to-contact-record/',
    alias: 'contacts_addAttributionToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAttributionToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-email-to-contact-record/',
    alias: 'contacts_addEmailToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddEmailToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-note-to-contact-record/',
    alias: 'contacts_addNoteToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddNoteToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-phone-number/',
    alias: 'contacts_addPhoneNumber',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPhoneNumberViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-phone-to-contact-record/',
    alias: 'contacts_addPhoneToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPhoneToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/add-tag-to-contact-record/',
    alias: 'contacts_addTagToContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddTagToContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/contact-records/import-header/',
    alias: 'contacts_getContactRecordImportHeader',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactRecordImportHeaderViewRequestData,
      },
    ],
    response: GetContactRecordImportHeaderViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/contact-records/import-jobs/',
    alias: 'contacts_listContactImportJobs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactImportJobsViewRequestData,
      },
    ],
    response: ListContactImportJobsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/contact-records/import/',
    alias: 'contacts_importContactRecords',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportContactRecordsViewRequestData,
      },
    ],
    response: ImportContactRecordsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/count-contacts/',
    alias: 'contacts_countContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CountContactsViewRequestData,
      },
    ],
    response: z.object({ num_contacts: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-address/',
    alias: 'contacts_createAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAddressViewRequestData,
      },
    ],
    response: CreateAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact-from-case/',
    alias: 'contacts_createContactFromCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactFromCaseViewRequestData,
      },
    ],
    response: CreateContactFromCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact-record/',
    alias: 'contacts_createContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactRecordViewRequestData,
      },
    ],
    response: CreateContactRecordViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact-relationship/',
    alias: 'contacts_createContactRelationship',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactRelationshipViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-contact/',
    alias: 'contacts_createContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateContactViewRequestData,
      },
    ],
    response: CreateContactViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-email-address/',
    alias: 'contacts_createEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateEmailAddressViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-lightweight-identity/',
    alias: 'contacts_createLightweightIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLightweightIdentityViewRequestData,
      },
    ],
    response: CreateLightweightIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/create-related-contact/',
    alias: 'contacts_createRelatedContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRelatedContactViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-contact-record/',
    alias: 'contacts_deleteContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-contact-relationship/',
    alias: 'contacts_deleteContactRelationship',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteContactRelationshipViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-contact/',
    alias: 'contacts_deleteContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteContactViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/delete-related-contact/',
    alias: 'contacts_deleteRelatedContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteRelatedContactViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/download-contact-book/',
    alias: 'contacts_downloadContactBook',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadContactBookViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/download-contacts/',
    alias: 'contacts_downloadContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadContactsViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/email-signup/',
    alias: 'contacts_emailSignup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EmailSignupViewRequestData,
      },
    ],
    response: EmailSignupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/generate-ai-contact-summary/',
    alias: 'contacts_generateAIContactSummary',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateAIContactSummaryViewRequestData,
      },
    ],
    response: GenerateAIContactSummaryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-cases/',
    alias: 'contacts_getContactCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactCasesViewRequestData,
      },
    ],
    response: GetContactCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry-expanded/',
    alias: 'contacts_getContactEntryExpanded',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryExpandedViewRequestData,
      },
    ],
    response: GetContactEntryExpandedViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry-identity-ids/',
    alias: 'contacts_getContactEntryIdentityIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryIdentityIdsViewRequestData,
      },
    ],
    response: GetContactEntryIdentityIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-entry/',
    alias: 'contacts_getContactEntry',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactEntryViewRequestData,
      },
    ],
    response: GetContactEntryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-outbound-comms/',
    alias: 'contacts_getContactOutboundComms',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactOutboundCommsViewRequestData,
      },
    ],
    response: GetContactOutboundCommsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-record-identity/',
    alias: 'contacts_getContactRecordIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactRecordIdentityViewRequestData,
      },
    ],
    response: GetContactRecordIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact-route-by-identity/',
    alias: 'contacts_getContactRouteByIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactRouteByIdentityViewRequestData,
      },
    ],
    response: GetContactRouteByIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contact/',
    alias: 'contacts_getContact',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactViewRequestData,
      },
    ],
    response: GetContactViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-contacts-header/',
    alias: 'contacts_getContactsHeader',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetContactsHeaderViewRequestData,
      },
    ],
    response: GetContactsHeaderViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-my-location/',
    alias: 'contacts_getMyLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetMyLocationViewRequestData,
      },
    ],
    response: GetMyLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-organization-location/',
    alias: 'contacts_getOrganizationLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationLocationViewRequestData,
      },
    ],
    response: GetOrganizationLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-user-account-address/',
    alias: 'contacts_getUserAccountAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserAccountAddressViewRequestData,
      },
    ],
    response: GetUserAccountAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-user-email-addresses/',
    alias: 'contacts_getUserEmailAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserEmailAddressesViewRequestData,
      },
    ],
    response: GetUserEmailAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/get-user-phone/',
    alias: 'contacts_getUserPhone',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserPhoneViewRequestData,
      },
    ],
    response: GetUserPhoneViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/handle-inbound-email/',
    alias: 'contacts_handleInboundEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: HandleInboundEmailViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/import-contacts/',
    alias: 'contacts_importContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportContactsViewRequestData,
      },
    ],
    response: ImportContactsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-addresses/',
    alias: 'contacts_listAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAddressesViewRequestData,
      },
    ],
    response: ListAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-case-addresses/',
    alias: 'contacts_listCaseAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseAddressesViewRequestData,
      },
    ],
    response: ListCaseAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-case-email-addresses/',
    alias: 'contacts_listCaseEmailAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListCaseEmailAddressesViewRequestData,
      },
    ],
    response: ListCaseEmailAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-addresses-for-map/',
    alias: 'contacts_listContactAddressesForMap',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactAddressesForMapViewRequestData,
      },
    ],
    response: ListContactAddressesForMapViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-book-tags/',
    alias: 'contacts_listContactBookTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactBookTagsViewRequestData,
      },
    ],
    response: ListContactBookTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-entries/',
    alias: 'contacts_listContactEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactEntriesViewRequestData,
      },
    ],
    response: ListContactEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-relations/',
    alias: 'contacts_listContactRelations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactRelationsViewRequestData,
      },
    ],
    response: ListContactRelationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-contact-relationships/',
    alias: 'contacts_listContactRelationships',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactRelationshipsViewRequestData,
      },
    ],
    response: ListContactRelationshipsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-email-addresses/',
    alias: 'contacts_listEmailAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailAddressesViewRequestData,
      },
    ],
    response: ListEmailAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-emails/',
    alias: 'contacts_listEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailsViewRequestData,
      },
    ],
    response: ListEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-organization-phone-numbers/',
    alias: 'contacts_listOrganizationPhoneNumbers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListOrganizationPhoneNumbersViewRequestData,
      },
    ],
    response: ListOrganizationPhoneNumbersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/list-permitted-contact-books/',
    alias: 'contacts_listPermittedContactBooks',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPermittedContactBooksViewRequestData,
      },
    ],
    response: ListPermittedContactBooksViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/materialize-virtual-contact/',
    alias: 'contacts_materializeVirtualContact',
    description: `Promote a virtual contact into a ContactRecord so staff edits have somewhere to live.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MaterializeVirtualContactViewRequestData,
      },
    ],
    response: MaterializeVirtualContactViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/obtain-email-address-with-user/',
    alias: 'contacts_obtainEmailAddressWithUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ObtainEmailAddressWithUserViewRequestData,
      },
    ],
    response: ObtainEmailAddressWithUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/obtain-email-address/',
    alias: 'contacts_obtainEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ObtainEmailAddressViewRequestData,
      },
    ],
    response: ObtainEmailAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/quick-create-address/',
    alias: 'contacts_quickCreateAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ address: z.string() }),
      },
    ],
    response: QuickCreateAddressViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-address-from-contact-record/',
    alias: 'contacts_removeAddressFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveAddressFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-address/',
    alias: 'contacts_removeAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveAddressViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-attribution-from-contact-record/',
    alias: 'contacts_removeAttributionFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveAttributionFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-email-address/',
    alias: 'contacts_removeEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveEmailAddressViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-email-from-contact-record/',
    alias: 'contacts_removeEmailFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveEmailFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-note-from-contact-record/',
    alias: 'contacts_removeNoteFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveNoteFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-phone-from-contact-record/',
    alias: 'contacts_removePhoneFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePhoneFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-phone-number/',
    alias: 'contacts_removePhoneNumber',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePhoneNumberViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/remove-tag-from-contact-record/',
    alias: 'contacts_removeTagFromContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveTagFromContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/reverse-search-addresses/',
    alias: 'contacts_reverseSearchAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReverseSearchAddressesViewRequestData,
      },
    ],
    response: ReverseSearchAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-addresses/',
    alias: 'contacts_searchAddresses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchAddressesViewRequestData,
      },
    ],
    response: SearchAddressesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-contact-documents/',
    alias: 'contacts_searchContactDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchContactDocumentsViewRequestData,
      },
    ],
    response: SearchContactDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-contact-entries/',
    alias: 'contacts_searchContactEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchContactEntriesViewRequestData,
      },
    ],
    response: SearchContactEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-contacts/',
    alias: 'contacts_searchContacts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchContactsViewRequestData,
      },
    ],
    response: SearchContactsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/search-emails/',
    alias: 'contacts_searchEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchEmailsViewRequestData,
      },
    ],
    response: SearchEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send_verification_email/',
    alias: 'contacts_sendVerificationEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendVerificationEmailViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send-constituent-email/',
    alias: 'contacts_sendConstituentEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendConstituentEmailViewRequestData,
      },
    ],
    response: SendConstituentEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/send-email/',
    alias: 'contacts_sendEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendEmailViewRequestData,
      },
    ],
    response: SendEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/set-email-signature/',
    alias: 'contacts_setEmailSignature',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetEmailSignatureViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/set-primary-email-address/',
    alias: 'contacts_setPrimaryEmailAddress',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPrimaryEmailAddressViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/update-contact-record-person-profile/',
    alias: 'contacts_updateContactRecordPersonProfile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateContactRecordPersonProfileViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/update-contact-record-type/',
    alias: 'contacts_updateContactRecordType',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateContactRecordTypeViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/update-note-on-contact-record/',
    alias: 'contacts_updateNoteOnContactRecord',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateNoteOnContactRecordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/verify-email/',
    alias: 'contacts_verifyEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyEmailViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/contacts/verify-login/',
    alias: 'contacts_verifyLogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyLoginViewRequestData,
      },
    ],
    response: VerifyLoginViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/abort-multipart-upload/',
    alias: 'core_abortMultipartUpload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AbortMultipartUploadViewRequestData,
      },
    ],
    response: z.object({ aborted: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/add-email-domain/',
    alias: 'core_addEmailDomain',
    description: `Gates access on organization settings permission for the workspace&#x27;s root
organization instead of the organization in the request.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddEmailDomainViewRequestData,
      },
    ],
    response: AddEmailDomainViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/breakdown-contact-tags/',
    alias: 'core_breakdownContactTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BreakdownContactTagsViewRequestData,
      },
    ],
    response: BreakdownContactTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/change-password/',
    alias: 'core_changePassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ChangePasswordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/check-email-domain-verification/',
    alias: 'core_checkEmailDomainVerification',
    description: `Gates access on organization settings permission for the workspace&#x27;s root
organization instead of the organization in the request.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CheckEmailDomainVerificationViewRequestData,
      },
    ],
    response: CheckEmailDomainVerificationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/complete-multipart-upload/',
    alias: 'core_completeMultipartUpload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CompleteMultipartUploadViewRequestData,
      },
    ],
    response: CompleteMultipartUploadViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-attribution/',
    alias: 'core_createAttribution',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAttributionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-department/',
    alias: 'core_createDepartment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateDepartmentViewRequestData,
      },
    ],
    response: CreateDepartmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-note/',
    alias: 'core_createNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateNoteViewRequestData,
      },
    ],
    response: CreateNoteViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-organization-contact-book/',
    alias: 'core_createOrganizationContactBook',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateOrganizationContactBookViewRequestData,
      },
    ],
    response: CreateOrganizationContactBookViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-presigned-upload/',
    alias: 'core_createPresignedUpload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePresignedUploadViewRequestData,
      },
    ],
    response: CreatePresignedUploadViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/create-tag/',
    alias: 'core_createTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateTagViewRequestData,
      },
    ],
    response: CreateTagViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/delete-note/',
    alias: 'core_deleteNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteNoteViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/delete-tag/',
    alias: 'core_deleteTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTagViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-birthday/',
    alias: 'core_editContactBirthday',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactBirthdayViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-image/',
    alias: 'core_editContactImage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactImageViewRequestData,
      },
    ],
    response: z.object({ image: z.string().nullable() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-contact-name/',
    alias: 'core_editContactName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditContactNameViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-customer-data/',
    alias: 'core_editCustomerData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditCustomerDataViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-payment-settings/',
    alias: 'core_editPaymentSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditPaymentSettingsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/edit-tag/',
    alias: 'core_editTag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTagViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-account/',
    alias: 'core_getAccount',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetAccountViewRequestData,
      },
    ],
    response: GetAccountViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-customer-purchased-statuses/',
    alias: 'core_getCustomerPurchasedStatuses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCustomerPurchasedStatusesViewRequestData,
      },
    ],
    response: GetCustomerPurchasedStatusesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-identity/',
    alias: 'core_getIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetIdentityViewRequestData,
      },
    ],
    response: GetIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-organization-contact-book-info/',
    alias: 'core_getOrganizationContactBookInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationContactBookInfoViewRequestData,
      },
    ],
    response: GetOrganizationContactBookInfoViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-organization-identity/',
    alias: 'core_getOrganizationIdentity',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationIdentityViewRequestData,
      },
    ],
    response: GetOrganizationIdentityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-organization/',
    alias: 'core_getOrganization',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationViewRequestData,
      },
    ],
    response: GetOrganizationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-password-policy/',
    alias: 'core_getPasswordPolicy',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPasswordPolicyViewRequestData,
      },
    ],
    response: GetPasswordPolicyViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-user-from-session/',
    alias: 'core_getUserFromSession',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ _body: z.string() }).partial(),
      },
    ],
    response: GetUserFromSessionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-user-ids/',
    alias: 'core_getUserIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserIdsViewRequestData,
      },
    ],
    response: GetUserIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/get-user/',
    alias: 'core_getUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserViewRequestData,
      },
    ],
    response: GetUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/identity-to-user/',
    alias: 'core_identityToUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: IdentityToUserViewRequestData,
      },
    ],
    response: IdentityToUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-attributions/',
    alias: 'core_listAttributions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAttributionsViewRequestData,
      },
    ],
    response: ListAttributionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-contact-entry-notes/',
    alias: 'core_listContactEntryNotes',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactEntryNotesViewRequestData,
      },
    ],
    response: ListContactEntryNotesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-distinct-email-identities/',
    alias: 'core_listDistinctEmailIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListDistinctEmailIdentitiesViewRequestData,
      },
    ],
    response: ListDistinctEmailIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-email-domains/',
    alias: 'core_listEmailDomains',
    description: `Gates access on organization settings permission for the workspace&#x27;s root
organization instead of the organization in the request.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailDomainsViewRequestData,
      },
    ],
    response: ListEmailDomainsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-identities-by-ids/',
    alias: 'core_listIdentitiesByIds',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdentitiesByIdsViewRequestData,
      },
    ],
    response: ListIdentitiesByIdsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-identities/',
    alias: 'core_listIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdentitiesViewRequestData,
      },
    ],
    response: ListIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-org-identities-tier/',
    alias: 'core_listOrgIdentitiesTier',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListOrgIdentitiesTierViewRequestData,
      },
    ],
    response: ListOrgIdentitiesTierViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-organizations-customer-data/',
    alias: 'core_listOrganizationsCustomerData',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListOrganizationsCustomerDataViewRequestData,
      },
    ],
    response: ListOrganizationsCustomerDataViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-organizations/',
    alias: 'core_listOrganizations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListOrganizationsViewRequestData,
      },
    ],
    response: ListOrganizationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-quill-dashboards/',
    alias: 'core_listQuillDashboards',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListQuillDashboardsViewRequestData,
      },
    ],
    response: ListQuillDashboardsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-staffers/',
    alias: 'core_listStaffers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStaffersViewRequestData,
      },
    ],
    response: ListStaffersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-taggings/',
    alias: 'core_listTaggings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTaggingsViewRequestData,
      },
    ],
    response: ListTaggingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/list-tags/',
    alias: 'core_listTags',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTagsViewRequestData,
      },
    ],
    response: ListTagsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/login/',
    alias: 'core_userLogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLoginViewRequestData,
      },
    ],
    response: UserLoginViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/logout/',
    alias: 'core_userLogout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLogoutViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/quick-search/',
    alias: 'core_quickSearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: QuickSearchViewRequestData,
      },
    ],
    response: QuickSearchViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/reset-password-request/',
    alias: 'core_resetPasswordRequest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ email: z.string().email() }),
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/reset-password/',
    alias: 'core_resetPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResetPasswordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/resume-multipart-upload/',
    alias: 'core_resumeMultipartUpload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ResumeMultipartUploadViewRequestData,
      },
    ],
    response: ResumeMultipartUploadViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/send-test-digest-email/',
    alias: 'core_sendTestDigestEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendTestDigestEmailViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-auto-cc-case-emails/',
    alias: 'core_setAutoCcCaseEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetAutoCcCaseEmailsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-digest-frequency/',
    alias: 'core_setDigestFrequency',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetDigestFrequencyViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-notify/',
    alias: 'core_setNotify',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetNotifyViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-password/',
    alias: 'core_setPassword',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPasswordViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-person-birthday/',
    alias: 'core_setPersonBirthday',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPersonBirthdayRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-person-name/',
    alias: 'core_setPersonName',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPersonNameRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/set-persona/',
    alias: 'core_setPersona',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetPersonaViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/confirm-link/',
    alias: 'sso_sSOConfirmLink',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SSOConfirmLinkViewRequestData,
      },
    ],
    response: SSOConfirmLinkViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/get-sso-config/',
    alias: 'sso_getSSOConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetSSOConfigViewRequestData,
      },
    ],
    response: GetSSOConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/list-idp-groups/',
    alias: 'sso_listIdPGroups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIdPGroupsViewRequestData,
      },
    ],
    response: ListIdPGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/saml-lookup/',
    alias: 'sso_sAMLLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SAMLLookupViewRequestData,
      },
    ],
    response: SAMLLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/saml/:org_handle/acs/',
    alias: 'core_sso_saml_acs_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_handle',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'get',
    path: '/core/sso/saml/:org_handle/login/',
    alias: 'core_sso_saml_login_retrieve',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_handle',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/core/sso/saml/:org_handle/login/',
    alias: 'core_sso_saml_login_create',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_handle',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'get',
    path: '/core/sso/saml/:org_handle/metadata/',
    alias: 'core_sso_saml_metadata_retrieve',
    requestFormat: 'json',
    parameters: [
      {
        name: 'org_handle',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: 'post',
    path: '/core/sso/setup-sso-config/',
    alias: 'sso_setupSSOConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetupSSOConfigViewRequestData,
      },
    ],
    response: SetupSSOConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/test-sso-connection/',
    alias: 'sso_testSSOConnection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestSSOConnectionViewRequestData,
      },
    ],
    response: TestSSOConnectionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/update-sso-group-mappings/',
    alias: 'sso_updateSSOGroupMappings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateSSOGroupMappingsViewRequestData,
      },
    ],
    response: UpdateSSOGroupMappingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/sso/update-sso-settings/',
    alias: 'sso_updateSSOSettings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateSSOSettingsViewRequestData,
      },
    ],
    response: UpdateSSOSettingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/start-email-domain-verification/',
    alias: 'core_startEmailDomainVerification',
    description: `Gates access on organization settings permission for the workspace&#x27;s root
organization instead of the organization in the request.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartEmailDomainVerificationViewRequestData,
      },
    ],
    response: StartEmailDomainVerificationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/super-org-access-add/',
    alias: 'core_superOrgAccessAdd',
    description: `Add super user access to a specific organization.
When include_sub_organizations is True and the organization is a governing unit,
also adds access to all sub-organizations under it.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperOrgAccessAddViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/super-org-access-get-current/',
    alias: 'core_superOrgAccessGetCurrent',
    description: `Get the current organization access selections for a super user.
Returns organizations where the user has super-granted access.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperOrgAccessGetCurrentViewRequestData,
      },
    ],
    response: SuperOrgAccessGetCurrentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/super-org-access-remove/',
    alias: 'core_superOrgAccessRemove',
    description: `Remove all user access from a specific organization, or all organizations if remove_all&#x3D;True.
Removes all role memberships for the user in the given organization(s).
When include_sub_organizations is True, also removes access from all sub-organizations
under the given organization&#x27;s governing unit.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperOrgAccessRemoveViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/super-org-access-search/',
    alias: 'core_superOrgAccessSearch',
    description: `Search for organizations and governing units for super user access management.
Returns a hierarchical structure where orgs are grouped under their governing units.
When a governing unit is discovered (either directly matched or referenced by a matched
sub-org), ALL of its sub-organizations are included in the response.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SuperOrgAccessSearchViewRequestData,
      },
    ],
    response: SuperOrgAccessSearchViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/tag/',
    alias: 'core_tag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TagViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/unattribute/',
    alias: 'core_unattribute',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UnattributeViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/untag/',
    alias: 'core_untag',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UntagViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/update-note/',
    alias: 'core_updateNote',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateNoteViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/core/upload-asset/',
    alias: 'core_uploadAsset',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadAssetViewRequestData,
      },
    ],
    response: UploadAssetViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/db-check/',
    alias: 'api_app_localDBCheck',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ _body: z.string() }).partial(),
      },
    ],
    response: z.object({ using_local_db: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/health/',
    alias: 'health_retrieve',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/handle-cityworks-event/',
    alias: 'hooks_handle_cityworks_event_create',
    description: `Inbound webhook endpoint for Cityworks Action Manager events.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/hooks/stripe/process-event',
    alias: 'hooks_stripe_process_event_create',
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/integrations/add-twilio-account-config/',
    alias: 'integrations_addTwilioAccountConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddTwilioAccountConfigViewRequestData,
      },
    ],
    response: AddTwilioAccountConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/append-openapi-draft-message/',
    alias: 'agentic_implementation_appendOpenAPIDraftMessage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AppendOpenAPIDraftMessageViewRequestData,
      },
    ],
    response: OpenAPIDraftSerializerOpenAPIDraft,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/create-api-authentication-token/',
    alias: 'integrations_createAPIAuthenticationToken',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIAuthenticationTokenViewRequestData,
      },
    ],
    response: CreateAPIAuthenticationTokenViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/create-api-connection/',
    alias: 'integrations_createAPIConnection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIConnectionViewRequestData,
      },
    ],
    response: CreateAPIConnectionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/create-api-endpoint-config/',
    alias: 'integrations_createAPIEndpointConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIEndpointConfigViewRequestData,
      },
    ],
    response: CreateAPIEndpointConfigViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/create-api-endpoint-parameter/',
    alias: 'integrations_createAPIEndpointParameter',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIEndpointParameterViewRequestData,
      },
    ],
    response: CreateAPIEndpointParameterViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/create-api-endpoint/',
    alias: 'integrations_createAPIEndpoint',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateAPIEndpointViewRequestData,
      },
    ],
    response: CreateAPIEndpointViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/delete-api-connection/',
    alias: 'integrations_deleteAPIConnection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAPIConnectionViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/delete-api-endpoint-config/',
    alias: 'integrations_deleteAPIEndpointConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteAPIEndpointConfigViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/delete-twilio-account-config/',
    alias: 'integrations_deleteTwilioAccountConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteTwilioAccountConfigViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/discard-openapi-draft/',
    alias: 'agentic_implementation_discardOpenAPIDraft',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DiscardOpenAPIDraftViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/edit-twilio-account-config/',
    alias: 'integrations_editTwilioAccountConfig',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditTwilioAccountConfigViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/execute-browser-test-action/',
    alias: 'integrations_executeBrowserTestAction',
    description: `Execute a browser action workflow using Stagehand&#x27;s autonomous agent.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ExecuteBrowserTestActionViewRequestData,
      },
    ],
    response: ExecuteBrowserTestActionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-cayenta-utility-billing-integration/',
    alias: 'integrations_generateCayentaUtilityBillingIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateCayentaUtilityBillingIntegrationViewRequestData,
      },
    ],
    response: GenerateCayentaUtilityBillingIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-central-square-integration/',
    alias: 'integrations_generateCentralSquareIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateCentralSquareIntegrationViewRequestData,
      },
    ],
    response: GenerateCentralSquareIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-cityworks-integration/',
    alias: 'integrations_generateCityworksIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateCityworksIntegrationViewRequestData,
      },
    ],
    response: GenerateCityworksIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-custom-integration/',
    alias: 'integrations_generateCustomIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateCustomIntegrationViewRequestData,
      },
    ],
    response: GenerateCustomIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-energov-epl-integration/',
    alias: 'integrations_generateEnergovEplIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateEnergovEplIntegrationViewRequestData,
      },
    ],
    response: GenerateEnergovEplIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-fasttrack-integration/',
    alias: 'integrations_generateFastTrackIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateFastTrackIntegrationViewRequestData,
      },
    ],
    response: GenerateFastTrackIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-munis-arws-integration/',
    alias: 'integrations_generateMunisArwsIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateMunisArwsIntegrationViewRequestData,
      },
    ],
    response: GenerateMunisArwsIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-opengov-integration/',
    alias: 'integrations_generateOpenGovIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateOpenGovIntegrationViewRequestData,
      },
    ],
    response: GenerateOpenGovIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-request311-integration/',
    alias: 'integrations_generateRequest311Integration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateRequest311IntegrationViewRequestData,
      },
    ],
    response: GenerateRequest311IntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/generate-sharepoint-integration/',
    alias: 'integrations_generateSharePointIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateSharePointIntegrationViewRequestData,
      },
    ],
    response: GenerateSharePointIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/get-openapi-draft/',
    alias: 'agentic_implementation_getOpenAPIDraft',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOpenAPIDraftViewRequestData,
      },
    ],
    response: GetOpenAPIDraftViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/list-api-connections/',
    alias: 'integrations_listAPIConnections',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAPIConnectionsViewRequestData,
      },
    ],
    response: ListAPIConnectionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/list-api-endpoint-parameters/',
    alias: 'integrations_listAPIEndpointParameters',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAPIEndpointParametersViewRequestData,
      },
    ],
    response: z.object({ parameters: z.array(z.unknown()) }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/list-api-endpoints/',
    alias: 'integrations_listAPIEndpoints',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAPIEndpointsViewRequestData,
      },
    ],
    response: ListAPIEndpointsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/list-integration-endpoint-definitions/',
    alias: 'integrations_listIntegrationEndpointDefinitions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListIntegrationEndpointDefinitionsViewRequestData,
      },
    ],
    response: ListIntegrationEndpointDefinitionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/list-twilio-account-configs/',
    alias: 'integrations_listTwilioAccountConfigs',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTwilioAccountConfigsViewRequestData,
      },
    ],
    response: ListTwilioAccountConfigsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/integrations/microsoft/oauth/callback/',
    alias: 'integrations_microsoft_oauth_callback_retrieve',
    description: `OAuth redirect target. Validates state, exchanges the code, stores the per-user token.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/integrations/microsoft/oauth/disconnect/',
    alias: 'integrations_disconnectMicrosoft',
    description: `Revoke and delete the requesting user&#x27;s delegated Microsoft link.

Personal to the requesting user; only a valid user+session is required.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DisconnectMicrosoftViewRequestData,
      },
    ],
    response: z.object({ disconnected: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/microsoft/oauth/start/',
    alias: 'integrations_startMicrosoftConnect',
    description: `Return an Entra authorize URL the frontend redirects the user to.

No org-resource policy: linking a Microsoft account is personal to the requesting user, so
only a valid user+session is required (&#x60;&#x60;UserAndSessionValid&#x60;&#x60; binds &#x60;&#x60;user_id&#x60;&#x60; to the
session cookie, preventing acting on another user&#x27;s behalf).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartMicrosoftConnectViewRequestData,
      },
    ],
    response: z.object({ authorize_url: z.string() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/microsoft/oauth/status/',
    alias: 'integrations_microsoftConnectionStatus',
    description: `Report whether the requesting user has an active delegated Microsoft link.

Personal to the requesting user; only a valid user+session is required.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: MicrosoftConnectionStatusViewRequestData,
      },
    ],
    response: z.object({ connected: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/start-browser-test-session/',
    alias: 'integrations_startBrowserTestSession',
    description: `Create a Stagehand/Browserbase session and return the live view URL for iframe embedding.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartBrowserTestSessionViewRequestData,
      },
    ],
    response: StartBrowserTestSessionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/stop-browser-test-session/',
    alias: 'integrations_stopBrowserTestSession',
    description: `Stop and release a Stagehand/Browserbase test session.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StopBrowserTestSessionViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/test-custom-endpoint/',
    alias: 'integrations_testCustomEndpoint',
    description: `Test a custom integration endpoint before saving.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestCustomEndpointViewRequestData,
      },
    ],
    response: TestCustomEndpointViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/test-hosted-integration-credentials/',
    alias: 'integrations_testHostedIntegrationCredentials',
    description: `Test credentials for a hosted integration without creating it.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestHostedIntegrationCredentialsViewRequestData,
      },
    ],
    response: TestHostedIntegrationCredentialsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/test-integration-endpoints/',
    alias: 'integrations_testIntegrationEndpoints',
    description: `Test all endpoints for a hosted integration type.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TestIntegrationEndpointsViewRequestData,
      },
    ],
    response: z.object({ results: z.array(z.unknown()) }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/update-api-connection/',
    alias: 'integrations_updateAPIConnection',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateAPIConnectionViewRequestData,
      },
    ],
    response: z.object({ success: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/update-api-endpoint-configs/',
    alias: 'integrations_updateEndpointConfigs',
    description: `Bulk update APIEndpointConfig active flags and statuses for endpoints on a connection.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateEndpointConfigsViewRequestData,
      },
    ],
    response: UpdateEndpointConfigsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/update-api-endpoints/',
    alias: 'integrations_updateAPIEndpoints',
    description: `Bulk update endpoints belonging to an API connection.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateAPIEndpointsViewRequestData,
      },
    ],
    response: UpdateAPIEndpointsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/update-custom-integration/',
    alias: 'integrations_updateCustomIntegration',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateCustomIntegrationViewRequestData,
      },
    ],
    response: UpdateCustomIntegrationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/update-endpoint-active/',
    alias: 'integrations_updateEndpointActive',
    description: `Update active status for individual endpoints.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateEndpointActiveViewRequestData,
      },
    ],
    response: UpdateEndpointActiveViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/integrations/upload-openapi-draft-attachment/',
    alias: 'agentic_implementation_uploadOpenAPIDraftAttachment',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UploadOpenAPIDraftAttachmentViewRequestData,
      },
    ],
    response: UploadOpenAPIDraftAttachmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/knowledge-base/get-domain-sitemap/',
    alias: 'knowledge_base_getDomainSitemap',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetDomainSitemapViewRequestData,
      },
    ],
    response: GetDomainSitemapViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/knowledge-base/get-item-usage/',
    alias: 'knowledge_base_getKnowledgeBaseItemUsage',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetKnowledgeBaseItemUsageViewRequestData,
      },
    ],
    response: GetKnowledgeBaseItemUsageViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/knowledge-base/list-items/',
    alias: 'knowledge_base_listKnowledgeBaseItems',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListKnowledgeBaseItemsViewRequestData,
      },
    ],
    response: ListKnowledgeBaseItemsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/knowledge-base/list-knowledge-findings/',
    alias: 'knowledge_base_listKnowledgeFindings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListKnowledgeFindingsViewRequestData,
      },
    ],
    response: ListKnowledgeFindingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/bulk-edit-license-templates/',
    alias: 'licenses_bulkEditLicenseTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkEditLicenseTemplatesViewRequestData,
      },
    ],
    response: BulkEditLicenseTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/bulk-edit-licenses/',
    alias: 'licenses_bulkEditLicenses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BulkEditLicensesViewRequestData,
      },
    ],
    response: BulkEditLicensesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/create-license-fields/',
    alias: 'licenses_createLicenseFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLicenseFieldsViewRequestData,
      },
    ],
    response: CreateLicenseFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/create-license-template-field/',
    alias: 'licenses_createLicenseTemplateField',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLicenseTemplateFieldViewRequestData,
      },
    ],
    response: CreateLicenseTemplateFieldViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/create-license-template/',
    alias: 'licenses_createLicenseTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLicenseTemplateViewRequestData,
      },
    ],
    response: CreateLicenseTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/create-license/',
    alias: 'licenses_createLicense',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLicenseViewRequestData,
      },
    ],
    response: CreateLicenseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/edit-license-fields/',
    alias: 'licenses_editLicenseFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLicenseFieldsViewRequestData,
      },
    ],
    response: EditLicenseFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/edit-license-template-field/',
    alias: 'licenses_editLicenseTemplateField',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLicenseTemplateFieldViewRequestData,
      },
    ],
    response: EditLicenseTemplateFieldViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/edit-license-template/',
    alias: 'licenses_editLicenseTemplate',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLicenseTemplateViewRequestData,
      },
    ],
    response: EditLicenseTemplateViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/edit-license/',
    alias: 'licenses_editLicense',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EditLicenseViewRequestData,
      },
    ],
    response: EditLicenseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-constituent-licenses/',
    alias: 'licenses_listConstituentLicenses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListConstituentLicensesViewRequestData,
      },
    ],
    response: ListConstituentLicensesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-contact-licenses/',
    alias: 'licenses_listContactLicenses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListContactLicensesViewRequestData,
      },
    ],
    response: ListContactLicensesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-cases/',
    alias: 'licenses_listLicenseCases',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseCasesViewRequestData,
      },
    ],
    response: ListLicenseCasesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-documents/',
    alias: 'licenses_listLicenseDocuments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseDocumentsViewRequestData,
      },
    ],
    response: ListLicenseDocumentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-field-mappings/',
    alias: 'licenses_listLicenseFieldMappings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseFieldMappingsViewRequestData,
      },
    ],
    response: ListLicenseFieldMappingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-fields/',
    alias: 'licenses_listLicenseFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseFieldsViewRequestData,
      },
    ],
    response: ListLicenseFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-history/',
    alias: 'licenses_listLicenseHistory',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseHistoryViewRequestData,
      },
    ],
    response: ListLicenseHistoryViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-template-fields/',
    alias: 'licenses_listLicenseTemplateFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseTemplateFieldsViewRequestData,
      },
    ],
    response: ListLicenseTemplateFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-license-templates/',
    alias: 'licenses_listLicenseTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicenseTemplatesViewRequestData,
      },
    ],
    response: ListLicenseTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-licenses-with-locations/',
    alias: 'licenses_listLicensesWithLocations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicensesWithLocationsViewRequestData,
      },
    ],
    response: ListLicensesWithLocationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/list-licenses/',
    alias: 'licenses_listLicenses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLicensesViewRequestData,
      },
    ],
    response: ListLicensesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/set-license-field-mappings/',
    alias: 'licenses_setLicenseFieldMappings',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetLicenseFieldMappingsViewRequestData,
      },
    ],
    response: SetLicenseFieldMappingsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/licenses/set-license-template-fields/',
    alias: 'licenses_setLicenseTemplateFields',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetLicenseTemplateFieldsViewRequestData,
      },
    ],
    response: SetLicenseTemplateFieldsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/cancel-schedule/',
    alias: 'mass_comms_cancelScheduledMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelScheduledMassCommunicationViewRequestData,
      },
    ],
    response: CancelScheduledMassCommunicationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/contact-book-address-points/',
    alias: 'mass_comms_listMassCommunicationContactBookAddressPoints',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMassCommunicationContactBookAddressPointsViewRequestData,
      },
    ],
    response: ListMassCommunicationContactBookAddressPointsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/create/',
    alias: 'mass_comms_createMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateMassCommunicationViewRequestData,
      },
    ],
    response: CreateMassCommunicationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/delete/',
    alias: 'mass_comms_deleteMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteMassCommunicationViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/email-capability/',
    alias: 'mass_comms_getOrganizationEmailCapability',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationEmailCapabilityViewRequestData,
      },
    ],
    response: GetOrganizationEmailCapabilityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/get/',
    alias: 'mass_comms_getMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetMassCommunicationViewRequestData,
      },
    ],
    response: GetMassCommunicationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/hooks/ses-events/',
    alias: 'mass_comms_hooks_ses_events_create',
    description: `Receives SES event notifications from SNS and updates receipt tracking fields.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/mass-comms/hooks/sms-status/',
    alias: 'mass_comms_hooks_sms_status_create',
    description: `Receives Twilio SMS status callback updates and updates receipt status.`,
    requestFormat: 'json',
    response: z.void(),
  },
  {
    method: 'post',
    path: '/mass-comms/list-receipts/',
    alias: 'mass_comms_listMassCommunicationReceipts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMassCommunicationReceiptsViewRequestData,
      },
    ],
    response: ListMassCommunicationReceiptsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/list-responses/',
    alias: 'mass_comms_listMassCommunicationResponses',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMassCommunicationResponsesViewRequestData,
      },
    ],
    response: ListMassCommunicationResponsesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/list/',
    alias: 'mass_comms_listMassCommunications',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListMassCommunicationsViewRequestData,
      },
    ],
    response: ListMassCommunicationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/preview-contact-book-recipients/',
    alias: 'mass_comms_previewMassCommunicationContactBookRecipients',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PreviewMassCommunicationContactBookRecipientsViewRequestData,
      },
    ],
    response: PreviewMassCommunicationContactBookRecipientsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/send-contact-book/',
    alias: 'mass_comms_sendMassCommunicationContactBook',
    description: `Send a mass communication to recipients resolved from the contact book index.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendMassCommunicationContactBookViewRequestData,
      },
    ],
    response: SendMassCommunicationContactBookViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/send-test/',
    alias: 'mass_comms_sendTestMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendTestMassCommunicationViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/sms-capability/',
    alias: 'mass_comms_getOrganizationSmsCapability',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetOrganizationSmsCapabilityViewRequestData,
      },
    ],
    response: GetOrganizationSmsCapabilityViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/mass-comms/update/',
    alias: 'mass_comms_updateMassCommunication',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateMassCommunicationViewRequestData,
      },
    ],
    response: UpdateMassCommunicationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/add-charge/',
    alias: 'payments_addCharge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddChargeViewRequestData,
      },
    ],
    response: AddChargeViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/add-payment/',
    alias: 'payments_addPayment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPaymentViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/cancel-terminal-payment/',
    alias: 'payments_cancelTerminalPayment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CancelTerminalPaymentViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/complete-bill/',
    alias: 'payments_completeBill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CompleteBillViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/confirm-payment/',
    alias: 'payments_confirmPayment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConfirmPaymentViewRequestData,
      },
    ],
    response: ConfirmPaymentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/connect-stripe/',
    alias: 'payments_connectStripe',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ConnectStripeViewRequestData,
      },
    ],
    response: ConnectStripeViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/count-lookup-entries/',
    alias: 'payments_countLookupEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CountLookupEntriesViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/count-payments/',
    alias: 'payments_countPayments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CountPaymentsViewRequestData,
      },
    ],
    response: z.object({ count: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-location/',
    alias: 'payments_createLocation',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLocationViewRequestData,
      },
    ],
    response: CreateLocationViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-lookup/',
    alias: 'payments_createLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateLookupViewRequestData,
      },
    ],
    response: CreateLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/create-payout/',
    alias: 'payments_createPayout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreatePayoutViewRequestData,
      },
    ],
    response: CreatePayoutViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/delete-charge/',
    alias: 'payments_deleteCharge',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteChargeViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/download-springbrook-csv/',
    alias: 'payments_downloadSpringbrookCSV',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DownloadSpringbrookCSVViewRequestData,
      },
    ],
    response: z.object({ email_recipient: z.string().email() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/ftp-auth/',
    alias: 'payments_fTPAuth',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FTPAuthViewRequestData,
      },
    ],
    response: FTPAuthViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-lookup/',
    alias: 'payments_getLookup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetLookupViewRequestData,
      },
    ],
    response: GetLookupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payment-for-receipt/',
    alias: 'payments_getPaymentForReceipt',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentForReceiptViewRequestData,
      },
    ],
    response: GetPaymentForReceiptViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payment-info/',
    alias: 'payments_getPaymentInfo',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentInfoViewRequestData,
      },
    ],
    response: GetPaymentInfoViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payment-total/',
    alias: 'payments_getPaymentTotal',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentTotalViewRequestData,
      },
    ],
    response: z.object({ total: z.number().int() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-payments-stats/',
    alias: 'payments_getPaymentsStats',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetPaymentsStatsViewRequestData,
      },
    ],
    response: GetPaymentsStatsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-revenue-breakdown/',
    alias: 'payments_getRevenueBreakdown',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRevenueBreakdownViewRequestData,
      },
    ],
    response: GetRevenueBreakdownViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/get-revenue-types-breakdown/',
    alias: 'payments_getRevenueTypesBreakdown',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRevenueTypesBreakdownViewRequestData,
      },
    ],
    response: GetRevenueTypesBreakdownViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/import-bills/',
    alias: 'payments_importBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportBillsViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-bills/',
    alias: 'payments_listBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListBillsViewRequestData,
      },
    ],
    response: ListBillsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-charge-substeps',
    alias: 'payments_listChargeSubsteps',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListChargeSubstepsViewRequestData,
      },
    ],
    response: ListChargeSubstepsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-contituent-case-bill/',
    alias: 'payments_listConstituentCaseBill',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListConstituentCaseBillViewRequestData,
      },
    ],
    response: ListConstituentCaseBillViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-locations/',
    alias: 'payments_listLocations',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLocationsViewRequestData,
      },
    ],
    response: ListLocationsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-lookup-entries/',
    alias: 'payments_listLookupEntries',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLookupEntriesViewRequestData,
      },
    ],
    response: ListLookupEntriesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-lookups/',
    alias: 'payments_listLookups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListLookupsViewRequestData,
      },
    ],
    response: ListLookupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payment-export-rules/',
    alias: 'payments_listPaymentExportRules',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPaymentExportRulesViewRequestData,
      },
    ],
    response: ListPaymentExportRulesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payments/',
    alias: 'payments_listPayments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPaymentsViewRequestData,
      },
    ],
    response: ListPaymentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-payouts/',
    alias: 'payments_listPayouts',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPayoutsViewRequestData,
      },
    ],
    response: ListPayoutsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-sources/',
    alias: 'payments_listStripeSources',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListStripeSourcesViewRequestData,
      },
    ],
    response: ListStripeSourcesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/list-terminals/',
    alias: 'payments_listTerminals',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTerminalsViewRequestData,
      },
    ],
    response: ListTerminalsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/obtain-payment-intent/',
    alias: 'payments_obtainPaymentIntent',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ObtainPaymentIntentViewRequestData,
      },
    ],
    response: ObtainPaymentIntentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/reconcile/',
    alias: 'payments_reconcile',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReconcileViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/register-terminal/',
    alias: 'payments_registerTerminal',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RegisterTerminalViewRequestData,
      },
    ],
    response: RegisterTerminalViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/search-bills/',
    alias: 'payments_searchBills',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SearchBillsViewRequestData,
      },
    ],
    response: SearchBillsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/send-bill-request/',
    alias: 'payments_sendBillRequest',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SendBillRequestViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/upsert-payment-export-rule/',
    alias: 'payments_upsertPaymentExportRule',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpsertPaymentExportRuleViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/payments/verify-stripe-source/',
    alias: 'payments_verifyStripeSource',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: VerifyStripeSourceViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-ad-hoc-block-instance-permissions/',
    alias: 'permissions_addAdHocBlockInstancePermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAdHocBlockInstancePermissionsViewRequestData,
      },
    ],
    response: AddAdHocBlockInstancePermissionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-ad-hoc-block-permissions/',
    alias: 'permissions_addAdHocBlockPermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAdHocBlockPermissionsViewRequestData,
      },
    ],
    response: AddAdHocBlockPermissionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-ad-hoc-case-permissions/',
    alias: 'permissions_addAdHocCasePermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAdHocCasePermissionsViewRequestData,
      },
    ],
    response: AddAdHocCasePermissionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-ad-hoc-workflow-permissions/',
    alias: 'permissions_addAdHocWorkflowPermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddAdHocWorkflowPermissionsViewRequestData,
      },
    ],
    response: AddAdHocWorkflowPermissionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-group-members/',
    alias: 'permissions_addGroupMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddGroupMembersViewRequestData,
      },
    ],
    response: AddGroupMembersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-group/',
    alias: 'permissions_addGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddGroupViewRequestData,
      },
    ],
    response: AddGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-permissions-role/',
    alias: 'permissions_addPermissionsRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddPermissionsRoleViewRequestData,
      },
    ],
    response: AddPermissionsRoleViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-role-policies/',
    alias: 'permissions_addRolePolicies',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddRolePoliciesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-singleton-group/',
    alias: 'permissions_addSingletonGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddSingletonGroupViewRequestData,
      },
    ],
    response: AddSingletonGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-workspace-administrator/',
    alias: 'permissions_addWorkspaceAdministrator',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddWorkspaceAdministratorViewRequestData,
      },
    ],
    response: AddWorkspaceAdministratorViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/add-workspace-group/',
    alias: 'permissions_addWorkspaceGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AddWorkspaceGroupViewRequestData,
      },
    ],
    response: AddWorkspaceGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/assign-group-roles/',
    alias: 'permissions_assignGroupRoles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AssignGroupRolesViewRequestData,
      },
    ],
    response: AssignGroupRolesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/check-user-block-access/',
    alias: 'permissions_checkUserBlockAccess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CheckUserBlockAccessViewRequestData,
      },
    ],
    response: z.object({ has_access: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/check-user-case-access/',
    alias: 'permissions_checkUserCaseAccess',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CheckUserCaseAccessViewRequestData,
      },
    ],
    response: z.object({ has_access: z.boolean() }),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/copy-organization-group/',
    alias: 'permissions_copyOrganizationGroup',
    description: `Creates a new group from an organization group, copying its members and (optionally) its
SSO group mappings. By default the copy is another group in the same organization; when
workspace_group_owner_ids is given the copy becomes a group in the organization&#x27;s workspace
with those members as owners.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyOrganizationGroupViewRequestData,
      },
    ],
    response: CopyOrganizationGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/copy-workspace-group/',
    alias: 'permissions_copyWorkspaceGroup',
    description: `Creates a new group from a workspace group, copying its members and (optionally) its SSO
group mappings. By default the copy is another group in the same workspace; when
organization_identity_id is given the copy becomes a group in that organization, which must
belong to the group&#x27;s workspace.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CopyWorkspaceGroupViewRequestData,
      },
    ],
    response: CopyWorkspaceGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/create-role-assignment/',
    alias: 'permissions_createRoleAssignment',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CreateRoleAssignmentViewRequestData,
      },
    ],
    response: CreateRoleAssignmentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/ensure-singleton-groups/',
    alias: 'permissions_ensureSingletonGroups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: EnsureSingletonGroupsViewRequestData,
      },
    ],
    response: EnsureSingletonGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-ad-hoc-permitted-users-for-block-instance/',
    alias: 'permissions_listAdHocPermittedUsersForBlockInstance',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAdHocPermittedUsersForBlockInstanceViewRequestData,
      },
    ],
    response: ListAdHocPermittedUsersForBlockInstanceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-ad-hoc-permitted-users-for-block/',
    alias: 'permissions_listAdHocPermittedUsersForBlock',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAdHocPermittedUsersForBlockViewRequestData,
      },
    ],
    response: ListAdHocPermittedUsersForBlockViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-ad-hoc-permitted-users-for-case/',
    alias: 'permissions_listAdHocPermittedUsersForCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAdHocPermittedUsersForCaseViewRequestData,
      },
    ],
    response: ListAdHocPermittedUsersForCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-ad-hoc-permitted-users-for-workflow/',
    alias: 'permissions_listAdHocPermittedUsersForWorkflow',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListAdHocPermittedUsersForWorkflowViewRequestData,
      },
    ],
    response: ListAdHocPermittedUsersForWorkflowViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-external-role-assignments/',
    alias: 'permissions_listExternalRoleAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListExternalRoleAssignmentsViewRequestData,
      },
    ],
    response: ListExternalRoleAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-general-permissions-for-group/',
    alias: 'permissions_listGeneralPermissionsForGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGeneralPermissionsForGroupViewRequestData,
      },
    ],
    response: ListGeneralPermissionsForGroupViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-general-permissions-for-user/',
    alias: 'permissions_listGeneralPermissionsForUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGeneralPermissionsForUserViewRequestData,
      },
    ],
    response: ListGeneralPermissionsForUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-group-members/',
    alias: 'permissions_listGroupMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGroupMembersViewRequestData,
      },
    ],
    response: ListGroupMembersViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-group-roles/',
    alias: 'permissions_listGroupRoles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGroupRolesViewRequestData,
      },
    ],
    response: ListGroupRolesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-groups/',
    alias: 'permissions_listGroups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListGroupsViewRequestData,
      },
    ],
    response: ListGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-inbound-external-roles/',
    alias: 'permissions_listInboundExternalRoles',
    description: `List the roles another organization shares with this one through its groups.

These roles can be added to and removed from this organization&#x27;s groups here, but their name
and policies stay under the owning organization&#x27;s control.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListInboundExternalRolesViewRequestData,
      },
    ],
    response: ListInboundExternalRolesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-permissions-roles/',
    alias: 'permissions_listPermissionsRoles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListPermissionsRolesViewRequestData,
      },
    ],
    response: ListPermissionsRolesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-role-assignments/',
    alias: 'permissions_listRoleAssignments',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRoleAssignmentsViewRequestData,
      },
    ],
    response: ListRoleAssignmentsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-user-ad-hoc-permissions/',
    alias: 'permissions_listUserAdHocPermissions',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListUserAdHocPermissionsRequestData,
      },
    ],
    response: ListUserAdHocPermissionsResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-user-groups/',
    alias: 'permissions_listUserGroups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListUserGroupsViewRequestData,
      },
    ],
    response: ListUserGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-user-permitted-org-identities/',
    alias: 'permissions_listUserPermittedOrgIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListUserPermittedOrgIdentitiesViewRequestData,
      },
    ],
    response: ListUserPermittedOrgIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-workspace-administrators/',
    alias: 'permissions_listWorkspaceAdministrators',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListWorkspaceAdministratorsViewRequestData,
      },
    ],
    response: ListWorkspaceAdministratorsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/list-workspace-groups/',
    alias: 'permissions_listWorkspaceGroups',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListWorkspaceGroupsViewRequestData,
      },
    ],
    response: ListWorkspaceGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/reassign-role-groups/',
    alias: 'permissions_reassignRoleGroups',
    description: `Move roles owned by another organization between groups of the requesting organization.

The requesting organization owns both the current and the new group, so a staffer with
&#x60;user_management_settings:write&#x60; there can move an inbound role without the role owner&#x27;s help.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReassignRoleGroupsViewRequestData,
      },
    ],
    response: ReassignRoleGroupsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-block-ad-hoc-permission/',
    alias: 'permissions_removeBlockAdHocPermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveBlockAdHocPermissionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-block-instance-ad-hoc-permission/',
    alias: 'permissions_removeBlockInstanceAdHocPermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveBlockInstanceAdHocPermissionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-case-ad-hoc-permission/',
    alias: 'permissions_removeCaseAdHocPermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveCaseAdHocPermissionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-group-members/',
    alias: 'permissions_removeGroupMembers',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveGroupMembersViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-group-roles/',
    alias: 'permissions_removeGroupRoles',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveGroupRolesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-group/',
    alias: 'permissions_removeGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveGroupViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-permissions-role/',
    alias: 'permissions_removePermissionsRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemovePermissionsRoleViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-role-policies/',
    alias: 'permissions_removeRolePolicies',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveRolePoliciesViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-workflow-ad-hoc-permission/',
    alias: 'permissions_removeWorkflowAdHocPermission',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveWorkflowAdHocPermissionViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-workspace-administrator/',
    alias: 'permissions_removeWorkspaceAdministrator',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveWorkspaceAdministratorViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/remove-workspace-group/',
    alias: 'permissions_removeWorkspaceGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RemoveWorkspaceGroupViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/rename-group/',
    alias: 'permissions_renameGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RenameGroupViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/rename-permissions-role/',
    alias: 'permissions_renamePermissionsRole',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RenamePermissionsRoleViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/permissions/rename-workspace-group/',
    alias: 'permissions_renameWorkspaceGroup',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RenameWorkspaceGroupViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/generate-response/',
    alias: 'plugins_outlookGenerateResponse',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GenerateResponseViewRequestData,
      },
    ],
    response: GenerateResponseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/get-case/',
    alias: 'plugins_outlookGetCase',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetCaseViewRequestData,
      },
    ],
    response: GetCaseViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/get-user/',
    alias: 'plugins_outlookGetUser',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetUserViewRequestData,
      },
    ],
    response: GetUserViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/import-email/',
    alias: 'plugins_outlookImportEmail',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ImportEmailViewRequestData,
      },
    ],
    response: ImportEmailViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/list-distinct-email-identities/',
    alias: 'plugins_outlookListDistinctEmailIdentities',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListDistinctEmailIdentitiesViewRequestData,
      },
    ],
    response: ListDistinctEmailIdentitiesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/list-emails/',
    alias: 'plugins_outlookListEmails',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListEmailsViewRequestData,
      },
    ],
    response: ListEmailsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/list-templates/',
    alias: 'plugins_outlookListTemplates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListTemplatesViewRequestData,
      },
    ],
    response: ListTemplatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/login/',
    alias: 'plugins_outlookUserLogin',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLoginViewRequestData,
      },
    ],
    response: UserLoginViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/plugins/outlook/logout/',
    alias: 'plugins_outlookUserLogout',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserLogoutViewRequestData,
      },
    ],
    response: z.object({ status: z.string().default('ok') }).partial(),
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/apply-redactions/',
    alias: 'privacy_applyRedactions',
    description: `Apply the persisted review of a stored document and link its redacted output.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ApplyRedactionsViewRequestData,
      },
    ],
    response: ApplyRedactionsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/delete-redaction-document/',
    alias: 'privacy_deleteRedactionDocument',
    description: `Drop a document from the request it was uploaded into, marking it deleted.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DeleteRedactionDocumentViewRequestData,
      },
    ],
    response: DeleteRedactionDocumentViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-active-redaction-request-export/',
    alias: 'privacy_getActiveRedactionRequestExport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetActiveRedactionRequestExportViewRequestData,
      },
    ],
    response: GetActiveRedactionRequestExportViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-document-source/',
    alias: 'privacy_getRedactionDocumentSource',
    description: `Re-extract the secured source of a document so the studio can render its pages.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionDocumentSourceViewRequestData,
      },
    ],
    response: GetRedactionDocumentSourceViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-job/',
    alias: 'privacy_getRedactionJob',
    description: `Poll the status of a detection job and of each of its documents.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionJobViewRequestData,
      },
    ],
    response: GetRedactionJobViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-request-export-download/',
    alias: 'privacy_getRedactionRequestExportDownload',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionRequestExportDownloadViewRequestData,
      },
    ],
    response: GetRedactionRequestExportDownloadViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-request-export/',
    alias: 'privacy_getRedactionRequestExport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionRequestExportViewRequestData,
      },
    ],
    response: GetRedactionRequestExportViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-request/',
    alias: 'privacy_getRedactionRequest',
    description: `Read a request and every document uploaded into it, across its upload batches.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionRequestViewRequestData,
      },
    ],
    response: GetRedactionRequestViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/get-redaction-size-limits/',
    alias: 'privacy_getRedactionSizeLimits',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: GetRedactionSizeLimitsViewRequestData,
      },
    ],
    response: GetRedactionSizeLimitsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/list-redaction-candidates/',
    alias: 'privacy_listRedactionCandidates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRedactionCandidatesViewRequestData,
      },
    ],
    response: ListRedactionCandidatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/list-redaction-requests/',
    alias: 'privacy_listRedactionRequests',
    description: `List the organization&#x27;s redaction requests, newest first.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListRedactionRequestsViewRequestData,
      },
    ],
    response: ListRedactionRequestsViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/retry-redaction-detection/',
    alias: 'privacy_retryRedactionDetection',
    description: `Queue detection again for the documents of a job that have not succeeded.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RetryRedactionDetectionViewRequestData,
      },
    ],
    response: RetryRedactionDetectionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/set-redaction-document-complete/',
    alias: 'privacy_setRedactionDocumentComplete',
    description: `Record whether a reviewer is done with a document, so a later visit reopens it as it was.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetRedactionDocumentCompleteViewRequestData,
      },
    ],
    response: SetRedactionDocumentCompleteViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/set-redaction-manual-candidates/',
    alias: 'privacy_setRedactionManualCandidates',
    description: `Replace a document&#x27;s hand-added redactions with the set the reviewer now has on screen.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetRedactionManualCandidatesViewRequestData,
      },
    ],
    response: SetRedactionManualCandidatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/set-redaction-request-name/',
    alias: 'privacy_setRedactionRequestName',
    description: `Name a redaction request, so the list reads as the requests a reviewer recognizes.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SetRedactionRequestNameViewRequestData,
      },
    ],
    response: SetRedactionRequestNameViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/start-redaction-detection/',
    alias: 'privacy_startRedactionDetection',
    description: `Persist a batch of presigned-uploaded documents and detect on each one in Celery.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartRedactionDetectionViewRequestData,
      },
    ],
    response: StartRedactionDetectionViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/start-redaction-request-export/',
    alias: 'privacy_startRedactionRequestExport',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StartRedactionRequestExportViewRequestData,
      },
    ],
    response: StartRedactionRequestExportViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/redaction/update-redaction-candidates/',
    alias: 'privacy_updateRedactionCandidates',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateRedactionCandidatesViewRequestData,
      },
    ],
    response: UpdateRedactionCandidatesViewResponseData,
    errors: [
      {
        status: 400,
        description: `Bad request`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Permission denied`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/schema/',
    alias: 'schema_retrieve',
    description: `OpenApi3 schema for this API. Format can be selected via content negotiation.

- YAML: application/vnd.oai.openapi
- JSON: application/vnd.oai.openapi+json`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'format',
        type: 'Query',
        schema: z.enum(['json', 'yaml']).optional(),
      },
      {
        name: 'lang',
        type: 'Query',
        schema: z
          .enum([
            'af',
            'ar',
            'ar-dz',
            'ast',
            'az',
            'be',
            'bg',
            'bn',
            'br',
            'bs',
            'ca',
            'ckb',
            'cs',
            'cy',
            'da',
            'de',
            'dsb',
            'el',
            'en',
            'en-au',
            'en-gb',
            'eo',
            'es',
            'es-ar',
            'es-co',
            'es-mx',
            'es-ni',
            'es-ve',
            'et',
            'eu',
            'fa',
            'fi',
            'fr',
            'fy',
            'ga',
            'gd',
            'gl',
            'he',
            'hi',
            'hr',
            'hsb',
            'hu',
            'hy',
            'ia',
            'id',
            'ig',
            'io',
            'is',
            'it',
            'ja',
            'ka',
            'kab',
            'kk',
            'km',
            'kn',
            'ko',
            'ky',
            'lb',
            'lt',
            'lv',
            'mk',
            'ml',
            'mn',
            'mr',
            'ms',
            'my',
            'nb',
            'ne',
            'nl',
            'nn',
            'os',
            'pa',
            'pl',
            'pt',
            'pt-br',
            'ro',
            'ru',
            'sk',
            'sl',
            'sq',
            'sr',
            'sr-latn',
            'sv',
            'sw',
            'ta',
            'te',
            'tg',
            'th',
            'tk',
            'tr',
            'tt',
            'udm',
            'ug',
            'uk',
            'ur',
            'uz',
            'vi',
            'zh-hans',
            'zh-hant',
          ])
          .optional(),
      },
    ],
    response: z.object({}).partial().passthrough(),
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
